import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
    Calculator, Plus, Save, FileText, Undo2, Printer, FileDown, LogOut, X,
    BookOpen, FileSpreadsheet, Scale, Receipt, CreditCard, Building2,
    Wallet, Users, ShoppingCart, ClipboardList, History, HelpCircle,
    ChevronRight, ChevronDown, ChevronLeft, Trash2, Sparkles, CheckCircle2, AlertCircle,
    Settings, Bell, Search, Menu, DollarSign, TrendingUp, Banknote, PiggyBank,
    Edit2, Lock, Home, ArrowRight, Play, CheckSquare, BarChart3, Shield, Eye, Send, RefreshCw
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { ScrollArea } from '../components/ui/scroll-area';
import { Dialog, DialogClose, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import ARAgingReport from './reports/ARAgingReport';
import APVouchersTab from './ap/APVouchersTab';
import APPaymentsTab from './ap/APPaymentsTab';
import APSummaryReport from './reports/APSummaryReport';
import APAgingReport from './reports/APAgingReport';
import APWithholdingReport from './reports/APWithholdingReport';
import ReportWorkflowTrail from './reports/ReportWorkflowTrail';
import ReportPeriodSelector, { getMonthDateRange, getMonthEndDate, getYearDateRange } from './reports/ReportPeriodSelector';
import ChartOfAccountsTab from './workspace/tabs/ChartOfAccountsTab';
import OpeningBalancesTab from './workspace/tabs/OpeningBalancesTab';
import SupplierListTab from './workspace/tabs/SupplierListTab';
import CustomerListTab from './workspace/tabs/CustomerListTab';
import BalanceSheetTab from './workspace/tabs/BalanceSheetTab';
import IncomeStatementTab from './workspace/tabs/IncomeStatementTab';
import ARSummaryTab from './workspace/tabs/ARSummaryTab';
import ARInvoicesTab from './workspace/tabs/ARInvoicesTab';
import ARCollectionsTab from './workspace/tabs/ARCollectionsTab';
import ReportErrorBoundary from './workspace/ReportErrorBoundary';
import useWorkspaceCourses from './workspace/useWorkspaceCourses';
import {
    buildNormalBalanceLookup,
    buildWorkspaceCourseUrl,
    buildWorkspaceTabUrl,
    getLedgerBalanceToneClass,
    getWorkspaceTabFromSearch,
    normalizeWorkspaceTab,
    shouldReplaceWorkspaceTabNavigation,
} from './workspace/workspacePresentation';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const dedupeDatabaseAccounts = (accounts = []) => {
    const seenCodes = new Set();
    return accounts.filter((account) => {
        const accountCode = String(account?.account_code || '').trim();
        if (!accountCode) return true;
        if (seenCodes.has(accountCode)) return false;
        seenCodes.add(accountCode);
        return true;
    });
};

const dedupeLegacyChartOfAccounts = (accounts = []) => {
    const accountMap = new Map();

    accounts.forEach((account) => {
        const accountCode = String(account?.code || '').trim();
        if (!accountCode) return;
        const existing = accountMap.get(accountCode);
        if (!existing) {
            accountMap.set(accountCode, {
                ...account,
                subs: dedupeLegacyChartOfAccounts(account.subs || []),
            });
            return;
        }

        const existingSubCodes = new Set((existing.subs || []).map(sub => String(sub?.code || '').trim()).filter(Boolean));
        const mergedSubs = [...(existing.subs || [])];
        (account.subs || []).forEach((sub) => {
            const subCode = String(sub?.code || '').trim();
            if (!subCode || existingSubCodes.has(subCode)) return;
            existingSubCodes.add(subCode);
            mergedSubs.push(sub);
        });
        existing.subs = mergedSubs;
    });

    return Array.from(accountMap.values());
};

const ACCOUNTS = ['Cash', 'Accounts Receivable', 'Inventory', 'Supplies', 'Equipment', 'Accumulated Depreciation', 'Accounts Payable', 'Notes Payable', 'Unearned Revenue', 'Owner\'s Capital', 'Owner\'s Drawing', 'Service Revenue', 'Sales Revenue', 'Rent Expense', 'Salary Expense', 'Utilities Expense', 'Supplies Expense'];

// Sidebar navigation structure - PRIMARY NAVIGATION
const SIDEBAR_NAV = [
    {
        id: 'setup',
        name: 'Setup',
        icon: Settings,
        defaultTab: 'coa',
        children: [
            { id: 'coa', label: 'Chart of Accounts', tab: 'coa' },
            { id: 'openingbalances', label: 'Opening Balance Setup', tab: 'openingbalances' },
            { id: 'suppliers', label: 'Supplier Master', tab: 'suppliers' },
            { id: 'customers', label: 'Customer Master', tab: 'customers' },
        ]
    },
    {
        id: 'record',
        name: 'Record',
        icon: Edit2,
        defaultTab: 'jv',
        children: [
            { id: 'jv', label: 'Journal Voucher', tab: 'jv' },
            { id: 'apvoucher', label: 'AP Voucher', tab: 'apvouchers' },
            { id: 'arvoucher', label: 'AR Invoice', tab: 'arinvoices' },
        ]
    },
    {
        id: 'review',
        name: 'Review',
        icon: CheckSquare,
        defaultTab: 'glentries',
        children: [
            { id: 'arapreview', label: 'AR/AP Review Queue', tab: 'arapreview', instructorOnly: true },
            { id: 'glentries', label: 'General Ledger', tab: 'glentries' },
            { id: 'trialbalance', label: 'Trial Balance', tab: 'trial' },
            { id: 'bankrecon', label: 'Bank Reconciliation', tab: 'bankrecon' },
            { id: 'audit', label: 'Audit Trail', tab: 'audit' },
        ]
    },
    {
        id: 'ar',
        name: 'Accounts Receivable',
        icon: Receipt,
        defaultTab: 'arinvoices',
        children: [
            { id: 'arinvoices', label: 'AR Invoices', tab: 'arinvoices' },
            { id: 'arcollections', label: 'Collections', tab: 'arcollections' },
            { id: 'arsummary', label: 'AR Summary', tab: 'arsummary' },
            { id: 'araging', label: 'AR Aging', tab: 'araging' },
        ]
    },
    {
        id: 'ap',
        name: 'Accounts Payable',
        icon: CreditCard,
        defaultTab: 'apvouchers',
        children: [
            { id: 'apvouchers', label: 'AP Vouchers', tab: 'apvouchers' },
            { id: 'appayments', label: 'Payments', tab: 'appayments' },
            { id: 'apsummary', label: 'AP Summary', tab: 'apsummary' },
            { id: 'apaging', label: 'AP Aging', tab: 'apaging' },
            { id: 'apwithholding', label: 'AP WTAX Report', tab: 'apwithholding' },
        ]
    },
    {
        id: 'report',
        name: 'Report',
        icon: BarChart3,
        defaultTab: 'incomestatement',
        children: [
            { id: 'incomestatement', label: 'Income Statement', tab: 'incomestatement' },
            { id: 'balancesheet', label: 'Balance Sheet', tab: 'balancesheet' },
            { id: 'cashflow', label: 'Cash Flow', tab: 'statements', disabled: true },
        ]
    },
];

const WORKSPACE_TAB_IDS = new Set([
    'home',
    ...SIDEBAR_NAV.flatMap(section => section.children.map(item => item.tab).filter(Boolean)),
]);

// Quick access shortcuts for bottom bar
const QUICK_ACCESS = [
    { id: 'jv', icon: Calculator, label: 'JV', tooltip: 'Journal Voucher', color: 'bg-blue-500', tab: 'jv' },
    { id: 'glentries', icon: BookOpen, label: 'GL', tooltip: 'General Ledger', color: 'bg-indigo-500', tab: 'glentries' },
    { id: 'tb', icon: Scale, label: 'TB', tooltip: 'Trial Balance', color: 'bg-purple-500', tab: 'trial' },
    { id: 'is', icon: TrendingUp, label: 'IS', tooltip: 'Income Statement', color: 'bg-emerald-500', tab: 'incomestatement' },
    { id: 'bs', icon: FileSpreadsheet, label: 'BS', tooltip: 'Balance Sheet', color: 'bg-teal-500', tab: 'balancesheet' },
    { id: 'divider1', divider: true },
    { id: 'supplier', icon: Users, label: 'Supplier', tooltip: 'Supplier Master', color: 'bg-amber-500', tab: 'suppliers' },
    { id: 'customer', icon: ShoppingCart, label: 'Customer', tooltip: 'Customer Master', color: 'bg-rose-500', tab: 'customers' },
    { id: 'divider2', divider: true },
    { id: 'audit', icon: History, label: 'Audit', tooltip: 'Audit Trail', color: 'bg-slate-500', tab: 'audit' },
];

// Module definitions for legacy compatibility
const MODULES = [
    { id: 'gl', name: 'General Ledger', icon: BookOpen, shortcut: 'GL' },
    { id: 'ap', name: 'Accounts Payable', icon: CreditCard, shortcut: 'AP' },
    { id: 'ar', name: 'Accounts Receivable', icon: Receipt, shortcut: 'AR' },
    { id: 'bank', name: 'Bank', icon: Building2, shortcut: 'Bank' },
    { id: 'utilities', name: 'Utilities', icon: Settings, shortcut: 'Util' },
];

// Toolbar buttons matching legacy prototype
const TOOLBAR_BUTTONS = [
    { id: 'supplier', icon: Users, label: 'Supplier', shortcut: 'F1' },
    { id: 'directpay', icon: Banknote, label: 'Direct Pay', shortcut: 'F2' },
    { id: 'pettycash', icon: PiggyBank, label: 'Petty Cash', shortcut: 'F3' },
    { id: 'payment', icon: DollarSign, label: 'Payment', shortcut: 'F4' },
    { id: 'multiple', icon: ClipboardList, label: 'Multiple', shortcut: 'F5' },
    { id: 'apvoucher', icon: FileText, label: 'AP Voucher', shortcut: 'F6' },
    { id: 'apsumm', icon: FileSpreadsheet, label: 'AP Summ', shortcut: 'F7' },
    { id: 'apa', icon: TrendingUp, label: 'AP A', shortcut: 'F8' },
    { id: 'divider1', divider: true },
    { id: 'jvdpapp', icon: Calculator, label: 'JV DP App', shortcut: 'F9' },
    { id: 'pettyapp', icon: Wallet, label: 'Petty App', shortcut: 'F10' },
    { id: 'divider2', divider: true },
    { id: 'unpost', icon: Undo2, label: 'Unpost', shortcut: 'Ctrl+U' },
    { id: 'logout', icon: LogOut, label: 'Logout', shortcut: 'Ctrl+L' },
    { id: 'close', icon: X, label: 'Close', shortcut: 'Esc', danger: true },
];

// Bottom quick access buttons
const BOTTOM_BUTTONS = [
    { id: 'jv', icon: Calculator, label: 'JV', color: 'bg-blue-500' },
    { id: 'glentries', icon: BookOpen, label: 'General Ledger', color: 'bg-indigo-500' },
    { id: 'tb', icon: Scale, label: 'TB', color: 'bg-purple-500' },
    { id: 'bs', icon: FileSpreadsheet, label: 'BS', color: 'bg-teal-500' },
    { id: 'is', icon: TrendingUp, label: 'IS', color: 'bg-emerald-500' },
    { id: 'sl', icon: ClipboardList, label: 'SL', color: 'bg-cyan-500' },
    { id: 'gjournal', icon: FileText, label: 'Gjournal', color: 'bg-slate-500' },
    { id: 'budget', icon: PiggyBank, label: 'Budget', color: 'bg-amber-500' },
    { id: 'divider', divider: true },
    { id: 'customer', icon: Users, label: 'Customer', color: 'bg-rose-500' },
    { id: 'invoice', icon: Receipt, label: 'Invoice', color: 'bg-pink-500' },
    { id: 'direct', icon: Banknote, label: 'Direct', color: 'bg-orange-500' },
    { id: 'multiple', icon: ClipboardList, label: 'Multiple', color: 'bg-lime-500' },
    { id: 'jvdcapp', icon: Calculator, label: 'JV DC App', color: 'bg-sky-500' },
    { id: 'arsumm', icon: FileSpreadsheet, label: 'AR Summ', color: 'bg-violet-500' },
    { id: 'araging', icon: History, label: 'AR Aging', color: 'bg-fuchsia-500' },
];

const AR_AP_REVIEW_ENDPOINTS = {
    arinvoices: { base: '/ar/invoices', label: 'AR Invoice' },
    arcollections: { base: '/ar/collections', label: 'AR Collection' },
    apvouchers: { base: '/ap/vouchers', label: 'AP Voucher' },
    appayments: { base: '/ap/payments', label: 'AP Payment' },
};

function ARAPReviewQueueTab({ selectedCourse, fmt, user, onRefresh, onOpenRecord }) {
    const [queue, setQueue] = useState([]);
    const [summary, setSummary] = useState({ pending: 0, ar: 0, ap: 0 });
    const [loading, setLoading] = useState(false);
    const [actionKey, setActionKey] = useState('');
    const [postTarget, setPostTarget] = useState(null);
    const [returnTarget, setReturnTarget] = useState(null);
    const [returnReason, setReturnReason] = useState('');
    const [moduleFilter, setModuleFilter] = useState('all');
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');

    const canReview = user?.role === 'instructor' || user?.role === 'admin';

    const fetchQueue = useCallback(async () => {
        if (!canReview) {
            setQueue([]);
            setSummary({ pending: 0, ar: 0, ap: 0 });
            return;
        }
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const params = new URLSearchParams();
            if (selectedCourse) params.append('course_id', selectedCourse);
            if (moduleFilter !== 'all') params.append('module', moduleFilter);
            if (dateFrom) params.append('date_from', dateFrom);
            if (dateTo) params.append('date_to', dateTo);
            const response = await axios.get(`${API}/controls/ar-ap-review-queue${params.toString() ? `?${params.toString()}` : ''}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setQueue(response.data?.items || []);
            setSummary(response.data?.summary || { pending: 0, ar: 0, ap: 0 });
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to load AR/AP review queue');
            setQueue([]);
        } finally {
            setLoading(false);
        }
    }, [canReview, selectedCourse, moduleFilter, dateFrom, dateTo]);

    useEffect(() => {
        fetchQueue();
    }, [fetchQueue]);

    useEffect(() => {
        if (!canReview) return undefined;
        const refreshVisibleQueue = () => fetchQueue();
        const refreshWhenVisible = () => {
            if (document.visibilityState === 'visible') fetchQueue();
        };
        window.addEventListener('focus', refreshVisibleQueue);
        document.addEventListener('visibilitychange', refreshWhenVisible);
        return () => {
            window.removeEventListener('focus', refreshVisibleQueue);
            document.removeEventListener('visibilitychange', refreshWhenVisible);
        };
    }, [canReview, fetchQueue]);

    const clearFilters = () => {
        setModuleFilter('all');
        setDateFrom('');
        setDateTo('');
    };

    const buildActionUrl = (item, action) => {
        const endpoint = AR_AP_REVIEW_ENDPOINTS[item.workspace_tab];
        const params = new URLSearchParams({ student_id: item.student_id });
        return `${API}${endpoint.base}/${encodeURIComponent(item.record_id)}/${action}?${params.toString()}`;
    };

    const handleApprovePost = async (item = postTarget) => {
        if (!item) return;
        const endpoint = AR_AP_REVIEW_ENDPOINTS[item.workspace_tab];
        if (!endpoint) return;
        const key = `post-${item.id}`;
        setActionKey(key);
        try {
            const token = localStorage.getItem('token');
            const response = await axios.put(buildActionUrl(item, 'post'), {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success(
                `${endpoint.label} ${item.record_number} approved and posted. It now appears in the student GL, Trial Balance, and related AR/AP reports.`
            );
            if (response.data?.next_location) {
                toast.info(`Find it now: ${response.data.next_location}`);
            }
            setPostTarget(null);
            await fetchQueue();
            if (onRefresh) onRefresh();
        } catch (error) {
            toast.error(error.response?.data?.detail || `Failed to post ${endpoint.label}`);
        } finally {
            setActionKey('');
        }
    };

    const handleReturn = async () => {
        const item = returnTarget;
        const endpoint = item ? AR_AP_REVIEW_ENDPOINTS[item.workspace_tab] : null;
        if (!item || !endpoint || !returnReason.trim()) return;
        const key = `return-${item.id}`;
        setActionKey(key);
        try {
            const token = localStorage.getItem('token');
            const response = await axios.put(buildActionUrl(item, 'return'), { reason: returnReason }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success(
                `${endpoint.label} ${item.record_number} returned for revision. The student can find it as Returned for Revision in their ${endpoint.label} list, read the row/details feedback, revise, then resubmit. No GL/Trial Balance impact until posted.`
            );
            if (response.data?.gl_impact) {
                toast.info(response.data.gl_impact);
            }
            setReturnTarget(null);
            setReturnReason('');
            await fetchQueue();
            if (onRefresh) onRefresh();
        } catch (error) {
            toast.error(error.response?.data?.detail || `Failed to return ${endpoint.label}`);
        } finally {
            setActionKey('');
        }
    };

    if (!canReview) {
        return (
            <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid="arap-review-queue">
                <h2 className="text-xl font-bold text-slate-900">AR/AP Review Queue</h2>
                <p className="mt-2 text-sm text-slate-500">Only instructors and admins can review submitted AR/AP student work.</p>
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="arap-review-queue">
            <div className="rounded-xl border border-blue-200 bg-blue-50 p-5">
                <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                    <div>
                        <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
                            <CheckSquare className="w-5 h-5 text-[#2F8FCB]" />
                            AR/AP Review Queue
                        </h2>
                        <p className="mt-1 text-sm text-blue-900">
                            Submitted student invoices, collections, vouchers, and payments wait here until an instructor/admin approves and posts or returns them with feedback.
                        </p>
                    </div>
                    <Button variant="outline" onClick={fetchQueue} disabled={loading} data-testid="refresh-arap-review-queue">
                        {loading ? 'Refreshing...' : 'Refresh Queue'}
                    </Button>
                </div>
                <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
                    <div className="rounded-lg border border-blue-100 bg-white p-3">
                        <p className="text-xs font-semibold uppercase text-slate-500">Pending</p>
                        <p className="mt-1 text-2xl font-bold text-[#0F2A44]">{summary.pending || queue.length}</p>
                    </div>
                    <div className="rounded-lg border border-blue-100 bg-white p-3">
                        <p className="text-xs font-semibold uppercase text-slate-500">AR Items</p>
                        <p className="mt-1 text-2xl font-bold text-[#0F2A44]">{summary.ar || 0}</p>
                    </div>
                    <div className="rounded-lg border border-blue-100 bg-white p-3">
                        <p className="text-xs font-semibold uppercase text-slate-500">AP Items</p>
                        <p className="mt-1 text-2xl font-bold text-[#0F2A44]">{summary.ap || 0}</p>
                    </div>
                </div>
            </div>

            <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="arap-review-filters">
                <div className="grid gap-3 lg:grid-cols-[220px_180px_180px_auto] lg:items-end">
                    <div>
                        <Label htmlFor="arap-module-filter">Module</Label>
                        <Select value={moduleFilter} onValueChange={setModuleFilter}>
                            <SelectTrigger id="arap-module-filter" className="mt-1" data-testid="arap-module-filter">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All AR/AP</SelectItem>
                                <SelectItem value="ar">AR only</SelectItem>
                                <SelectItem value="ap">AP only</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label htmlFor="arap-date-from">Source date from</Label>
                        <Input
                            id="arap-date-from"
                            type="date"
                            value={dateFrom}
                            onChange={(event) => setDateFrom(event.target.value)}
                            className="mt-1"
                            data-testid="arap-date-from"
                        />
                    </div>
                    <div>
                        <Label htmlFor="arap-date-to">Source date to</Label>
                        <Input
                            id="arap-date-to"
                            type="date"
                            value={dateTo}
                            onChange={(event) => setDateTo(event.target.value)}
                            className="mt-1"
                            data-testid="arap-date-to"
                        />
                    </div>
                    <Button
                        type="button"
                        variant="outline"
                        onClick={clearFilters}
                        disabled={moduleFilter === 'all' && !dateFrom && !dateTo}
                        data-testid="clear-arap-review-filters"
                    >
                        Clear Filters
                    </Button>
                </div>
                <p className="mt-3 text-xs text-slate-500">
                    Filters use the source document date, not the submission timestamp. Course filtering follows the workspace course selector above.
                </p>
            </div>

            <ReportWorkflowTrail
                title="AR/AP review trail"
                testId="arap-review-workflow-trail"
                scope="Instructor/Admin queue"
                items={[
                    { label: 'What changed', value: 'Students submit saved AR/AP source documents from their own workspace.' },
                    { label: 'Where it goes', value: 'Submitted work appears here under Review > AR/AP Review Queue.' },
                    { label: 'Who reviews next', value: 'Instructor/admin checks source evidence, then approves/posts or returns with feedback.' },
                    { label: 'GL and TB', value: 'Submitted work is evidence only. Approve & Post writes GL entries into the selected student ledger and affects Trial Balance.' },
                    { label: 'Next safe action', value: 'Open the source, confirm student/course/counterparty/date/amount, then Approve & Post only when correct.' },
                ]}
            />

            <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
                <div className="border-b border-slate-200 bg-slate-50 px-5 py-4">
                    <h3 className="font-semibold text-slate-900">Submitted AR/AP student work</h3>
                    <p className="text-sm text-slate-500">Returned items leave this queue and appear in the student list as Returned for Revision.</p>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full min-w-[1120px] text-sm">
                        <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                            <tr>
                                <th className="px-4 py-3 text-left">Student</th>
                                <th className="px-4 py-3 text-left">Course</th>
                                <th className="px-4 py-3 text-left">Source</th>
                                <th className="px-4 py-3 text-left">Counterparty</th>
                                <th className="px-4 py-3 text-left">Date</th>
                                <th className="px-4 py-3 text-right">Amount</th>
                                <th className="px-4 py-3 text-left">Status</th>
                                <th className="sticky right-0 z-10 bg-slate-50 px-4 py-3 text-center shadow-lg">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {loading ? (
                                <tr><td colSpan="8" className="px-4 py-8 text-center text-slate-400">Loading review queue...</td></tr>
                            ) : queue.length === 0 ? (
                                <tr><td colSpan="8" className="px-4 py-8 text-center text-slate-400">No submitted AR/AP work waiting for review.</td></tr>
                            ) : (
                                queue.map((item) => {
                                    const endpoint = AR_AP_REVIEW_ENDPOINTS[item.workspace_tab];
                                    const isPosting = actionKey === `post-${item.id}`;
                                    const isReturning = actionKey === `return-${item.id}`;
                                    return (
                                        <tr key={item.id} className="hover:bg-blue-50/40">
                                            <td className="px-4 py-3">
                                                <p className="font-medium text-slate-900">{item.student_name || 'Student'}</p>
                                                <p className="text-xs text-slate-500">{item.student_email || item.student_id}</p>
                                            </td>
                                            <td className="px-4 py-3">
                                                <p className="font-medium text-slate-700">{item.course_code || 'Course'}</p>
                                                <p className="max-w-[180px] truncate text-xs text-slate-500">{item.course_title || item.course_id || 'Enrolled course'}</p>
                                            </td>
                                            <td className="px-4 py-3">
                                                <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${
                                                    item.module === 'ar' ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'
                                                }`}>
                                                    {item.record_type}
                                                </span>
                                                <p className="mt-1 font-mono text-xs text-slate-600">{item.record_number}</p>
                                                {item.source_reference && <p className="text-xs text-slate-400">{item.source_reference}</p>}
                                            </td>
                                            <td className="px-4 py-3">
                                                <p className="text-xs text-slate-500">{item.counterparty_label}</p>
                                                <p className="font-medium text-slate-800">{item.counterparty_name || '-'}</p>
                                            </td>
                                            <td className="px-4 py-3">
                                                <p>{item.record_date || '-'}</p>
                                                <p className="text-xs text-slate-500">{item.submitted_at ? new Date(item.submitted_at).toLocaleString() : 'Submitted'}</p>
                                            </td>
                                            <td className="px-4 py-3 text-right font-mono">{fmt(item.amount || 0)}</td>
                                            <td className="px-4 py-3">
                                                <span className="rounded-full bg-blue-100 px-2 py-1 text-xs font-semibold text-blue-700">
                                                    {item.status_label || 'Submitted for Review'}
                                                </span>
                                                <p className="mt-1 text-xs text-slate-600">{item.review_state || 'Needs instructor/admin review'}</p>
                                                <p className="mt-1 max-w-[220px] text-xs text-slate-500">
                                                    {item.next_safe_action || item.next_action || 'Open the source, then approve/post or return with feedback.'}
                                                </p>
                                                <p className="mt-1 max-w-[220px] text-xs text-blue-700">
                                                    {item.downstream_impact || item.gl_impact || 'No GL or Trial Balance impact until posted.'}
                                                </p>
                                            </td>
                                            <td className="sticky right-0 bg-white px-4 py-3 shadow-lg">
                                                <div className="flex items-center justify-center gap-2">
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        onClick={() => onOpenRecord(item)}
                                                        data-testid={`open-arap-source-${item.id}`}
                                                    >
                                                        Open Source
                                                    </Button>
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        className="border-orange-200 text-orange-700 hover:bg-orange-50"
                                                        onClick={() => setReturnTarget(item)}
                                                        disabled={isPosting || isReturning}
                                                        data-testid={`return-arap-${item.id}`}
                                                    >
                                                        Return
                                                    </Button>
                                                    <Button
                                                        size="sm"
                                                        className="bg-emerald-600 hover:bg-emerald-700"
                                                        onClick={() => setPostTarget(item)}
                                                        disabled={!endpoint || isPosting || isReturning}
                                                        data-testid={`post-arap-${item.id}`}
                                                    >
                                                        Approve & Post
                                                    </Button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            <Dialog open={!!returnTarget} onOpenChange={(open) => {
                if (!open) {
                    setReturnTarget(null);
                    setReturnReason('');
                }
            }}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="text-orange-700">Return AR/AP Work</DialogTitle>
                        <DialogDescription>
                            Return the submitted AR/AP source document to the student without posting to GL or Trial Balance.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3 text-sm text-slate-600">
                        <p>
                            This returns the submitted source document to the student as an editable draft. It will not post to GL or Trial Balance.
                        </p>
                        {returnTarget && (
                            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                                <p><strong>Student:</strong> {returnTarget.student_name || 'Student'}</p>
                                <p><strong>Source:</strong> {returnTarget.record_type} {returnTarget.record_number}</p>
                                <p><strong>Amount:</strong> {fmt(returnTarget.amount || 0)}</p>
                            </div>
                        )}
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="arap-return-reason">Feedback for student *</Label>
                        <Textarea
                            id="arap-return-reason"
                            value={returnReason}
                            onChange={(event) => setReturnReason(event.target.value)}
                            placeholder="Example: Please correct the date/supporting reference, then resubmit."
                            rows={3}
                            data-testid="arap-return-reason"
                        />
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setReturnTarget(null)} disabled={!!actionKey}>Cancel</Button>
                        <Button
                            className="bg-orange-600 hover:bg-orange-700"
                            onClick={handleReturn}
                            disabled={!returnReason.trim() || !!actionKey}
                            data-testid="confirm-arap-return"
                        >
                            {actionKey ? 'Returning...' : 'Return to Student'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={!!postTarget} onOpenChange={(open) => {
                if (!open && !actionKey) {
                    setPostTarget(null);
                }
            }}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="text-emerald-700">Approve & Post AR/AP Work</DialogTitle>
                        <DialogDescription>
                            Approve the submitted AR/AP source document and create posted GL evidence for the selected student.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3 text-sm text-slate-600">
                        <p>
                            This approves the submitted source document and creates posted GL evidence for the student's workspace.
                        </p>
                        {postTarget && (
                            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                                <p><strong>Student:</strong> {postTarget.student_name || 'Student'}</p>
                                <p><strong>Source:</strong> {postTarget.record_type} {postTarget.record_number}</p>
                                <p><strong>Date:</strong> {postTarget.record_date || '-'}</p>
                                <p><strong>Amount:</strong> {fmt(postTarget.amount || 0)}</p>
                            </div>
                        )}
                        <p className="text-emerald-700">
                            After posting, find it in the source list, Review &gt; General Ledger, Trial Balance, and related AR/AP reports.
                        </p>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setPostTarget(null)} disabled={!!actionKey}>Cancel</Button>
                        <Button
                            className="bg-emerald-600 hover:bg-emerald-700"
                            onClick={() => handleApprovePost(postTarget)}
                            disabled={!postTarget || !!actionKey}
                            data-testid="confirm-arap-post"
                        >
                            {actionKey === `post-${postTarget?.id}` ? 'Posting...' : 'Confirm Post'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

export default function AccountingWorkspace() {
    const { user } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const isAuditor = user?.role === 'auditor';
    const canReviewAccountingQueues = user?.role === 'instructor' || user?.role === 'admin';
    const { courses, selectedCourse, setSelectedCourse, loading } = useWorkspaceCourses({
        api: API, userId: user?.id, role: user?.role, search: location.search,
    });
    const [entries, setEntries] = useState([]);
    const [ledger, setLedger] = useState({});
    const [trialBalance, setTrialBalance] = useState({ accounts: [], total_debit: 0, total_credit: 0 });
    const [aiLoading, setAiLoading] = useState(false);
    const [activeModule, setActiveModule] = useState('gl');
    const [activeTab, setActiveTab] = useState(() => getWorkspaceTabFromSearch(location.search, WORKSPACE_TAB_IDS));
    const activeTabRef = useRef(activeTab);
    const [openNavSection, setOpenNavSection] = useState(null);
    const [showHelp, setShowHelp] = useState(() => {
        const saved = localStorage.getItem('macesuite_guide_open');
        return saved !== null ? JSON.parse(saved) : false;
    });
    const [expandedSections, setExpandedSections] = useState(['setup', 'record', 'review']); // Sidebar sections
    const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
        // Persist sidebar state in localStorage, default to collapsed
        const saved = localStorage.getItem('macesuite_sidebar_collapsed');
        return saved !== null ? JSON.parse(saved) : true;
    });
    const [currentStep, setCurrentStep] = useState(1);
    const [auditLogs, setAuditLogs] = useState([]);
    const [chartOfAccounts, setChartOfAccounts] = useState([]);
    const [journalVouchers, setJournalVouchers] = useState([]);
    
    const [form, setForm] = useState({
        date: new Date().toISOString().split('T')[0],
        description: '',
        debit_account: '',
        credit_account: '',
        amount: ''
    });
    const [suppliers, setSuppliers] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [atcCodes, setAtcCodes] = useState([]);
    const [accounts, setAccounts] = useState([]);  // Database-backed accounts for AP vouchers
    const [permissions, setPermissions] = useState(null);  // RBAC permissions
    const [controlSummary, setControlSummary] = useState(null);
    const [periodCloseSummary, setPeriodCloseSummary] = useState(null);
    const [accountingActivity, setAccountingActivity] = useState(null);
    const [practiceCleanupPreview, setPracticeCleanupPreview] = useState(null);
    const [practiceCleanupLoading, setPracticeCleanupLoading] = useState(false);
    const [showPracticeCleanupDialog, setShowPracticeCleanupDialog] = useState(false);
    const [practiceCleanupConfirm, setPracticeCleanupConfirm] = useState('');
    const [trialBalanceDate, setTrialBalanceDate] = useState(new Date().toISOString().split('T')[0]);
    const [closeReadinessPeriod, setCloseReadinessPeriod] = useState(new Date().toISOString().slice(0, 7));
    const [bankReconRequestedPeriod, setBankReconRequestedPeriod] = useState('');
    const [glTraceRequest, setGlTraceRequest] = useState(null);
    const [trialBalanceTraceRequest, setTrialBalanceTraceRequest] = useState(null);
    const [jvSourceOpenRequest, setJvSourceOpenRequest] = useState(null);
    const [moduleSourceOpenRequest, setModuleSourceOpenRequest] = useState(null);
    const routeTargetApplied = useRef('');
    const routeTabSyncPending = useRef(false);
    const fetchDataRunId = useRef(0);

    // Student Workspace Viewer (instructor only)
    const [studentView, setStudentView] = useState(null);
    const [enrolledStudents, setEnrolledStudents] = useState([]);
    const studentViewId = studentView?.id || '';
    const studentViewName = studentView?.full_name || '';

    const routeSourceOpenRequest = useMemo(() => {
        const params = new URLSearchParams(location.search);
        if (params.get('open_source') !== '1') return { jv: null, module: null };

        const requestedTab = normalizeWorkspaceTab(params.get('tab'));
        const requestedStudentId = params.get('student_id');
        const requestedCourseId = params.get('course_id');
        const requestedSourceId = params.get('source_id') || '';
        const requestedRefNo = params.get('ref_no') || '';
        if (!requestedTab || (!requestedSourceId && !requestedRefNo)) return { jv: null, module: null };

        const routeUsesSelectedStudent =
            requestedStudentId && (user?.role === 'instructor' || user?.role === 'admin' || user?.role === 'auditor');
        if (routeUsesSelectedStudent && studentView?.id !== requestedStudentId) {
            return { jv: null, module: null };
        }
        if (requestedCourseId && selectedCourse !== requestedCourseId) {
            return { jv: null, module: null };
        }

        const moduleSourceTypes = {
            arinvoices: 'ar_invoice',
            arcollections: 'ar_collection',
            apvouchers: 'ap_voucher',
            appayments: 'ap_payment',
        };
        const requestedSourceType = params.get('source_type');
        const requestedSourceLabel = params.get('source_label') || '';
        const requestedSourceOrigin = params.get('source_origin') || 'Dashboard activity';
        const sourceType = requestedSourceType || moduleSourceTypes[requestedTab];
        const requestId = `route-source-${requestedTab}-${sourceType || 'source'}-${requestedSourceId || requestedRefNo}-${requestedStudentId || ''}-${requestedCourseId || ''}`;
        const sourceImpact = 'Opened from the dashboard activity trail. Source review does not change GL, Trial Balance, reports, or source records.';

        if (requestedTab === 'jv' || sourceType === 'jv') {
            return {
                jv: {
                    id: requestId,
                    type: 'jv',
                    jvId: requestedSourceId,
                    refNo: requestedRefNo,
                    sourceLabel: requestedSourceLabel || 'Journal Voucher',
                    sourceLocation: requestedSourceOrigin,
                    sourceImpact,
                    sourceNextAction: 'Review the source JV, then use General Ledger or Trial Balance to verify posted impact.',
                    sourceOrigin: requestedSourceOrigin,
                },
                module: null,
            };
        }

        if (sourceType && moduleSourceTypes[requestedTab]) {
            return {
                jv: null,
                module: {
                    id: requestId,
                    sourceType,
                    sourceId: requestedSourceId,
                    refNo: requestedRefNo,
                    label: requestedSourceLabel || sourceType.replace('_', ' '),
                    sourceLocation: requestedSourceOrigin,
                    sourceImpact,
                    sourceNextAction: 'Review the source status and evidence, then use General Ledger or Trial Balance to verify posted impact.',
                    sourceOrigin: requestedSourceOrigin,
                },
            };
        }

        return { jv: null, module: null };
    }, [location.search, selectedCourse, studentView?.id, user?.role]);

    const visibleSidebarNav = useMemo(() => (
        SIDEBAR_NAV
            .map(section => ({
                ...section,
                children: section.children.filter(item => !item.instructorOnly || canReviewAccountingQueues),
            }))
            .filter(section => section.children.length > 0 && (!section.instructorOnly || canReviewAccountingQueues))
    ), [canReviewAccountingQueues]);

    const effectiveJvSourceOpenRequest = jvSourceOpenRequest || routeSourceOpenRequest.jv;
    const effectiveModuleSourceOpenRequest = moduleSourceOpenRequest || routeSourceOpenRequest.module;

    useEffect(() => {
        activeTabRef.current = activeTab;
    }, [activeTab]);

    useEffect(() => {
        if (location.pathname !== '/workspace') return;
        const routeTab = getWorkspaceTabFromSearch(location.search, WORKSPACE_TAB_IDS);
        if (activeTabRef.current === routeTab) return;
        routeTabSyncPending.current = true;
        setActiveTab(routeTab);
    }, [location.pathname, location.search]);

    useEffect(() => {
        if (location.pathname !== '/workspace') return;
        const nextUrl = buildWorkspaceTabUrl({
            pathname: location.pathname,
            search: location.search,
            hash: location.hash,
            tab: activeTab,
            fallbackCourseId: selectedCourse,
        });
        const currentUrl = `${location.pathname}${location.search}${location.hash}`;
        if (nextUrl !== currentUrl) {
            if (routeTabSyncPending.current) {
                routeTabSyncPending.current = false;
                return;
            }
            navigate(nextUrl, {
                replace: (!!selectedCourse && !new URLSearchParams(location.search).get('course_id')) || shouldReplaceWorkspaceTabNavigation({
                    search: location.search,
                    validTabs: WORKSPACE_TAB_IDS,
                }),
            });
        }
    }, [activeTab, location.hash, location.pathname, location.search, navigate, selectedCourse]);

    const reviewableStudents = useMemo(() => {
        const seen = new Set();
        return enrolledStudents.filter((student) => {
            if (selectedCourse && student.course_id && student.course_id !== selectedCourse) {
                return false;
            }
            if (seen.has(student.id)) {
                return false;
            }
            seen.add(student.id);
            return true;
        });
    }, [enrolledStudents, selectedCourse]);

    useEffect(() => {
        if (!studentView?.id) return;
        if (user?.role !== 'instructor' && user?.role !== 'admin') return;
        if (reviewableStudents.length === 0) return;
        const matchingStudent = reviewableStudents.find((student) => student.id === studentView.id);
        if (!matchingStudent) {
            setStudentView(null);
        } else if (matchingStudent.course_id !== studentView.course_id) {
            setStudentView(matchingStudent);
        }
    }, [reviewableStudents, studentView, user?.role]);

    const focusStudentWorkspace = useCallback((record) => {
        if (!record?.student_id || record.student_id === user?.id) return;
        if (record.course_id && record.course_id !== selectedCourse) {
            setSelectedCourse(record.course_id);
        }
        const matchingStudent = reviewableStudents.find((student) => student.id === record.student_id);
        setStudentView(prev => {
            const fallbackName = record.student_name || record.submitted_by_name || record.processed_by || prev?.full_name || 'Student';
            if (prev?.id === record.student_id) {
                return {
                    ...prev,
                    full_name: fallbackName,
                    email: record.student_email || prev.email || '',
                    course_id: record.course_id || prev.course_id || selectedCourse,
                    course_code: record.course_code || prev.course_code || '',
                    course_title: record.course_title || prev.course_title || '',
                };
            }
            return matchingStudent || {
                id: record.student_id,
                full_name: fallbackName,
                email: record.student_email || '',
                course_id: record.course_id || selectedCourse,
                course_code: record.course_code || '',
                course_title: record.course_title || '',
            };
        });
    }, [reviewableStudents, selectedCourse, setSelectedCourse, user?.id]);

    const resolveStudentViewFromScope = useCallback((scope) => {
        if (!scope?.owner_id || !scope?.owner_name || scope.scope_type !== 'student_workspace') return;
        setStudentView(prev => {
            if (!prev || prev.id !== scope.owner_id || prev.full_name === scope.owner_name) return prev;
            return {
                ...prev,
                full_name: scope.owner_name,
                course_id: prev.course_id || selectedCourse,
            };
        });
    }, [selectedCourse]);

    const handleCourseChange = useCallback((courseId) => {
        if (courseId === selectedCourse || !courses.some(course => course.id === courseId)) return;
        const nextStudent = studentView?.id
            ? enrolledStudents.find(student => student.id === studentView.id && student.course_id === courseId)
            : null;
        setSelectedCourse(courseId);
        setStudentView(nextStudent || null);
        setJvSourceOpenRequest(null);
        setModuleSourceOpenRequest(null);
        setGlTraceRequest(null);
        setTrialBalanceTraceRequest(null);
        navigate(buildWorkspaceCourseUrl({
            pathname: location.pathname, search: location.search, hash: location.hash,
            courseId, studentId: nextStudent?.id,
        }));
    }, [courses, enrolledStudents, location.hash, location.pathname, location.search, navigate, selectedCourse, setSelectedCourse, studentView?.id]);

    useEffect(() => {
        const token = localStorage.getItem('token');
        
        // Fetch Chart of Accounts
        axios.get(`${API}/coa`, { headers: { Authorization: `Bearer ${token}` } }).then(res => {
            setChartOfAccounts(dedupeLegacyChartOfAccounts(res.data));
        }).catch(() => {});
        
        // Fetch database accounts for AP vouchers
        axios.get(`${API}/accounts`, { headers: { Authorization: `Bearer ${token}` } }).then(res => {
            setAccounts(dedupeDatabaseAccounts(res.data));
        }).catch(() => {});
        
        // Fetch ATC Codes
        axios.get(`${API}/atc-codes`).then(res => {
            setAtcCodes(res.data);
        }).catch(() => {});
        
        // Fetch RBAC permissions
        axios.get(`${API}/auth/permissions`, { headers: { Authorization: `Bearer ${token}` } }).then(res => {
            setPermissions(res.data);
        }).catch(() => {
            // Default to student permissions if fetch fails
            setPermissions({
                role: 'student',
                ap: { can_create: true, can_edit_draft: true, can_post: false, can_cancel: false, can_unpost: false, can_view_reports: true },
                ar: { can_create: true, can_edit_draft: true, can_post: false, can_cancel: false, can_unpost: false, can_view_reports: true },
                jv: { can_create: true, can_edit_draft: true, can_post: false, can_unpost: false }
            });
        });

        // Fetch enrolled students (instructor only)
        if (user?.role === 'instructor' || user?.role === 'admin') {
            axios.get(`${API}/instructor/students`, { headers: { Authorization: `Bearer ${token}` } }).then(res => {
                setEnrolledStudents(res.data);
            }).catch(() => {});
        }
    }, [location.search, user?.role]);

    useEffect(() => {
        const routeTargetKey = location.search || '__workspace_default__';
        if (routeTargetApplied.current === routeTargetKey) return;

        const params = new URLSearchParams(location.search);
        const requestedTab = normalizeWorkspaceTab(params.get('tab'));
        const requestedStudentId = params.get('student_id');
        const requestedCourseId = params.get('course_id');
        const requestedCourseCode = params.get('course_code') || '';
        const requestedCourseTitle = params.get('course_title') || '';
        const shouldOpenSource = params.get('open_source') === '1';
        if (!requestedTab && !requestedStudentId && !requestedCourseId) {
            routeTargetApplied.current = routeTargetKey;
            return;
        }

        if (requestedCourseId && selectedCourse !== requestedCourseId) {
            const requestedCourse = courses.find(course => course.id === requestedCourseId);
            if (requestedCourse) {
                setSelectedCourse(requestedCourse.id);
                return;
            }
            if ((user?.role === 'admin' || user?.role === 'auditor') && requestedStudentId) {
                setSelectedCourse(requestedCourseId);
                return;
            }
        }

        // History may leave selected-student review for a different course.
        // A route without a student must not retain the previous course's student.
        if (requestedCourseId && !requestedStudentId && studentView?.id && studentView.course_id !== requestedCourseId) {
            setStudentView(null);
        }

        if (requestedTab && WORKSPACE_TAB_IDS.has(requestedTab)) {
            setActiveTab(requestedTab);
        }

        if (requestedStudentId && (user?.role === 'instructor' || user?.role === 'admin' || user?.role === 'auditor')) {
            if (user?.role === 'instructor' && enrolledStudents.length === 0) return;
            const matchingStudent =
                enrolledStudents.find(student => student.id === requestedStudentId && (!requestedCourseId || student.course_id === requestedCourseId)) ||
                enrolledStudents.find(student => student.id === requestedStudentId);
            if (matchingStudent || user?.role === 'admin' || user?.role === 'auditor') {
                const student = matchingStudent || {
                    id: requestedStudentId,
                    full_name: 'Selected Student',
                    email: '',
                    course_id: requestedCourseId || selectedCourse,
                    course_code: requestedCourseCode,
                    course_title: requestedCourseTitle,
                };
                setStudentView(student);
                const tabLabel = visibleSidebarNav.flatMap(section => section.children).find(item => item.tab === requestedTab)?.label || 'Workspace';
                toast.info(`Reviewing ${student.full_name}. Opened ${tabLabel} from the accounting activity trail.`);
            }
        }

        const routeUsesSelectedStudent =
            requestedStudentId && (user?.role === 'instructor' || user?.role === 'admin' || user?.role === 'auditor');
        if (shouldOpenSource && routeUsesSelectedStudent && studentView?.id !== requestedStudentId) {
            return;
        }

        routeTargetApplied.current = routeTargetKey;
    }, [location.search, courses, selectedCourse, setSelectedCourse, enrolledStudents, user?.role, studentView?.id, studentView?.course_id, visibleSidebarNav]);

    const fetchPracticeCleanupPreview = useCallback(async () => {
        const token = localStorage.getItem('token');
        const params = new URLSearchParams();
        if (studentView?.id) params.append('student_id', studentView.id);
        const query = params.toString();
        const response = await axios.get(`${API}/controls/practice-cleanup/preview${query ? `?${query}` : ''}`, {
            headers: { Authorization: `Bearer ${token}` },
        });
        setPracticeCleanupPreview(response.data);
        return response.data;
    }, [studentView?.id]);

    const fetchData = useCallback(async () => {
        const token = localStorage.getItem('token');
        const runId = fetchDataRunId.current + 1;
        fetchDataRunId.current = runId;
        const isCurrentFetch = () => fetchDataRunId.current === runId;
        const controlQuery = studentViewId ? `?student_id=${encodeURIComponent(studentViewId)}` : '';
        axios.get(`${API}/controls/approval-summary${controlQuery}`, {
            headers: { Authorization: `Bearer ${token}` }
        }).then(res => {
            if (!isCurrentFetch()) return;
            setControlSummary(res.data);
        }).catch(() => {
            if (!isCurrentFetch()) return;
            setControlSummary(null);
        });
        const closeParams = new URLSearchParams();
        if (studentViewId) closeParams.append('student_id', studentViewId);
        const [closeYear, closeMonth] = String(closeReadinessPeriod || '').split('-');
        if (closeYear && closeMonth) {
            closeParams.append('year', closeYear);
            closeParams.append('month', String(Number(closeMonth)));
        }
        const closeQuery = closeParams.toString();
        axios.get(`${API}/controls/period-close-summary${closeQuery ? `?${closeQuery}` : ''}`, {
            headers: { Authorization: `Bearer ${token}` }
        }).then(res => {
            if (!isCurrentFetch()) return;
            const summary = res.data;
            setPeriodCloseSummary(summary);
            const ownerName = summary?.scope?.owner_name;
            if (studentViewId && ownerName && (!studentViewName || studentViewName === 'Selected Student')) {
                setStudentView(prev => (
                    prev?.id === studentViewId
                        ? { ...prev, full_name: ownerName }
                        : prev
                ));
            }
        }).catch(() => {
            if (!isCurrentFetch()) return;
            setPeriodCloseSummary(null);
        });
        const activityParams = new URLSearchParams();
        if (selectedCourse) activityParams.append('course_id', selectedCourse);
        activityParams.append('limit', '20');
        activityParams.append('days', '365');
        axios.get(`${API}/controls/accounting-activity?${activityParams.toString()}`, {
            headers: { Authorization: `Bearer ${token}` }
        }).then(res => {
            if (!isCurrentFetch()) return;
            setAccountingActivity(res.data);
        }).catch(() => {
            if (!isCurrentFetch()) return;
            setAccountingActivity(null);
        });
        fetchPracticeCleanupPreview().catch(() => {
            setPracticeCleanupPreview(null);
        });

        if (!selectedCourse && !studentViewId) return;
        const scopedParams = new URLSearchParams();
        const courseOnlyParams = new URLSearchParams();
        if (selectedCourse) {
            scopedParams.append('course_id', selectedCourse);
            courseOnlyParams.append('course_id', selectedCourse);
        }
        if (studentViewId) scopedParams.append('student_id', studentViewId);
        const trialParams = new URLSearchParams(scopedParams);
        if (trialBalanceDate) trialParams.append('date', trialBalanceDate);

        const requests = [
            { key: 'entries', action: () => axios.get(`${API}/journal-entries?${courseOnlyParams.toString()}`), apply: (data) => setEntries(data) },
            { key: 'ledger', action: () => axios.get(`${API}/ledger?${scopedParams.toString()}`), apply: (data) => setLedger(data) },
            { key: 'trialBalance', action: () => axios.get(`${API}/trial-balance?${trialParams.toString()}`), apply: (data) => setTrialBalance(data) },
            { key: 'auditLogs', action: () => {
                const auditParams = new URLSearchParams();
                if (selectedCourse) auditParams.append('course_id', selectedCourse);
                if (studentViewId) auditParams.append('student_id', studentViewId);
                auditParams.append('limit', '100');
                return axios.get(`${API}/audit-logs?${auditParams.toString()}`);
            }, apply: (data) => setAuditLogs(data) },
            { key: 'journalVouchers', action: () => axios.get(`${API}/journal-vouchers?${scopedParams.toString()}`), apply: (data) => setJournalVouchers(data) },
            { key: 'suppliers', action: () => axios.get(`${API}/suppliers`), apply: (data) => setSuppliers(data) },
            { key: 'customers', action: () => axios.get(`${API}/customers`), apply: (data) => setCustomers(data) }
        ];

        const results = await Promise.allSettled(requests.map(request => request.action()));
        if (!isCurrentFetch()) return;
        const failedKeys = [];
        results.forEach((result, index) => {
            const request = requests[index];
            if (result.status === 'fulfilled') {
                request.apply(result.value.data);
            } else {
                failedKeys.push(request.key);
            }
        });

        const visibleFailedKeys = failedKeys.filter(key => key !== 'auditLogs');
        if (failedKeys.includes('suppliers') || failedKeys.includes('customers')) {
            toast.error('Failed to load setup master data. AR/AP selectors may be incomplete until this reloads.');
        } else if (visibleFailedKeys.length) {
            toast.warning('Some workspace evidence panels could not load, but setup lists and transaction forms are still available.');
        }
    }, [selectedCourse, studentViewId, studentViewName, trialBalanceDate, closeReadinessPeriod, fetchPracticeCleanupPreview]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handlePracticeCleanupDialogChange = (open) => {
        setShowPracticeCleanupDialog(open);
        if (!open) setPracticeCleanupConfirm('');
    };

    const handleRefreshPracticeCleanup = useCallback(async () => {
        setPracticeCleanupLoading(true);
        try {
            await fetchPracticeCleanupPreview();
            toast.success('Practice cleanup count refreshed');
        } catch {
            toast.error('Failed to refresh cleanup count');
        } finally {
            setPracticeCleanupLoading(false);
        }
    }, [fetchPracticeCleanupPreview]);

    const handleClearPracticeCleanup = useCallback(async () => {
        if (!practiceCleanupPreview?.confirm_phrase) return;
        setPracticeCleanupLoading(true);
        try {
            const token = localStorage.getItem('token');
            const payload = {
                confirm: practiceCleanupConfirm,
            };
            if (studentView?.id) payload.student_id = studentView.id;
            const response = await axios.post(`${API}/controls/practice-cleanup/clear-drafts`, payload, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setPracticeCleanupPreview(response.data.preview);
            setShowPracticeCleanupDialog(false);
            setPracticeCleanupConfirm('');
            await fetchData();
            toast.success(
                `${response.data.message} Where now: use the module lists again. Posted history remains in GL/TB/reports.`
            );
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to clear draft practice work');
        } finally {
            setPracticeCleanupLoading(false);
        }
    }, [fetchData, practiceCleanupConfirm, practiceCleanupPreview?.confirm_phrase, studentView?.id]);

    const handleSubmit = async (e, asDraft = false) => {
        e.preventDefault();
        
        // Frontend validation with helpful messages
        if (!form.date) {
            toast.error('Date is required. Select the transaction date.');
            return;
        }
        if (!form.description || !form.description.trim()) {
            toast.error('Description is required. Describe the business transaction.');
            return;
        }
        if (!form.amount || parseFloat(form.amount) <= 0) {
            toast.error('Amount must be greater than zero.');
            return;
        }
        if (!form.debit_account) {
            toast.error('Debit account is required. Which account increases/receives?');
            return;
        }
        if (!form.credit_account) {
            toast.error('Credit account is required. Which account decreases/gives?');
            return;
        }
        if (form.debit_account === form.credit_account) {
            toast.error('Debit and Credit accounts cannot be the same. Fix: Select different accounts.');
            return;
        }
        
        try {
            const response = await axios.post(`${API}/journal-entries`, {
                ...form,
                course_id: selectedCourse,
                amount: parseFloat(form.amount),
                status: asDraft ? 'draft' : 'posted'
            });
            setForm({ date: new Date().toISOString().split('T')[0], description: '', debit_account: '', credit_account: '', amount: '' });
            fetchData();
            toast.success(
                asDraft 
                    ? `Draft entry saved. Ref: ${response.data.ref_no}. Find it in the entry list as Draft; it does not affect GL, Trial Balance, or reports until posted.`
                    : `Entry posted to GL. Ref: ${response.data.ref_no}. Review it in Review > General Ledger and Trial Balance by transaction date.`
            );
            setCurrentStep(1);
        } catch (error) {
            const detail = error.response?.data?.detail || 'Failed to save entry';
            toast.error(detail);
        }
    };

    const handleDelete = async (id, entry) => {
        if (entry?.status === 'posted') {
            toast.error('Posted entries cannot be deleted. Use Unpost/Reverse instead.');
            return;
        }
        if (!window.confirm('Delete this draft entry?')) return;
        try {
            await axios.delete(`${API}/journal-entries/${id}`);
            fetchData();
            toast.success('Draft entry removed from the active list. No posted GL, Trial Balance, or report records were deleted.');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to delete');
        }
    };

    const handleUnpost = async (entry) => {
        const reason = window.prompt('Enter reason for reversing this entry:');
        if (!reason || !reason.trim()) {
            toast.error('Reason is required for audit trail');
            return;
        }
        try {
            const response = await axios.post(`${API}/journal-entries/${entry.id}/unpost`, {
                entry_id: entry.id,
                reason: reason.trim()
            });
            fetchData();
            toast.success(`Entry reversed. Reversal ref: ${response.data.reversal_ref}. Original evidence is preserved; review both entries in Review > General Ledger and Trial Balance.`);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to reverse entry');
        }
    };

    const handlePostDraft = async (entry) => {
        try {
            await axios.put(`${API}/journal-entries/${entry.id}/post`);
            fetchData();
            toast.success(`Draft entry ${entry.ref_no} posted to GL. Review it in Review > General Ledger and Trial Balance by transaction date.`);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to post entry');
        }
    };

    const handleAI = async () => {
        if (!form.description || !form.amount) {
            toast.error('Enter description and amount first');
            return;
        }
        setAiLoading(true);
        try {
            const res = await axios.post(`${API}/ai/categorize`, {
                description: form.description,
                amount: parseFloat(form.amount)
            });
            setForm({ ...form, debit_account: res.data.debit_account, credit_account: res.data.credit_account });
            toast.success(res.data.explanation || 'Accounts suggested by AI!');
            setCurrentStep(3);
        } catch {
            toast.error('AI categorization failed');
        }
        setAiLoading(false);
    };

    const handleToolbarClick = (id) => {
        switch(id) {
            case 'supplier':
                setActiveTab('suppliers');
                break;
            case 'customer':
                setActiveTab('customers');
                break;
            case 'logout':
                toast.info('Use the sidebar logout button');
                break;
            case 'close':
                toast.info('Workspace session active');
                break;
            case 'unpost':
                toast.info('Select an entry to unpost');
                break;
            default:
                toast.info(`${id} feature coming soon`);
        }
    };

    const handleBottomClick = (id) => {
        if (id === 'jv') setActiveTab('jv');
        else if (id === 'glentries') setActiveTab('glentries');
        else if (id === 'tb') setActiveTab('trial');
        else if (id === 'bs') setActiveTab('balancesheet');
        else if (id === 'is') setActiveTab('incomestatement');
        else toast.info(`${id} module coming soon`);
    };

    const fmt = (n) => {
        const value = Number(n);
        return new Intl.NumberFormat('en-PH', { style: 'currency', currency: 'PHP' }).format(Number.isFinite(value) ? value : 0);
    };

    // Toggle sidebar section expansion
    const toggleSection = (sectionId) => {
        setExpandedSections(prev =>
            prev.includes(sectionId)
                ? prev.filter(s => s !== sectionId)
                : [...prev, sectionId]
        );
    };

    // Navigate to tab from sidebar or quick access
    const navigateTo = (tab) => {
        const normalizedTab = normalizeWorkspaceTab(tab);
        if (normalizedTab) {
            setActiveTab(normalizedTab);
            setOpenNavSection(null);
        }
    };

    const openBankReconForClosePeriod = useCallback(() => {
        const selectedPeriod = closeReadinessPeriod || new Date().toISOString().slice(0, 7);
        const periodLabel = new Date(`${selectedPeriod}-01T00:00:00`).toLocaleDateString('en-PH', {
            month: 'long',
            year: 'numeric',
        });
        setBankReconRequestedPeriod(selectedPeriod);
        setActiveTab('bankrecon');
        setOpenNavSection('review');
        toast.info(`Opening Bank Reconciliation for ${periodLabel}. Close Readiness and Bank Recon are using the same monthly period.`);
    }, [closeReadinessPeriod]);

    const openBankReconForReviewPeriod = useCallback((periodValue, sourceLabel = 'close review trail') => {
        const selectedPeriod = String(periodValue || closeReadinessPeriod || new Date().toISOString().slice(0, 7)).slice(0, 7);
        const periodLabel = new Date(`${selectedPeriod}-01T00:00:00`).toLocaleDateString('en-PH', {
            month: 'long',
            year: 'numeric',
        });
        setBankReconRequestedPeriod(selectedPeriod);
        setActiveTab('bankrecon');
        setOpenNavSection('review');
        toast.info(`Opening Bank Reconciliation for ${periodLabel} from ${sourceLabel}. This keeps the close review trail on the same month.`);
    }, [closeReadinessPeriod]);

    const openGlForClosePeriod = useCallback(() => {
        const selectedPeriod = closeReadinessPeriod || new Date().toISOString().slice(0, 7);
        const [year, month] = selectedPeriod.split('-').map(value => Number(value));
        const periodLabel = new Date(`${selectedPeriod}-01T00:00:00`).toLocaleDateString('en-PH', {
            month: 'long',
            year: 'numeric',
        });
        const range = Number.isFinite(year) && Number.isFinite(month)
            ? getMonthDateRange(year, month)
            : { dateFrom: '', dateTo: '' };
        const scopeLabel = studentView?.full_name || periodCloseSummary?.scope?.owner_name || 'Current workspace';
        setGlTraceRequest({
            id: `period-close-gl-${selectedPeriod}-${Date.now()}`,
            sourceReport: 'Period Close Readiness',
            label: `${scopeLabel} - ${periodLabel}`,
            dateFrom: range.dateFrom,
            dateTo: range.dateTo,
            accountFrom: '',
            accountTo: '',
            accountLabel: 'All posted GL lines in the selected close month',
            scopeLabel,
            amount: periodCloseSummary?.bank_reconciliation?.net_movement || 0,
            nextAction: 'Review posted GL lines for the selected close month, then trace cash/bank and bank-recon adjustment lines back to Bank Reconciliation evidence.',
        });
        setActiveTab('glentries');
        setOpenNavSection('review');
        toast.info(`Opening General Ledger for ${periodLabel}. This uses the same Close Readiness month and student scope.`);
    }, [closeReadinessPeriod, periodCloseSummary?.bank_reconciliation?.net_movement, periodCloseSummary?.scope?.owner_name, studentView?.full_name]);

    const openTrialBalanceForClosePeriod = useCallback(() => {
        const selectedPeriod = closeReadinessPeriod || new Date().toISOString().slice(0, 7);
        const [year, month] = selectedPeriod.split('-').map(value => Number(value));
        const periodLabel = new Date(`${selectedPeriod}-01T00:00:00`).toLocaleDateString('en-PH', {
            month: 'long',
            year: 'numeric',
        });
        const scopeLabel = studentView?.full_name || periodCloseSummary?.scope?.owner_name || 'Current workspace';
        const asOfDate = Number.isFinite(year) && Number.isFinite(month)
            ? getMonthEndDate(year, month)
            : '';
        if (Number.isFinite(year) && Number.isFinite(month)) {
            setTrialBalanceDate(asOfDate);
        }
        setTrialBalanceTraceRequest({
            id: `period-close-tb-${selectedPeriod}-${Date.now()}`,
            sourceReport: 'Period Close Readiness',
            label: `${scopeLabel} - ${periodLabel}`,
            scopeLabel,
            asOfDate,
            sourceLocation: 'Home > Close Readiness',
            evidenceLocation: 'Review > Bank Reconciliation',
            reviewer: 'Student clears blockers. Instructor/admin reviews evidence. Auditor path is read-only.',
            impact: 'Trial Balance includes posted GL entries on or before this date. Drafts, statement lines, matches, and preflight evidence do not change balances until a source is posted.',
            nextAction: 'Confirm debit and credit totals as close support, then trace unusual balances back to General Ledger and Bank Reconciliation evidence before any future close approval.',
        });
        setActiveTab('trial');
        setOpenNavSection('review');
        toast.info(`Opening Trial Balance as of the end of ${periodLabel}. Drafts and evidence still do not affect balances until posted.`);
    }, [closeReadinessPeriod, periodCloseSummary?.scope?.owner_name, studentView?.full_name]);

    const traceTrialBalanceForReview = useCallback((request) => {
        if (!request) return;
        const nextRequest = {
            ...request,
            id: `${request.sourceReport || 'trial-balance-trace'}-${Date.now()}`,
        };
        if (request.asOfDate) {
            setTrialBalanceDate(String(request.asOfDate).slice(0, 10));
        }
        setTrialBalanceTraceRequest(nextRequest);
        setActiveTab('trial');
        setOpenNavSection('review');
        toast.info(`Opening Trial Balance for ${nextRequest.label || 'the selected evidence'}. Verify posted balances without changing source records.`);
    }, []);

    const openArApReviewRecord = useCallback((record) => {
        if (!record) return;
        focusStudentWorkspace(record);
        if (record.workspace_tab) {
            setActiveTab(normalizeWorkspaceTab(record.workspace_tab));
            if (record.module === 'ar') setOpenNavSection('ar');
            if (record.module === 'ap') setOpenNavSection('ap');
        }
        toast.info(`Opening ${record.record_type || 'source'} ${record.record_number || ''} in the student's workspace scope.`);
    }, [focusStudentWorkspace]);

    const openReturnedWork = useCallback((record) => {
        if (!record) return;
        focusStudentWorkspace(record);
        if (record.workspace_tab) {
            setActiveTab(normalizeWorkspaceTab(record.workspace_tab));
            if (record.module === 'ar') setOpenNavSection('ar');
            if (record.module === 'ap') setOpenNavSection('ap');
            if (record.module === 'jv') setOpenNavSection('record');
        }
        toast.info(`Opening returned ${record.record_type || 'work'} ${record.record_number || ''}. Read the feedback, revise, then resubmit.`);
    }, [focusStudentWorkspace]);

    const openPostedEvidence = useCallback((record, target = 'source') => {
        if (!record) return;
        focusStudentWorkspace(record);
        if (target === 'gl') {
            setGlTraceRequest({
                id: `posted-evidence-gl-${record.record_id || record.record_number || record.id}-${Date.now()}`,
                sourceReport: record.record_type || 'Posted evidence',
                label: record.record_number || record.record_id || 'Posted source',
                dateFrom: record.record_date ? String(record.record_date).slice(0, 10) : '',
                dateTo: record.record_date ? String(record.record_date).slice(0, 10) : '',
                accountFrom: '',
                accountTo: '',
                accountLabel: 'All accounts linked to the posted source',
                scopeLabel: record.student_name || record.submitted_by_name || record.processed_by || 'Selected student',
                amount: record.amount,
            });
            setActiveTab('glentries');
            setOpenNavSection('review');
            toast.info(`Opening General Ledger for posted ${record.record_type || 'record'} ${record.record_number || ''}. Use Source Ref to trace back to the original record.`);
            return;
        }
        if (target === 'trial') {
            const asOfDate = record.record_date ? String(record.record_date).slice(0, 10) : '';
            if (asOfDate) setTrialBalanceDate(asOfDate);
            setTrialBalanceTraceRequest({
                id: `posted-evidence-tb-${record.record_id || record.record_number || record.id}-${Date.now()}`,
                sourceReport: record.record_type || 'Posted evidence',
                label: record.record_number || record.record_id || 'Posted source',
                scopeLabel: record.student_name || record.submitted_by_name || record.processed_by || 'Selected student',
                asOfDate,
                sourceLocation: record.module_label || record.record_type || 'Source module',
                evidenceLocation: 'Review > General Ledger and source record',
                reviewer: 'No reviewer is needed after posting. Reviewers verify source evidence and posted impact.',
                impact: record.downstream_impact || 'Posted source evidence affects GL, Trial Balance, and financial reports by posting date.',
                nextAction: record.next_safe_action || 'Confirm the source appears in Trial Balance, then use General Ledger or source evidence if a correction is needed.',
            });
            setActiveTab('trial');
            setOpenNavSection('review');
            toast.info(`Opening Trial Balance. Use an as-of date on or after ${record.record_date || 'the posting date'} to include ${record.record_number || 'this posted record'}.`);
            return;
        }
        if (record.workspace_tab) {
            setActiveTab(normalizeWorkspaceTab(record.workspace_tab));
            if (record.module === 'ar') setOpenNavSection('ar');
            if (record.module === 'ap') setOpenNavSection('ap');
            if (record.module === 'jv') setOpenNavSection('record');
            if (record.module === 'gl') setOpenNavSection('review');
        }
        const moduleSourceTypes = {
            arinvoices: 'ar_invoice',
            arcollections: 'ar_collection',
            apvouchers: 'ap_voucher',
            appayments: 'ap_payment',
        };
        const sourceType = moduleSourceTypes[record.workspace_tab];
        if (sourceType) {
            setModuleSourceOpenRequest({
                id: `posted-${sourceType}-${record.record_id || record.record_number || record.id}-${Date.now()}`,
                sourceType,
                sourceId: record.record_id || '',
                refNo: record.record_number || '',
                label: record.record_type || sourceType.replace('_', ' '),
                sourceLocation: record.module_label || record.record_type || 'Source module',
                sourceImpact: record.downstream_impact || 'Posted source evidence affects GL and Trial Balance.',
                sourceNextAction: record.next_safe_action || 'Open the source record and verify General Ledger and Trial Balance.',
            });
        } else if (record.workspace_tab === 'jv') {
            setJvSourceOpenRequest({
                id: `posted-jv-${record.record_id || record.record_number || record.id}-${Date.now()}`,
                type: 'jv',
                jvId: record.record_id || '',
                refNo: record.record_number || '',
                sourceLabel: record.record_type || 'Journal Voucher',
                sourceLocation: record.module_label || 'Record > Journal Voucher',
                sourceImpact: record.downstream_impact || 'Posted Journal Voucher affects GL and Trial Balance.',
                sourceNextAction: record.next_safe_action || 'Open the source JV and verify General Ledger and Trial Balance.',
            });
        }
        toast.info(`Opening source ${record.record_type || 'record'} ${record.record_number || ''}. It is already posted, so verify its GL and Trial Balance effect next.`);
    }, [focusStudentWorkspace]);

    const openReportSourceRecord = useCallback((record) => {
        if (!record) return;
        const sourceTypeByTab = {
            arinvoices: 'ar_invoice',
            arcollections: 'ar_collection',
            apvouchers: 'ap_voucher',
            appayments: 'ap_payment',
        };
        const tabBySourceType = {
            ar_invoice: 'arinvoices',
            ar_collection: 'arcollections',
            ap_voucher: 'apvouchers',
            ap_payment: 'appayments',
        };
        const sourceType = record.source_type || record.sourceType || sourceTypeByTab[record.workspace_tab];
        const workspaceTab = record.workspace_tab || tabBySourceType[sourceType];
        if (!workspaceTab || !sourceType) {
            toast.info('Open the related source module from this report, then use the document number to find the evidence row.');
            return;
        }

        setActiveTab(normalizeWorkspaceTab(workspaceTab));
        setOpenNavSection(workspaceTab.startsWith('ar') ? 'ar' : 'ap');
        setModuleSourceOpenRequest({
            id: `report-source-${sourceType}-${record.record_id || record.source_id || record.record_number || record.doc_number || Date.now()}-${Date.now()}`,
            sourceType,
            sourceId: record.record_id || record.source_id || '',
            refNo: record.record_number || record.doc_number || record.invoice_number || record.collection_number || record.voucher_number || record.payment_number || '',
            label: record.record_type || record.source_label || sourceType.replace('_', ' '),
            sourceLocation: record.source_location || 'Opened from an AR/AP report row.',
            sourceImpact: record.source_impact || 'Report preview only. Opening the source does not change GL, Trial Balance, reports, or source records.',
            sourceNextAction: record.source_next_action || 'Review the source status, posting date, and GL evidence before creating any correction.',
        });
        toast.info(`Opening ${record.record_type || 'source'} ${record.record_number || record.doc_number || ''}. The report row stays read-only; source records only change through normal module actions.`);
    }, []);

    const exitStudentReview = useCallback(() => {
        setStudentView(null);
        const params = new URLSearchParams(location.search);
        const requestedTab = normalizeWorkspaceTab(params.get('tab'));
        if (requestedTab && WORKSPACE_TAB_IDS.has(requestedTab)) {
            setActiveTab(requestedTab);
            const matchingSection = visibleSidebarNav.find(section =>
                section.children?.some(item => item.tab === requestedTab)
            );
            if (matchingSection) {
                setOpenNavSection(matchingSection.id);
            }
        }
    }, [location.search, visibleSidebarNav]);

    const traceReportToGlEntries = useCallback((request) => {
        if (!request) return;
        const nextRequest = {
            ...request,
            id: `${request.sourceReport || 'report'}-${Date.now()}`,
        };
        setGlTraceRequest(nextRequest);
        setActiveTab('glentries');
        setOpenNavSection('review');
        toast.info(`Opening General Ledger for ${nextRequest.label}. Review Source Ref to find the original voucher or module record.`);
    }, []);

    const openGlSourceRecord = useCallback((entry) => {
        const sourceRef = entry?.ref_no || entry?.jv_id;
        const sourceType = entry?.source_type || '';
        const sourceId = entry?.source_id || entry?.source_adjustment_id || entry?.jv_id || '';
        if (!sourceRef && !sourceId) {
            toast.info('This GL line does not have a source voucher reference yet.');
            return;
        }

        const moduleSourceRoutes = {
            ar_invoice: { tab: 'arinvoices', section: 'ar', label: 'AR Invoice' },
            ar_invoice_reversal: { tab: 'arinvoices', section: 'ar', label: 'AR Invoice reversal' },
            ar_collection: { tab: 'arcollections', section: 'ar', label: 'AR Collection' },
            ar_collection_reversal: { tab: 'arcollections', section: 'ar', label: 'AR Collection reversal' },
            ap_voucher: { tab: 'apvouchers', section: 'ap', label: 'AP Voucher' },
            ap_voucher_reversal: { tab: 'apvouchers', section: 'ap', label: 'AP Voucher reversal' },
            ap_payment: { tab: 'appayments', section: 'ap', label: 'AP Payment' },
            ap_payment_reversal: { tab: 'appayments', section: 'ap', label: 'AP Payment reversal' },
        };
        const moduleRoute = moduleSourceRoutes[sourceType];

        if (moduleRoute) {
            setModuleSourceOpenRequest({
                id: `gl-module-${sourceType}-${sourceId || sourceRef}-${entry?.id || 'entry'}-${Date.now()}`,
                sourceType,
                sourceId,
                refNo: sourceRef || '',
                label: entry?.source_label || moduleRoute.label,
                sourceLocation: entry?.source_location || moduleRoute.label,
                sourceImpact: entry?.source_impact || 'Posted GL source evidence.',
                sourceNextAction: entry?.source_next_action || 'Open the source record and review status, amount, and support.',
            });
            setActiveTab(moduleRoute.tab);
            setOpenNavSection(moduleRoute.section);
            toast.info(`Opening ${entry?.source_label || moduleRoute.label} ${sourceRef || sourceId}. Review the source record and confirm the posted GL/Trial Balance impact.`);
            return;
        }

        const isBankReconSource = sourceType === 'bank_reconciliation_adjustment';

        setJvSourceOpenRequest({
            id: `gl-jv-${sourceRef}-${entry?.id || 'entry'}-${Date.now()}`,
            type: 'jv',
            jvId: entry?.jv_id || '',
            refNo: entry?.ref_no || '',
            accountCode: entry?.account_code || '',
            accountTitle: entry?.account_title || '',
            date: entry?.date || '',
            description: entry?.description || '',
            sourceType: entry?.source_type || '',
            sourceLabel: entry?.source_label || '',
            sourceLocation: entry?.source_location || '',
            sourceImpact: entry?.source_impact || '',
            sourceNextAction: entry?.source_next_action || '',
            sourceStatementReference: entry?.source_statement_reference || '',
            sourceStatementLineId: entry?.source_statement_line_id || '',
            bankReconciliationMatched: Boolean(entry?.bank_reconciliation_matched),
            bankReconciliationMatchStatus: entry?.bank_reconciliation_match_status || '',
        });
        setActiveTab('jv');
        setOpenNavSection('record');
        toast.info(
            isBankReconSource
                ? `Opening bank reconciliation source JV ${sourceRef}. It also appears in Bank Reconciliation > Posted Adjustment Evidence.`
                : `Opening source JV ${sourceRef}. It lives in Record > Journal Voucher and affects GL/Trial Balance because it is posted.`
        );
    }, []);

    const openNavSectionDefault = (section) => {
        const defaultItem =
            section.children.find(item => !item.disabled && item.tab === section.defaultTab) ||
            section.children.find(item => !item.disabled && item.tab);

        if (defaultItem?.tab) {
            setActiveTab(defaultItem.tab);
        }
        setOpenNavSection(section.id);
    };

    const resetWorkspaceHome = useCallback(() => {
        setActiveTab('home');
        setOpenNavSection(null);
        setGlTraceRequest(null);
        setTrialBalanceTraceRequest(null);
        setJvSourceOpenRequest(null);
        setModuleSourceOpenRequest(null);
    }, []);

    useEffect(() => {
        window.addEventListener('macesuite:workspace-home', resetWorkspaceHome);
        return () => window.removeEventListener('macesuite:workspace-home', resetWorkspaceHome);
    }, [resetWorkspaceHome]);

    useEffect(() => {
        const resetFromWorkspaceSidebarLink = (event) => {
            const target = event.target;
            if (!(target instanceof Element)) return;
            const workspaceLink = target.closest('[data-testid="nav-accounting-workspace"]');
            if (!workspaceLink || window.location.pathname !== '/workspace') return;
            event.preventDefault();
            event.stopPropagation();
            resetWorkspaceHome();
        };

        document.addEventListener('click', resetFromWorkspaceSidebarLink, true);
        return () => document.removeEventListener('click', resetFromWorkspaceSidebarLink, true);
    }, [resetWorkspaceHome]);

    const handleQuickAccessClick = (btn) => {
        if (btn?.tab) {
            setActiveTab(btn.tab);
            return;
        }
        toast.info(`${btn?.label || 'Module'} coming soon`);
    };

    // Toggle sidebar collapsed state
    const toggleSidebar = () => {
        setSidebarCollapsed(prev => {
            const newState = !prev;
            localStorage.setItem('macesuite_sidebar_collapsed', JSON.stringify(newState));
            return newState;
        });
    };

    // Toggle Student Guide drawer
    const toggleGuide = () => {
        setShowHelp(prev => {
            const next = !prev;
            localStorage.setItem('macesuite_guide_open', JSON.stringify(next));
            return next;
        });
    };

    if (loading) {
        return (
            <div className="h-screen flex items-center justify-center bg-slate-100">
                <div className="text-center">
                    <div className="w-12 h-12 border-4 border-[#2F8FCB] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                    <p className="text-slate-600">Loading Workspace...</p>
                </div>
            </div>
        );
    }

    if (courses.length === 0 && !isAuditor) {
        return (
            <div className="min-h-screen bg-slate-100 flex items-center justify-center p-8">
                <div className="bg-white rounded-2xl border shadow-lg p-12 text-center max-w-md">
                    <Calculator className="w-20 h-20 text-[#2F8FCB] mx-auto mb-6" />
                    <h2 className="text-2xl font-bold font-['Manrope'] text-slate-900 mb-2">No Courses Enrolled</h2>
                    <p className="text-slate-500 mb-6">Enroll in a course to access the Accounting Workspace</p>
                    <Button className="bg-[#0F2A44] hover:bg-[#1E3A8A] rounded-full" onClick={() => window.location.href = '/courses'}>
                        Browse Courses
                    </Button>
                </div>
            </div>
        );
    }

    const selectedCourseRecord = courses.find(c => c.id === selectedCourse);
    const routeCourseParams = new URLSearchParams(location.search);
    const routeCourseCode = routeCourseParams.get('course_code') || '';
    const routeCourseTitle = routeCourseParams.get('course_title') || '';
    const routeCourseId = routeCourseParams.get('course_id') || '';
    const statusCourseLabel =
        selectedCourseRecord?.code ||
        selectedCourseRecord?.title ||
        studentView?.course_code ||
        studentView?.course_title ||
        routeCourseCode ||
        routeCourseTitle ||
        studentView?.course_id ||
        selectedCourse ||
        routeCourseId ||
        'N/A';
    const canSelectCurrentCourse = courses.some(c => c.id === selectedCourse);

    return (
        <TooltipProvider>
            <div className="min-h-[calc(100vh-4rem)] flex flex-col -mx-4 md:-mx-6 lg:-mx-8 -mt-4 md:-mt-6 lg:-mt-8" data-testid="accounting-workspace">
                {/* TOP SUB-NAVIGATION BAR */}
                <div className="bg-[#0F2A44] text-white px-3 py-1 flex items-center gap-2 flex-shrink-0">
                    {/* Home Button */}
                    <button
                        onClick={resetWorkspaceHome}
                        className={`flex items-center gap-1.5 px-2.5 py-1 rounded transition-colors text-sm ${
                            activeTab === 'home' ? 'bg-[#2F8FCB]' : 'hover:bg-white/10'
                        }`}
                        data-testid="sidebar-home"
                    >
                        <Home className="w-3.5 h-3.5" />
                        <span className="font-medium">Home</span>
                    </button>
                    
                    {/* Section Navigation */}
                    <div className="flex items-center gap-0.5 border-l border-white/20 pl-2">
                        {visibleSidebarNav.map((section) => (
                            <div
                                key={section.id}
                                className="relative"
                                onMouseEnter={() => setOpenNavSection(section.id)}
                                onMouseLeave={() => setOpenNavSection(current => current === section.id ? null : current)}
                                onBlur={(event) => {
                                    if (!event.currentTarget.contains(event.relatedTarget)) {
                                        setOpenNavSection(current => current === section.id ? null : current);
                                    }
                                }}
                            >
                                <button
                                    type="button"
                                    onClick={() => openNavSectionDefault(section)}
                                    onKeyDown={(event) => {
                                        if (event.key === 'Enter' || event.key === ' ') {
                                            event.preventDefault();
                                            openNavSectionDefault(section);
                                        }
                                        if (event.key === 'ArrowDown') {
                                            event.preventDefault();
                                            setOpenNavSection(section.id);
                                        }
                                        if (event.key === 'Escape') {
                                            setOpenNavSection(null);
                                        }
                                    }}
                                    aria-haspopup="menu"
                                    aria-expanded={openNavSection === section.id}
                                    className={`flex items-center gap-1 px-2.5 py-1 rounded text-sm transition-colors hover:bg-white/10 ${
                                        section.children.some(c => c.tab === activeTab) ? 'bg-white/20' : ''
                                    }`}
                                    data-testid={`nav-section-${section.id}`}
                                >
                                    <section.icon className="w-3.5 h-3.5" />
                                    <span>{section.name}</span>
                                    <ChevronDown className="w-3 h-3 opacity-50" />
                                </button>
                                
                                {/* Dropdown Menu */}
                                <div
                                    className={`absolute left-0 top-full mt-0.5 bg-[#0F2A44] border border-white/10 rounded-lg shadow-xl transition-all z-50 min-w-[160px] ${
                                        openNavSection === section.id ? 'opacity-100 visible' : 'opacity-0 invisible'
                                    }`}
                                    role="menu"
                                    aria-label={`${section.name} menu`}
                                >
                                    {section.children.map((item) => (
                                        <button
                                            key={item.id}
                                            type="button"
                                            onClick={() => !item.disabled && navigateTo(item.tab)}
                                            onKeyDown={(event) => {
                                                if (event.key === 'Escape') {
                                                    setOpenNavSection(null);
                                                }
                                            }}
                                            disabled={item.disabled}
                                            role="menuitem"
                                            className={`w-full text-left px-4 py-2 text-sm transition-colors first:rounded-t-lg last:rounded-b-lg ${
                                                activeTab === item.tab
                                                    ? 'bg-[#2F8FCB] text-white'
                                                    : item.disabled
                                                        ? 'text-white/30 cursor-not-allowed'
                                                        : 'text-white/80 hover:bg-white/10 hover:text-white'
                                            }`}
                                            data-testid={`nav-item-${item.id}`}
                                        >
                                            {item.label}
                                            {item.disabled && <span className="ml-1 text-xs opacity-50">(Soon)</span>}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                    
                    {/* Right: Course Selector + Guide Toggle */}
                    <div className="ml-auto flex items-center gap-2">
                        {activeTab !== 'home' && !isAuditor && (
                            <button
                                onClick={toggleGuide}
                                className={`flex items-center gap-1 px-2 py-1 rounded text-xs transition-colors ${
                                    showHelp ? 'bg-[#2F8FCB] text-white' : 'bg-white/10 text-white/70 hover:bg-white/20 hover:text-white'
                                }`}
                                data-testid="guide-toggle-btn"
                            >
                                <BookOpen className="w-3.5 h-3.5" />
                                <span className="hidden sm:inline">Guide</span>
                            </button>
                        )}
                        {/* Student Workspace Viewer (instructor/admin only) */}
                        {(user?.role === 'instructor' || user?.role === 'admin') && reviewableStudents.length > 0 && (
                            <Select
                                value={studentView?.id || ''}
                                onValueChange={(val) => {
                                    if (!val || val === '__mine__') { setStudentView(null); }
                                    else { setStudentView(reviewableStudents.find(s => s.id === val) || null); }
                                }}
                            >
                                <SelectTrigger className="w-60 h-7 text-xs bg-white/10 border-white/20 text-white" data-testid="student-view-select">
                                    <Users className="w-3 h-3 mr-1 opacity-60" />
                                    <SelectValue placeholder="Review: My Workspace" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="__mine__">Review: My Workspace</SelectItem>
                                    {reviewableStudents.map(s => (
                                        <SelectItem key={s.id} value={s.id}>Review: {s.full_name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        )}
                        <div className="flex items-center gap-2 border-l border-white/20 pl-2">
                            <span className="text-xs text-white/50">Course:</span>
                            {canSelectCurrentCourse ? (
                                <Select value={selectedCourse} onValueChange={handleCourseChange}>
                                    <SelectTrigger className="w-36 h-6 text-xs bg-white/10 border-white/20 text-white" data-testid="course-select">
                                        <SelectValue placeholder="Select" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {courses.map(c => (
                                            <SelectItem key={c.id} value={c.id}>{c.code}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            ) : (
                                <span className="min-w-24 rounded bg-white/10 px-2 py-1 text-xs font-medium text-white" data-testid="course-scope-label">
                                    {statusCourseLabel}
                                </span>
                            )}
                        </div>
                    </div>
                </div>

                {/* BREADCRUMB BAR - shown on all tabs except home */}
                {activeTab !== 'home' && (
                    <div className="bg-slate-100 border-b border-slate-200 px-4 py-1.5 flex items-center gap-2 text-xs text-slate-500 flex-shrink-0">
                        <button
                            onClick={resetWorkspaceHome}
                            className="flex items-center gap-1 text-[#2F8FCB] hover:underline font-medium"
                            data-testid="breadcrumb-home"
                        >
                            <Calculator className="w-3 h-3" />
                            Accounting Workspace
                        </button>
                        <span>/</span>
                        <span className="text-slate-700 font-medium capitalize">
                            {visibleSidebarNav.flatMap(s => s.children).find(c => c.tab === activeTab)?.label || activeTab}
                        </span>
                    </div>
                )}

                {/* Auditor read-only banner */}
                {isAuditor && (
                    <div className="bg-amber-50 border-b border-amber-200 px-4 py-2 flex items-center gap-2 text-sm text-amber-700 flex-shrink-0" data-testid="auditor-readonly-banner">
                        <Shield className="w-4 h-4" />
                        Read-Only Access - You can view and export all reports but cannot create or modify any records.
                    </div>
                )}

                {/* Student view banner (instructor viewing student's workspace) */}
                {studentView && (
                    <div className="bg-blue-50 border-b border-blue-200 px-4 py-2 flex items-center justify-between text-sm text-blue-700 flex-shrink-0" data-testid="student-view-banner">
                        <div className="flex items-center gap-2">
                            <Eye className="w-4 h-4" />
                            Reviewing student workspace: <strong>{studentView.full_name}</strong> - JVs, AR/AP, Bank Reconciliation, GL, and Trial Balance are shown read-only for this student.
                        </div>
                        <button onClick={exitStudentReview} className="text-blue-500 hover:underline text-xs" data-testid="exit-student-view-btn">
                            &larr; Return to My Workspace
                        </button>
                    </div>
                )}

                {/* MAIN CONTENT AREA */}
                <main className="flex-1 overflow-hidden bg-slate-50 relative flex">
                    <ScrollArea className="h-full flex-1">
                        <div className="px-6 py-6 mx-auto max-w-7xl">
                            {/* Accounting Home View */}
                            {activeTab === 'home' && (
                                <AccountingHomeView 
                                    onNavigate={navigateTo} 
                                    controlSummary={controlSummary}
                                    periodCloseSummary={periodCloseSummary}
                                    accountingActivity={accountingActivity}
                                    practiceCleanupPreview={practiceCleanupPreview}
                                    practiceCleanupLoading={practiceCleanupLoading}
                                    onRefreshPracticeCleanup={handleRefreshPracticeCleanup}
                                    onOpenPracticeCleanup={() => setShowPracticeCleanupDialog(true)}
                                    onOpenReturnedWork={openReturnedWork}
                                    onOpenPostedEvidence={openPostedEvidence}
                                    closeReadinessPeriod={closeReadinessPeriod}
                                    onCloseReadinessPeriodChange={setCloseReadinessPeriod}
                                    onOpenCloseBankRecon={openBankReconForClosePeriod}
                                    onOpenCloseGl={openGlForClosePeriod}
                                    onOpenCloseTrial={openTrialBalanceForClosePeriod}
                                    onPeriodLockChanged={fetchData}
                                    userRole={user?.role}
                                    studentView={studentView}
                                    enrolledStudentsCount={enrolledStudents.length}
                                    stats={{
                                        vouchers: journalVouchers.length,
                                        suppliers: suppliers.length,
                                        customers: customers.length,
                                    }}
                                />
                            )}

                            {activeTab === 'arapreview' && (
                                <ARAPReviewQueueTab
                                    selectedCourse={selectedCourse}
                                    fmt={fmt}
                                    user={user}
                                    onRefresh={fetchData}
                                    onOpenRecord={openArApReviewRecord}
                                />
                            )}
                            
                            {activeTab === 'coa' && (
                                <ChartOfAccountsTab
                                    onRefresh={fetchData}
                                    isReadOnly={isAuditor || !!studentView}
                                />
                            )}
                            {activeTab === 'openingbalances' && (
                                <OpeningBalancesTab
                                    selectedCourse={selectedCourse}
                                    chartOfAccounts={chartOfAccounts}
                                    onRefresh={fetchData}
                                    fmt={fmt}
                                    isReadOnly={isAuditor || !!studentView}
                                    permissions={permissions?.jv}
                                    user={user}
                                    onNavigate={navigateTo}
                                    onSetTrialBalanceDate={setTrialBalanceDate}
                                />
                            )}
                            {activeTab === 'jv' && (
                                <JournalVoucherTab
                                    selectedCourse={selectedCourse}
                                    user={user}
                                    chartOfAccounts={chartOfAccounts}
                                    vouchers={journalVouchers}
                                    onRefresh={fetchData}
                                    fmt={fmt}
                                    permissions={permissions?.jv}
                                    isAuditor={isAuditor}
                                    viewingStudentId={studentView?.id || null}
                                    onReviewStudent={focusStudentWorkspace}
                                    onSetTrialBalanceDate={setTrialBalanceDate}
                                    sourceOpenRequest={effectiveJvSourceOpenRequest}
                                    onOpenPostedEvidence={openPostedEvidence}
                                    onOpenBankReconPeriod={openBankReconForReviewPeriod}
                                />
                            )}
                            {activeTab === 'glentries' && (
                                <GLEntriesTab
                                    selectedCourse={selectedCourse}
                                    chartOfAccounts={chartOfAccounts}
                                    fmt={fmt}
                                    viewingStudentId={studentView?.id || null}
                                    viewingStudentName={studentView?.full_name || null}
                                    traceRequest={glTraceRequest}
                                    onOpenSource={openGlSourceRecord}
                                    onNavigate={navigateTo}
                                    onTraceTrialBalance={traceTrialBalanceForReview}
                                    onOpenBankReconPeriod={openBankReconForReviewPeriod}
                                />
                            )}
                            {activeTab === 'suppliers' && (
                                <SupplierListTab
                                    suppliers={suppliers}
                                    atcCodes={atcCodes}
                                    onRefresh={fetchData}
                                    fmt={fmt}
                                    isReadOnly={isAuditor || !!studentView}
                                />
                            )}
                            {activeTab === 'customers' && (
                                <CustomerListTab
                                    customers={customers}
                                    atcCodes={atcCodes}
                                    onRefresh={fetchData}
                                    fmt={fmt}
                                    isReadOnly={isAuditor || !!studentView}
                                />
                            )}
                            {activeTab === 'ledger' && (
                                <ReportErrorBoundary>
                                    <LedgerTab
                                        ledger={ledger}
                                        fmt={fmt}
                                        viewingStudentName={studentView?.full_name || null}
                                        onTraceGl={traceReportToGlEntries}
                                    />
                                </ReportErrorBoundary>
                            )}
                            {activeTab === 'trial' && (
                                <ReportErrorBoundary>
                                    <TrialBalanceTab
                                        trialBalance={trialBalance}
                                        fmt={fmt}
                                        asOfDate={trialBalanceDate}
                                        onAsOfDateChange={setTrialBalanceDate}
                                        traceRequest={trialBalanceTraceRequest}
                                        onClearTrace={() => setTrialBalanceTraceRequest(null)}
                                        viewingStudentName={studentView?.full_name || null}
                                        journalVouchers={journalVouchers}
                                        selectedCourse={selectedCourse}
                                        chartOfAccounts={chartOfAccounts}
                                        onRefresh={fetchData}
                                        isReadOnly={isAuditor || !!studentView}
                                        permissions={permissions?.jv}
                                        user={user}
                                        onNavigate={navigateTo}
                                        onTraceGl={traceReportToGlEntries}
                                        onOpenBankReconPeriod={openBankReconForReviewPeriod}
                                    />
                                </ReportErrorBoundary>
                            )}
                            {activeTab === 'bankrecon' && (
                                <ReportErrorBoundary>
                                    <BankReconciliationTab
                                        selectedCourse={selectedCourse}
                                        fmt={fmt}
                                        onRefresh={fetchData}
                                        viewingStudentId={studentView?.id || null}
                                        viewingStudentName={studentView?.full_name || null}
                                        isReadOnly={isAuditor || !!studentView}
                                        onOpenPostedEvidence={openPostedEvidence}
                                        onSetTrialBalanceDate={setTrialBalanceDate}
                                        onResolveViewingStudent={resolveStudentViewFromScope}
                                        requestedPeriod={bankReconRequestedPeriod}
                                        onNavigate={navigateTo}
                                        onTraceGl={traceReportToGlEntries}
                                        onTraceTrialBalance={traceTrialBalanceForReview}
                                    />
                                </ReportErrorBoundary>
                            )}
                            {activeTab === 'balancesheet' && (
                                <ReportErrorBoundary>
                                    <BalanceSheetTab
                                        fmt={fmt}
                                        viewingStudentId={studentView?.id || null}
                                        viewingStudentName={studentView?.full_name || null}
                                        onTraceGl={traceReportToGlEntries}
                                    />
                                </ReportErrorBoundary>
                            )}
                            {activeTab === 'incomestatement' && (
                                <ReportErrorBoundary>
                                    <IncomeStatementTab
                                        fmt={fmt}
                                        viewingStudentId={studentView?.id || null}
                                        viewingStudentName={studentView?.full_name || null}
                                        onTraceGl={traceReportToGlEntries}
                                    />
                                </ReportErrorBoundary>
                            )}
                            {activeTab === 'statements' && <ReportErrorBoundary><StatementsTab trialBalance={trialBalance} fmt={fmt} /></ReportErrorBoundary>}
                            {activeTab === 'arsummary' && (
                                <ReportErrorBoundary>
                                    <ARSummaryTab
                                        customers={customers}
                                        fmt={fmt}
                                        viewingStudentId={studentView?.id || null}
                                        viewingStudentName={studentView?.full_name || null}
                                        onNavigate={navigateTo}
                                        onOpenSource={openReportSourceRecord}
                                    />
                                </ReportErrorBoundary>
                            )}
                            {activeTab === 'arinvoices' && (
                                <ARInvoicesTab
                                    customers={customers}
                                    fmt={fmt}
                                    onRefresh={fetchData}
                                    selectedCourse={selectedCourse}
                                    permissions={permissions?.ar}
                                    viewingStudentId={studentView?.id || null}
                                    viewingStudentName={studentView?.full_name || null}
                                    isReadOnly={isAuditor || !!studentView}
                                    sourceOpenRequest={effectiveModuleSourceOpenRequest}
                                />
                            )}
                            {activeTab === 'arcollections' && (
                                <ARCollectionsTab
                                    customers={customers}
                                    fmt={fmt}
                                    onRefresh={fetchData}
                                    selectedCourse={selectedCourse}
                                    permissions={permissions?.ar}
                                    viewingStudentId={studentView?.id || null}
                                    viewingStudentName={studentView?.full_name || null}
                                    isReadOnly={isAuditor || !!studentView}
                                    sourceOpenRequest={effectiveModuleSourceOpenRequest}
                                />
                            )}
                            {activeTab === 'araging' && (
                                <ReportErrorBoundary>
                                    <ARAgingReport
                                        customers={customers}
                                        fmt={fmt}
                                        viewingStudentId={studentView?.id || null}
                                        viewingStudentName={studentView?.full_name || null}
                                        onNavigate={navigateTo}
                                        onOpenSource={openReportSourceRecord}
                                    />
                                </ReportErrorBoundary>
                            )}
                            {activeTab === 'apvouchers' && (
                                <APVouchersTab
                                    suppliers={suppliers}
                                    accounts={accounts}
                                    fmt={fmt}
                                    onRefresh={fetchData}
                                    selectedCourse={selectedCourse}
                                    permissions={permissions?.ap}
                                    viewingStudentId={studentView?.id || null}
                                    viewingStudentName={studentView?.full_name || null}
                                    isReadOnly={isAuditor || !!studentView}
                                    sourceOpenRequest={effectiveModuleSourceOpenRequest}
                                />
                            )}
                            {activeTab === 'appayments' && (
                                <APPaymentsTab
                                    suppliers={suppliers}
                                    fmt={fmt}
                                    onRefresh={fetchData}
                                    selectedCourse={selectedCourse}
                                    permissions={permissions?.ap}
                                    viewingStudentId={studentView?.id || null}
                                    viewingStudentName={studentView?.full_name || null}
                                    isReadOnly={isAuditor || !!studentView}
                                    sourceOpenRequest={effectiveModuleSourceOpenRequest}
                                />
                            )}
                            {activeTab === 'apsummary' && (
                                <ReportErrorBoundary>
                                    <APSummaryReport
                                        suppliers={suppliers}
                                        fmt={fmt}
                                        viewingStudentId={studentView?.id || null}
                                        viewingStudentName={studentView?.full_name || null}
                                        onNavigate={navigateTo}
                                        onOpenSource={openReportSourceRecord}
                                    />
                                </ReportErrorBoundary>
                            )}
                            {activeTab === 'apaging' && (
                                <ReportErrorBoundary>
                                    <APAgingReport
                                        suppliers={suppliers}
                                        fmt={fmt}
                                        viewingStudentId={studentView?.id || null}
                                        viewingStudentName={studentView?.full_name || null}
                                        onNavigate={navigateTo}
                                        onOpenSource={openReportSourceRecord}
                                    />
                                </ReportErrorBoundary>
                            )}
                            {activeTab === 'apwithholding' && (
                                <ReportErrorBoundary>
                                    <APWithholdingReport
                                        suppliers={suppliers}
                                        fmt={fmt}
                                        viewingStudentId={studentView?.id || null}
                                        viewingStudentName={studentView?.full_name || null}
                                        onNavigate={navigateTo}
                                        onOpenSource={openReportSourceRecord}
                                    />
                                </ReportErrorBoundary>
                            )}
                            {activeTab === 'audit' && (
                                <AuditTrailTab
                                    auditLogs={auditLogs}
                                    entries={entries}
                                    fmt={fmt}
                                    user={user}
                                    viewingStudentName={studentView?.full_name || null}
                                    isReadOnly={isAuditor || !!studentView}
                                    closeReadinessPeriod={closeReadinessPeriod}
                                    onNavigate={navigateTo}
                                    onOpenBankReconPeriod={openBankReconForReviewPeriod}
                                />
                            )}
                        </div>
                    </ScrollArea>

                {/* Student Guide Drawer - responsive: side on xl, overlay on smaller */}
                    {activeTab !== 'home' && showHelp && (
                        <aside
                            className="hidden xl:flex w-80 border-l border-slate-200 bg-white flex-shrink-0 flex-col z-30"
                            data-testid="student-guide-drawer"
                        >
                            <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between bg-slate-50">
                                <div className="flex items-center gap-2">
                                    <BookOpen className="w-4 h-4 text-[#2F8FCB]" />
                                    <span className="font-semibold text-sm text-slate-900">Student Guide</span>
                                </div>
                                <button onClick={toggleGuide} className="p-1 hover:bg-slate-200 rounded" data-testid="guide-close-btn">
                                    <X className="w-4 h-4 text-slate-400" />
                                </button>
                            </div>
                            <ScrollArea className="flex-1 p-4">
                                <HelpContent activeTab={activeTab} currentStep={currentStep} />
                            </ScrollArea>
                        </aside>
                    )}
                    {/* Mobile/tablet overlay version */}
                    {activeTab !== 'home' && showHelp && (
                        <div className="xl:hidden absolute inset-0 z-40" onClick={toggleGuide}>
                            <div className="absolute inset-0 bg-black/10" />
                            <div
                                className="absolute top-0 right-0 h-full w-80 bg-white border-l border-slate-200 shadow-2xl flex flex-col"
                                onClick={(e) => e.stopPropagation()}
                            >
                                <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between bg-slate-50">
                                    <div className="flex items-center gap-2">
                                        <BookOpen className="w-4 h-4 text-[#2F8FCB]" />
                                        <span className="font-semibold text-sm text-slate-900">Student Guide</span>
                                    </div>
                                    <button onClick={toggleGuide} className="p-1 hover:bg-slate-200 rounded">
                                        <X className="w-4 h-4 text-slate-400" />
                                    </button>
                                </div>
                                <ScrollArea className="flex-1 p-4">
                                    <HelpContent activeTab={activeTab} currentStep={currentStep} />
                                </ScrollArea>
                            </div>
                        </div>
                    )}
                </main>

                {/* BOTTOM BAR - Quick Access Shortcuts Only */}
                <div className="bg-slate-50 border-t border-slate-200 px-2 py-2 overflow-x-auto flex-shrink-0">
                    <div className="flex items-center gap-1 min-w-max">
                        <span className="text-xs text-slate-500 font-medium px-2">Quick Access:</span>
                        {QUICK_ACCESS.map((btn) => (
                            btn.divider ? (
                                <div key={btn.id} className="w-px h-10 bg-slate-300 mx-2" />
                            ) : (
                                <Tooltip key={btn.id}>
                                    <TooltipTrigger asChild>
                                        <button
                                            type="button"
                                            onClick={() => handleQuickAccessClick(btn)}
                                            className={`flex flex-col items-center justify-center p-1.5 rounded-lg hover:bg-white hover:shadow transition-all min-w-[50px] ${
                                                activeTab === btn.tab ? 'bg-white shadow ring-2 ring-[#2F8FCB]/30' : ''
                                            }`}
                                            data-testid={`quick-${btn.id}`}
                                        >
                                            <div className={`w-8 h-8 rounded-lg ${btn.color} text-white flex items-center justify-center mb-0.5`}>
                                                <btn.icon className="w-4 h-4" />
                                            </div>
                                            <span className="text-[9px] text-slate-600 font-medium">{btn.label}</span>
                                        </button>
                                    </TooltipTrigger>
                                    <TooltipContent><p>{btn.tooltip || btn.label}</p></TooltipContent>
                                </Tooltip>
                            )
                        ))}
                    </div>
                </div>

                <Dialog open={showPracticeCleanupDialog} onOpenChange={handlePracticeCleanupDialogChange}>
                    <DialogContent className="max-w-2xl">
                        <DialogHeader>
                            <DialogTitle>Clear Draft Practice Work</DialogTitle>
                            <DialogDescription>
                                Remove only draft or unposted practice records while preserving posted accounting evidence and audit history.
                            </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4">
                            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                                This removes only draft/unposted practice records for{' '}
                                <strong>{practiceCleanupPreview?.scope?.owner_name || 'this workspace'}</strong>.
                                Posted, reversed, cancelled, matched, and posted bank reconciliation evidence stays in General Ledger, Trial Balance, reports, and audit trail.
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                                    <p className="text-xs uppercase tracking-wide text-slate-500">Will be removed</p>
                                    <p className="text-2xl font-bold text-slate-900 mt-1">{practiceCleanupPreview?.removable_total || 0}</p>
                                    <p className="text-xs text-slate-500 mt-1">Draft/unposted practice records and pending practice review items.</p>
                                </div>
                                <div className="rounded-lg border border-emerald-100 bg-emerald-50 p-3">
                                    <p className="text-xs uppercase tracking-wide text-emerald-600">Protected</p>
                                    <p className="text-2xl font-bold text-emerald-700 mt-1">{practiceCleanupPreview?.protected_total || 0}</p>
                                    <p className="text-xs text-emerald-700 mt-1">Posted records, review items, and evidence remain available where protected.</p>
                                </div>
                            </div>
                            <div>
                                <Label htmlFor="practice-cleanup-confirm">Type confirmation phrase</Label>
                                <Input
                                    id="practice-cleanup-confirm"
                                    value={practiceCleanupConfirm}
                                    onChange={(event) => setPracticeCleanupConfirm(event.target.value)}
                                    placeholder={practiceCleanupPreview?.confirm_phrase || 'CLEAR DRAFT PRACTICE WORK'}
                                    className="mt-1 font-mono"
                                    data-testid="practice-cleanup-confirm-input"
                                />
                                <p className="text-xs text-slate-500 mt-2">
                                    Required phrase: <span className="font-mono font-semibold">{practiceCleanupPreview?.confirm_phrase || 'CLEAR DRAFT PRACTICE WORK'}</span>
                                </p>
                            </div>
                        </div>
                        <DialogFooter>
                            <DialogClose asChild>
                                <Button type="button" variant="outline">Cancel</Button>
                            </DialogClose>
                            <Button
                                type="button"
                                onClick={handleClearPracticeCleanup}
                                disabled={
                                    practiceCleanupLoading ||
                                    practiceCleanupConfirm !== (practiceCleanupPreview?.confirm_phrase || 'CLEAR DRAFT PRACTICE WORK') ||
                                    !(practiceCleanupPreview?.removable_total > 0)
                                }
                                className="bg-amber-600 hover:bg-amber-700"
                                data-testid="practice-cleanup-confirm-clear"
                            >
                                <Trash2 className="w-4 h-4 mr-2" />
                                {practiceCleanupLoading ? 'Clearing...' : 'Clear Draft Practice Work'}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>

                {/* Status Bar */}
                <footer className="bg-[#0F2A44] text-white/80 px-4 py-1.5 flex items-center justify-between text-xs">
                    <div className="flex items-center gap-4">
                        <span>User: <strong className="text-white">{user?.full_name}</strong></span>
                        <span>Role: <strong className="text-white capitalize">{user?.role}</strong></span>
                        {studentView && <span>Reviewing: <strong className="text-white">{studentView.full_name}</strong></span>}
                        <span>Course: <strong className="text-white">{statusCourseLabel}</strong></span>
                    </div>
                    <div className="flex items-center gap-4">
                        <span>Database: <strong className="text-[#2F8FCB]">macesuite_db</strong></span>
                        <span>
                            Server:{' '}
                            <strong className="inline-flex items-center gap-1 text-emerald-400">
                                <span className="h-2 w-2 rounded-full bg-emerald-400" aria-hidden="true" />
                                Connected
                            </strong>
                        </span>
                    </div>
                </footer>
            </div>
        </TooltipProvider>
    );
}

// Accounting Home View Component - Workflow Map
function AccountingHomeView({
    onNavigate,
    stats,
    controlSummary,
    periodCloseSummary,
    accountingActivity,
    practiceCleanupPreview,
    practiceCleanupLoading,
    onRefreshPracticeCleanup,
    onOpenPracticeCleanup,
    onOpenReturnedWork,
    onOpenPostedEvidence,
    closeReadinessPeriod,
    onCloseReadinessPeriodChange,
    onOpenCloseBankRecon,
    onOpenCloseGl,
    onOpenCloseTrial,
    onPeriodLockChanged,
    userRole,
    studentView,
    enrolledStudentsCount,
}) {
    const workflowSteps = [
        { 
            id: 'setup', 
            title: 'Setup', 
            icon: Settings, 
            color: 'bg-blue-500',
            description: 'Configure master data',
            items: ['Chart of Accounts', 'Opening Balance Setup', 'Masters'],
            action: () => onNavigate('openingbalances')
        },
        { 
            id: 'record', 
            title: 'Record', 
            icon: Edit2, 
            color: 'bg-emerald-500',
            description: 'Enter transactions',
            items: ['Journal Vouchers', 'AP Vouchers', 'AR Invoices'],
            action: () => onNavigate('jv')
        },
        { 
            id: 'post', 
            title: 'Post', 
            icon: FileDown, 
            color: 'bg-amber-500',
            description: 'Finalize entries',
            items: ['Post to GL', 'Unpost if needed'],
            action: () => onNavigate('jv')
        },
        { 
            id: 'review', 
            title: 'Review', 
            icon: CheckSquare, 
            color: 'bg-purple-500',
            description: 'Verify accuracy',
            items: ['General Ledger', 'Trial Balance', 'Audit Trail'],
            action: () => onNavigate('glentries')
        },
        { 
            id: 'report', 
            title: 'Report', 
            icon: BarChart3, 
            color: 'bg-rose-500',
            description: 'Generate statements',
            items: ['Income Statement', 'Balance Sheet'],
            action: () => onNavigate('incomestatement')
        },
    ];

    return (
        <div className="max-w-6xl mx-auto">
            {/* Header */}
            <div className="text-center mb-8">
                <h1 className="text-3xl font-bold text-[#0F2A44] font-['Manrope'] mb-2">
                    Accounting Workflow
                </h1>
                <p className="text-slate-600">
                    Follow the accounting cycle from setup to financial reporting
                </p>
            </div>

            {/* Workflow Map */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8 mb-8">
                <div className="flex items-center justify-between gap-2">
                    {workflowSteps.map((step, index) => (
                        <div key={step.id} className="flex items-center">
                            <button
                                onClick={step.action}
                                className="flex flex-col items-center group"
                                data-testid={`workflow-${step.id}`}
                            >
                                <div className={`w-16 h-16 rounded-2xl ${step.color} text-white flex items-center justify-center mb-3 group-hover:scale-110 transition-transform shadow-lg`}>
                                    <step.icon className="w-8 h-8" />
                                </div>
                                <h3 className="font-semibold text-slate-900 text-sm">{step.title}</h3>
                                <p className="text-xs text-slate-500 text-center mt-1 max-w-[100px]">{step.description}</p>
                            </button>
                            {index < workflowSteps.length - 1 && (
                                <ArrowRight className="w-6 h-6 text-slate-300 mx-4 flex-shrink-0" />
                            )}
                        </div>
                    ))}
                </div>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl bg-blue-100 text-blue-600 flex items-center justify-center">
                            <Calculator className="w-6 h-6" />
                        </div>
                        <div>
                            <p className="text-2xl font-bold text-slate-900">{stats.vouchers}</p>
                            <p className="text-sm text-slate-500">Journal Vouchers</p>
                        </div>
                    </div>
                </div>
                <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl bg-amber-100 text-amber-600 flex items-center justify-center">
                            <Users className="w-6 h-6" />
                        </div>
                        <div>
                            <p className="text-2xl font-bold text-slate-900">{stats.suppliers}</p>
                            <p className="text-sm text-slate-500">Suppliers</p>
                        </div>
                    </div>
                </div>
                <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl bg-rose-100 text-rose-600 flex items-center justify-center">
                            <ShoppingCart className="w-6 h-6" />
                        </div>
                        <div>
                            <p className="text-2xl font-bold text-slate-900">{stats.customers}</p>
                            <p className="text-sm text-slate-500">Customers</p>
                        </div>
                    </div>
                </div>
            </div>

            <ControlReadinessPanel summary={controlSummary} onNavigate={onNavigate} />
            <ReturnedWorkHomePanel
                activity={accountingActivity}
                studentView={studentView}
                onOpenReturnedWork={onOpenReturnedWork}
            />
            <PostedEvidenceHomePanel
                activity={accountingActivity}
                studentView={studentView}
                onOpenPostedEvidence={onOpenPostedEvidence}
                onNavigate={onNavigate}
            />
            <PracticeCleanupPanel
                preview={practiceCleanupPreview}
                loading={practiceCleanupLoading}
                onRefresh={onRefreshPracticeCleanup}
                onOpenCleanup={onOpenPracticeCleanup}
                onNavigate={onNavigate}
            />
            <PeriodCloseReadinessPanel
                summary={periodCloseSummary}
                onNavigate={onNavigate}
                selectedPeriod={closeReadinessPeriod}
                onSelectedPeriodChange={onCloseReadinessPeriodChange}
                onOpenBankReconPeriod={onOpenCloseBankRecon}
                onOpenCloseGl={onOpenCloseGl}
                onOpenCloseTrial={onOpenCloseTrial}
                onPeriodLockChanged={onPeriodLockChanged}
            />

            {(userRole === 'instructor' || userRole === 'admin') && (
                <section className="bg-white border border-blue-100 rounded-2xl shadow-sm p-5 mb-8" data-testid="student-review-workflow-card">
                    <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                        <div className="flex items-start gap-3">
                            <div className="w-11 h-11 rounded-xl bg-blue-50 text-[#2F8FCB] flex items-center justify-center shrink-0">
                                <Eye className="w-5 h-5" />
                            </div>
                            <div>
                                <p className="text-xs uppercase tracking-widest text-blue-500 font-semibold">Instructor review path</p>
                                <h2 className="text-lg font-bold text-[#0F2A44] mt-1">
                                    {studentView ? `Now reviewing ${studentView.full_name}` : 'Need to find a student transaction?'}
                                </h2>
                                <p className="text-sm text-slate-600 mt-1 max-w-3xl">
                                    {studentView
                                        ? 'This workspace is filtered to the selected student. Use Journal Voucher, AR, AP, Bank Reconciliation, General Ledger, or Trial Balance to inspect their work.'
                                        : enrolledStudentsCount > 0
                                            ? 'Use the Review student dropdown in the top bar, then open Journal Voucher, AR, AP, Bank Reconciliation, General Ledger, or Trial Balance. The blue banner confirms whose records are on screen.'
                                            : 'No enrolled students are available for this instructor account yet.'}
                                </p>
                            </div>
                        </div>
                        <div className="flex flex-wrap gap-2">
                            <Button type="button" variant="outline" onClick={() => onNavigate('arinvoices')} data-testid="review-path-ar-invoices">
                                <Receipt className="w-4 h-4 mr-2" /> AR Invoices
                            </Button>
                            <Button type="button" variant="outline" onClick={() => onNavigate('apvouchers')} data-testid="review-path-ap-vouchers">
                                <CreditCard className="w-4 h-4 mr-2" /> AP Vouchers
                            </Button>
                            <Button type="button" variant="outline" onClick={() => onNavigate('appayments')} data-testid="review-path-ap-payments">
                                <Wallet className="w-4 h-4 mr-2" /> AP Payments
                            </Button>
                            <Button type="button" variant="outline" onClick={() => onNavigate('jv')} data-testid="review-path-jv">
                                <Calculator className="w-4 h-4 mr-2" /> Journal Voucher
                            </Button>
                            <Button type="button" variant="outline" onClick={() => onNavigate('trial')} data-testid="review-path-trial">
                                <Scale className="w-4 h-4 mr-2" /> Trial Balance
                            </Button>
                        </div>
                    </div>
                </section>
            )}

            {/* Quick Actions */}
            <div className="bg-gradient-to-r from-[#0F2A44] to-[#1E3A8A] rounded-2xl p-6 text-white">
                <h2 className="font-semibold mb-1">Quick Actions</h2>
                <p className="text-white/50 text-xs mb-4">Click to go directly to a module and start working</p>

                {/* Record Transactions */}
                <p className="text-white/40 text-xs uppercase tracking-widest mb-2">Record Transactions</p>
                <div className="grid grid-cols-3 gap-3 mb-4">
                    <button
                        type="button"
                        onClick={() => onNavigate('jv')}
                        className="bg-white/10 hover:bg-white/20 rounded-xl p-4 text-left transition-colors"
                        data-testid="qa-new-jv"
                    >
                        <Calculator className="w-6 h-6 mb-2 text-[#2F8FCB]" />
                        <p className="font-medium text-sm">Journal Voucher</p>
                        <p className="text-white/40 text-xs mt-0.5">DR / CR entries</p>
                    </button>
                    <button
                        type="button"
                        onClick={() => onNavigate('apvouchers')}
                        className="bg-white/10 hover:bg-white/20 rounded-xl p-4 text-left transition-colors"
                        data-testid="qa-new-apv"
                    >
                        <FileText className="w-6 h-6 mb-2 text-amber-400" />
                        <p className="font-medium text-sm">AP Vouchers</p>
                        <p className="text-white/40 text-xs mt-0.5">Open bills list</p>
                    </button>
                    <button
                        type="button"
                        onClick={() => onNavigate('arinvoices')}
                        className="bg-white/10 hover:bg-white/20 rounded-xl p-4 text-left transition-colors"
                        data-testid="qa-new-ari"
                    >
                        <Receipt className="w-6 h-6 mb-2 text-emerald-400" />
                        <p className="font-medium text-sm">AR Invoices</p>
                        <p className="text-white/40 text-xs mt-0.5">Open invoice list</p>
                    </button>
                </div>

                {/* Master Data & Reports */}
                <p className="text-white/40 text-xs uppercase tracking-widest mb-2">Master Data & Reports</p>
                <div className="grid grid-cols-3 gap-3">
                    <button
                        onClick={() => onNavigate('suppliers')}
                        className="bg-white/10 hover:bg-white/20 rounded-xl p-4 text-left transition-colors"
                        data-testid="qa-suppliers"
                    >
                        <Users className="w-6 h-6 mb-2 text-amber-400" />
                        <p className="font-medium text-sm">Suppliers</p>
                        <p className="text-white/40 text-xs mt-0.5">Manage supplier list</p>
                    </button>
                    <button
                        onClick={() => onNavigate('customers')}
                        className="bg-white/10 hover:bg-white/20 rounded-xl p-4 text-left transition-colors"
                        data-testid="qa-customers"
                    >
                        <ShoppingCart className="w-6 h-6 mb-2 text-rose-400" />
                        <p className="font-medium text-sm">Customers</p>
                        <p className="text-white/40 text-xs mt-0.5">Manage customer list</p>
                    </button>
                    <button
                        onClick={() => onNavigate('trial')}
                        className="bg-white/10 hover:bg-white/20 rounded-xl p-4 text-left transition-colors"
                        data-testid="qa-trial-balance"
                    >
                        <Scale className="w-6 h-6 mb-2 text-purple-400" />
                        <p className="font-medium text-sm">Trial Balance</p>
                        <p className="text-white/40 text-xs mt-0.5">Check DR = CR</p>
                    </button>
                </div>
            </div>
        </div>
    );
}

function PracticeCleanupPanel({ preview, loading, onRefresh, onOpenCleanup, onNavigate }) {
    if (!preview) return null;

    const removable = preview.removable || [];
    const protectedRows = preview.protected || [];
    const removableTotal = preview.removable_total || 0;
    const protectedTotal = preview.protected_total || 0;
    const canClear = preview.can_clear && removableTotal > 0;
    const topRemovable = removable.filter(item => item.count > 0).slice(0, 4);
    const topProtected = protectedRows.filter(item => item.count > 0).slice(0, 3);

    return (
        <section className="bg-white border border-slate-200 rounded-2xl shadow-sm p-6 mb-8" data-testid="practice-cleanup-panel">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                <div className="flex items-start gap-3">
                    <div className="w-12 h-12 rounded-xl bg-amber-50 text-amber-700 flex items-center justify-center shrink-0">
                        <Trash2 className="w-6 h-6" />
                    </div>
                    <div>
                        <p className="text-xs uppercase tracking-widest text-amber-500 font-semibold">Practice Cleanup</p>
                        <h2 className="text-xl font-bold text-[#0F2A44] mt-1">
                            {removableTotal ? 'Clear draft practice clutter' : 'Practice workspace is clean'}
                        </h2>
                        <p className="text-sm text-slate-600 mt-1 max-w-2xl">
                            {preview.what_changes} {preview.report_impact}
                        </p>
                        <p className="text-xs text-slate-400 mt-2">
                            Scope: {preview.scope?.owner_name || 'Current workspace'} - Status: {preview.status?.replace(/_/g, ' ')}
                        </p>
                    </div>
                </div>

                <div className="grid grid-cols-2 gap-2 min-w-[260px]">
                    <div className="rounded-xl bg-amber-50 border border-amber-100 p-3 text-center">
                        <p className="text-lg font-bold text-amber-700">{removableTotal}</p>
                        <p className="text-[11px] uppercase tracking-wide text-amber-600">Draft/Unposted</p>
                    </div>
                    <div className="rounded-xl bg-emerald-50 border border-emerald-100 p-3 text-center">
                        <p className="text-lg font-bold text-emerald-700">{protectedTotal}</p>
                        <p className="text-[11px] uppercase tracking-wide text-emerald-600">Protected</p>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-4 mt-5">
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between mb-3">
                        <div>
                            <p className="font-semibold text-sm text-slate-900">What will be cleared</p>
                            <p className="text-xs text-slate-500">Only non-posting records are removed from active lists.</p>
                        </div>
                        <div className="flex flex-wrap gap-2">
                            <Button type="button" variant="outline" size="sm" onClick={onRefresh} disabled={loading} data-testid="practice-cleanup-refresh">
                                <Eye className="w-4 h-4 mr-2" /> {loading ? 'Checking...' : 'Refresh Count'}
                            </Button>
                            <Button
                                type="button"
                                size="sm"
                                onClick={onOpenCleanup}
                                disabled={!canClear || loading}
                                className="bg-amber-600 hover:bg-amber-700"
                                data-testid="practice-cleanup-open-dialog"
                            >
                                <Trash2 className="w-4 h-4 mr-2" /> Clear Drafts
                            </Button>
                        </div>
                    </div>

                    {topRemovable.length > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                            {topRemovable.map((item) => (
                                <button
                                    key={item.id}
                                    type="button"
                                    onClick={() => {
                                        if (item.location.includes('Journal Voucher')) onNavigate('jv');
                                        else if (item.location.includes('AR Invoices')) onNavigate('arinvoices');
                                        else if (item.location.includes('Collections')) onNavigate('arcollections');
                                        else if (item.location.includes('AP Vouchers')) onNavigate('apvouchers');
                                        else if (item.location.includes('Payments')) onNavigate('appayments');
                                        else if (item.location.includes('Bank Reconciliation')) onNavigate('bankrecon');
                                        else onNavigate('glentries');
                                    }}
                                    className="rounded-lg border border-white bg-white hover:border-amber-200 p-3 text-left transition-colors"
                                    data-testid={`practice-cleanup-removable-${item.id}`}
                                >
                                    <div className="flex items-center justify-between gap-2">
                                        <p className="font-semibold text-sm text-slate-900">{item.label}</p>
                                        <span className="rounded-full bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-700">{item.count}</span>
                                    </div>
                                    <p className="text-xs text-slate-500 mt-1">{item.location}</p>
                                    <p className="text-[11px] text-slate-400 mt-1">{item.impact}</p>
                                </button>
                            ))}
                        </div>
                    ) : (
                        <div className="rounded-lg border border-emerald-100 bg-emerald-50 p-3 text-sm text-emerald-700">
                            No active draft/unposted practice records were found for cleanup.
                        </div>
                    )}
                </div>

                <div className="rounded-xl border border-emerald-100 bg-emerald-50 p-4">
                    <div className="flex items-center gap-2 mb-3">
                        <Shield className="w-4 h-4 text-emerald-700" />
                        <p className="font-semibold text-sm text-emerald-900">Protected accounting/review evidence</p>
                    </div>
                    <p className="text-xs text-emerald-800 leading-snug mb-3">
                        Posted records stay in General Ledger, Trial Balance, reports, Bank Reconciliation evidence, and audit history. Student-submitted work may remain protected for instructor review.
                    </p>
                    <div className="space-y-2">
                        {topProtected.length > 0 ? topProtected.map((item) => (
                            <div key={item.id} className="rounded-lg bg-white border border-emerald-100 p-2">
                                <div className="flex items-center justify-between gap-2">
                                    <p className="text-xs font-semibold text-slate-800">{item.label}</p>
                                    <span className="text-xs font-bold text-emerald-700">{item.count}</span>
                                </div>
                                <p className="text-[11px] text-slate-500 mt-1">{item.impact}</p>
                            </div>
                        )) : (
                            <p className="text-xs text-emerald-700">No protected posted records counted in this workspace yet.</p>
                        )}
                    </div>
                    <p className="text-xs font-semibold text-emerald-900 mt-3">
                        Next safe action: {preview.next_safe_action}
                    </p>
                </div>
            </div>
        </section>
    );
}

function ReturnedWorkHomePanel({ activity, studentView, onOpenReturnedWork }) {
    const returnedItems = (activity?.items || [])
        .filter(item => item.review_status === 'returned')
        .filter(item => !studentView?.id || item.student_id === studentView.id)
        .slice(0, 5);

    if (returnedItems.length === 0) return null;

    return (
        <section className="bg-orange-50 border border-orange-200 rounded-2xl shadow-sm p-5 mb-8" data-testid="returned-work-home-panel">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                <div>
                    <p className="text-xs uppercase tracking-widest text-orange-600 font-semibold">Returned work to fix</p>
                    <h2 className="text-lg font-bold text-[#0F2A44] mt-1">
                        {studentView ? `${studentView.full_name} has returned accounting work` : 'Returned accounting work needs revision'}
                    </h2>
                    <p className="text-sm text-orange-900 mt-1 max-w-3xl">
                        Returned items are back in the student's source list with feedback. They are editable evidence only and do not affect GL, Trial Balance, or reports until approved and posted.
                    </p>
                </div>
                <span className="inline-flex w-fit rounded-full bg-white px-3 py-1 text-xs font-semibold text-orange-700 border border-orange-200">
                    {returnedItems.length} visible returned item{returnedItems.length === 1 ? '' : 's'}
                </span>
            </div>

            <div className="mt-4 grid gap-3">
                {returnedItems.map(item => (
                    <div key={item.id} className="rounded-xl border border-orange-100 bg-white p-4" data-testid={`returned-work-home-${item.id}`}>
                        <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                            <div className="min-w-0">
                                <div className="flex flex-wrap items-center gap-2">
                                    <span className="rounded-full bg-orange-100 px-2 py-0.5 text-xs font-semibold text-orange-700">
                                        Returned for Revision
                                    </span>
                                    <span className="text-xs text-slate-500">{item.module_label}</span>
                                </div>
                                <p className="mt-2 font-semibold text-slate-900">
                                    {item.record_type} {item.record_number || ''}
                                </p>
                                <p className="text-sm text-slate-600">
                                    {item.counterparty_label}: {item.counterparty_name || 'N/A'}
                                    {item.student_name ? ` | Student: ${item.student_name}` : ''}
                                </p>
                                <p className="mt-2 text-xs text-orange-800">
                                    Next safe action: {item.next_safe_action || 'Open the source record, read feedback, revise, then resubmit.'}
                                </p>
                                <p className="mt-1 text-xs text-slate-500">
                                    GL / Trial Balance: {item.downstream_impact || 'No GL or Trial Balance impact until posted.'}
                                </p>
                            </div>
                            <Button
                                type="button"
                                variant="outline"
                                className="shrink-0 border-orange-200 text-orange-700 hover:bg-orange-50"
                                onClick={() => onOpenReturnedWork?.(item)}
                                data-testid={`open-returned-work-${item.id}`}
                            >
                                Open Returned Work <ArrowRight className="w-4 h-4 ml-2" />
                            </Button>
                        </div>
                    </div>
                ))}
            </div>
        </section>
    );
}

function PostedEvidenceHomePanel({ activity, studentView, onOpenPostedEvidence, onNavigate }) {
    const postedItems = (activity?.items || [])
        .filter(item => item.status === 'posted' || item.affects_gl || item.affects_tb)
        .filter(item => item.record_type !== 'Posted GL Entry')
        .filter(item => !studentView?.id || item.student_id === studentView.id)
        .slice(0, 5);

    if (postedItems.length === 0) return null;

    const postedTotal = activity?.summary?.posted || postedItems.length;

    return (
        <section className="bg-white border border-emerald-200 rounded-2xl shadow-sm p-5 mb-8" data-testid="posted-evidence-home-panel">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                <div>
                    <p className="text-xs uppercase tracking-widest text-emerald-600 font-semibold">Posted evidence trail</p>
                    <h2 className="text-lg font-bold text-[#0F2A44] mt-1">
                        {studentView ? `${studentView.full_name}'s posted work appears in reports` : 'Recent posted work appears in GL and reports'}
                    </h2>
                    <p className="text-sm text-slate-600 mt-1 max-w-3xl">
                        Posted records are no longer editable drafts. They remain in their source module, write to the General Ledger, and appear in Trial Balance and reports based on transaction date.
                    </p>
                </div>
                <div className="flex flex-wrap gap-2">
                    <span className="inline-flex w-fit rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700 border border-emerald-100">
                        {postedTotal} posted in activity
                    </span>
                    <Button type="button" variant="outline" size="sm" onClick={() => onNavigate?.('glentries')} data-testid="posted-evidence-open-gl">
                        <BookOpen className="w-4 h-4 mr-2" /> General Ledger
                    </Button>
                    <Button type="button" variant="outline" size="sm" onClick={() => onNavigate?.('trial')} data-testid="posted-evidence-open-trial">
                        <Scale className="w-4 h-4 mr-2" /> Trial Balance
                    </Button>
                </div>
            </div>

            <div className="mt-4 grid gap-3">
                {postedItems.map(item => (
                    <div key={item.id} className="rounded-xl border border-emerald-100 bg-emerald-50/40 p-4" data-testid={`posted-evidence-home-${item.id}`}>
                        <div className="flex flex-col gap-3 xl:flex-row xl:items-start xl:justify-between">
                            <div className="min-w-0">
                                <div className="flex flex-wrap items-center gap-2">
                                    <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-700">
                                        Posted
                                    </span>
                                    <span className="text-xs text-slate-500">{item.module_label}</span>
                                    {item.record_date && <span className="text-xs text-slate-400">Date: {item.record_date}</span>}
                                </div>
                                <p className="mt-2 font-semibold text-slate-900">
                                    {item.record_type} {item.record_number || ''}
                                </p>
                                <p className="text-sm text-slate-600">
                                    {item.counterparty_label}: {item.counterparty_name || 'N/A'}
                                    {item.student_name ? ` | Student: ${item.student_name}` : ''}
                                </p>
                                <p className="mt-2 text-xs text-emerald-800">
                                    Where it appears now: source module, Review &gt; General Ledger, Trial Balance, and related reports for its transaction date.
                                </p>
                                <p className="mt-1 text-xs text-slate-500">
                                    Next safe action: {item.next_safe_action || 'Verify source evidence against General Ledger and Trial Balance.'}
                                </p>
                            </div>
                            <div className="flex flex-wrap gap-2 shrink-0">
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={() => onOpenPostedEvidence?.(item, 'source')}
                                    data-testid={`open-posted-source-${item.id}`}
                                >
                                    Source <ArrowRight className="w-4 h-4 ml-2" />
                                </Button>
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={() => onOpenPostedEvidence?.(item, 'gl')}
                                    data-testid={`open-posted-gl-${item.id}`}
                                >
                                    GL
                                </Button>
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={() => onOpenPostedEvidence?.(item, 'trial')}
                                    data-testid={`open-posted-trial-${item.id}`}
                                >
                                    TB
                                </Button>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </section>
    );
}

function ControlReadinessPanel({ summary, onNavigate }) {
    if (!summary) return null;

    const modules = summary.modules || [];
    const capabilities = summary.capabilities || [];
    const lms = summary.lms_control || {};
    const mode = summary.mode || {};
    const visibleModules = modules.slice(0, 5);
    const totals = modules.reduce((acc, module) => {
        acc.open += module.open_count || 0;
        acc.posted += module.posted_count || 0;
        acc.exceptions += module.exception_count || 0;
        return acc;
    }, { open: 0, posted: 0, exceptions: 0 });

    return (
        <section className="bg-white border border-slate-200 rounded-2xl shadow-sm p-6 mb-8" data-testid="control-readiness-panel">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between mb-5">
                <div className="flex items-start gap-3">
                    <div className="w-12 h-12 rounded-xl bg-[#0F2A44] text-white flex items-center justify-center shrink-0">
                        <Shield className="w-6 h-6" />
                    </div>
                    <div>
                        <p className="text-xs uppercase tracking-widest text-slate-400 font-semibold">Phase C Control Readiness</p>
                        <h2 className="text-xl font-bold text-[#0F2A44] mt-1">{mode.title || 'Role control view'}</h2>
                        <p className="text-sm text-slate-600 mt-1 max-w-2xl">{mode.description || summary.control_note}</p>
                        <p className="text-xs text-slate-400 mt-2">
                            Scope: {summary.scope?.owner_name || 'Current workspace'} - Role: {summary.role_label}
                        </p>
                    </div>
                </div>

                <div className="grid grid-cols-3 gap-2 min-w-[260px]">
                    <div className="rounded-xl bg-slate-50 border border-slate-200 p-3 text-center">
                        <p className="text-lg font-bold text-slate-900">{totals.open}</p>
                        <p className="text-[11px] uppercase tracking-wide text-slate-500">Draft/Review</p>
                    </div>
                    <div className="rounded-xl bg-emerald-50 border border-emerald-100 p-3 text-center">
                        <p className="text-lg font-bold text-emerald-700">{totals.posted}</p>
                        <p className="text-[11px] uppercase tracking-wide text-emerald-600">Posted</p>
                    </div>
                    <div className="rounded-xl bg-amber-50 border border-amber-100 p-3 text-center">
                        <p className="text-lg font-bold text-amber-700">{totals.exceptions}</p>
                        <p className="text-[11px] uppercase tracking-wide text-amber-600">Reversed/Cancelled</p>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-5">
                <div>
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-2 mb-4" data-testid="control-capabilities">
                        {capabilities.map((capability) => (
                            <div
                                key={capability.id}
                                className={`rounded-xl border px-3 py-3 ${
                                    capability.enabled
                                        ? 'bg-blue-50 border-blue-100 text-blue-900'
                                        : 'bg-slate-50 border-slate-200 text-slate-400'
                                }`}
                            >
                                <div className="flex items-center gap-2">
                                    {capability.enabled ? (
                                        <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                                    ) : (
                                        <Lock className="w-4 h-4" />
                                    )}
                                    <p className="font-semibold text-sm">{capability.label}</p>
                                </div>
                                <p className="text-xs mt-1 leading-snug">{capability.note}</p>
                            </div>
                        ))}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-3" data-testid="control-module-cards">
                        {visibleModules.map((module) => (
                            <button
                                key={module.id}
                                type="button"
                                onClick={() => onNavigate(module.tab)}
                                className="text-left rounded-xl border border-slate-200 bg-slate-50 hover:bg-white hover:border-[#2F8FCB]/50 hover:shadow-sm transition-all p-3"
                                data-testid={`control-module-${module.id}`}
                            >
                                <div className="flex items-center justify-between gap-2 mb-2">
                                    <p className="font-semibold text-sm text-slate-900">{module.label}</p>
                                    <ChevronRight className="w-4 h-4 text-slate-400" />
                                </div>
                                <div className="flex flex-wrap gap-1.5">
                                    <span className="rounded-full bg-white border border-slate-200 px-2 py-0.5 text-[11px] text-slate-600">
                                        Open {module.open_count || 0}
                                    </span>
                                    <span className="rounded-full bg-emerald-50 border border-emerald-100 px-2 py-0.5 text-[11px] text-emerald-700">
                                        Posted {module.posted_count || 0}
                                    </span>
                                </div>
                                <p className="text-xs text-slate-500 mt-2 leading-snug">{module.next_action}</p>
                            </button>
                        ))}
                    </div>
                </div>

                <div className="rounded-xl border border-slate-200 bg-slate-50 p-4" data-testid="control-lms-card">
                    <div className="flex items-center gap-2 mb-3">
                        <BookOpen className="w-4 h-4 text-[#2F8FCB]" />
                        <p className="font-semibold text-sm text-slate-900">LMS Control Link</p>
                    </div>
                    <div className="grid grid-cols-2 gap-2 mb-3">
                        <div className="bg-white rounded-lg border border-slate-200 p-2">
                            <p className="text-lg font-bold text-slate-900">{lms.courses || 0}</p>
                            <p className="text-[11px] text-slate-500">Courses</p>
                        </div>
                        <div className="bg-white rounded-lg border border-slate-200 p-2">
                            <p className="text-lg font-bold text-slate-900">{lms.assignments || 0}</p>
                            <p className="text-[11px] text-slate-500">Assignments</p>
                        </div>
                    </div>
                    <p className="text-sm font-medium text-slate-700">{lms.next_action || 'Review learning evidence'}</p>
                    <p className="text-xs text-slate-500 mt-1 leading-snug">
                        Student practice, instructor approval, and audit evidence should stay connected.
                    </p>
                </div>
            </div>
        </section>
    );
}

function PeriodCloseReadinessPanel({
    summary,
    onNavigate,
    selectedPeriod,
    onSelectedPeriodChange,
    onOpenBankReconPeriod,
    onOpenCloseGl,
    onOpenCloseTrial,
    onPeriodLockChanged,
}) {
    const [showLockDialog, setShowLockDialog] = useState(false);
    const [showUnlockDialog, setShowUnlockDialog] = useState(false);
    const [lockReason, setLockReason] = useState('');
    const [unlockReason, setUnlockReason] = useState('');
    const [periodLockSubmitting, setPeriodLockSubmitting] = useState(false);
    const [approvalNote, setApprovalNote] = useState('');
    const [approvalSupersedeReason, setApprovalSupersedeReason] = useState('');
    const [periodApprovalSubmitting, setPeriodApprovalSubmitting] = useState(false);

    if (!summary) return null;

    const fmt = (n) => new Intl.NumberFormat('en-PH', { style: 'currency', currency: 'PHP' }).format(Number(n) || 0);
    const currentPeriod = new Date().toISOString().slice(0, 7);
    const selectedPeriodValue = selectedPeriod || currentPeriod;
    const readiness = summary.readiness || {};
    const openItems = summary.open_items || {};
    const glCheck = summary.gl_check || {};
    const bank = summary.bank_reconciliation || {};
    const bankWorkflow = summary.bank_reconciliation_workflow || {};
    const closeChecklist = summary.close_checklist || [];
    const recentBankMovements = bank.recent_movements || [];
    const bankAccounts = bank.accounts || [];
    const bankSources = bank.by_source || [];
    const isReady = readiness.status === 'ready';
    const lockPreview = summary.period_lock_preview || {};
    const lockStatus = lockPreview.status || (isReady ? 'ready_to_lock' : 'blocked');
    const approvalPreview = summary.final_close_approval_preview || {};
    const approvalStatus = approvalPreview.status || (isReady ? 'lock_required' : 'blocked_by_readiness');
    const approvalHistory = approvalPreview.approval_history || [];
    const finalEvidencePackage = summary.final_close_evidence_package || {};
    const finalEvidenceSteps = finalEvidencePackage.evidence_steps || [];
    const finalEvidenceStatus = finalEvidencePackage.status || (isReady ? 'ready_for_review' : 'attention_required');
    const lockPreviewClass = {
        locked: 'bg-slate-900 border-slate-900 text-white',
        ready_to_lock: 'bg-emerald-50 border-emerald-100 text-emerald-800',
        blocked: 'bg-amber-50 border-amber-100 text-amber-800',
        select_student: 'bg-slate-50 border-slate-200 text-slate-700',
    }[lockStatus] || 'bg-slate-50 border-slate-200 text-slate-700';
    const approvalPreviewClass = {
        approved: 'bg-blue-50 border-blue-100 text-blue-800',
        ready_for_approval: 'bg-emerald-50 border-emerald-100 text-emerald-800',
        lock_required: 'bg-indigo-50 border-indigo-100 text-indigo-800',
        blocked_by_readiness: 'bg-amber-50 border-amber-100 text-amber-800',
        select_student: 'bg-slate-50 border-slate-200 text-slate-700',
    }[approvalStatus] || 'bg-slate-50 border-slate-200 text-slate-700';
    const lockStatusPillClass = {
        locked: 'bg-white/15 border-white/25 text-white',
        ready_to_lock: 'bg-emerald-100 border-emerald-200 text-emerald-800',
        blocked: 'bg-amber-100 border-amber-200 text-amber-800',
        select_student: 'bg-white border-slate-200 text-slate-600',
    }[lockStatus] || 'bg-white border-slate-200 text-slate-600';
    const approvalStatusPillClass = {
        approved: 'bg-blue-100 border-blue-200 text-blue-800',
        ready_for_approval: 'bg-emerald-100 border-emerald-200 text-emerald-800',
        lock_required: 'bg-indigo-100 border-indigo-200 text-indigo-800',
        blocked_by_readiness: 'bg-amber-100 border-amber-200 text-amber-800',
        select_student: 'bg-white border-slate-200 text-slate-600',
    }[approvalStatus] || 'bg-white border-slate-200 text-slate-600';
    const finalEvidencePackageClass = {
        approved: 'bg-blue-50 border-blue-100 text-blue-800',
        ready_for_approval: 'bg-emerald-50 border-emerald-100 text-emerald-800',
        lock_required: 'bg-indigo-50 border-indigo-100 text-indigo-800',
        attention_required: 'bg-amber-50 border-amber-100 text-amber-800',
        select_student: 'bg-slate-50 border-slate-200 text-slate-700',
    }[finalEvidenceStatus] || 'bg-slate-50 border-slate-200 text-slate-700';
    const finalEvidenceBadgeClass = {
        approved: 'bg-blue-100 border-blue-200 text-blue-800',
        ready_for_approval: 'bg-emerald-100 border-emerald-200 text-emerald-800',
        lock_required: 'bg-indigo-100 border-indigo-200 text-indigo-800',
        attention_required: 'bg-amber-100 border-amber-200 text-amber-800',
        select_student: 'bg-white border-slate-200 text-slate-600',
    }[finalEvidenceStatus] || 'bg-white border-slate-200 text-slate-600';
    const evidenceStepIconMap = {
        close_readiness: ClipboardList,
        bank_reconciliation: Building2,
        period_lock: Lock,
        final_approval: CheckSquare,
        gl_entries: FileSpreadsheet,
        trial_balance: Scale,
        audit_trail: History,
    };
    const blockingCloseItems = closeChecklist.filter((item) => item.blocking_close || item.status === 'attention');
    const postedAdjustmentChecklist = closeChecklist.find((item) => item.id === 'bank_reconciliation_posted_adjustments') || {};
    const resolvedPostedAdjustments = Number(postedAdjustmentChecklist.resolved_count || bankWorkflow.posted_adjustments_resolved || 0);
    const unmatchedPostedAdjustments = Number(postedAdjustmentChecklist.unmatched_count || bankWorkflow.posted_adjustments_unmatched || 0);
    const checklistStatusClass = (item) => {
        if (item.status === 'ready' && item.id === 'bank_reconciliation_posted_adjustments' && (item.resolved_count || 0) > 0) {
            return 'bg-blue-50 border-blue-100 text-blue-700';
        }
        return item.status === 'ready'
            ? 'bg-emerald-50 border-emerald-100 text-emerald-700'
            : 'bg-amber-50 border-amber-100 text-amber-700';
    };
    const handleActionTab = (actionTab) => {
        if (!actionTab) return;
        if (actionTab === 'bankrecon' && onOpenBankReconPeriod) {
            onOpenBankReconPeriod();
            return;
        }
        onNavigate(actionTab);
    };
    const evidenceStepClass = (status) => {
        if (status === 'ready' || status === 'approved') return 'bg-emerald-50 border-emerald-100 text-emerald-800';
        if (status === 'history') return 'bg-blue-50 border-blue-100 text-blue-800';
        if (status === 'attention') return 'bg-amber-50 border-amber-100 text-amber-800';
        return 'bg-slate-50 border-slate-200 text-slate-700';
    };
    const handleEvidenceStepAction = (actionTab) => {
        if (!actionTab || actionTab === 'home') return;
        if (actionTab === 'bankrecon') {
            handleActionTab('bankrecon');
            return;
        }
        if (actionTab === 'glentries') {
            if (onOpenCloseGl) onOpenCloseGl();
            else onNavigate('glentries');
            return;
        }
        if (actionTab === 'trial') {
            if (onOpenCloseTrial) onOpenCloseTrial();
            else onNavigate('trial');
            return;
        }
        onNavigate(actionTab);
    };
    const escapePrintHtml = (value) => String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
    const handlePrintFinalCloseEvidencePackage = () => {
        const printWindow = window.open('', '_blank', 'width=1050,height=800');
        if (!printWindow) {
            toast.error('Print preview was blocked. Please allow pop-ups for MaceSuite.');
            return;
        }

        const packageSummary = finalEvidencePackage.evidence_summary || {};
        const packageSteps = finalEvidenceSteps.length ? finalEvidenceSteps : [];
        const blockerRows = readiness.blockers || [];
        const checklistRows = closeChecklist || [];
        const packageScope = `${finalEvidencePackage.owner_name || summary.scope?.owner_name || 'Current workspace'} - ${summary.period?.label || 'Selected period'}`;
        const stepRows = packageSteps.map((step, index) => `
            <tr>
                <td>${index + 1}. ${escapePrintHtml(step.label)}</td>
                <td><span class="badge ${step.status === 'attention' ? 'attention' : step.status === 'history' ? 'history' : 'ready'}">${escapePrintHtml(step.status_label || step.status || 'Review')}</span></td>
                <td>${escapePrintHtml(step.location || '')}</td>
                <td>${escapePrintHtml(step.review_next || '')}</td>
                <td>${escapePrintHtml(step.report_impact || '')}</td>
                <td>${escapePrintHtml(step.next_safe_action || '')}</td>
            </tr>
        `).join('');
        const checklistPrintRows = checklistRows.map(item => `
            <tr>
                <td>${escapePrintHtml(item.label)}</td>
                <td><span class="badge ${item.blocking_close || item.status === 'attention' ? 'attention' : 'ready'}">${escapePrintHtml(item.status_label || item.status || 'Review')}</span></td>
                <td class="amount">${escapePrintHtml(item.count || 0)}</td>
                <td>${escapePrintHtml(item.where_to_find || '')}</td>
                <td>${escapePrintHtml(item.next_safe_action || item.note || '')}</td>
            </tr>
        `).join('');
        const blockerHtml = blockerRows.length
            ? blockerRows.map(blocker => `<li>${escapePrintHtml(blocker)}</li>`).join('')
            : '<li>No close-readiness blockers in this package.</li>';
        const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>MaceSuite Final Close Evidence Package</title>
  <style>
    :root { color: #0f172a; font-family: Arial, sans-serif; }
    body { margin: 0; background: #f8fafc; color: #0f172a; }
    main { max-width: 1080px; margin: 0 auto; padding: 32px; }
    .toolbar { position: sticky; top: 0; z-index: 2; background: #f8fafc; border-bottom: 1px solid #e2e8f0; padding: 12px 32px; }
    .toolbar-inner { max-width: 1080px; margin: 0 auto; display: flex; justify-content: space-between; align-items: center; gap: 12px; }
    .toolbar button { background: #0F2A44; color: #fff; border: 0; border-radius: 6px; padding: 9px 14px; font-weight: 700; cursor: pointer; }
    header { background: #0F2A44; color: #fff; border-radius: 10px; padding: 24px; }
    h1, h2, h3, p { margin-top: 0; }
    h1 { font-size: 26px; margin-bottom: 8px; }
    h2 { color: #0F2A44; font-size: 18px; margin-bottom: 10px; }
    .subtitle { color: #dbeafe; margin-bottom: 0; }
    .meta { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; margin-top: 18px; }
    .meta div, section { background: #fff; border: 1px solid #e2e8f0; border-radius: 8px; }
    .meta div { padding: 12px; }
    section { margin-top: 18px; padding: 18px; page-break-inside: avoid; }
    .label { color: #64748b; font-size: 11px; font-weight: 700; text-transform: uppercase; }
    .value { font-size: 16px; font-weight: 700; margin: 4px 0 0; }
    table { width: 100%; border-collapse: collapse; font-size: 11px; }
    th { background: #eff6ff; color: #0F2A44; text-align: left; padding: 8px; border-bottom: 1px solid #bfdbfe; }
    td { padding: 8px; border-bottom: 1px solid #e2e8f0; vertical-align: top; }
    ul { margin: 0; padding-left: 18px; }
    li { margin-bottom: 5px; }
    .amount { text-align: right; white-space: nowrap; }
    .badge { display: inline-block; border-radius: 999px; padding: 3px 8px; font-size: 10px; font-weight: 700; white-space: nowrap; }
    .ready { background: #dcfce7; color: #166534; }
    .attention { background: #fef3c7; color: #92400e; }
    .history { background: #dbeafe; color: #1e40af; }
    .note { background: #ecfeff; border: 1px solid #bae6fd; border-radius: 8px; color: #0f172a; padding: 12px; font-size: 12px; }
    @media print {
      body { background: #fff; }
      main { padding: 0; }
      .toolbar { display: none; }
      section, header { break-inside: avoid; }
    }
  </style>
</head>
<body>
  <div class="toolbar">
    <div class="toolbar-inner">
      <p>Printable preview only. Use this as close evidence support; it does not change MaceSuite records.</p>
      <button type="button" onclick="window.print()">Print / Save PDF</button>
    </div>
  </div>
  <main>
    <header>
      <div class="label">MaceSuite Phase D Control Evidence</div>
      <h1>Final Close Evidence Package</h1>
      <p class="subtitle">${escapePrintHtml(packageScope)}. Printable evidence only; this action does not post, approve, lock, unlock, edit, or delete records.</p>
    </header>

    <div class="meta">
      <div><p class="label">Package Status</p><p class="value">${escapePrintHtml(finalEvidencePackage.status_label || 'Evidence review')}</p></div>
      <div><p class="label">Close Blockers</p><p class="value">${escapePrintHtml(packageSummary.blocker_count ?? readiness.blocker_count ?? 0)}</p></div>
      <div><p class="label">Bank Attention</p><p class="value">${escapePrintHtml(packageSummary.bank_attention_count ?? bankWorkflow.attention_count ?? 0)}</p></div>
      <div><p class="label">GL Status</p><p class="value">${escapePrintHtml(packageSummary.gl_balanced ? 'Balanced' : 'Needs review')}</p></div>
      <div><p class="label">Posted Adjustments</p><p class="value">${escapePrintHtml(packageSummary.posted_adjustments ?? bankWorkflow.posted_adjustments ?? 0)}</p></div>
      <div><p class="label">Lock History</p><p class="value">${escapePrintHtml(packageSummary.lock_history_count ?? lockPreview.lock_history_count ?? 0)}</p></div>
      <div><p class="label">Approval History</p><p class="value">${escapePrintHtml(packageSummary.approval_history_count ?? approvalPreview.approval_history_count ?? 0)}</p></div>
      <div><p class="label">Generated</p><p class="value">${escapePrintHtml(new Date().toLocaleString('en-PH'))}</p></div>
    </div>

    <section>
      <h2>Control Boundary</h2>
      <p class="note">${escapePrintHtml(finalEvidencePackage.report_impact || 'This package is read-only. It does not change GL, Trial Balance, reports, source records, or bank reconciliation evidence.')}</p>
    </section>

    <section>
      <h2>Evidence Steps</h2>
      <table>
        <thead>
          <tr>
            <th>Step</th>
            <th>Status</th>
            <th>Where to Find</th>
            <th>Reviewer</th>
            <th>GL / Report Impact</th>
            <th>Next Safe Action</th>
          </tr>
        </thead>
        <tbody>
          ${stepRows || '<tr><td colspan="6">No evidence package steps were available.</td></tr>'}
        </tbody>
      </table>
    </section>

    <section>
      <h2>Close Readiness Blockers</h2>
      <ul>${blockerHtml}</ul>
    </section>

    <section>
      <h2>Detailed Checklist</h2>
      <table>
        <thead>
          <tr>
            <th>Item</th>
            <th>Status</th>
            <th class="amount">Count</th>
            <th>Where to Find</th>
            <th>Next Safe Action</th>
          </tr>
        </thead>
        <tbody>
          ${checklistPrintRows || '<tr><td colspan="5">No close checklist rows were available.</td></tr>'}
        </tbody>
      </table>
    </section>

    <section>
      <h2>Where This Appears Next</h2>
      <p>${escapePrintHtml(finalEvidencePackage.where_to_find || 'Home > Phase D Close Readiness, Review > Bank Reconciliation, Review > General Ledger, Review > Trial Balance, and Review > Audit Trail.')}</p>
      <p><strong>Reviewer path:</strong> ${escapePrintHtml(finalEvidencePackage.review_next || 'Instructor/admin reviews evidence. Auditor reviews read-only.')}</p>
      <p><strong>Next safe action:</strong> ${escapePrintHtml(finalEvidencePackage.next_safe_action || 'Review evidence before lock or approval.')}</p>
    </section>
  </main>
</body>
</html>`;

        printWindow.document.open();
        printWindow.document.write(html);
        printWindow.document.close();
        printWindow.focus();
        toast.success('Final Close Evidence Package preview opened. Use Print / Save PDF there. No GL, Trial Balance, source, bank, lock, or approval records were changed.');
    };
    const handlePeriodChange = (event) => {
        if (event.currentTarget.value) {
            onSelectedPeriodChange?.(event.currentTarget.value);
        }
    };
    const closeLockDialog = (force = false) => {
        if (periodLockSubmitting && !force) return;
        setShowLockDialog(false);
        setLockReason('');
    };
    const closeUnlockDialog = (force = false) => {
        if (periodLockSubmitting && !force) return;
        setShowUnlockDialog(false);
        setUnlockReason('');
    };
    const handleCreatePeriodLock = async () => {
        const reason = lockReason.trim();
        if (reason.length < 5) {
            toast.error('Add a short reason before creating the period lock record.');
            return;
        }
        if (!lockPreview.owner_id || !lockPreview.period?.year || !lockPreview.period?.month) {
            toast.error('Select a student-period before creating the lock record.');
            return;
        }
        const token = localStorage.getItem('token');
        setPeriodLockSubmitting(true);
        try {
            const response = await axios.post(`${API}/controls/period-locks`, {
                student_id: lockPreview.owner_id,
                year: lockPreview.period.year,
                month: lockPreview.period.month,
                reason,
            }, {
                headers: { Authorization: `Bearer ${token}` },
            });
            toast.success(response.data?.message || 'Period lock record created. Find it on Home > Phase D Close Readiness.');
            closeLockDialog(true);
            await onPeriodLockChanged?.();
        } catch (error) {
            const detail = error.response?.data?.detail;
            toast.error(typeof detail === 'string' ? detail : detail?.message || 'Failed to create period lock record');
        } finally {
            setPeriodLockSubmitting(false);
        }
    };
    const handleUnlockPeriod = async () => {
        const reason = unlockReason.trim();
        if (reason.length < 5) {
            toast.error('Add a short reason before reopening the period.');
            return;
        }
        const lockId = lockPreview.active_lock?.id;
        if (!lockId) {
            toast.error('Active period lock not found on this card.');
            return;
        }
        const token = localStorage.getItem('token');
        setPeriodLockSubmitting(true);
        try {
            const response = await axios.post(`${API}/controls/period-locks/${lockId}/unlock`, {
                reason,
            }, {
                headers: { Authorization: `Bearer ${token}` },
            });
            toast.success(response.data?.message || 'Period reopened. Review close readiness before creating a new lock.');
            closeUnlockDialog(true);
            await onPeriodLockChanged?.();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to reopen period lock');
        } finally {
            setPeriodLockSubmitting(false);
        }
    };
    const handleCreateFinalCloseApproval = async () => {
        const note = approvalNote.trim();
        if (note.length < 5) {
            toast.error('Add a short approval note before creating the final close approval.');
            return;
        }
        if (!approvalPreview.owner_id || !approvalPreview.period?.year || !approvalPreview.period?.month) {
            toast.error('Select a student-period before creating final close approval.');
            return;
        }
        const token = localStorage.getItem('token');
        setPeriodApprovalSubmitting(true);
        try {
            const response = await axios.post(`${API}/controls/period-close-approvals`, {
                student_id: approvalPreview.owner_id,
                year: approvalPreview.period.year,
                month: approvalPreview.period.month,
                approval_note: note,
            }, {
                headers: { Authorization: `Bearer ${token}` },
            });
            toast.success(response.data?.message || 'Final close approved. Find it on Home > Phase D Close Readiness and Review > Audit Trail.');
            setApprovalNote('');
            await onPeriodLockChanged?.();
        } catch (error) {
            const detail = error.response?.data?.detail;
            toast.error(typeof detail === 'string' ? detail : detail?.message || 'Failed to create final close approval');
        } finally {
            setPeriodApprovalSubmitting(false);
        }
    };
    const handleSupersedeFinalCloseApproval = async () => {
        const reason = approvalSupersedeReason.trim();
        if (reason.length < 5) {
            toast.error('Add an admin reason before superseding final close approval.');
            return;
        }
        const approvalId = approvalPreview.active_approval?.id;
        if (!approvalId) {
            toast.error('Active final close approval not found on this card.');
            return;
        }
        const token = localStorage.getItem('token');
        setPeriodApprovalSubmitting(true);
        try {
            const response = await axios.post(`${API}/controls/period-close-approvals/${approvalId}/supersede`, {
                reason,
            }, {
                headers: { Authorization: `Bearer ${token}` },
            });
            toast.success(response.data?.message || 'Final close approval superseded. Review the period before creating a new approval.');
            setApprovalSupersedeReason('');
            await onPeriodLockChanged?.();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to supersede final close approval');
        } finally {
            setPeriodApprovalSubmitting(false);
        }
    };
    const cards = [
        {
            id: 'open-items',
            label: 'Open Items',
            value: openItems.total || 0,
            note: 'Draft, saved, or submitted records in this period',
            Icon: FileText,
            className: openItems.total ? 'bg-amber-50 border-amber-100 text-amber-800' : 'bg-emerald-50 border-emerald-100 text-emerald-700'
        },
        {
            id: 'gl-check',
            label: 'GL Check',
            value: glCheck.balanced ? 'Balanced' : 'Variance',
            note: `${glCheck.entry_count || 0} posted GL lines checked`,
            Icon: Scale,
            className: glCheck.balanced ? 'bg-emerald-50 border-emerald-100 text-emerald-700' : 'bg-red-50 border-red-100 text-red-700'
        },
        {
            id: 'bank-recon',
            label: 'Bank Recon',
            value: bank.unmatched_count || 0,
            note: `${fmt(bank.net_movement)} net cash/bank movement`,
            Icon: Building2,
            className: bank.unmatched_count ? 'bg-blue-50 border-blue-100 text-blue-800' : 'bg-emerald-50 border-emerald-100 text-emerald-700'
        }
    ];

    return (
        <>
        <section className="bg-white border border-slate-200 rounded-2xl shadow-sm p-6 mb-8" data-testid="period-close-readiness-panel">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between mb-5">
                <div className="flex items-start gap-3">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${
                        isReady ? 'bg-emerald-600 text-white' : 'bg-[#0F2A44] text-white'
                    }`}>
                        {isReady ? <CheckCircle2 className="w-6 h-6" /> : <AlertCircle className="w-6 h-6" />}
                    </div>
                    <div>
                        <p className="text-xs uppercase tracking-widest text-slate-400 font-semibold">Phase D Period Close Readiness</p>
                        <h2 className="text-xl font-bold text-[#0F2A44] mt-1">
                            {summary.period?.label || 'Current period'} close check
                        </h2>
                        <p className="text-sm text-slate-600 mt-1 max-w-2xl">
                            Readiness view for month-end close. It checks open accounting work, posted GL balance, bank-reconciliation workload, period locks, and final approval readiness.
                        </p>
                        <p className="text-xs text-slate-400 mt-2">
                            Scope: {summary.scope?.owner_name || 'Current workspace'} - {summary.control_note}
                        </p>
                    </div>
                </div>

                <div className="w-full lg:w-[330px] space-y-3">
                    <div className={`rounded-xl border px-4 py-3 ${
                        isReady ? 'bg-emerald-50 border-emerald-100' : 'bg-amber-50 border-amber-100'
                    }`}>
                        <p className={`text-sm font-semibold ${isReady ? 'text-emerald-700' : 'text-amber-700'}`}>
                            {isReady ? 'Ready for accounting close' : 'Attention required'}
                        </p>
                        <p className="text-xs text-slate-500 mt-1">
                            {readiness.blocker_count || 0} blocker{readiness.blocker_count === 1 ? '' : 's'} detected
                        </p>
                    </div>
                    <div className={`rounded-xl border p-3 ${lockPreviewClass}`} data-testid="period-lock-preview-card">
                        <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                                <p className="text-xs uppercase tracking-wide opacity-75 font-semibold">Period Lock Preview</p>
                                <p className="text-sm font-bold mt-1 truncate" data-testid="period-lock-preview-status">
                                    {lockPreview.status_label || (isReady ? 'Ready to lock preview' : 'Lock blocked by readiness')}
                                </p>
                            </div>
                            <Lock className="w-5 h-5 shrink-0 opacity-80" />
                        </div>
                        <div className="mt-2 flex flex-wrap gap-1.5">
                            <span className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold ${lockStatusPillClass}`}>
                                {lockPreview.lock_scope === 'student_period' ? 'Student-period' : 'Preview scope'}
                            </span>
                            <span className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold ${lockStatusPillClass}`}>
                                {lockPreview.is_locked ? 'Locked now' : 'No lock enforced'}
                            </span>
                            <span className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold ${lockStatusPillClass}`}>
                                {lockPreview.blocker_count || 0} blocker{(lockPreview.blocker_count || 0) === 1 ? '' : 's'}
                            </span>
                        </div>
                        <p className={`text-xs mt-2 leading-snug ${lockStatus === 'locked' ? 'text-white/80' : 'text-slate-600'}`}>
                            {lockPreview.gl_impact || 'Preview only. Lock status does not post, approve, reverse, delete, or change GL/Trial Balance.'}
                        </p>
                        <p className={`text-[11px] mt-2 leading-snug font-semibold ${lockStatus === 'locked' ? 'text-white/90' : 'text-slate-700'}`}>
                            Next safe action: {lockPreview.next_safe_action || 'Review readiness first. Actual lock buttons are intentionally deferred.'}
                        </p>
                        {lockPreview.active_lock?.locked_by_name && (
                            <p className={`text-[11px] mt-1 leading-snug ${lockStatus === 'locked' ? 'text-white/75' : 'text-slate-500'}`}>
                                Locked by {lockPreview.active_lock.locked_by_name} on {lockPreview.active_lock.locked_at?.slice(0, 10) || 'recorded date'}.
                            </p>
                        )}
                        {lockPreview.lock_action_available && (
                            <form
                                className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50 p-3"
                                data-testid="period-lock-create-inline"
                                onSubmit={(event) => {
                                    event.preventDefault();
                                    handleCreatePeriodLock();
                                }}
                            >
                                <Label htmlFor="period-inline-lock-reason" className="text-xs font-semibold text-emerald-900">
                                    Lock reason / evidence note
                                </Label>
                                <Textarea
                                    id="period-inline-lock-reason"
                                    value={lockReason}
                                    onChange={(event) => setLockReason(event.currentTarget.value)}
                                    placeholder="Example: Evidence package reviewed with instructor and period is ready for lock record."
                                    rows={3}
                                    className="mt-2 bg-white text-slate-900 placeholder:text-slate-400"
                                    data-testid="period-lock-create-inline-reason"
                                />
                                <button
                                    type="submit"
                                    className="mt-2 inline-flex h-8 items-center justify-center rounded-md bg-emerald-700 px-3 text-sm font-medium text-white shadow-sm transition-colors hover:bg-emerald-800 disabled:cursor-not-allowed disabled:opacity-60"
                                    disabled={periodLockSubmitting || lockReason.trim().length < 5}
                                    data-testid="period-lock-create-submit"
                                >
                                    <Lock className="w-4 h-4 mr-2" /> {periodLockSubmitting ? 'Creating...' : 'Create Lock Record'}
                                </button>
                            </form>
                        )}
                        {lockPreview.unlock_action_available && (
                            <form
                                className="mt-3 rounded-lg border border-white/20 bg-white/10 p-3"
                                data-testid="period-lock-unlock-inline"
                                onSubmit={(event) => {
                                    event.preventDefault();
                                    handleUnlockPeriod();
                                }}
                            >
                                <Label htmlFor="period-inline-unlock-reason" className="text-xs font-semibold text-white/90">
                                    Admin reopen reason
                                </Label>
                                <Textarea
                                    id="period-inline-unlock-reason"
                                    value={unlockReason}
                                    onChange={(event) => setUnlockReason(event.currentTarget.value)}
                                    placeholder="Example: Reopened for correction review after instructor request."
                                    rows={3}
                                    className="mt-2 bg-white text-slate-900 placeholder:text-slate-400"
                                    data-testid="period-lock-unlock-inline-reason"
                                />
                                <button
                                    type="submit"
                                    className="mt-2 inline-flex h-8 items-center justify-center rounded-md bg-white px-3 text-sm font-medium text-slate-900 shadow-sm transition-colors hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60"
                                    disabled={periodLockSubmitting || unlockReason.trim().length < 5}
                                    data-testid="period-lock-unlock-inline-submit"
                                >
                                    <Undo2 className="w-4 h-4 mr-2" /> {periodLockSubmitting ? 'Reopening...' : 'Reopen Period'}
                                </button>
                            </form>
                        )}
                    </div>
                    <div className={`rounded-xl border p-3 ${approvalPreviewClass}`} data-testid="final-close-approval-preview-card">
                        <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                                <p className="text-xs uppercase tracking-wide opacity-75 font-semibold">Final Close Approval Preview</p>
                                <p className="text-sm font-bold mt-1" data-testid="final-close-approval-preview-status">
                                    {approvalPreview.status_label || (isReady ? 'Lock required before approval' : 'Approval blocked by readiness')}
                                </p>
                            </div>
                            <CheckSquare className="w-5 h-5 shrink-0 opacity-80" />
                        </div>
                        <div className="mt-2 flex flex-wrap gap-1.5">
                            <span className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold ${approvalStatusPillClass}`}>
                                {approvalPreview.approval_scope === 'student_period' ? 'Student-period' : 'Preview scope'}
                            </span>
                            <span className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold ${approvalStatusPillClass}`}>
                                {approvalPreview.is_locked ? 'Lock active' : 'Lock required'}
                            </span>
                            <span className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold ${approvalStatusPillClass}`}>
                                {approvalPreview.is_ready ? 'Readiness ready' : `${readiness.blocker_count || 0} blocker${(readiness.blocker_count || 0) === 1 ? '' : 's'}`}
                            </span>
                        </div>
                        <p className="text-xs mt-2 leading-snug opacity-85">
                            {approvalPreview.gl_impact || 'Preview only. Final close approval is not created in this slice and does not change GL, Trial Balance, reports, or source evidence.'}
                        </p>
                        <p className="text-[11px] mt-2 leading-snug font-semibold opacity-90">
                            Next safe action: {approvalPreview.next_safe_action || 'Review lock and close-readiness status before future final approval.'}
                        </p>
                        <p className="text-[11px] mt-1 leading-snug opacity-85">
                            Reviewer path: {approvalPreview.review_next || 'Instructor/admin reviews the final evidence package before any future approval action.'}
                        </p>
                        {approvalPreview.active_approval?.approved_by_name && (
                            <p className="text-[11px] mt-1 leading-snug opacity-75">
                                Approved by {approvalPreview.active_approval.approved_by_name} on {approvalPreview.active_approval.approved_at?.slice(0, 10) || 'recorded date'}.
                            </p>
                        )}
                        {approvalPreview.requirements?.length > 0 && (
                            <div className="mt-3 space-y-1.5">
                                {approvalPreview.requirements.slice(0, 4).map((requirement) => {
                                    const requirementReady = requirement.status === 'ready';
                                    return (
                                        <div
                                            key={requirement.id}
                                            className={`rounded-lg border bg-white/70 px-2 py-1.5 text-[11px] ${
                                                requirementReady ? 'border-emerald-100 text-emerald-800' : 'border-amber-100 text-amber-800'
                                            }`}
                                            data-testid={`final-close-approval-requirement-${requirement.id}`}
                                        >
                                            <div className="flex items-center justify-between gap-2">
                                                <span className="font-semibold">{requirement.label}</span>
                                                <span className="font-bold">{requirement.status_label || (requirementReady ? 'Ready' : 'Needs action')}</span>
                                            </div>
                                            <p className="mt-0.5 leading-snug opacity-80">{requirement.next_safe_action}</p>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                        {approvalPreview.active_approval?.approval_note && (
                            <div className="mt-3 rounded-lg border border-white/50 bg-white/70 px-2 py-2 text-[11px]" data-testid="final-close-approval-active-note">
                                <p className="font-semibold">Approval note</p>
                                <p className="mt-1 leading-snug opacity-85">{approvalPreview.active_approval.approval_note}</p>
                            </div>
                        )}
                        {approvalHistory.length > 0 && (
                            <div className="mt-3 rounded-lg border border-white/50 bg-white/70 px-2 py-2 text-[11px]" data-testid="final-close-approval-history">
                                <div className="flex items-center justify-between gap-2">
                                    <p className="font-semibold">Approval history</p>
                                    <span className="rounded-full border border-slate-200 bg-white px-2 py-0.5 font-semibold text-slate-600">
                                        {approvalPreview.approval_history_count || approvalHistory.length} record{(approvalPreview.approval_history_count || approvalHistory.length) === 1 ? '' : 's'}
                                    </span>
                                </div>
                                <div className="mt-2 space-y-1.5">
                                    {approvalHistory.slice(0, 3).map((approval) => {
                                        const isSuperseded = approval.status === 'superseded';
                                        return (
                                            <div
                                                key={approval.id}
                                                className={`rounded-md border px-2 py-1.5 ${
                                                    isSuperseded ? 'border-amber-100 bg-amber-50 text-amber-800' : 'border-blue-100 bg-blue-50 text-blue-800'
                                                }`}
                                                data-testid={`final-close-approval-history-${approval.id}`}
                                            >
                                                <div className="flex flex-wrap items-center justify-between gap-1">
                                                    <span className="font-bold">{isSuperseded ? 'Superseded' : 'Approved'}</span>
                                                    <span className="font-semibold">
                                                        {(isSuperseded ? approval.superseded_at : approval.approved_at)?.slice(0, 10) || approval.updated_at?.slice(0, 10) || 'Recorded'}
                                                    </span>
                                                </div>
                                                <p className="mt-0.5 leading-snug opacity-85">
                                                    {isSuperseded
                                                        ? `Superseded by ${approval.superseded_by_name || 'admin'}: ${approval.supersede_reason || 'Reason recorded.'}`
                                                        : `Approved by ${approval.approved_by_name || 'reviewer'}: ${approval.approval_note || 'Approval note recorded.'}`}
                                                </p>
                                            </div>
                                        );
                                    })}
                                </div>
                                <p className="mt-2 leading-snug opacity-80">
                                    History stays here as control evidence. It does not change GL, Trial Balance, reports, source records, or bank reconciliation evidence.
                                </p>
                            </div>
                        )}
                        {approvalPreview.approval_action_available && (
                            <form
                                className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-slate-900"
                                data-testid="final-close-approval-create-inline"
                                onSubmit={(event) => {
                                    event.preventDefault();
                                    handleCreateFinalCloseApproval();
                                }}
                            >
                                <Label htmlFor="final-close-approval-note" className="text-xs font-semibold text-emerald-900">
                                    Final approval note
                                </Label>
                                <Textarea
                                    id="final-close-approval-note"
                                    value={approvalNote}
                                    onChange={(event) => setApprovalNote(event.currentTarget.value)}
                                    placeholder="Example: Bank recon evidence, General Ledger, and Trial Balance reviewed for this locked period."
                                    rows={3}
                                    className="mt-2 bg-white text-slate-900 placeholder:text-slate-400"
                                    data-testid="final-close-approval-note"
                                />
                                <p className="mt-2 text-[11px] leading-snug text-emerald-800">
                                    Creates approval evidence only. GL, Trial Balance, reports, source records, bank reconciliation evidence, and the period lock are unchanged.
                                </p>
                                <button
                                    type="submit"
                                    className="mt-2 inline-flex h-8 items-center justify-center rounded-md bg-emerald-700 px-3 text-sm font-medium text-white shadow-sm transition-colors hover:bg-emerald-800 disabled:cursor-not-allowed disabled:opacity-60"
                                    disabled={periodApprovalSubmitting || approvalNote.trim().length < 5}
                                    data-testid="final-close-approval-submit"
                                >
                                    <CheckSquare className="w-4 h-4 mr-2" /> {periodApprovalSubmitting ? 'Approving...' : 'Approve Final Close'}
                                </button>
                            </form>
                        )}
                        {approvalPreview.supersede_action_available && (
                            <form
                                className="mt-3 rounded-lg border border-blue-200 bg-white/80 p-3 text-slate-900"
                                data-testid="final-close-approval-supersede-inline"
                                onSubmit={(event) => {
                                    event.preventDefault();
                                    handleSupersedeFinalCloseApproval();
                                }}
                            >
                                <Label htmlFor="final-close-approval-supersede-reason" className="text-xs font-semibold text-blue-900">
                                    Admin supersede reason
                                </Label>
                                <Textarea
                                    id="final-close-approval-supersede-reason"
                                    value={approvalSupersedeReason}
                                    onChange={(event) => setApprovalSupersedeReason(event.currentTarget.value)}
                                    placeholder="Example: Superseded for correction review before a new final approval is created."
                                    rows={3}
                                    className="mt-2 bg-white text-slate-900 placeholder:text-slate-400"
                                    data-testid="final-close-approval-supersede-reason"
                                />
                                <p className="mt-2 text-[11px] leading-snug text-blue-800">
                                    Supersede preserves approval history. It does not reopen the period lock or change accounting records.
                                </p>
                                <button
                                    type="submit"
                                    className="mt-2 inline-flex h-8 items-center justify-center rounded-md bg-blue-700 px-3 text-sm font-medium text-white shadow-sm transition-colors hover:bg-blue-800 disabled:cursor-not-allowed disabled:opacity-60"
                                    disabled={periodApprovalSubmitting || approvalSupersedeReason.trim().length < 5}
                                    data-testid="final-close-approval-supersede-submit"
                                >
                                    <Undo2 className="w-4 h-4 mr-2" /> {periodApprovalSubmitting ? 'Superseding...' : 'Supersede Approval'}
                                </button>
                            </form>
                        )}
                    </div>
                    <div className="rounded-xl border border-slate-200 bg-slate-50 p-3" data-testid="period-close-period-selector">
                        <Label htmlFor="period-close-month" className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                            Close Month
                        </Label>
                        <div className="mt-2 flex flex-col gap-2 sm:flex-row">
                            <Input
                                id="period-close-month"
                                type="month"
                                value={selectedPeriodValue}
                                onChange={handlePeriodChange}
                                onInput={handlePeriodChange}
                                className="h-9 bg-white"
                                data-testid="period-close-month-input"
                            />
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => onSelectedPeriodChange?.(currentPeriod)}
                                disabled={selectedPeriodValue === currentPeriod}
                                data-testid="period-close-current-month"
                            >
                                Current
                            </Button>
                        </div>
                        <p className="text-[11px] text-slate-500 mt-2 leading-snug">
                            Reviewing {summary.period?.label || 'the selected month'} only. Open Bank Recon uses this same monthly period.
                        </p>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
                {cards.map((card) => (
                    <div key={card.id} className={`rounded-xl border p-4 ${card.className}`} data-testid={`period-close-${card.id}`}>
                        <div className="flex items-center justify-between gap-3">
                            <div>
                                <p className="text-xs uppercase tracking-wide opacity-70">{card.label}</p>
                                <p className="text-2xl font-bold mt-1">{card.value}</p>
                            </div>
                            <card.Icon className="w-7 h-7 opacity-70" />
                        </div>
                        <p className="text-xs mt-2 leading-snug opacity-80">{card.note}</p>
                    </div>
                ))}
            </div>

            {readiness.blockers?.length > 0 && (
                <div className="rounded-xl border border-amber-100 bg-amber-50 p-4 mb-4" data-testid="period-close-blockers">
                    <p className="font-semibold text-sm text-amber-800 mb-2">Before closing this period</p>
                    <ul className="space-y-1">
                        {readiness.blockers.map((blocker, index) => (
                            <li key={`${blocker}-${index}`} className="text-sm text-amber-700 flex gap-2">
                                <span className="mt-1 h-1.5 w-1.5 rounded-full bg-amber-500 shrink-0" />
                                <span>{blocker}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-4" data-testid="period-close-resolution-trail">
                <div className="rounded-2xl border border-amber-100 bg-amber-50/70 p-4">
                    <div className="flex items-start justify-between gap-3">
                        <div>
                            <p className="text-xs uppercase tracking-widest text-amber-600 font-semibold">Blocking Before Close</p>
                            <h3 className="text-sm font-bold text-amber-900 mt-1">
                                {blockingCloseItems.length ? `${blockingCloseItems.length} item${blockingCloseItems.length === 1 ? '' : 's'} need action` : 'No blocking close items'}
                            </h3>
                        </div>
                        <AlertCircle className={`w-5 h-5 shrink-0 ${blockingCloseItems.length ? 'text-amber-600' : 'text-slate-300'}`} />
                    </div>
                    <div className="mt-3 space-y-2">
                        {blockingCloseItems.length ? blockingCloseItems.slice(0, 4).map((item) => (
                            <button
                                key={`blocking-${item.id}`}
                                type="button"
                                onClick={() => handleActionTab(item.action_tab)}
                                className="w-full text-left rounded-lg border border-amber-100 bg-white/80 p-2 hover:bg-white transition-colors"
                                data-testid={`period-close-blocking-${item.id}`}
                            >
                                <div className="flex items-center justify-between gap-2">
                                    <span className="text-xs font-semibold text-amber-900">{item.label}</span>
                                    <span className="text-xs font-bold text-amber-700">{item.count || 0}</span>
                                </div>
                                <p className="text-[11px] text-amber-700 mt-1 leading-snug">{item.next_safe_action || item.note}</p>
                            </button>
                        )) : (
                            <p className="text-xs text-slate-600 leading-snug">
                                The selected period has no open close blockers in this readiness view. Review posted evidence before any future hard close.
                            </p>
                        )}
                        {blockingCloseItems.length > 4 && (
                            <p className="text-[11px] font-semibold text-amber-700">
                                {blockingCloseItems.length - 4} more item{blockingCloseItems.length - 4 === 1 ? '' : 's'} are listed in the detailed checklist below.
                            </p>
                        )}
                    </div>
                </div>

                <div className="rounded-2xl border border-blue-100 bg-blue-50/70 p-4">
                    <div className="flex items-start justify-between gap-3">
                        <div>
                            <p className="text-xs uppercase tracking-widest text-blue-600 font-semibold">Resolved Posted Evidence</p>
                            <h3 className="text-sm font-bold text-blue-900 mt-1">
                                {resolvedPostedAdjustments} posted bank-recon adjustment{resolvedPostedAdjustments === 1 ? '' : 's'} resolved
                            </h3>
                        </div>
                        <CheckCircle2 className={`w-5 h-5 shrink-0 ${resolvedPostedAdjustments ? 'text-blue-600' : 'text-slate-300'}`} />
                    </div>
                    <div className="mt-3 rounded-lg border border-blue-100 bg-white/80 p-3 text-xs text-slate-700">
                        <p className="font-semibold text-slate-900">
                            {unmatchedPostedAdjustments
                                ? `${unmatchedPostedAdjustments} posted item${unmatchedPostedAdjustments === 1 ? '' : 's'} still need source matching`
                                : 'Posted bank-recon evidence is not blocking when source matched.'}
                        </p>
                        <p className="mt-1 leading-snug">
                            Find it in Review &gt; Bank Reconciliation, Review &gt; General Ledger, and Trial Balance. Posted JVs affect GL, Trial Balance, and reports.
                        </p>
                        <button
                            type="button"
                            onClick={() => handleActionTab('bankrecon')}
                            className="mt-3 inline-flex items-center gap-1 rounded-lg border border-blue-100 bg-blue-50 px-3 py-1.5 text-[11px] font-semibold text-blue-700 hover:bg-blue-100"
                            data-testid="period-close-open-posted-evidence"
                        >
                            Open Bank Recon Evidence
                            <ArrowRight className="w-3 h-3" />
                        </button>
                    </div>
                </div>
            </div>

            {closeChecklist.length > 0 && (
                <div className="rounded-2xl border border-slate-200 bg-white p-4 mb-4" data-testid="period-close-checklist">
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between mb-3">
                        <div>
                            <p className="text-xs uppercase tracking-widest text-slate-400 font-semibold">Close Readiness Checklist</p>
                            <h3 className="text-base font-bold text-[#0F2A44] mt-1">Review before lock or final approval</h3>
                        </div>
                        <span className="rounded-full bg-slate-50 border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-600 w-fit">
                            {bankWorkflow.attention_count || 0} bank recon attention item{bankWorkflow.attention_count === 1 ? '' : 's'}
                        </span>
                    </div>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                        {closeChecklist.map((item) => {
                            const ready = item.status === 'ready';
                            return (
                                <button
                                    key={item.id}
                                    type="button"
                                    onClick={() => handleActionTab(item.action_tab)}
                                    className={`text-left rounded-xl border p-3 transition-colors ${
                                        ready
                                            ? 'bg-emerald-50 border-emerald-100 hover:bg-emerald-100/70'
                                            : 'bg-amber-50 border-amber-100 hover:bg-amber-100/70'
                                    }`}
                                    data-testid={`period-close-checklist-${item.id}`}
                                >
                                    <div className="flex items-start justify-between gap-2">
                                        <div className="min-w-0">
                                            <p className={`font-semibold text-sm ${ready ? 'text-emerald-800' : 'text-amber-800'}`}>{item.label}</p>
                                            <span className={`mt-1 inline-flex rounded-full border px-2 py-0.5 text-[11px] font-semibold ${checklistStatusClass(item)}`}>
                                                {item.status_label || (ready ? 'Ready' : 'Blocks close')}
                                            </span>
                                        </div>
                                        {ready ? (
                                            <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                                        ) : (
                                            <AlertCircle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                                        )}
                                    </div>
                                    <p className={`text-2xl font-bold mt-2 ${ready ? 'text-emerald-700' : 'text-amber-700'}`}>{item.count || 0}</p>
                                    {item.id === 'bank_reconciliation_matching' && item.count > 0 && (
                                        <p className="text-[11px] text-slate-500 mt-1">
                                            {item.book_unmatched_count || 0} book GL, {item.statement_unmatched_count || 0} statement lines
                                        </p>
                                    )}
                                    {item.id === 'bank_reconciliation_jv_drafts' && item.count > 0 && (
                                        <p className="text-[11px] text-slate-500 mt-1">
                                            {item.saved_count || 0} saved, {item.submitted_count || 0} submitted, {item.returned_count || 0} returned
                                        </p>
                                    )}
                                    {item.id === 'bank_reconciliation_posted_adjustments' && item.count > 0 && (
                                        <p className="text-[11px] text-slate-500 mt-1">
                                            {item.resolved_count || 0} resolved, {item.unmatched_count || 0} need source match
                                        </p>
                                    )}
                                    <p className="text-xs text-slate-600 mt-2 leading-snug">{item.note}</p>
                                    {item.where_to_find && (
                                        <p className="text-[11px] text-slate-500 mt-2 leading-snug">
                                            <span className="font-semibold text-slate-700">Where:</span> {item.where_to_find}
                                        </p>
                                    )}
                                    {item.report_impact && (
                                        <p className="text-[11px] text-slate-500 mt-1 leading-snug">
                                            <span className="font-semibold text-slate-700">Impact:</span> {item.report_impact}
                                        </p>
                                    )}
                                    {item.review_next && (
                                        <p className="text-[11px] text-slate-500 mt-1 leading-snug">
                                            <span className="font-semibold text-slate-700">Reviewer:</span> {item.review_next}
                                        </p>
                                    )}
                                    {item.next_safe_action && (
                                        <p className="text-[11px] font-semibold text-slate-700 mt-2 leading-snug">
                                            Next safe action: {item.next_safe_action}
                                        </p>
                                    )}
                                    {item.unmatched_count > 0 && (
                                        <p className="text-[11px] font-semibold text-amber-700 mt-1">
                                            {item.unmatched_count} posted item{item.unmatched_count === 1 ? '' : 's'} still need matching.
                                        </p>
                                    )}
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}

            <div className="rounded-2xl border border-blue-100 bg-blue-50/60 p-4 mb-4" data-testid="bank-reconciliation-preview">
                <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                    <div>
                        <p className="text-xs uppercase tracking-widest text-blue-500 font-semibold">Bank Reconciliation Preview</p>
                        <h3 className="text-base font-bold text-[#0F2A44] mt-1">Posted cash/bank GL lines to match</h3>
                        <p className="text-xs text-slate-600 mt-1 max-w-2xl">
                            Read-only preview for teaching and QA. Use Bank Reconciliation to match statement lines and review posted adjustment evidence before close.
                        </p>
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-right w-full lg:min-w-[360px] lg:w-auto">
                        <div className="rounded-xl bg-white border border-blue-100 px-3 py-2">
                            <p className="text-[11px] uppercase tracking-wide text-slate-400">Inflows</p>
                            <p className="text-sm font-bold text-emerald-700">{fmt(bank.inflow_total)}</p>
                        </div>
                        <div className="rounded-xl bg-white border border-blue-100 px-3 py-2">
                            <p className="text-[11px] uppercase tracking-wide text-slate-400">Outflows</p>
                            <p className="text-sm font-bold text-rose-700">{fmt(bank.outflow_total)}</p>
                        </div>
                        <div className="rounded-xl bg-white border border-blue-100 px-3 py-2">
                            <p className="text-[11px] uppercase tracking-wide text-slate-400">Unmatched</p>
                            <p className="text-sm font-bold text-blue-800">{bank.unmatched_count || 0}</p>
                        </div>
                    </div>
                </div>

                {recentBankMovements.length > 0 ? (
                    <div className="mt-4 grid grid-cols-1 xl:grid-cols-[1.3fr_0.7fr] gap-3">
                        <div className="overflow-x-auto rounded-xl border border-blue-100 bg-white">
                            <div className="min-w-[720px]">
                                <div className="grid grid-cols-[110px_130px_1fr_120px] gap-3 px-3 py-2 bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500 font-semibold">
                                    <span>Date</span>
                                    <span>Reference</span>
                                    <span>Account / Source</span>
                                    <span className="text-right">Amount</span>
                                </div>
                                <div className="divide-y divide-slate-100">
                                    {recentBankMovements.slice(0, 5).map((movement) => (
                                        <div key={movement.id || `${movement.reference}-${movement.account_code}-${movement.amount}`} className="grid grid-cols-[110px_130px_1fr_120px] gap-3 px-3 py-2 text-xs text-slate-700">
                                            <span className="font-medium">{movement.date || '-'}</span>
                                            <span className="font-mono text-[11px] truncate">{movement.reference || '-'}</span>
                                            <span className="min-w-0">
                                                <span className="block font-semibold text-slate-900 truncate">{movement.account_name || movement.account_code}</span>
                                                <span className="block text-[11px] text-slate-500 truncate">{movement.source} - {movement.matching_hint}</span>
                                            </span>
                                            <span className={`text-right font-semibold ${movement.direction === 'inflow' ? 'text-emerald-700' : 'text-rose-700'}`}>
                                                {movement.direction === 'inflow' ? '+' : '-'}{fmt(movement.amount)}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <div className="space-y-3">
                            <div className="rounded-xl bg-white border border-blue-100 p-3">
                                <p className="text-xs font-semibold text-slate-700 mb-2">Cash/bank accounts</p>
                                <div className="space-y-2">
                                    {bankAccounts.slice(0, 3).map((account) => (
                                        <div key={`${account.account_code}-${account.account_name}`} className="flex items-center justify-between gap-2 text-xs">
                                            <span className="truncate text-slate-600">{account.account_code} - {account.account_name}</span>
                                            <span className="font-semibold text-slate-900">{account.movement_count}</span>
                                        </div>
                                    ))}
                                    {!bankAccounts.length && <p className="text-xs text-slate-500">No posted cash/bank movement this period.</p>}
                                </div>
                            </div>
                            <div className="rounded-xl bg-white border border-blue-100 p-3">
                                <p className="text-xs font-semibold text-slate-700 mb-2">Source mix</p>
                                <div className="flex flex-wrap gap-1.5">
                                    {bankSources.slice(0, 5).map((source) => (
                                        <span key={source.source} className="rounded-full bg-slate-100 px-2 py-1 text-[11px] text-slate-600">
                                            {source.source}: {source.movement_count}
                                        </span>
                                    ))}
                                    {!bankSources.length && <span className="text-xs text-slate-500">No source activity.</span>}
                                </div>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="mt-4 rounded-xl border border-blue-100 bg-white p-3 text-sm text-slate-600">
                        No posted cash/bank movements were found for this selected period.
                    </div>
                )}
            </div>

            <div className={`rounded-2xl border p-4 mb-4 ${finalEvidencePackageClass}`} data-testid="final-close-evidence-package">
                <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                    <div>
                        <p className="text-xs uppercase tracking-widest opacity-70 font-semibold">Final Close Evidence Package</p>
                        <h3 className="text-base font-bold text-[#0F2A44] mt-1">Follow the same month through evidence, GL, and Trial Balance</h3>
                        <p className="text-xs text-slate-600 mt-1 max-w-3xl">
                            {finalEvidencePackage.where_to_find || `Start with Bank Reconciliation evidence for ${summary.period?.label || 'the selected month'}, then confirm posted GL lines, Trial Balance, and audit history.`}
                        </p>
                        <p className="mt-2 text-xs leading-snug text-slate-600">
                            {finalEvidencePackage.report_impact || 'This package is review-only; it does not post, approve, lock, unlock, delete, or change GL, Trial Balance, reports, source records, or bank reconciliation evidence.'}
                        </p>
                    </div>
                    <span className={`rounded-full border px-3 py-1 text-xs font-semibold w-fit ${finalEvidenceBadgeClass}`} data-testid="final-close-evidence-package-status">
                        {finalEvidencePackage.status_label || (isReady ? 'Evidence ready to review' : `${readiness.blocker_count || 0} blocker${readiness.blocker_count === 1 ? '' : 's'} to clear`)}
                    </span>
                </div>
                <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                    <div className="rounded-lg border border-white/50 bg-white/70 px-3 py-2">
                        <span className="block font-semibold uppercase text-slate-500">Bank attention</span>
                        <span className="mt-1 block text-base font-bold text-slate-900">{finalEvidencePackage.evidence_summary?.bank_attention_count ?? bankWorkflow.attention_count ?? 0}</span>
                    </div>
                    <div className="rounded-lg border border-white/50 bg-white/70 px-3 py-2">
                        <span className="block font-semibold uppercase text-slate-500">Posted adjustments</span>
                        <span className="mt-1 block text-base font-bold text-slate-900">{finalEvidencePackage.evidence_summary?.posted_adjustments ?? bankWorkflow.posted_adjustments ?? 0}</span>
                    </div>
                    <div className="rounded-lg border border-white/50 bg-white/70 px-3 py-2">
                        <span className="block font-semibold uppercase text-slate-500">Lock history</span>
                        <span className="mt-1 block text-base font-bold text-slate-900">{finalEvidencePackage.evidence_summary?.lock_history_count ?? lockPreview.lock_history_count ?? 0}</span>
                    </div>
                    <div className="rounded-lg border border-white/50 bg-white/70 px-3 py-2">
                        <span className="block font-semibold uppercase text-slate-500">Approval history</span>
                        <span className="mt-1 block text-base font-bold text-slate-900">{finalEvidencePackage.evidence_summary?.approval_history_count ?? approvalPreview.approval_history_count ?? 0}</span>
                    </div>
                </div>
                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                    {(finalEvidenceSteps.length ? finalEvidenceSteps : [
                        {
                            id: 'bank_reconciliation',
                            label: 'Bank Reconciliation Evidence',
                            status: bankWorkflow.attention_count ? 'attention' : 'ready',
                            status_label: bankWorkflow.attention_count ? 'Needs review' : 'Ready',
                            what_it_answers: 'Review statement lines, matches, blockers, and posted adjustment evidence.',
                            location: 'Review > Bank Reconciliation',
                            action_tab: 'bankrecon',
                            next_safe_action: 'Review bank evidence for the selected close month.',
                        },
                        {
                            id: 'gl_entries',
                            label: 'General Ledger',
                            status: glCheck.balanced ? 'ready' : 'attention',
                            status_label: glCheck.balanced ? 'Balanced' : 'Variance',
                            what_it_answers: 'Trace posted ledger lines in the selected close month.',
                            location: 'Review > General Ledger',
                            action_tab: 'glentries',
                            next_safe_action: 'Review posted source refs.',
                        },
                        {
                            id: 'trial_balance',
                            label: 'Trial Balance',
                            status: glCheck.balanced ? 'ready' : 'attention',
                            status_label: glCheck.balanced ? 'Balanced' : 'Variance',
                            what_it_answers: 'Confirm posted debit and credit totals as of month end.',
                            location: 'Review > Trial Balance',
                            action_tab: 'trial',
                            next_safe_action: 'Use the same student scope and month-end date.',
                        },
                    ]).map((step, index) => {
                        const StepIcon = evidenceStepIconMap[step.id] || FileText;
                        const hasAction = step.action_tab && step.action_tab !== 'home';
                        return (
                            <div
                                key={step.id || step.label}
                                className={`rounded-xl border p-3 ${evidenceStepClass(step.status)}`}
                                data-testid={`final-close-evidence-step-${step.id || index}`}
                            >
                                <div className="flex items-start justify-between gap-2">
                                    <div className="min-w-0">
                                        <p className="text-[11px] font-semibold uppercase tracking-wide opacity-75">Step {index + 1}</p>
                                        <p className="mt-1 text-sm font-bold text-slate-900">{step.label}</p>
                                    </div>
                                    <StepIcon className="w-4 h-4 shrink-0 opacity-75" />
                                </div>
                                <span className="mt-2 inline-flex rounded-full border border-white/60 bg-white/70 px-2 py-0.5 text-[11px] font-semibold text-slate-700">
                                    {step.status_label || (step.status === 'ready' ? 'Ready' : 'Needs review')}
                                </span>
                                <p className="mt-2 text-xs leading-snug text-slate-700">{step.what_it_answers || step.location}</p>
                                {step.location && (
                                    <p className="mt-2 text-[11px] leading-snug text-slate-600">
                                        <span className="font-semibold text-slate-800">Where:</span> {step.location}
                                    </p>
                                )}
                                {step.report_impact && (
                                    <p className="mt-1 text-[11px] leading-snug text-slate-600">
                                        <span className="font-semibold text-slate-800">Impact:</span> {step.report_impact}
                                    </p>
                                )}
                                {step.review_next && (
                                    <p className="mt-1 text-[11px] leading-snug text-slate-600">
                                        <span className="font-semibold text-slate-800">Reviewer:</span> {step.review_next}
                                    </p>
                                )}
                                <p className="mt-2 text-[11px] font-semibold leading-snug text-slate-800">
                                    Next safe action: {step.next_safe_action || 'Review this evidence before close approval.'}
                                </p>
                                {hasAction && (
                                    <button
                                        type="button"
                                        onClick={() => handleEvidenceStepAction(step.action_tab)}
                                        className="mt-3 inline-flex items-center rounded-lg border border-white/70 bg-white px-3 py-1.5 text-[11px] font-semibold text-slate-700 shadow-sm hover:bg-slate-50"
                                        data-testid={`final-close-evidence-open-${step.id}`}
                                    >
                                        Open evidence
                                        <ArrowRight className="ml-1.5 h-3 w-3" />
                                    </button>
                                )}
                            </div>
                        );
                    })}
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                    <Button type="button" variant="outline" size="sm" onClick={() => handleActionTab('bankrecon')} data-testid="period-close-trail-open-bank-recon">
                        <Building2 className="w-4 h-4 mr-2" /> Bank Recon Evidence
                    </Button>
                    <Button type="button" variant="outline" size="sm" onClick={() => onOpenCloseGl ? onOpenCloseGl() : onNavigate('glentries')} data-testid="period-close-trail-open-gl">
                        <FileSpreadsheet className="w-4 h-4 mr-2" /> General Ledger
                    </Button>
                    <Button type="button" variant="outline" size="sm" onClick={() => onOpenCloseTrial ? onOpenCloseTrial() : onNavigate('trial')} data-testid="period-close-trail-open-trial">
                        <Scale className="w-4 h-4 mr-2" /> Trial Balance
                    </Button>
                    <Button type="button" variant="outline" size="sm" onClick={() => onNavigate('audit')} data-testid="period-close-trail-open-audit">
                        <History className="w-4 h-4 mr-2" /> Audit Trail
                    </Button>
                    <Button type="button" variant="outline" size="sm" onClick={handlePrintFinalCloseEvidencePackage} data-testid="final-close-evidence-print">
                        <Printer className="w-4 h-4 mr-2" /> Print Summary
                    </Button>
                </div>
                <p className="mt-3 text-[11px] font-semibold leading-snug text-slate-700">
                    Next safe action: {finalEvidencePackage.next_safe_action || (isReady ? 'Review evidence package before lock or approval.' : 'Clear blockers first, then return to this package.')}
                </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                <button
                    type="button"
                    onClick={() => onNavigate('jv')}
                    className="rounded-xl border border-slate-200 bg-slate-50 hover:bg-white hover:border-[#2F8FCB]/50 p-3 text-left transition-colors"
                    data-testid="period-close-review-jv"
                >
                    <p className="font-semibold text-sm text-slate-900">Review JVs</p>
                    <p className="text-xs text-slate-500 mt-1">Clear saved/submitted vouchers.</p>
                </button>
                <button
                    type="button"
                    onClick={() => onOpenCloseTrial ? onOpenCloseTrial() : onNavigate('trial')}
                    className="rounded-xl border border-slate-200 bg-slate-50 hover:bg-white hover:border-[#2F8FCB]/50 p-3 text-left transition-colors"
                    data-testid="period-close-review-tb"
                >
                    <p className="font-semibold text-sm text-slate-900">Trial Balance</p>
                    <p className="text-xs text-slate-500 mt-1">Check posted debit/credit totals.</p>
                </button>
                <button
                    type="button"
                    onClick={() => onOpenCloseGl ? onOpenCloseGl() : onNavigate('glentries')}
                    className="rounded-xl border border-slate-200 bg-slate-50 hover:bg-white hover:border-[#2F8FCB]/50 p-3 text-left transition-colors"
                    data-testid="period-close-review-gl"
                >
                    <p className="font-semibold text-sm text-slate-900">General Ledger</p>
                    <p className="text-xs text-slate-500 mt-1">Inspect posted activity for this close month.</p>
                </button>
                <button
                    type="button"
                    onClick={() => handleActionTab('bankrecon')}
                    className="rounded-xl border border-slate-200 bg-slate-50 hover:bg-white hover:border-[#2F8FCB]/50 p-3 text-left transition-colors"
                    data-testid="period-close-bank-preview"
                >
                    <p className="font-semibold text-sm text-slate-900">Bank Recon</p>
                    <p className="text-xs text-slate-500 mt-1">Open the selected close month.</p>
                </button>
            </div>
        </section>
        <Dialog open={showLockDialog} onOpenChange={(open) => open ? setShowLockDialog(true) : closeLockDialog()}>
            <DialogContent className="max-w-md">
                <DialogHeader>
                    <DialogTitle>Create period lock record</DialogTitle>
                    <DialogDescription>
                        Create an audited period lock record without changing GL, Trial Balance, reports, or bank reconciliation evidence.
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-3 text-sm text-slate-600">
                    <p>
                        This creates an audited lock record for {lockPreview.owner_name || summary.scope?.owner_name || 'the selected student'} - {summary.period?.label || 'selected period'}.
                    </p>
                    <div className="rounded-lg border border-blue-100 bg-blue-50 p-3 text-xs leading-relaxed text-blue-800">
                        It will show on Home &gt; Phase D Close Readiness. It does not post, approve, reverse, void, delete, or change GL, Trial Balance, reports, or bank-reconciliation evidence.
                    </div>
                    <Label htmlFor="period-lock-reason">Reason / close evidence note</Label>
                    <Textarea
                        id="period-lock-reason"
                        value={lockReason}
                        onChange={(event) => setLockReason(event.currentTarget.value)}
                        placeholder="Example: Evidence package reviewed with instructor and period is ready for lock record."
                        rows={4}
                        data-testid="period-lock-reason"
                    />
                </div>
                <DialogFooter className="mt-4">
                    <Button type="button" variant="outline" onClick={() => closeLockDialog()} disabled={periodLockSubmitting}>
                        Cancel
                    </Button>
                    <Button
                        type="button"
                        onClick={handleCreatePeriodLock}
                        disabled={periodLockSubmitting || lockReason.trim().length < 5}
                        data-testid="period-lock-create-submit"
                    >
                        {periodLockSubmitting ? 'Creating...' : 'Create Lock Record'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
        <Dialog open={showUnlockDialog} onOpenChange={(open) => open ? setShowUnlockDialog(true) : closeUnlockDialog()}>
            <DialogContent className="max-w-md">
                <DialogHeader>
                    <DialogTitle>Reopen locked period</DialogTitle>
                    <DialogDescription>
                        Reopen the period lock record while preserving posted records, reports, and the lock audit trail.
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-3 text-sm text-slate-600">
                    <p>
                        This reopens the period lock record for {lockPreview.owner_name || summary.scope?.owner_name || 'the selected student'} - {summary.period?.label || 'selected period'}.
                    </p>
                    <div className="rounded-lg border border-amber-100 bg-amber-50 p-3 text-xs leading-relaxed text-amber-800">
                        The original lock history stays in the audit trail. Reopening does not delete posted records or change GL, Trial Balance, reports, or bank-reconciliation evidence.
                    </div>
                    <Label htmlFor="period-unlock-reason">Admin reopen reason</Label>
                    <Textarea
                        id="period-unlock-reason"
                        value={unlockReason}
                        onChange={(event) => setUnlockReason(event.currentTarget.value)}
                        placeholder="Example: Reopened for correction review after instructor request."
                        rows={4}
                        data-testid="period-lock-unlock-reason"
                    />
                </div>
                <DialogFooter className="mt-4">
                    <Button type="button" variant="outline" onClick={() => closeUnlockDialog()} disabled={periodLockSubmitting}>
                        Cancel
                    </Button>
                    <Button
                        type="button"
                        variant="destructive"
                        onClick={handleUnlockPeriod}
                        disabled={periodLockSubmitting || unlockReason.trim().length < 5}
                        data-testid="period-lock-unlock-submit"
                    >
                        {periodLockSubmitting ? 'Reopening...' : 'Reopen Period'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
        </>
    );
}

function BankReconciliationTab({ selectedCourse, fmt, onRefresh, viewingStudentId, viewingStudentName, isReadOnly, onOpenPostedEvidence, onSetTrialBalanceDate, onResolveViewingStudent, requestedPeriod, onNavigate, onTraceGl, onTraceTrialBalance }) {
    const currentMonth = new Date().toISOString().slice(0, 7);
    const [period, setPeriod] = useState(currentMonth);
    const [workspace, setWorkspace] = useState(null);
    const workspaceScope = workspace?.scope || {};
    const workspaceScopeType = workspaceScope.scope_type || '';
    const workspaceOwnerId = workspaceScope.owner_id || '';
    const workspaceOwnerName = workspaceScope.owner_name || '';
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [matchingId, setMatchingId] = useState(null);
    const [adjustingId, setAdjustingId] = useState(null);
    const [draftingAdjustmentId, setDraftingAdjustmentId] = useState(null);
    const [form, setForm] = useState({
        statement_date: new Date().toISOString().split('T')[0],
        account_code: '',
        account_name: '',
        reference: '',
        description: '',
        direction: 'inflow',
        amount: '',
    });

    const fetchWorkspace = useCallback(async () => {
        const token = localStorage.getItem('token');
        const [year, month] = period.split('-');
        const params = new URLSearchParams({ year, month: String(Number(month)) });
        if (viewingStudentId) params.append('student_id', viewingStudentId);
        setLoading(true);
        try {
            const response = await axios.get(`${API}/controls/bank-reconciliation-workspace?${params.toString()}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setWorkspace(response.data);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to load bank reconciliation workspace');
        } finally {
            setLoading(false);
        }
    }, [period, viewingStudentId]);

    useEffect(() => {
        fetchWorkspace();
    }, [fetchWorkspace]);

    useEffect(() => {
        if (requestedPeriod && requestedPeriod !== period) {
            setPeriod(requestedPeriod);
        }
    }, [requestedPeriod, period]);

    useEffect(() => {
        if (workspaceScopeType !== 'student_workspace') return;
        if (!workspaceOwnerId || !workspaceOwnerName) return;
        onResolveViewingStudent?.({
            scope_type: workspaceScopeType,
            owner_id: workspaceOwnerId,
            owner_name: workspaceOwnerName,
        });
    }, [workspaceScopeType, workspaceOwnerId, workspaceOwnerName, onResolveViewingStudent]);

    useEffect(() => {
        const defaultAccount = workspace?.bank_reconciliation?.accounts?.[0];
        if (!defaultAccount || form.account_code) return;
        setForm(prev => ({
            ...prev,
            account_code: defaultAccount.account_code || '',
            account_name: defaultAccount.account_name || '',
        }));
    }, [workspace, form.account_code]);

    const handleAccountChange = (accountCode) => {
        const account = workspace?.bank_reconciliation?.accounts?.find(row => row.account_code === accountCode);
        setForm(prev => ({
            ...prev,
            account_code: accountCode,
            account_name: account?.account_name || '',
        }));
    };
    const handleBankReconPeriodChange = (event) => {
        setPeriod(event.target.value || currentMonth);
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        const token = localStorage.getItem('token');
        const [year, month] = period.split('-');
        if (!form.account_code && !form.account_name) {
            toast.error('Choose a cash/bank account first');
            return;
        }
        if (!form.description.trim()) {
            toast.error('Statement description is required');
            return;
        }
        if (!form.amount || Number(form.amount) <= 0) {
            toast.error('Amount must be greater than zero');
            return;
        }
        setSaving(true);
        try {
            await axios.post(`${API}/controls/bank-statement-lines`, {
                ...form,
                year: Number(year),
                month: Number(month),
                amount: Number(form.amount),
            }, {
                headers: { Authorization: `Bearer ${token}` },
            });
            toast.success('Statement line added. Find it under Bank Statement Lines; it is evidence only and does not affect GL or Trial Balance.');
            setForm(prev => ({
                ...prev,
                reference: '',
                description: '',
                amount: '',
            }));
            fetchWorkspace();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to add statement line');
        } finally {
            setSaving(false);
        }
    };

    const handleMatch = async (lineId, glId) => {
        const token = localStorage.getItem('token');
        setMatchingId(`${lineId}-${glId}`);
        try {
            await axios.put(`${API}/controls/bank-statement-lines/${lineId}/match`, {
                gl_id: glId,
            }, {
                headers: { Authorization: `Bearer ${token}` },
            });
            toast.success('Statement line matched to GL evidence. It now appears in Matched Evidence; GL and Trial Balance amounts are unchanged.');
            fetchWorkspace();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to match statement line');
        } finally {
            setMatchingId(null);
        }
    };

    const handleUnmatch = async (lineId) => {
        const token = localStorage.getItem('token');
        setMatchingId(`${lineId}-unmatch`);
        try {
            await axios.put(`${API}/controls/bank-statement-lines/${lineId}/unmatch`, {}, {
                headers: { Authorization: `Bearer ${token}` },
            });
            toast.success('Statement line returned to unmatched. Review it again under Bank Statement Lines before close.');
            fetchWorkspace();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to unmatch statement line');
        } finally {
            setMatchingId(null);
        }
    };

    const handlePrepareAdjustment = async (lineId, adjustmentType) => {
        const token = localStorage.getItem('token');
        setAdjustingId(`${lineId}-${adjustmentType}`);
        try {
            await axios.post(`${API}/controls/bank-reconciliation-adjustments`, {
                statement_line_id: lineId,
                adjustment_type: adjustmentType,
            }, {
                headers: { Authorization: `Bearer ${token}` },
            });
            toast.success('Adjustment prepared. Find it in Prepared Adjustments; create a JV draft before instructor review.');
            fetchWorkspace();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to prepare adjustment candidate');
        } finally {
            setAdjustingId(null);
        }
    };

    const handleVoidAdjustment = async (adjustmentId) => {
        const token = localStorage.getItem('token');
        setAdjustingId(`${adjustmentId}-void`);
        try {
            await axios.put(`${API}/controls/bank-reconciliation-adjustments/${adjustmentId}/void`, {
                reason: 'Voided from Bank Reconciliation workspace',
            }, {
                headers: { Authorization: `Bearer ${token}` },
            });
            toast.success('Adjustment candidate voided. It no longer blocks this bank reconciliation trail.');
            fetchWorkspace();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to void adjustment candidate');
        } finally {
            setAdjustingId(null);
        }
    };

    const handleCreateAdjustmentJVDraft = async (adjustmentId) => {
        if (!selectedCourse) {
            toast.error('Select a course before creating the adjustment JV draft');
            return;
        }
        const token = localStorage.getItem('token');
        setDraftingAdjustmentId(adjustmentId);
        try {
            const response = await axios.post(`${API}/controls/bank-reconciliation-adjustments/${adjustmentId}/jv-draft`, {
                course_id: selectedCourse,
            }, {
                headers: { Authorization: `Bearer ${token}` },
            });
            const jvNumber = response.data?.jv?.jv_number;
            toast.success(response.data?.message || `Saved JV draft ${jvNumber || ''} created. Open Record > Journal Voucher to submit it for review.`);
            await fetchWorkspace();
            if (onRefresh) await onRefresh();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to create adjustment JV draft');
        } finally {
            setDraftingAdjustmentId(null);
        }
    };

    const bank = workspace?.bank_reconciliation || {};
    const statementLines = workspace?.statement_lines || [];
    const statementSummary = workspace?.statement_summary || {};
    const position = workspace?.reconciliation_position || {};
    const evidenceReport = workspace?.evidence_report || {};
    const reportSummary = evidenceReport.period_summary || {};
    const adjustmentSummary = workspace?.adjustment_summary || evidenceReport.adjustment_summary || {};
    const reconcilingItems = evidenceReport.reconciling_items || {};
    const matchedPairs = evidenceReport.matched_pairs || [];
    const preparedAdjustments = workspace?.adjustment_candidates || evidenceReport.adjustment_candidates || [];
    const postedAdjustmentEvidence = evidenceReport.posted_adjustment_evidence || [];
    const preflightChecks = evidenceReport.preflight_checks || [];
    const finalReport = evidenceReport.final_report || {};
    const isReconciled = evidenceReport.status === 'reconciled';
    const accounts = bank.accounts || [];
    const canAdd = workspace?.can_add_statement_lines && !isReadOnly;
    const canMatch = workspace?.can_match_statement_lines && !isReadOnly;
    const canPrepareAdjustments = workspace?.can_prepare_adjustments && !isReadOnly;
    const canCreateAdjustmentJvDraft = workspace?.can_create_adjustment_jv_drafts && !isReadOnly;
    const displayViewingStudentName = workspaceScopeType === 'student_workspace' && workspaceOwnerName
        ? workspaceOwnerName
        : viewingStudentName;
    const getAdjustmentStatusLabel = (status) => {
        if (status === 'jv_draft_created') return 'JV draft created';
        if (status === 'jv_posted') return 'JV posted';
        if (status === 'voided') return 'Voided';
        return 'Prepared';
    };
    const getAdjustmentTrail = (adjustment) => {
        if (adjustment.status === 'jv_posted') {
            const matched = adjustment.posted_adjustment_match_status === 'matched' || !adjustment.close_blocking;
            return {
                tone: matched ? 'emerald' : 'amber',
                location: adjustment.next_location || 'Review > Bank Reconciliation and Review > General Ledger.',
                review: matched ? 'Resolved posted evidence' : 'Needs posted GL evidence match',
                impact: adjustment.downstream_impact || 'Posted to GL and Trial Balance.',
                next: adjustment.next_safe_action || (matched ? 'Review as posted evidence before close.' : 'Match the posted cash/bank GL line to statement evidence.'),
            };
        }
        if (adjustment.jv_status === 'submitted') {
            return {
                tone: 'blue',
                location: adjustment.next_location || 'Record > Journal Voucher > Instructor JV Review Queue.',
                review: 'Instructor/admin review required',
                impact: adjustment.downstream_impact || 'Draft/review evidence only. No GL or Trial Balance impact until posted.',
                next: adjustment.next_safe_action || 'Instructor/admin should approve and post or return it with feedback.',
            };
        }
        if (adjustment.jv_review_status === 'returned') {
            return {
                tone: 'orange',
                location: adjustment.next_location || 'Record > Journal Voucher > student Recent Vouchers.',
                review: 'Returned for student revision',
                impact: adjustment.downstream_impact || 'Draft/review evidence only. No GL or Trial Balance impact until posted.',
                next: adjustment.next_safe_action || 'Student should revise and resubmit the JV.',
            };
        }
        if (adjustment.status === 'jv_draft_created') {
            return {
                tone: 'blue',
                location: adjustment.next_location || 'Record > Journal Voucher.',
                review: 'Saved JV draft',
                impact: adjustment.downstream_impact || 'Draft evidence only. No GL or Trial Balance impact until posted.',
                next: adjustment.next_safe_action || 'Open the JV draft and submit it for instructor review.',
            };
        }
        return {
            tone: 'amber',
            location: adjustment.next_location || 'Review > Bank Reconciliation > Prepared Adjustments.',
            review: 'Prepared candidate',
            impact: adjustment.downstream_impact || 'Prepared evidence only. No GL or Trial Balance impact.',
            next: adjustment.next_safe_action || 'Create a JV draft or void the candidate if it is not valid.',
        };
    };
    const trailClass = (tone) => {
        if (tone === 'emerald') return 'bg-emerald-50 border-emerald-100 text-emerald-800';
        if (tone === 'blue') return 'bg-blue-50 border-blue-100 text-blue-800';
        if (tone === 'orange') return 'bg-orange-50 border-orange-100 text-orange-800';
        return 'bg-amber-50 border-amber-100 text-amber-800';
    };
    const adjustmentOptions = [
        { type: 'bank_charge', label: 'Bank Charge', direction: 'outflow' },
        { type: 'interest_income', label: 'Interest Income', direction: 'inflow' },
        { type: 'nsf_check', label: 'NSF Check', direction: 'outflow' },
        { type: 'recording_error', label: 'Recording Error', direction: 'either' },
    ];
    const unresolvedGroups = [
        {
            title: 'Outstanding Deposits',
            description: 'Book inflows not yet matched to the statement.',
            items: reconcilingItems.outstanding_deposits || [],
            empty: 'No unmatched book inflows.',
        },
        {
            title: 'Outstanding Payments',
            description: 'Book outflows not yet matched to the statement.',
            items: reconcilingItems.outstanding_payments || [],
            empty: 'No unmatched book outflows.',
        },
        {
            title: 'Unmatched Statement Inflows',
            description: 'Statement receipts not yet matched to GL.',
            items: reconcilingItems.unmatched_statement_inflows || [],
            empty: 'No unmatched statement inflows.',
        },
        {
            title: 'Unmatched Statement Outflows',
            description: 'Statement payments not yet matched to GL.',
            items: reconcilingItems.unmatched_statement_outflows || [],
            empty: 'No unmatched statement outflows.',
        },
    ];
    const selectedPeriodLabel = (() => {
        const [year, month] = period.split('-');
        if (!year || !month) return 'Selected period';
        return new Date(Number(year), Number(month) - 1, 1).toLocaleDateString('en-US', {
            month: 'long',
            year: 'numeric',
        });
    })();
    const bankReconTrailScope = displayViewingStudentName
        ? `Reviewing ${displayViewingStudentName}`
        : isReadOnly
            ? 'Read-only review'
            : 'Your workspace';
    const bankReconTrailItems = [
        {
            label: 'Period selected',
            value: `${selectedPeriodLabel} only. Bank Reconciliation is a monthly control workspace; annual/custom views belong in reports.`,
        },
        {
            label: 'What appears here',
            value: `${statementSummary.line_count || 0} statement line${statementSummary.line_count === 1 ? '' : 's'} and ${bank.cash_movement_count || 0} posted cash/bank GL line${bank.cash_movement_count === 1 ? '' : 's'} for this month.`,
        },
        {
            label: 'Where work goes next',
            value: 'Matches appear in Matched Evidence. Prepared adjustments appear in Prepared Adjustments, then generated drafts appear in Record > Journal Voucher.',
        },
        {
            label: 'GL / Trial Balance',
            value: 'Statement lines and matches are evidence only. Only posted bank-recon adjustment JVs affect GL, Trial Balance, and financial reports.',
        },
        {
            label: 'Close readiness',
            value: `${reportSummary.unmatched_statement_count || 0} unmatched statement item${reportSummary.unmatched_statement_count === 1 ? '' : 's'}, ${reportSummary.unmatched_book_count || 0} unmatched book item${reportSummary.unmatched_book_count === 1 ? '' : 's'}, and ${reportSummary.adjustment_jv_posted_unmatched_count || 0} posted adjustment item${reportSummary.adjustment_jv_posted_unmatched_count === 1 ? '' : 's'} still needing source evidence.`,
        },
    ];
    const preflightDisplayValue = (check) => {
        if (check.id === 'reconciliation_difference_zero') return fmt(check.count);
        return Number(check.count || 0).toLocaleString();
    };
    const isPreflightBlocking = (check) => !!check.blocking_close || check.status === 'attention';
    const preflightStatusLabel = (check) => (
        isPreflightBlocking(check) ? 'Blocks close evidence review' : 'Resolved / not blocking'
    );
    const preflightCloseReadinessNote = (check) => {
        const blocking = isPreflightBlocking(check);
        if (check.id === 'posted_adjustment_evidence_linked') {
            const resolved = Number(check.resolved_count || 0);
            const unmatched = Number(check.unmatched_count || 0);
            if (unmatched > 0) {
                return `${unmatched} posted adjustment JV${unmatched === 1 ? '' : 's'} still need source matching; ${resolved} resolved.`;
            }
            if (resolved > 0) {
                return `${resolved} posted adjustment JV${resolved === 1 ? '' : 's'} resolved as close evidence.`;
            }
        }
        return blocking
            ? 'Blocking until the next action is completed.'
            : 'Resolved for close evidence review.';
    };
    const postedAdjustmentCloseStatus = (item) => (
        item.match_status === 'matched'
            ? 'Resolved / not blocking close'
            : 'Blocks close evidence review until source matched'
    );
    const renderStatementPostedAdjustmentTrail = (adjustment) => {
        if (!adjustment || adjustment.status !== 'jv_posted') return null;
        const matched = adjustment.posted_adjustment_match_status === 'matched' || !adjustment.close_blocking;
        const trail = getAdjustmentTrail(adjustment);
        const closeStatus = matched ? 'Resolved / not blocking close' : 'Needs source match before close review';
        const sourceStatement = adjustment.statement_reference || adjustment.statement_description || adjustment.statement_line_id || 'statement evidence';
        return (
            <div
                className={`mt-2 rounded-lg border p-2 text-[11px] ${trailClass(trail.tone)}`}
                data-testid={`bank-recon-inline-posted-adjustment-${adjustment.id}`}
            >
                <p className="font-semibold">
                    Posted adjustment evidence: {adjustment.jv_number || 'JV posted'} - {closeStatus}
                </p>
                <p className="mt-1"><span className="font-semibold">Where:</span> {trail.location}</p>
                <p className="mt-1"><span className="font-semibold">Source:</span> {sourceStatement}</p>
                <p className="mt-1"><span className="font-semibold">Impact:</span> {trail.impact}</p>
                <p className="mt-1"><span className="font-semibold">Next:</span> {trail.next}</p>
            </div>
        );
    };
    const finalReviewBlockingCount = Number(finalReport.blocking_count || 0);
    const finalReviewReady = !!finalReport.ready_for_close_review;
    const finalReviewStatusLabel = finalReviewReady ? 'Ready for evidence signoff review' : 'Not ready for evidence signoff';
    const finalReviewOwner = displayViewingStudentName
        ? `${displayViewingStudentName} - ${selectedPeriodLabel}`
        : selectedPeriodLabel;
    const finalReviewImpact = 'This panel is evidence only. Posted adjustment JVs affect GL, Trial Balance, and financial reports; statement lines, matches, and preflight review do not post or lock anything.';
    const handleFinalEvidenceNavigation = (target) => {
        const [year, month] = period.split('-');
        const yearNumber = Number(year);
        const monthNumber = Number(month);
        if (target === 'trial') {
            const asOfDate = Number.isFinite(yearNumber) && Number.isFinite(monthNumber)
                ? getMonthEndDate(yearNumber, monthNumber)
                : '';
            if (onSetTrialBalanceDate && Number.isFinite(yearNumber) && Number.isFinite(monthNumber)) {
                onSetTrialBalanceDate(asOfDate);
            }
            if (onTraceTrialBalance) {
                onTraceTrialBalance({
                    sourceReport: 'Bank Reconciliation evidence package',
                    label: finalReviewOwner,
                    scopeLabel: displayViewingStudentName || 'Current workspace',
                    asOfDate,
                    sourceLocation: 'Review > Bank Reconciliation',
                    evidenceLocation: 'Close Evidence Signoff Preview',
                    reviewer: 'Student clears blockers. Instructor/admin reviews evidence. Auditor path is read-only.',
                    impact: 'Trial Balance includes posted bank-recon adjustment JVs and other posted GL entries through the selected month end.',
                    nextAction: 'Confirm posted totals as close support, then return to Bank Reconciliation evidence if blockers or source matches remain.',
                });
                return;
            }
            onNavigate?.('trial');
            toast.info(`Opening Trial Balance for ${finalReviewOwner}. Use posted balances to verify this Bank Reconciliation evidence package.`);
            return;
        }
        if (target === 'gl') {
            const range = Number.isFinite(yearNumber) && Number.isFinite(monthNumber)
                ? getMonthDateRange(yearNumber, monthNumber)
                : { dateFrom: '', dateTo: '' };
            onTraceGl?.({
                sourceReport: 'Bank Reconciliation evidence package',
                label: finalReviewOwner,
                dateFrom: range.dateFrom,
                dateTo: range.dateTo,
                accountFrom: '',
                accountTo: '',
                accountLabel: 'All posted GL lines in the selected bank-recon month',
                scopeLabel: displayViewingStudentName || 'Current workspace',
                amount: reportSummary.difference || position.difference || 0,
                nextAction: 'Review posted GL lines for this month, then trace cash/bank lines and any posted bank-recon adjustment JVs back to their source evidence.',
            });
            toast.info(`Opening General Ledger for ${finalReviewOwner}. Trace posted cash/bank lines and any posted bank-recon adjustment JVs.`);
        }
    };
    const openPostedAdjustmentTrail = (item, target = 'source') => {
        const recordDate = item.statement_date || item.posted_at || period;
        if (target === 'trial' && onSetTrialBalanceDate && recordDate) {
            onSetTrialBalanceDate(String(recordDate).slice(0, 10));
        }
        if (onOpenPostedEvidence) {
            onOpenPostedEvidence({
                id: item.adjustment_id,
                student_id: item.student_id || viewingStudentId || null,
                student_name: displayViewingStudentName || '',
                course_id: selectedCourse || '',
                workspace_tab: 'jv',
                module: 'jv',
                module_label: 'Record > Journal Voucher',
                record_id: item.jv_id || '',
                record_number: item.jv_number || '',
                record_type: 'Bank Recon posted JV',
                record_date: recordDate,
                amount: item.amount,
                downstream_impact: item.downstream_impact || 'Posted bank-recon adjustment affects GL, Trial Balance, and financial reports.',
                next_safe_action: item.next_safe_action || 'Review the source JV, General Ledger, and Trial Balance before close evidence review.',
            }, target);
            return;
        }
        toast.info('Open the source JV, General Ledger, or Trial Balance from the workspace navigation to review this posted evidence.');
    };

    const escapeHtml = (value) => String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');

    const renderPrintRows = (rows, emptyMessage, columns) => {
        if (!rows.length) {
            return `<tr><td colspan="${columns.length}" class="muted center">${escapeHtml(emptyMessage)}</td></tr>`;
        }
        return rows.map(row => `
            <tr>
                ${columns.map(column => `<td class="${column.className || ''}">${escapeHtml(column.render(row))}</td>`).join('')}
            </tr>
        `).join('');
    };

    const handlePrintEvidencePackage = () => {
        const printWindow = window.open('', '_blank', 'width=1100,height=800');
        if (!printWindow) {
            toast.error('Print preview was blocked. Please allow pop-ups for MaceSuite.');
            return;
        }

        const statementCount = statementSummary.line_count || 0;
        const matchedCount = reportSummary.matched_pair_count || 0;
        const postedResolvedCount = finalReport.resolved_posted_adjustment_count || reportSummary.adjustment_jv_posted_resolved_count || 0;
        const postedUnmatchedCount = finalReport.unmatched_posted_adjustment_count || reportSummary.adjustment_jv_posted_unmatched_count || 0;
        const reportScope = displayViewingStudentName ? `${displayViewingStudentName} - ${selectedPeriodLabel}` : `${selectedPeriodLabel}`;
        const reconcilingRows = unresolvedGroups.flatMap(group => (
            (group.items || []).map(item => ({
                group: group.title,
                date: item.date || item.statement_date || '-',
                reference: item.reference || '-',
                description: item.description || 'No description',
                amount: `${item.direction === 'inflow' ? '+' : '-'}${fmt(item.amount)}`,
            }))
        ));

        const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>Bank Reconciliation Evidence Package</title>
  <style>
    :root { color: #0f172a; font-family: Arial, sans-serif; }
    body { margin: 0; background: #f8fafc; color: #0f172a; }
    main { max-width: 1040px; margin: 0 auto; padding: 32px; }
    .toolbar { position: sticky; top: 0; z-index: 2; background: #f8fafc; border-bottom: 1px solid #e2e8f0; padding: 12px 32px; }
    .toolbar-inner { max-width: 1040px; margin: 0 auto; display: flex; align-items: center; justify-content: space-between; gap: 12px; }
    .toolbar button { background: #0F2A44; color: #fff; border: 0; border-radius: 6px; padding: 9px 14px; font-weight: 700; cursor: pointer; }
    .toolbar p { margin: 0; color: #475569; font-size: 12px; }
    header { background: #0F2A44; color: #fff; border-radius: 10px; padding: 24px; }
    h1, h2, h3 { margin: 0; }
    h1 { font-size: 26px; }
    h2 { color: #0F2A44; font-size: 18px; margin-bottom: 10px; }
    h3 { font-size: 14px; margin-bottom: 6px; }
    .subtitle { color: #bfdbfe; margin-top: 8px; }
    .meta { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; margin-top: 18px; }
    .meta div, section { background: #fff; border: 1px solid #e2e8f0; border-radius: 8px; }
    .meta div { padding: 12px; }
    section { margin-top: 18px; padding: 18px; page-break-inside: avoid; }
    .label { color: #64748b; font-size: 11px; font-weight: 700; text-transform: uppercase; }
    .value { font-size: 16px; font-weight: 700; margin-top: 4px; }
    table { width: 100%; border-collapse: collapse; font-size: 12px; }
    th { background: #eff6ff; color: #0F2A44; text-align: left; padding: 9px; border-bottom: 1px solid #bfdbfe; }
    td { padding: 9px; border-bottom: 1px solid #e2e8f0; vertical-align: top; }
    .amount { text-align: right; white-space: nowrap; }
    .center { text-align: center; }
    .muted { color: #64748b; }
    .badge { display: inline-block; border-radius: 999px; padding: 3px 8px; font-size: 11px; font-weight: 700; }
    .ready { background: #dcfce7; color: #166534; }
    .attention { background: #fef3c7; color: #92400e; }
    .note { background: #ecfeff; border: 1px solid #bae6fd; border-radius: 8px; color: #0f172a; padding: 12px; font-size: 12px; }
    @media print {
      body { background: #fff; }
      main { padding: 0; }
      .toolbar { display: none; }
      section, header { break-inside: avoid; }
    }
  </style>
</head>
<body>
  <div class="toolbar">
    <div class="toolbar-inner">
      <p>Printable preview only. Review the evidence, then print or save as PDF from this page.</p>
      <button type="button" onclick="window.print()">Print / Save PDF</button>
    </div>
  </div>
  <main>
    <header>
      <div class="label">MaceSuite Phase D Control Evidence</div>
      <h1>Bank Reconciliation Evidence Package</h1>
      <p class="subtitle">${escapeHtml(reportScope)}. Printable evidence only; this action does not post, lock, edit, or delete records.</p>
    </header>

    <div class="meta">
      <div><p class="label">Status</p><p class="value">${escapeHtml(finalReport.status_label || evidenceReport.status_label || 'Preflight checks')}</p></div>
      <div><p class="label">Statement Lines</p><p class="value">${statementCount}</p></div>
      <div><p class="label">Matched Pairs</p><p class="value">${matchedCount}</p></div>
      <div><p class="label">Difference</p><p class="value">${escapeHtml(fmt(reportSummary.difference || position.difference || 0))}</p></div>
    </div>

    <section>
      <h2>Close Evidence Signoff Preview</h2>
      <p class="note">${escapeHtml(finalReviewStatusLabel)} for ${escapeHtml(finalReviewOwner)}. This printable package is evidence only; it does not post, lock, edit, approve, or delete records.</p>
      <table>
        <thead><tr><th>Question</th><th>Answer</th></tr></thead>
        <tbody>
          <tr><td>What was reviewed?</td><td>${escapeHtml(finalReviewOwner)} Bank Reconciliation evidence package.</td></tr>
          <tr><td>Where does this live?</td><td>Review &gt; Bank Reconciliation. Use this print package as PDF/print support.</td></tr>
          <tr><td>Who reviews next?</td><td>Student resolves blockers. Instructor/admin reviews evidence. Auditor path is read-only.</td></tr>
          <tr><td>GL / Trial Balance impact</td><td>${escapeHtml(finalReviewImpact)}</td></tr>
          <tr><td>Status</td><td>${escapeHtml(finalReviewReady ? 'Evidence package ready' : `${finalReviewBlockingCount} blocker${finalReviewBlockingCount === 1 ? '' : 's'} before signoff`)}</td></tr>
          <tr><td>Next safe action</td><td>${escapeHtml(finalReport.next_safe_action || 'Review matched evidence, posted adjustment evidence, General Ledger, and Trial Balance before close evidence review.')}</td></tr>
        </tbody>
      </table>
    </section>

    <section>
      <h2>Preflight Checklist</h2>
      <table>
        <thead><tr><th>Check</th><th>Close Readiness</th><th>Count</th><th>Location</th><th>Impact / Next Action</th></tr></thead>
        <tbody>
          ${renderPrintRows(preflightChecks, 'No preflight checks loaded.', [
            { render: row => row.label },
            { render: row => `${preflightStatusLabel(row)} - ${preflightCloseReadinessNote(row)}` },
            { render: row => preflightDisplayValue(row), className: 'amount' },
            { render: row => row.location },
            { render: row => `${row.impact || ''} Next: ${row.next_safe_action || ''}` },
          ])}
        </tbody>
      </table>
    </section>

    <section>
      <h2>Posted Adjustment Evidence</h2>
      <p class="note">Posted bank-reconciliation adjustment JVs affect GL, Trial Balance, and financial reports. They must remain linked to source statement evidence before close review. Resolved: ${postedResolvedCount}; needs source match: ${postedUnmatchedCount}.</p>
      <table>
        <thead><tr><th>Statement</th><th>JV</th><th>Close Readiness</th><th>GL Evidence</th><th>Amount</th><th>Next Safe Action</th></tr></thead>
        <tbody>
          ${renderPrintRows(postedAdjustmentEvidence, 'No posted adjustment evidence for this period.', [
            { render: row => `${row.statement_reference || '-'} ${row.statement_date || ''}` },
            { render: row => row.jv_number || '-' },
            { render: row => `${row.match_status === 'matched' ? 'Source matched' : 'Needs source match'} - ${postedAdjustmentCloseStatus(row)}` },
            { render: row => row.matched_gl ? `${row.matched_gl.reference || row.matched_gl.gl_id || '-'} - ${row.matched_gl.account_name || row.matched_gl.account_code || ''}` : '-' },
            { render: row => fmt(row.amount), className: 'amount' },
            { render: row => row.next_safe_action || '-' },
          ])}
        </tbody>
      </table>
    </section>

    <section>
      <h2>Matched Evidence</h2>
      <table>
        <thead><tr><th>Statement</th><th>Posted GL</th><th>Description</th><th>Direction</th><th>Amount</th><th>Matched By</th></tr></thead>
        <tbody>
          ${renderPrintRows(matchedPairs, 'No matched evidence yet.', [
            { render: row => `${row.statement_reference || '-'} ${row.statement_date || ''}` },
            { render: row => `${row.gl_reference || '-'} ${row.gl_date || ''}` },
            { render: row => row.description || '-' },
            { render: row => row.direction || '-' },
            { render: row => fmt(row.amount), className: 'amount' },
            { render: row => row.matched_by_name || '-' },
          ])}
        </tbody>
      </table>
    </section>

    <section>
      <h2>Prepared Adjustments and JV Draft Trail</h2>
      <table>
        <thead><tr><th>Adjustment</th><th>Statement</th><th>Status</th><th>JV</th><th>Amount</th><th>Next Safe Action</th></tr></thead>
        <tbody>
          ${renderPrintRows(preparedAdjustments, 'No prepared adjustments for this period.', [
            { render: row => row.adjustment_label || '-' },
            { render: row => `${row.statement_reference || '-'} ${row.statement_date || ''}` },
            { render: row => getAdjustmentStatusLabel(row.status) },
            { render: row => row.jv_number || '-' },
            { render: row => fmt(row.amount), className: 'amount' },
            { render: row => getAdjustmentTrail(row).next },
          ])}
        </tbody>
      </table>
    </section>

    <section>
      <h2>Reconciling Items Still Requiring Review</h2>
      <table>
        <thead><tr><th>Group</th><th>Date</th><th>Reference</th><th>Description</th><th>Amount</th></tr></thead>
        <tbody>
          ${renderPrintRows(reconcilingRows, 'No reconciling items requiring review.', [
            { render: row => row.group },
            { render: row => row.date },
            { render: row => row.reference },
            { render: row => row.description },
            { render: row => row.amount, className: 'amount' },
          ])}
        </tbody>
      </table>
    </section>

    <section>
      <h2>Review Notes</h2>
      <p class="note">${escapeHtml(finalReport.next_safe_action || evidenceReport.readiness_note || 'Review matched evidence, posted adjustment evidence, General Ledger, and Trial Balance before close evidence review.')}</p>
    </section>
  </main>
</body>
</html>`;

        printWindow.document.open();
        printWindow.document.write(html);
        printWindow.document.close();
        printWindow.focus();
        toast.success('Bank Reconciliation Evidence Package preview opened. Use Print / Save PDF inside the preview when ready. No GL, Trial Balance, source, or close records were changed.');
    };

    const renderReconItem = (item) => (
        <div key={item.id || `${item.reference}-${item.amount}`} className="rounded-lg border border-slate-100 bg-white p-3">
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <p className="text-sm font-semibold text-slate-900 truncate">{item.description || item.reference || 'No description'}</p>
                    <p className="text-xs text-slate-500 mt-1">
                        {item.date || item.statement_date || '-'} - {item.reference || 'No reference'} - {item.account_code || '-'} {item.account_name || ''}
                    </p>
                </div>
                <p className={`text-sm font-bold shrink-0 ${item.direction === 'inflow' ? 'text-emerald-700' : 'text-rose-700'}`}>
                    {item.direction === 'inflow' ? '+' : '-'}{fmt(item.amount)}
                </p>
            </div>
        </div>
    );

    return (
        <div className="space-y-5" data-testid="bank-reconciliation-tab">
            <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
                <div className="bg-[#0F2A44] text-white px-6 py-5 flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                    <div>
                        <p className="text-xs uppercase tracking-widest text-blue-200">Phase D Control Workspace</p>
                        <h2 className="text-xl font-bold mt-1">Bank Reconciliation</h2>
                        <p className="text-sm text-blue-100 mt-1">
                            Enter statement lines, match them to posted cash/bank GL movements, and review reconciliation evidence.
                        </p>
                        {displayViewingStudentName && (
                            <p className="text-xs text-blue-200 mt-2">Reviewing student: {displayViewingStudentName}</p>
                        )}
                    </div>
                    <div className="flex flex-wrap items-end gap-2">
                        <div>
                            <Label className="text-xs text-blue-100">Reconciliation Month</Label>
                            <Input
                                type="month"
                                value={period}
                                onChange={handleBankReconPeriodChange}
                                onInput={handleBankReconPeriodChange}
                                className="bg-white text-slate-900 border-white/30"
                                data-testid="bank-recon-period-input"
                            />
                            <p className="mt-1 text-[11px] text-blue-100" data-testid="bank-recon-period-label">
                                {selectedPeriodLabel} monthly evidence
                            </p>
                        </div>
                        <Button
                            type="button"
                            variant="secondary"
                            onClick={fetchWorkspace}
                            disabled={loading}
                            className="bg-white text-[#0F2A44] hover:bg-blue-50"
                            data-testid="bank-recon-refresh-btn"
                        >
                            Refresh
                        </Button>
                    </div>
                </div>

                <div className="p-5 border-b border-slate-200 bg-slate-50" data-testid="bank-recon-period-trail">
                    <ReportWorkflowTrail
                        title="Bank Reconciliation period trail"
                        scope={bankReconTrailScope}
                        items={bankReconTrailItems}
                    />
                </div>

                <div className="p-5 grid grid-cols-1 md:grid-cols-4 gap-3">
                    <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                        <p className="text-xs uppercase tracking-wide text-slate-500">Book Cash/Bank Net</p>
                        <p className="text-xl font-bold text-[#0F2A44] mt-1">{fmt(position.book_net_movement)}</p>
                        <p className="text-xs text-slate-500 mt-1">{bank.cash_movement_count || 0} GL lines</p>
                    </div>
                    <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                        <p className="text-xs uppercase tracking-wide text-slate-500">Statement Net</p>
                        <p className="text-xl font-bold text-[#0F2A44] mt-1">{fmt(position.statement_net_movement)}</p>
                        <p className="text-xs text-slate-500 mt-1">{statementSummary.line_count || 0} statement lines</p>
                    </div>
                    <div className={`rounded-xl border p-4 ${Math.abs(position.difference || 0) < 0.01 ? 'bg-emerald-50 border-emerald-100' : 'bg-amber-50 border-amber-100'}`}>
                        <p className="text-xs uppercase tracking-wide text-slate-500">Difference</p>
                        <p className={`text-xl font-bold mt-1 ${Math.abs(position.difference || 0) < 0.01 ? 'text-emerald-700' : 'text-amber-700'}`}>
                            {fmt(position.difference)}
                        </p>
                        <p className="text-xs text-slate-500 mt-1">Statement net minus book net</p>
                    </div>
                    <div className="rounded-xl border border-blue-100 bg-blue-50 p-4">
                        <p className="text-xs uppercase tracking-wide text-blue-500">Likely Matches</p>
                        <p className="text-xl font-bold text-blue-800 mt-1">{statementSummary.candidate_count || 0}</p>
                        <p className="text-xs text-blue-700 mt-1">Suggestions only, not posted</p>
                    </div>
                </div>
            </div>

            <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden" data-testid="bank-recon-evidence-report">
                <div className="px-5 py-4 border-b border-slate-200 flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                    <div>
                        <p className="text-xs uppercase tracking-widest text-slate-400 font-semibold">Reconciliation Evidence Report</p>
                        <h3 className="text-lg font-bold text-[#0F2A44] mt-1">Period Status: {evidenceReport.status_label || 'Loading'}</h3>
                        <p className="text-sm text-slate-500 mt-1">{evidenceReport.readiness_note || 'Load the period to review reconciliation status.'}</p>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={handlePrintEvidencePackage}
                            disabled={!workspace || loading}
                            className="border-blue-200 text-[#0F2A44] hover:bg-blue-50"
                            data-testid="bank-recon-print-evidence-package"
                        >
                            <Printer className="w-4 h-4 mr-2" /> Print Evidence Package
                        </Button>
                        <div className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-sm font-semibold w-fit ${
                            isReconciled ? 'bg-emerald-50 border-emerald-100 text-emerald-700' : 'bg-amber-50 border-amber-100 text-amber-700'
                        }`}>
                            {isReconciled ? <CheckCircle2 className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
                            {isReconciled ? 'Ready for close review' : 'Needs reconciliation review'}
                        </div>
                    </div>
                </div>
                <div className="p-5 space-y-5">
                    <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
                        <div className="rounded-xl bg-slate-50 border border-slate-100 p-3">
                            <p className="text-xs text-slate-500 uppercase tracking-wide">Matched Pairs</p>
                            <p className="text-xl font-bold text-[#0F2A44]">{reportSummary.matched_pair_count || 0}</p>
                        </div>
                        <div className="rounded-xl bg-slate-50 border border-slate-100 p-3">
                            <p className="text-xs text-slate-500 uppercase tracking-wide">Adjustments</p>
                            <p className="text-xl font-bold text-[#0F2A44]">{adjustmentSummary.candidate_count || 0}</p>
                        </div>
                        <div className="rounded-xl bg-slate-50 border border-slate-100 p-3">
                            <p className="text-xs text-slate-500 uppercase tracking-wide">Book Unmatched</p>
                            <p className="text-xl font-bold text-[#0F2A44]">{reportSummary.unmatched_book_count || 0}</p>
                        </div>
                        <div className="rounded-xl bg-slate-50 border border-slate-100 p-3">
                            <p className="text-xs text-slate-500 uppercase tracking-wide">Statement Unmatched</p>
                            <p className="text-xl font-bold text-[#0F2A44]">{reportSummary.unmatched_statement_count || 0}</p>
                        </div>
                        <div className="rounded-xl bg-slate-50 border border-slate-100 p-3">
                            <p className="text-xs text-slate-500 uppercase tracking-wide">Book Net</p>
                            <p className="text-lg font-bold text-[#0F2A44]">{fmt(reportSummary.book_net_movement)}</p>
                        </div>
                        <div className="rounded-xl bg-slate-50 border border-slate-100 p-3">
                            <p className="text-xs text-slate-500 uppercase tracking-wide">Difference</p>
                            <p className={`text-lg font-bold ${Math.abs(reportSummary.difference || 0) < 0.01 ? 'text-emerald-700' : 'text-amber-700'}`}>
                                {fmt(reportSummary.difference)}
                            </p>
                        </div>
                    </div>

                    <div className={`rounded-xl border p-4 ${
                        finalReviewReady
                            ? 'border-emerald-200 bg-emerald-50/70'
                            : 'border-amber-200 bg-amber-50/70'
                    }`} data-testid="bank-recon-signoff-preview">
                        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                            <div>
                                <p className={`text-xs uppercase tracking-widest font-semibold ${
                                    finalReviewReady ? 'text-emerald-700' : 'text-amber-700'
                                }`}>
                                    Close Evidence Signoff Preview
                                </p>
                                <h4 className="font-semibold text-[#0F2A44] mt-1">{finalReviewStatusLabel}</h4>
                                <p className="text-sm text-slate-600 mt-1 max-w-3xl">
                                    {finalReport.next_safe_action || 'Review this evidence package before any future hard close. This panel does not lock, post, edit, or delete records.'}
                                </p>
                            </div>
                            <span className={`rounded-full border px-3 py-1 text-xs font-semibold w-fit ${
                                finalReviewReady
                                    ? 'bg-white border-emerald-200 text-emerald-700'
                                    : 'bg-white border-amber-200 text-amber-700'
                            }`}>
                                {finalReviewReady ? 'Evidence package ready' : `${finalReviewBlockingCount} blocker${finalReviewBlockingCount === 1 ? '' : 's'} before signoff`}
                            </span>
                        </div>
                        <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-5">
                            <div className="rounded-lg border border-white/70 bg-white/80 p-3">
                                <p className="text-xs font-semibold uppercase text-slate-500">What I reviewed</p>
                                <p className="mt-1 text-sm text-slate-700">{finalReviewOwner} Bank Reconciliation evidence package.</p>
                            </div>
                            <div className="rounded-lg border border-white/70 bg-white/80 p-3">
                                <p className="text-xs font-semibold uppercase text-slate-500">Where it lives</p>
                                <p className="mt-1 text-sm text-slate-700">Review &gt; Bank Reconciliation. Use Print Evidence Package for PDF/print support.</p>
                            </div>
                            <div className="rounded-lg border border-white/70 bg-white/80 p-3">
                                <p className="text-xs font-semibold uppercase text-slate-500">Reviewer</p>
                                <p className="mt-1 text-sm text-slate-700">Student resolves blockers. Instructor/admin reviews evidence. Auditor path is read-only.</p>
                            </div>
                            <div className="rounded-lg border border-white/70 bg-white/80 p-3">
                                <p className="text-xs font-semibold uppercase text-slate-500">GL/TB impact</p>
                                <p className="mt-1 text-sm text-slate-700">{finalReviewImpact}</p>
                            </div>
                            <div className="rounded-lg border border-white/70 bg-white/80 p-3">
                                <p className="text-xs font-semibold uppercase text-slate-500">Next safe action</p>
                                <p className="mt-1 text-sm text-slate-700">
                                    {finalReviewReady
                                        ? 'Review General Ledger, Trial Balance, and the printable evidence package before future close approval.'
                                        : 'Clear preflight attention items before treating this as close support.'}
                                </p>
                            </div>
                        </div>
                        <div className="mt-4 flex flex-wrap gap-2">
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => handleFinalEvidenceNavigation('gl')}
                                data-testid="bank-recon-final-open-gl"
                            >
                                <FileText className="w-4 h-4 mr-2" /> General Ledger
                            </Button>
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => handleFinalEvidenceNavigation('trial')}
                                data-testid="bank-recon-final-open-trial"
                            >
                                <Scale className="w-4 h-4 mr-2" /> Trial Balance
                            </Button>
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={handlePrintEvidencePackage}
                                disabled={!workspace || loading}
                                data-testid="bank-recon-final-print-evidence"
                            >
                                <Printer className="w-4 h-4 mr-2" /> Print Evidence Package
                            </Button>
                        </div>
                    </div>

                    {preflightChecks.length > 0 && (
                        <div className="rounded-xl border border-blue-100 bg-blue-50/60 p-4" data-testid="bank-recon-final-preflight">
                            <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                                <div>
                                    <p className="text-xs uppercase tracking-widest text-blue-500 font-semibold">Final Reconciliation Preflight</p>
                                    <h4 className="font-semibold text-[#0F2A44] mt-1">{finalReport.status_label || 'Preflight checks'}</h4>
                                    <p className="text-xs text-slate-600 mt-1 max-w-2xl">
                                        {finalReport.next_safe_action || 'Clear attention items before close evidence review. This panel does not lock, post, or change accounting records.'}
                                    </p>
                                </div>
                                <span className={`rounded-full border px-3 py-1 text-xs font-semibold w-fit ${
                                    finalReport.ready_for_close_review
                                        ? 'bg-emerald-50 border-emerald-100 text-emerald-700'
                                        : 'bg-amber-50 border-amber-100 text-amber-700'
                                }`}>
                                    {finalReport.ready_for_close_review ? 'Ready for evidence review' : `${finalReport.blocking_count || 0} attention item${finalReport.blocking_count === 1 ? '' : 's'}`}
                                </span>
                            </div>
                            <div className="mt-3 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                                {preflightChecks.map((check) => {
                                    const ready = check.status === 'ready';
                                    return (
                                        <div
                                            key={check.id}
                                            className={`rounded-lg border p-3 ${
                                                ready
                                                    ? 'bg-white border-emerald-100'
                                                    : 'bg-white border-amber-100'
                                            }`}
                                            data-testid={`bank-recon-preflight-${check.id}`}
                                        >
                                            <div className="flex items-start justify-between gap-2">
                                                <div className="min-w-0">
                                                    <p className="text-sm font-semibold text-slate-900">{check.label}</p>
                                                    <p className="text-[11px] text-slate-500 mt-1">{check.location}</p>
                                                </div>
                                                {ready ? (
                                                    <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                                                ) : (
                                                    <AlertCircle className="w-4 h-4 text-amber-600 shrink-0" />
                                                )}
                                            </div>
                                            <span className={`mt-2 inline-flex rounded-full border px-2 py-0.5 text-[11px] font-semibold ${
                                                isPreflightBlocking(check)
                                                    ? 'bg-amber-50 text-amber-700 border-amber-100'
                                                    : 'bg-emerald-50 text-emerald-700 border-emerald-100'
                                            }`}>
                                                {preflightStatusLabel(check)}
                                            </span>
                                            <p className={`text-xl font-bold mt-2 ${ready ? 'text-emerald-700' : 'text-amber-700'}`}>
                                                {preflightDisplayValue(check)}
                                            </p>
                                            {check.id === 'bank_recon_jv_drafts_cleared' && check.count > 0 && (
                                                <p className="text-[11px] text-slate-500 mt-1">
                                                    {check.waiting_count || 0} saved, {check.submitted_count || 0} submitted, {check.returned_count || 0} returned
                                                </p>
                                            )}
                                            {check.id === 'posted_adjustment_evidence_linked' && check.count > 0 && (
                                                <p className="text-[11px] text-slate-500 mt-1">
                                                    {check.resolved_count || 0} resolved, {check.unmatched_count || 0} need source match
                                                </p>
                                            )}
                                            <div className="mt-2 rounded-md bg-slate-50 border border-slate-100 p-2 text-xs text-slate-600">
                                                <p><span className="font-semibold text-slate-700">Close readiness:</span> {preflightCloseReadinessNote(check)}</p>
                                                <p><span className="font-semibold text-slate-700">Impact:</span> {check.impact}</p>
                                                <p className="mt-1"><span className="font-semibold text-slate-700">Next:</span> {check.next_safe_action}</p>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                            {finalReport.sections?.length > 0 && (
                                <div className="mt-3 rounded-lg border border-blue-100 bg-white p-3">
                                    <p className="text-xs font-semibold text-slate-700">Final evidence report includes</p>
                                    <div className="mt-2 flex flex-wrap gap-2">
                                        {finalReport.sections.map((section) => (
                                            <span key={section} className="rounded-full bg-slate-50 border border-slate-200 px-2 py-1 text-[11px] font-medium text-slate-600">
                                                {section}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                        <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                            <h4 className="font-semibold text-[#0F2A44]">Matched Evidence</h4>
                            <p className="text-xs text-slate-500 mt-1">Statement line to posted GL line pairs.</p>
                            <div className="mt-3 space-y-2 max-h-64 overflow-y-auto pr-1">
                                {matchedPairs.length ? matchedPairs.slice(0, 8).map((pair) => (
                                    <div key={`${pair.statement_line_id}-${pair.gl_id}`} className="rounded-lg border border-emerald-100 bg-white p-3">
                                        <div className="flex items-start justify-between gap-3">
                                            <div className="min-w-0">
                                                <p className="text-sm font-semibold text-slate-900 truncate">{pair.description || pair.statement_reference}</p>
                                                <p className="text-xs text-slate-500 mt-1">
                                                    Statement {pair.statement_reference || '-'} -> GL {pair.gl_reference || '-'}
                                                </p>
                                            </div>
                                            <p className={`text-sm font-bold shrink-0 ${pair.direction === 'inflow' ? 'text-emerald-700' : 'text-rose-700'}`}>
                                                {pair.direction === 'inflow' ? '+' : '-'}{fmt(pair.amount)}
                                            </p>
                                        </div>
                                    </div>
                                )) : (
                                    <p className="text-sm text-slate-500 py-4">No matched evidence yet.</p>
                                )}
                            </div>
                        </div>

                        <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                            <h4 className="font-semibold text-[#0F2A44]">Review Steps</h4>
                            <p className="text-xs text-slate-500 mt-1">Use this before considering period close.</p>
                            <ol className="mt-3 space-y-2">
                                {(evidenceReport.review_steps || []).map((step, index) => (
                                    <li key={step} className="flex gap-2 text-sm text-slate-700">
                                        <span className="w-5 h-5 rounded-full bg-[#2F8FCB] text-white text-xs font-bold flex items-center justify-center shrink-0">{index + 1}</span>
                                        <span>{step}</span>
                                    </li>
                                ))}
                            </ol>
                        </div>
                    </div>

                    {postedAdjustmentEvidence.length > 0 && (
                        <div className="rounded-xl border border-emerald-200 bg-emerald-50/70 p-4" data-testid="posted-adjustment-evidence">
                            <div className="flex flex-col gap-2 lg:flex-row lg:items-start lg:justify-between">
                                <div>
                                    <h4 className="font-semibold text-[#0F2A44]">Posted Adjustment Evidence</h4>
                                    <p className="text-xs text-slate-600 mt-1">
                                        Bank-recon JVs that are already posted. These affect GL and Trial Balance and should remain linked to source statement evidence.
                                    </p>
                                </div>
                                <span className="rounded-full bg-white border border-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-700 w-fit">
                                    {postedAdjustmentEvidence.filter(item => item.match_status === 'matched').length}/{postedAdjustmentEvidence.length} linked
                                </span>
                            </div>
                            <div className="mt-3 grid grid-cols-1 xl:grid-cols-2 gap-3">
                                {postedAdjustmentEvidence.slice(0, 8).map((item) => (
                                    <div key={item.adjustment_id} className="rounded-lg border border-emerald-100 bg-white p-3">
                                        <div className="flex items-start justify-between gap-3">
                                            <div className="min-w-0">
                                                <div className="flex flex-wrap items-center gap-2">
                                                    <p className="text-sm font-semibold text-slate-900 truncate">{item.adjustment_label || 'Posted adjustment'}</p>
                                                    <span className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold ${
                                                        item.match_status === 'matched'
                                                            ? 'bg-emerald-50 text-emerald-700 border-emerald-100'
                                                            : 'bg-amber-50 text-amber-700 border-amber-100'
                                                    }`}>
                                                        {item.match_status === 'matched' ? 'Source matched' : 'Needs source match'}
                                                    </span>
                                                    <span className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold ${
                                                        item.match_status === 'matched'
                                                            ? 'bg-emerald-50 text-emerald-700 border-emerald-100'
                                                            : 'bg-amber-50 text-amber-700 border-amber-100'
                                                    }`}>
                                                        {postedAdjustmentCloseStatus(item)}
                                                    </span>
                                                </div>
                                                <p className="text-xs text-slate-500 mt-1">
                                                    Statement {item.statement_reference || '-'} -> JV <span className="font-mono">{item.jv_number || '-'}</span>
                                                </p>
                                                {item.matched_gl && (
                                                    <p className="text-xs text-emerald-700 mt-1">
                                                        GL evidence: {item.matched_gl.reference || item.matched_gl.gl_id} - {item.matched_gl.account_name || item.matched_gl.account_code}
                                                    </p>
                                                )}
                                            </div>
                                            <p className={`text-sm font-bold shrink-0 ${item.direction === 'inflow' ? 'text-emerald-700' : 'text-rose-700'}`}>
                                                {fmt(item.amount)}
                                            </p>
                                        </div>
                                        <div className="mt-3 grid grid-cols-1 md:grid-cols-4 gap-2 text-xs">
                                            <div className="rounded-lg border border-slate-100 bg-slate-50 p-2">
                                                <p className="font-semibold text-slate-700">Close readiness</p>
                                                <p className="text-slate-500 mt-1">{postedAdjustmentCloseStatus(item)}</p>
                                            </div>
                                            <div className="rounded-lg border border-slate-100 bg-slate-50 p-2">
                                                <p className="font-semibold text-slate-700">Where it is now</p>
                                                <p className="text-slate-500 mt-1">{item.next_location}</p>
                                            </div>
                                            <div className="rounded-lg border border-slate-100 bg-slate-50 p-2">
                                                <p className="font-semibold text-slate-700">Accounting impact</p>
                                                <p className="text-slate-500 mt-1">{item.downstream_impact}</p>
                                            </div>
                                            <div className="rounded-lg border border-slate-100 bg-slate-50 p-2">
                                                <p className="font-semibold text-slate-700">Next safe action</p>
                                                <p className="text-slate-500 mt-1">{item.next_safe_action}</p>
                                            </div>
                                        </div>
                                        <div className="mt-3 flex flex-wrap gap-2">
                                            <Button
                                                type="button"
                                                variant="outline"
                                                size="sm"
                                                onClick={() => openPostedAdjustmentTrail(item, 'source')}
                                                className="h-8 border-emerald-200 text-emerald-800 hover:bg-emerald-50"
                                                data-testid={`bank-recon-open-source-jv-${item.adjustment_id}`}
                                                aria-label={`Open source Journal Voucher ${item.jv_number || item.adjustment_label || item.adjustment_id} for posted bank reconciliation adjustment evidence`}
                                                title={`Open source Journal Voucher ${item.jv_number || item.adjustment_label || item.adjustment_id}`}
                                            >
                                                <FileText className="w-3.5 h-3.5 mr-1.5" /> Open JV
                                            </Button>
                                            <Button
                                                type="button"
                                                variant="outline"
                                                size="sm"
                                                onClick={() => openPostedAdjustmentTrail(item, 'gl')}
                                                className="h-8 border-blue-200 text-[#0F2A44] hover:bg-blue-50"
                                                data-testid={`bank-recon-open-gl-${item.adjustment_id}`}
                                                aria-label={`Open General Ledger for posted bank reconciliation adjustment ${item.jv_number || item.adjustment_label || item.adjustment_id}`}
                                                title={`Open General Ledger for posted adjustment ${item.jv_number || item.adjustment_label || item.adjustment_id}`}
                                            >
                                                <FileSpreadsheet className="w-3.5 h-3.5 mr-1.5" /> General Ledger
                                            </Button>
                                            <Button
                                                type="button"
                                                variant="outline"
                                                size="sm"
                                                onClick={() => openPostedAdjustmentTrail(item, 'trial')}
                                                className="h-8 border-purple-200 text-purple-800 hover:bg-purple-50"
                                                data-testid={`bank-recon-open-trial-${item.adjustment_id}`}
                                                aria-label={`Open Trial Balance for posted bank reconciliation adjustment ${item.jv_number || item.adjustment_label || item.adjustment_id}`}
                                                title={`Open Trial Balance for posted adjustment ${item.jv_number || item.adjustment_label || item.adjustment_id}`}
                                            >
                                                <Scale className="w-3.5 h-3.5 mr-1.5" /> Trial Balance
                                            </Button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                        <div className="flex flex-col gap-2 lg:flex-row lg:items-start lg:justify-between">
                            <div>
                                <h4 className="font-semibold text-[#0F2A44]">Prepared Adjustments</h4>
                                <p className="text-xs text-slate-500 mt-1">Adjustment trail from prepared evidence to JV draft, review, and posted GL/TB impact.</p>
                            </div>
                            <span className="rounded-full bg-white border border-slate-200 px-2 py-0.5 text-xs font-semibold text-slate-600 w-fit">
                                {fmt(adjustmentSummary.total_amount || 0)}
                            </span>
                        </div>
                        <div className="mt-3 grid grid-cols-1 xl:grid-cols-2 gap-3">
                            {preparedAdjustments.length ? preparedAdjustments.slice(0, 8).map((adjustment) => {
                                const trail = getAdjustmentTrail(adjustment);
                                return (
                                    <div key={adjustment.id} className="rounded-lg border border-blue-100 bg-white p-3">
                                        <div className="flex items-start justify-between gap-3">
                                            <div className="min-w-0">
                                                <p className="text-sm font-semibold text-slate-900 truncate">{adjustment.adjustment_label}</p>
                                                <p className="text-xs text-slate-500 mt-1 truncate">{adjustment.statement_reference || 'No reference'} - {adjustment.statement_description}</p>
                                            </div>
                                            <p className={`text-sm font-bold shrink-0 ${adjustment.direction === 'inflow' ? 'text-emerald-700' : 'text-rose-700'}`}>
                                                {fmt(adjustment.amount)}
                                            </p>
                                        </div>
                                        <div className="mt-2 rounded-lg bg-blue-50 border border-blue-100 p-2 text-xs text-blue-900">
                                            <p>DR {adjustment.proposed_debit_account}</p>
                                            <p>CR {adjustment.proposed_credit_account}</p>
                                        </div>
                                        <div className="mt-2 flex flex-wrap items-center gap-2">
                                            <span className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold ${
                                                adjustment.status === 'jv_posted'
                                                    ? 'bg-emerald-50 text-emerald-700 border-emerald-100'
                                                    : adjustment.status === 'jv_draft_created'
                                                        ? 'bg-blue-50 text-blue-700 border-blue-100'
                                                        : 'bg-amber-50 text-amber-700 border-amber-100'
                                            }`}>
                                                {getAdjustmentStatusLabel(adjustment.status)}
                                            </span>
                                            {adjustment.jv_number && (
                                                <span className="text-[11px] font-mono text-slate-500">{adjustment.jv_number}</span>
                                            )}
                                            <span className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold ${trailClass(trail.tone)}`}>
                                                {trail.review}
                                            </span>
                                        </div>
                                        <div className={`mt-2 rounded-lg border p-2 text-xs ${trailClass(trail.tone)}`}>
                                            <p><span className="font-semibold">Where it went:</span> {trail.location}</p>
                                            <p className="mt-1"><span className="font-semibold">Impact:</span> {trail.impact}</p>
                                            <p className="mt-1"><span className="font-semibold">Next:</span> {trail.next}</p>
                                        </div>
                                        <div className="mt-2 flex items-center justify-between gap-2">
                                            <p className="text-[11px] text-slate-500">Prepared by {adjustment.created_by_name || 'user'}</p>
                                            <div className="flex flex-wrap justify-end gap-2">
                                                {canCreateAdjustmentJvDraft && adjustment.status === 'prepared' && adjustment.adjustment_type !== 'recording_error' && (
                                                    <Button
                                                        type="button"
                                                        variant="outline"
                                                        size="sm"
                                                        onClick={() => handleCreateAdjustmentJVDraft(adjustment.id)}
                                                        disabled={draftingAdjustmentId === adjustment.id || !selectedCourse}
                                                        className="h-7 border-emerald-200 text-emerald-700 hover:bg-emerald-50 text-xs"
                                                        data-testid={`bank-adjustment-jv-draft-${adjustment.id}`}
                                                    >
                                                        {draftingAdjustmentId === adjustment.id ? 'Creating...' : 'Create JV Draft'}
                                                    </Button>
                                                )}
                                                {canCreateAdjustmentJvDraft && adjustment.status === 'prepared' && adjustment.adjustment_type === 'recording_error' && (
                                                    <span className="text-[11px] text-slate-500">Manual JV review required</span>
                                                )}
                                                {canPrepareAdjustments && adjustment.status === 'prepared' && (
                                                    <Button
                                                        type="button"
                                                        variant="outline"
                                                        size="sm"
                                                        onClick={() => handleVoidAdjustment(adjustment.id)}
                                                        disabled={adjustingId === `${adjustment.id}-void`}
                                                        className="h-7 border-slate-200 text-slate-600 hover:bg-slate-100 text-xs"
                                                        data-testid={`bank-adjustment-void-${adjustment.id}`}
                                                    >
                                                        {adjustingId === `${adjustment.id}-void` ? 'Voiding...' : 'Void'}
                                                    </Button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                );
                            }) : (
                                <p className="text-sm text-slate-500 py-3">No prepared adjustments yet.</p>
                            )}
                        </div>
                    </div>

                    <div>
                        <h4 className="font-semibold text-[#0F2A44]">Reconciling Items</h4>
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mt-3">
                            {unresolvedGroups.map((group) => (
                                <div key={group.title} className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                                    <div className="flex items-start justify-between gap-3">
                                        <div>
                                            <p className="font-semibold text-sm text-slate-900">{group.title}</p>
                                            <p className="text-xs text-slate-500 mt-1">{group.description}</p>
                                        </div>
                                        <span className="rounded-full bg-white border border-slate-200 px-2 py-0.5 text-xs font-semibold text-slate-600">
                                            {group.items.length}
                                        </span>
                                    </div>
                                    <div className="mt-3 space-y-2 max-h-56 overflow-y-auto pr-1">
                                        {group.items.length ? group.items.slice(0, 6).map(renderReconItem) : (
                                            <p className="text-sm text-slate-500 py-3">{group.empty}</p>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                    <p className="text-xs text-slate-500">{evidenceReport.control_note}</p>
                </div>
            </div>

            {canAdd && (
                <form onSubmit={handleSubmit} className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5" data-testid="bank-statement-line-form">
                    <div className="flex items-start gap-3 mb-4">
                        <div className="w-10 h-10 rounded-xl bg-blue-50 text-[#2F8FCB] flex items-center justify-center">
                            <Plus className="w-5 h-5" />
                        </div>
                        <div>
                            <h3 className="font-bold text-[#0F2A44]">Add Statement Line</h3>
                            <p className="text-sm text-slate-500">Practice data only. This does not create GL entries or close the period.</p>
                        </div>
                    </div>
                    <div className="grid grid-cols-1 lg:grid-cols-6 gap-3">
                        <div>
                            <Label>Date</Label>
                            <Input
                                type="date"
                                value={form.statement_date}
                                onChange={(event) => setForm(prev => ({ ...prev, statement_date: event.target.value }))}
                                data-testid="bank-statement-date"
                            />
                        </div>
                        <div>
                            <Label>Account</Label>
                            {accounts.length ? (
                                <Select value={form.account_code} onValueChange={handleAccountChange}>
                                    <SelectTrigger data-testid="bank-statement-account">
                                        <SelectValue placeholder="Select cash/bank" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {accounts.map((account) => (
                                            <SelectItem key={`${account.account_code}-${account.account_name}`} value={account.account_code}>
                                                {account.account_code} - {account.account_name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            ) : (
                                <Input
                                    value={form.account_name}
                                    onChange={(event) => setForm(prev => ({ ...prev, account_name: event.target.value }))}
                                    placeholder="Cash in Bank"
                                />
                            )}
                        </div>
                        <div>
                            <Label>Direction</Label>
                            <Select value={form.direction} onValueChange={(value) => setForm(prev => ({ ...prev, direction: value }))}>
                                <SelectTrigger data-testid="bank-statement-direction">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="inflow">Inflow</SelectItem>
                                    <SelectItem value="outflow">Outflow</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label>Amount</Label>
                            <Input
                                type="number"
                                min="0"
                                step="0.01"
                                value={form.amount}
                                onChange={(event) => setForm(prev => ({ ...prev, amount: event.target.value }))}
                                placeholder="0.00"
                                data-testid="bank-statement-amount"
                            />
                        </div>
                        <div>
                            <Label>Reference</Label>
                            <Input
                                value={form.reference}
                                onChange={(event) => setForm(prev => ({ ...prev, reference: event.target.value }))}
                                placeholder="DEP-001"
                                data-testid="bank-statement-reference"
                            />
                        </div>
                        <div className="flex items-end">
                            <Button type="submit" disabled={saving} className="w-full bg-[#0F2A44] hover:bg-[#1E3A8A]" data-testid="bank-statement-save-btn">
                                <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving...' : 'Add Line'}
                            </Button>
                        </div>
                    </div>
                    <div className="mt-3">
                        <Label>Description</Label>
                        <Input
                            value={form.description}
                            onChange={(event) => setForm(prev => ({ ...prev, description: event.target.value }))}
                            placeholder="Bank deposit, transfer, check clearing, bank charge..."
                            data-testid="bank-statement-description"
                        />
                    </div>
                </form>
            )}

            <div className="grid grid-cols-1 xl:grid-cols-[1fr_1fr] gap-5">
                <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden" data-testid="bank-statement-lines-card">
                    <div className="px-5 py-4 border-b border-slate-200">
                        <h3 className="font-bold text-[#0F2A44]">Bank Statement Lines</h3>
                        <p className="text-sm text-slate-500">Manual statement lines matched against posted cash/bank GL evidence.</p>
                    </div>
                    <div className="divide-y divide-slate-100">
                        {statementLines.length ? statementLines.map((line) => (
                            <div key={line.id} className="p-4">
                                <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0">
                                        <div className="flex flex-wrap items-center gap-2">
                                            <p className="font-semibold text-sm text-slate-900 truncate">{line.description}</p>
                                            <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                                                line.status === 'matched'
                                                    ? 'bg-emerald-50 text-emerald-700 border border-emerald-100'
                                                    : 'bg-amber-50 text-amber-700 border border-amber-100'
                                            }`}>
                                                {line.status === 'matched' ? 'Matched' : 'Unmatched'}
                                            </span>
                                        </div>
                                        <p className="text-xs text-slate-500 mt-1">
                                            {line.statement_date} - {line.reference || 'No reference'} - {line.account_code} {line.account_name}
                                        </p>
                                    </div>
                                    <div className="text-right shrink-0">
                                        <p className={`font-bold ${line.direction === 'inflow' ? 'text-emerald-700' : 'text-rose-700'}`}>
                                            {line.direction === 'inflow' ? '+' : '-'}{fmt(line.amount)}
                                        </p>
                                        <p className="text-[11px] text-slate-500">{line.candidate_count || 0} candidate{line.candidate_count === 1 ? '' : 's'}</p>
                                    </div>
                                </div>
                                {line.status === 'matched' && line.matched_movement && (
                                    <div className="mt-3 rounded-xl bg-emerald-50 border border-emerald-100 p-3">
                                        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                                            <div>
                                                <p className="text-xs font-semibold text-emerald-800">Matched GL evidence</p>
                                                <p className="text-xs text-emerald-900 mt-1">
                                                    {line.matched_movement.date} - {line.matched_movement.reference} - {line.matched_movement.account_name} - {fmt(line.matched_movement.amount)}
                                                </p>
                                                {renderStatementPostedAdjustmentTrail(line.adjustment_candidate)}
                                            </div>
                                            {canMatch && (
                                                <Button
                                                    type="button"
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => handleUnmatch(line.id)}
                                                    disabled={matchingId === `${line.id}-unmatch`}
                                                    className="border-emerald-200 text-emerald-700 hover:bg-emerald-100"
                                                    data-testid={`bank-unmatch-${line.id}`}
                                                >
                                                    {matchingId === `${line.id}-unmatch` ? 'Unmatching...' : 'Unmatch'}
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                )}
                                {line.status !== 'matched' && line.adjustment_candidate && (
                                    <div className="mt-3 rounded-xl bg-blue-50 border border-blue-100 p-3">
                                        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                                            <div>
                                                <p className="text-xs font-semibold text-blue-800">
                                                    {line.adjustment_candidate.status === 'jv_posted' ? 'Posted adjustment evidence' : 'Prepared adjustment candidate'}
                                                </p>
                                                <p className="text-xs text-blue-900 mt-1">
                                                    {line.adjustment_candidate.adjustment_label}: DR {line.adjustment_candidate.proposed_debit_account} / CR {line.adjustment_candidate.proposed_credit_account}
                                                </p>
                                                {line.adjustment_candidate.jv_number && (
                                                    <p className={`text-[11px] mt-1 ${line.adjustment_candidate.status === 'jv_posted' ? 'text-emerald-700' : 'text-blue-700'}`}>
                                                        {line.adjustment_candidate.status === 'jv_posted' ? 'Posted JV evidence' : 'JV draft created'}: {line.adjustment_candidate.jv_number}
                                                    </p>
                                                )}
                                                {renderStatementPostedAdjustmentTrail(line.adjustment_candidate)}
                                                {line.adjustment_candidate.next_safe_action && (
                                                    <p className="text-[11px] text-blue-800 mt-1">{line.adjustment_candidate.next_safe_action}</p>
                                                )}
                                            </div>
                                            {canPrepareAdjustments && line.adjustment_candidate.status === 'prepared' && (
                                                <Button
                                                    type="button"
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => handleVoidAdjustment(line.adjustment_candidate.id)}
                                                    disabled={adjustingId === `${line.adjustment_candidate.id}-void`}
                                                    className="border-blue-200 text-blue-700 hover:bg-blue-100"
                                                    data-testid={`bank-adjustment-void-inline-${line.adjustment_candidate.id}`}
                                                >
                                                    {adjustingId === `${line.adjustment_candidate.id}-void` ? 'Voiding...' : 'Void candidate'}
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                )}
                                {line.status !== 'matched' && !line.adjustment_candidate && canPrepareAdjustments && (
                                    <div className="mt-3 rounded-xl bg-slate-50 border border-slate-200 p-3">
                                        <p className="text-xs font-semibold text-slate-700 mb-2">Prepare adjustment candidate</p>
                                        <div className="flex flex-wrap gap-2">
                                            {adjustmentOptions
                                                .filter(option => option.direction === 'either' || option.direction === line.direction)
                                                .map((option) => (
                                                    <Button
                                                        key={`${line.id}-${option.type}`}
                                                        type="button"
                                                        variant="outline"
                                                        size="sm"
                                                        onClick={() => handlePrepareAdjustment(line.id, option.type)}
                                                        disabled={adjustingId === `${line.id}-${option.type}`}
                                                        className="h-7 text-xs border-slate-200 hover:bg-white"
                                                        data-testid={`bank-adjustment-prepare-${line.id}-${option.type}`}
                                                    >
                                                        {adjustingId === `${line.id}-${option.type}` ? 'Preparing...' : option.label}
                                                    </Button>
                                                ))}
                                        </div>
                                    </div>
                                )}
                                {!line.adjustment_candidate && line.candidates?.length > 0 && (
                                    <div className="mt-3 rounded-xl bg-blue-50 border border-blue-100 p-3">
                                        <p className="text-xs font-semibold text-blue-800 mb-2">Likely GL candidates</p>
                                        <div className="space-y-2">
                                            {line.candidates.map((candidate) => (
                                                <div key={`${line.id}-${candidate.gl_id}`} className="text-xs text-blue-900 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                                                    <span className="truncate">{candidate.date} - {candidate.reference} - {candidate.account_name}</span>
                                                    <div className="flex items-center gap-2 shrink-0">
                                                        <span className="font-semibold">{candidate.score}%</span>
                                                        {canMatch && (
                                                            <Button
                                                                type="button"
                                                                size="sm"
                                                                onClick={() => handleMatch(line.id, candidate.gl_id)}
                                                                disabled={matchingId === `${line.id}-${candidate.gl_id}`}
                                                                className="h-7 bg-[#0F2A44] hover:bg-[#1E3A8A] text-xs"
                                                                data-testid={`bank-match-${line.id}-${candidate.gl_id}`}
                                                            >
                                                                {matchingId === `${line.id}-${candidate.gl_id}` ? 'Matching...' : 'Match'}
                                                            </Button>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )) : (
                            <div className="p-6 text-center text-sm text-slate-500">
                                No statement lines yet for this period.
                            </div>
                        )}
                    </div>
                </div>

                <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden" data-testid="bank-book-lines-card">
                    <div className="px-5 py-4 border-b border-slate-200">
                        <h3 className="font-bold text-[#0F2A44]">Book Cash/Bank GL Lines</h3>
                        <p className="text-sm text-slate-500">Posted system-side movements from JVs, AR, and AP.</p>
                    </div>
                    <div className="divide-y divide-slate-100">
                        {(bank.recent_movements || []).length ? bank.recent_movements.slice(0, 12).map((movement) => (
                            <div key={movement.id || `${movement.reference}-${movement.amount}`} className="p-4 flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                    <p className="font-semibold text-sm text-slate-900 truncate">{movement.description || movement.reference}</p>
                                    <p className="text-xs text-slate-500 mt-1">
                                        {movement.date} - {movement.reference} - {movement.account_code} {movement.account_name}
                                    </p>
                                    {movement.source === 'bank_reconciliation_adjustment' && (
                                        <p className="text-[11px] text-emerald-700 mt-1">
                                            Bank recon adjustment JV evidence {movement.matched ? 'linked to statement line - resolved / not blocking close' : 'needs statement match - blocks close evidence review'}
                                        </p>
                                    )}
                                </div>
                                <div className="text-right shrink-0">
                                    <p className={`font-bold ${movement.direction === 'inflow' ? 'text-emerald-700' : 'text-rose-700'}`}>
                                        {movement.direction === 'inflow' ? '+' : '-'}{fmt(movement.amount)}
                                    </p>
                                    <p className="text-[11px] text-slate-500">{movement.matched ? 'Matched' : 'Unmatched'}</p>
                                </div>
                            </div>
                        )) : (
                            <div className="p-6 text-center text-sm text-slate-500">
                                No posted cash/bank GL lines for this period.
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

// Sub-components

function SubMenuItem({ label, active, onClick }) {
    return (
        <button
            onClick={onClick}
            className={`w-full text-left px-3 py-1.5 rounded text-sm transition-colors ${
                active ? 'bg-white/20 text-white' : 'text-white/60 hover:text-white hover:bg-white/10'
            }`}
        >
            {label}
        </button>
    );
}

function TabButton({ active, onClick, icon: Icon, label }) {
    return (
        <button
            onClick={onClick}
            className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors ${
                active 
                    ? 'border-[#2F8FCB] text-[#2F8FCB] bg-white' 
                    : 'border-transparent text-slate-600 hover:text-slate-900 hover:bg-white/50'
            }`}
        >
            <Icon className="w-4 h-4" />
            <span className="text-sm font-medium">{label}</span>
        </button>
    );
}

// ================= CHART OF ACCOUNTS TAB =================

const JV_REVIEW_ACTIONS = {
    jv_draft_created: {
        label: 'JV Draft Created',
        Icon: Save,
        dotClass: 'bg-[#2F8FCB]',
        badgeClass: 'bg-blue-50 text-blue-700 border-blue-200'
    },
    submitted: {
        label: 'Submitted for Review',
        Icon: Send,
        dotClass: 'bg-[#2F8FCB]',
        badgeClass: 'bg-blue-50 text-blue-700 border-blue-200'
    },
    returned: {
        label: 'Returned for Revision',
        Icon: Undo2,
        dotClass: 'bg-orange-500',
        badgeClass: 'bg-orange-50 text-orange-700 border-orange-200'
    },
    approved: {
        label: 'Approved and Posted',
        Icon: CheckCircle2,
        dotClass: 'bg-emerald-500',
        badgeClass: 'bg-emerald-50 text-emerald-700 border-emerald-200'
    },
    posted: {
        label: 'Posted to GL',
        Icon: FileDown,
        dotClass: 'bg-emerald-500',
        badgeClass: 'bg-emerald-50 text-emerald-700 border-emerald-200'
    }
};

const isBankReconVoucher = (voucher) =>
    voucher?.source === 'bank_reconciliation_adjustment' ||
    voucher?.review_context?.type === 'bank_reconciliation';

const isOpeningBalanceVoucher = (voucher) => {
    const source = String(voucher?.source || '').toLowerCase();
    const reference = String(voucher?.reference || '').toLowerCase();
    const particulars = String(voucher?.particulars || '').toLowerCase();
    return source === 'opening_balance'
        || reference.includes('opening-balance')
        || reference.includes('opening balance')
        || particulars.includes('opening balance')
        || particulars.includes('opening balances');
};

const formatBankReconPeriod = (voucher) => {
    if (voucher?.review_context?.period_label) return voucher.review_context.period_label;
    if (!voucher?.source_period_year || !voucher?.source_period_month) return 'Period not set';
    const month = String(voucher.source_period_month).padStart(2, '0');
    return `${voucher.source_period_year}-${month}`;
};

const getBankReconReviewContext = (voucher) => {
    if (!isBankReconVoucher(voucher)) return null;
    return {
        label: voucher?.review_context?.label || 'Bank Reconciliation JV',
        badge: voucher?.review_context?.badge || 'Bank Recon Draft',
        period: formatBankReconPeriod(voucher),
        statementReference: voucher?.review_context?.statement_reference || voucher?.source_statement_reference || voucher?.reference || 'No statement reference',
        instruction: voucher?.review_context?.review_instruction || 'Verify the statement line, adjustment reason, and debit/credit treatment before posting.',
    };
};

const voucherStatusLabel = (voucher) => {
    if (voucher?.review_status === 'returned' && voucher?.status === 'saved') return 'returned';
    if (isBankReconVoucher(voucher) && voucher?.status === 'saved') return 'bank recon draft';
    return voucher?.status || '';
};

function JournalVoucherTab({
    selectedCourse,
    user,
    chartOfAccounts,
    vouchers,
    onRefresh,
    fmt,
    permissions,
    isAuditor,
    viewingStudentId,
    onReviewStudent,
    onSetTrialBalanceDate,
    sourceOpenRequest,
    onOpenPostedEvidence,
    onOpenBankReconPeriod,
}) {
    const isReadOnly = isAuditor || !!viewingStudentId;
    const [currentVoucher, setCurrentVoucher] = useState(null);
    const [headerForm, setHeaderForm] = useState({
        date: new Date().toISOString().split('T')[0],
        reference: '',
        particulars: ''
    });
    const [details, setDetails] = useState([]);
    const [showDetailsPopup, setShowDetailsPopup] = useState(false);
    const [editingIndex, setEditingIndex] = useState(null);
    const [detailForm, setDetailForm] = useState({
        account_code: '',
        account_title: '',
        sub1: '',
        sub2: '',
        debit: '',
        credit: '',
        notes: ''
    });
    const [sub1Options, setSub1Options] = useState([]);
    const [sub2Options, setSub2Options] = useState([]);
    const [saving, setSaving] = useState(false);
    const [showReversalDialog, setShowReversalDialog] = useState(false);
    const [reversalTarget, setReversalTarget] = useState(null);
    const [reversalReason, setReversalReason] = useState('');
    const [reversing, setReversing] = useState(false);
    const [showReturnDialog, setShowReturnDialog] = useState(false);
    const [returnTarget, setReturnTarget] = useState(null);
    const [returnReason, setReturnReason] = useState('');
    const [returning, setReturning] = useState(false);
    const [confirmAction, setConfirmAction] = useState(null);
    const [studentVouchers, setStudentVouchers] = useState([]);
    const [reviewQueue, setReviewQueue] = useState([]);
    const [sourceOpenNotice, setSourceOpenNotice] = useState(null);
    const accountCodeRef = useRef(null);
    const jvDateInputRef = useRef(null);
    const lastSourceOpenRequestId = useRef(null);
    
    const perms = permissions || { can_post: false, can_unpost: false };
    const displayVouchers = viewingStudentId ? studentVouchers : vouchers;
    const canReviewJvs = user?.role === 'instructor' || user?.role === 'admin';
    const reviewScopeLabel = selectedCourse ? 'the selected course' : 'all assigned courses';
    const isReviewerViewingStudentVoucher = Boolean(
        canReviewJvs &&
        currentVoucher?.student_id &&
        currentVoucher.student_id !== user?.id
    );

    // Fetch student's JVs when viewing a student
    useEffect(() => {
        if (viewingStudentId) {
            const token = localStorage.getItem('token');
            axios.get(`${API}/journal-vouchers?student_id=${viewingStudentId}`, {
                headers: { Authorization: `Bearer ${token}` }
            }).then(res => setStudentVouchers(res.data)).catch(() => setStudentVouchers([]));
        }
    }, [viewingStudentId]);

    const fetchReviewQueue = useCallback(() => {
        if (!canReviewJvs) {
            setReviewQueue([]);
            return;
        }
        const token = localStorage.getItem('token');
        const courseQuery = selectedCourse ? `?course_id=${encodeURIComponent(selectedCourse)}` : '';
        axios.get(`${API}/journal-vouchers/review-queue${courseQuery}`, {
            headers: { Authorization: `Bearer ${token}` }
        }).then(res => setReviewQueue(res.data)).catch(() => setReviewQueue([]));
    }, [canReviewJvs, selectedCourse]);

    useEffect(() => {
        fetchReviewQueue();
    }, [fetchReviewQueue]);

    useEffect(() => {
        if (!canReviewJvs || viewingStudentId) return undefined;
        const refreshVisibleQueue = () => fetchReviewQueue();
        const refreshWhenVisible = () => {
            if (document.visibilityState === 'visible') fetchReviewQueue();
        };
        window.addEventListener('focus', refreshVisibleQueue);
        document.addEventListener('visibilitychange', refreshWhenVisible);
        return () => {
            window.removeEventListener('focus', refreshVisibleQueue);
            document.removeEventListener('visibilitychange', refreshWhenVisible);
        };
    }, [canReviewJvs, fetchReviewQueue, viewingStudentId]);
    
    const totalDebit = details.reduce((sum, d) => sum + (parseFloat(d.debit) || 0), 0);
    const totalCredit = details.reduce((sum, d) => sum + (parseFloat(d.credit) || 0), 0);
    const isBalanced = Math.abs(totalDebit - totalCredit) < 0.01;
    const isPosted = currentVoucher?.status === 'posted';
    const isSaved = currentVoucher?.status === 'saved';
    const isSubmitted = currentVoucher?.status === 'submitted';
    const isReturned = isSaved && currentVoucher?.review_status === 'returned';
    const formLocked = isReadOnly || isReviewerViewingStudentVoucher || isPosted || isSubmitted;
    const isReadOnlyEmptyVoucher = isReadOnly && !currentVoucher;
    const jvHeaderSubtitle = currentVoucher?.jv_number
        ? `JV#: ${currentVoucher.jv_number}`
        : isReadOnly
            ? 'Select an existing JV to review source, status, and GL impact.'
            : 'JV# will be assigned on save';
    const jvNumberDisplay = currentVoucher?.jv_number || (isReadOnly ? 'Select existing JV to review' : '(Auto-generated on save)');

    const formatTimelineDate = (value) => {
        if (!value) return 'Time not recorded';
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return 'Time not recorded';
        return date.toLocaleString();
    };

    const buildReviewTimeline = (voucher) => {
        if (!voucher) return [];

        const events = [];
        const seen = new Set();
        const addEvent = (action, reviewedAt, reviewedByName, reason, source = 'history') => {
            if (!action || !JV_REVIEW_ACTIONS[action]) return;
            const key = `${action}|${reviewedAt || ''}|${reviewedByName || ''}|${reason || ''}`;
            if (seen.has(key)) return;
            seen.add(key);
            events.push({
                action,
                reviewed_at: reviewedAt,
                reviewed_by_name: reviewedByName,
                reason,
                source
            });
        };

        (voucher.review_history || []).forEach((event) => {
            addEvent(
                event.action,
                event.reviewed_at,
                event.reviewed_by_name,
                event.reason,
                'history'
            );
        });

        if (voucher.submitted_at || voucher.status === 'submitted' || voucher.review_status === 'submitted') {
            addEvent('submitted', voucher.submitted_at, voucher.submitted_by_name || voucher.processed_by, null, 'metadata');
        }
        if (voucher.returned_at || voucher.review_status === 'returned' || voucher.return_reason) {
            addEvent('returned', voucher.returned_at, voucher.returned_by_name, voucher.return_reason, 'metadata');
        }
        if (voucher.review_status === 'approved' || voucher.status === 'posted') {
            addEvent('approved', voucher.reviewed_at || voucher.posted_at, voucher.reviewed_by_name || voucher.posted_by, null, 'metadata');
        }

        return events.sort((a, b) => {
            const aTime = a.reviewed_at ? new Date(a.reviewed_at).getTime() : 0;
            const bTime = b.reviewed_at ? new Date(b.reviewed_at).getTime() : 0;
            return aTime - bTime;
        });
    };

    const reviewTimeline = buildReviewTimeline(currentVoucher);
    const currentBankReconContext = getBankReconReviewContext(currentVoucher);
    const currentVoucherNumber = currentVoucher?.jv_number || 'this Journal Voucher';
    const currentBankReconPeriodValue = currentVoucher?.source_period_year && currentVoucher?.source_period_month
        ? `${currentVoucher.source_period_year}-${String(currentVoucher.source_period_month).padStart(2, '0')}`
        : String(sourceOpenNotice?.date || currentVoucher?.date || currentVoucher?.posted_at || '').slice(0, 7);
    const currentWorkflowTrail = currentVoucher ? (() => {
        const statusLabel = voucherStatusLabel(currentVoucher) || 'saved';
        const statusClass = isPosted
            ? 'bg-emerald-100 text-emerald-700 border-emerald-200'
            : isSubmitted
                ? 'bg-blue-100 text-blue-700 border-blue-200'
                : isReturned
                    ? 'bg-orange-100 text-orange-700 border-orange-200'
                : 'bg-amber-100 text-amber-700 border-amber-200';
        const isStudentSavedWork = user?.role === 'student' && isSaved && !isReturned && !perms.can_post;
        const isOtherStudentVoucher = currentVoucher?.student_id && currentVoucher.student_id !== user?.id;
        const studentName = currentVoucher?.student_name || currentVoucher?.submitted_by_name || currentVoucher?.processed_by || 'the student';
        const studentScopeNote = isOtherStudentVoucher
            ? ` Use the student review scope for ${studentName} when opening General Ledger or Trial Balance.`
            : '';
        const bankReconSourceNote = isBankReconVoucher(currentVoucher)
            ? ' Review > Bank Reconciliation keeps the statement-line evidence and posted adjustment status.'
            : '';

        if (isPosted) {
            return {
                statusLabel,
                statusClass,
                whatChanged: `${currentVoucherNumber} is posted.`,
                whereToFind: isBankReconVoucher(currentVoucher)
                    ? `Record > Journal Voucher, Review > Bank Reconciliation, and Review > General Ledger.${studentScopeNote}`
                    : `Record > Journal Voucher and Review > General Ledger.${studentScopeNote}`,
                reviewNext: 'No further approval is pending.',
                reportImpact: `Posted to General Ledger and included in Trial Balance/reports by transaction date.${studentScopeNote}${bankReconSourceNote}`,
                nextSafeAction: isOtherStudentVoucher
                    ? `Stay in ${studentName}'s workspace scope, then open Bank Reconciliation, General Ledger, or Trial Balance for the posting result.`
                    : isBankReconVoucher(currentVoucher)
                        ? 'Review Bank Reconciliation evidence, General Ledger, and Trial Balance for the posting result.'
                        : 'Review General Ledger or Trial Balance for the posting result.',
            };
        }
        if (isSubmitted) {
            return {
                statusLabel,
                statusClass,
                whatChanged: `${currentVoucherNumber} was submitted for instructor review.`,
                whereToFind: 'Student: Record > Journal Voucher. Instructor: Instructor JV Review Queue.',
                reviewNext: 'Instructor/admin reviews next, then posts or returns with feedback.',
                reportImpact: 'No GL, Trial Balance, or report impact until posted.',
                nextSafeAction: 'Wait for instructor action, or monitor this voucher from the JV list.',
            };
        }
        if (isReturned) {
            return {
                statusLabel,
                statusClass,
                whatChanged: `${currentVoucherNumber} was returned for revision.`,
                whereToFind: 'Record > Journal Voucher list as Returned for Revision.',
                reviewNext: 'Student revises and resubmits. Instructor reviews again after resubmission.',
                reportImpact: 'No GL, Trial Balance, or report impact until posted.',
                nextSafeAction: 'Review the feedback, update the lines if needed, then resubmit for review.',
            };
        }
        return {
            statusLabel,
            statusClass,
            whatChanged: `${currentVoucherNumber} was successfully saved.`,
            whereToFind: 'Record > Journal Voucher list, on this same page.',
            reviewNext: isStudentSavedWork
                ? 'Submit it for instructor review when ready.'
                : 'Instructor/admin may post it when ready.',
            reportImpact: 'Saved draft only. It does not affect GL, Trial Balance, or reports yet.',
            nextSafeAction: isStudentSavedWork
                ? 'Click Submit for Review so the instructor can approve/post it.'
                : 'Review the voucher, then post only when the entry is final.',
        };
    })() : null;
    const readOnlyWorkflowTrail = isReadOnlyEmptyVoucher ? {
        statusLabel: 'Read-only',
        statusClass: 'bg-slate-100 text-slate-700 border-slate-200',
        whatChanged: 'No Journal Voucher is selected yet.',
        whereToFind: viewingStudentId
            ? 'Select a student JV from this list, or open the Instructor JV Review Queue for submitted work.'
            : 'Open an existing JV from Record > Journal Voucher, or review posted effects in Review > General Ledger and Trial Balance.',
        reviewNext: viewingStudentId
            ? 'Instructor/admin reviews submitted vouchers through the Instructor JV Review Queue.'
            : 'Auditor view is evidence-only and cannot create, submit, post, return, or reverse vouchers.',
        reportImpact: 'Opening this page does not affect GL, Trial Balance, or reports.',
        nextSafeAction: 'Select an existing voucher to review its details, status badge, source evidence, and downstream report impact.',
    } : null;
    const displayWorkflowTrail = currentWorkflowTrail || readOnlyWorkflowTrail;
    const openCurrentBankReconEvidence = () => {
        if (!onOpenBankReconPeriod || !currentVoucher) return;
        onOpenBankReconPeriod(
            currentBankReconPeriodValue,
            `${currentVoucherNumber} Bank Recon source`
        );
    };
    const openCurrentVoucherEvidence = (target) => {
        if (!currentVoucher || currentVoucher.status !== 'posted' || !onOpenPostedEvidence) return;
        onOpenPostedEvidence({
            record_id: currentVoucher.id || currentVoucher._id || '',
            record_number: currentVoucher.jv_number || '',
            record_date: currentVoucher.date || currentVoucher.posted_at || '',
            record_type: 'Journal Voucher',
            module: 'jv',
            workspace_tab: 'jv',
            module_label: isOpeningBalanceVoucher(currentVoucher) ? 'Opening Balance Setup' : 'Record > Journal Voucher',
            student_id: currentVoucher.student_id || viewingStudentId || '',
            student_name: currentVoucher.student_name || currentVoucher.submitted_by_name || currentVoucher.processed_by || '',
            processed_by: currentVoucher.processed_by || '',
            submitted_by_name: currentVoucher.submitted_by_name || '',
            amount: currentVoucher.total_debit || totalDebit || totalCredit || 0,
            downstream_impact: 'Posted Journal Voucher affects General Ledger, Trial Balance, and financial reports by transaction date.',
            next_safe_action: 'Verify the posted debit and credit lines in General Ledger, then confirm Trial Balance includes the voucher by transaction date.',
        }, target);
    };

    const buildSavedVoucherToast = (voucher, wasExistingVoucher) => {
        const number = voucher?.jv_number || 'the Journal Voucher';
        const action = wasExistingVoucher ? 'saved and updated' : 'successfully created and saved';
        const next = user?.role === 'student' && !perms.can_post
            ? 'Status: Saved Draft. Next: submit it for instructor review. No GL, Trial Balance, or report impact until posted.'
            : 'Status: Saved Draft. Next: review and post only when final. No GL, Trial Balance, or report impact until posted.';
        return `Journal Voucher ${number} was ${action}. Find it in Record > Journal Voucher on this same list. ${next}`;
    };

    const runConfirmedAction = async () => {
        const action = confirmAction?.onConfirm;
        setConfirmAction(null);
        if (action) {
            await action();
        }
    };

    const escapeCSV = (value) => `"${String(value ?? '').replace(/"/g, '""')}"`;

    const handleExportVoucherListCSV = () => {
        const rows = [
            ['JV #', 'Date', 'Particulars', 'Debit', 'Credit', 'Status'],
            ...displayVouchers.map(v => [
                v.jv_number || '(Unsaved)',
                v.date || '',
                v.particulars || '',
                v.total_debit || 0,
                v.total_credit || 0,
                v.status || ''
            ])
        ];
        const csv = rows.map(row => row.map(escapeCSV).join(',')).join('\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `journal-vouchers-${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        toast.success('Journal voucher list exported as CSV. Export only; no JV, GL, or Trial Balance records changed.');
    };

    const handlePrintVoucherList = () => {
        const escapeHtml = (value) => String(value ?? '')
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#039;');
        const generatedAt = new Date();
        const reportDate = generatedAt.toLocaleDateString('en-PH');
        const rows = displayVouchers.map((v) => `
            <tr>
                <td class="code">${escapeHtml(v.jv_number || '(Unsaved)')}</td>
                <td>${escapeHtml(v.date ? new Date(v.date).toLocaleDateString('en-PH') : '')}</td>
                <td>${escapeHtml(v.particulars || '')}</td>
                <td class="amount">${escapeHtml(fmt(v.total_debit || 0))}</td>
                <td class="status-cell">${escapeHtml(v.status || '')}</td>
            </tr>
        `).join('');
        const printWindow = window.open('', '_blank', 'width=900,height=700');
        if (!printWindow) {
            toast.error('Print preview was blocked. Please allow pop-ups for MaceSuite.');
            return;
        }
        printWindow.document.write(`
            <!doctype html>
            <html>
                <head>
                    <title>MaceSuite Journal Vouchers - ${escapeHtml(reportDate)}</title>
                    <style>
                        @page { size: A4 portrait; margin: 16mm; }
                        * { box-sizing: border-box; }
                        body {
                            margin: 0;
                            color: #0f172a;
                            font-family: Arial, Helvetica, sans-serif;
                            font-size: 12px;
                        }
                        .report-header {
                            border-bottom: 2px solid #0f2a44;
                            margin-bottom: 18px;
                            padding-bottom: 12px;
                        }
                        .brand {
                            color: #0f2a44;
                            font-size: 20px;
                            font-weight: 700;
                            margin-bottom: 10px;
                        }
                        h1 {
                            font-size: 18px;
                            margin: 0 0 4px;
                        }
                        .meta {
                            color: #475569;
                            display: flex;
                            gap: 14px;
                            flex-wrap: wrap;
                            font-size: 12px;
                        }
                        table {
                            width: 100%;
                            border-collapse: collapse;
                        }
                        th {
                            background: #e2e8f0;
                            color: #334155;
                            font-size: 11px;
                            letter-spacing: 0.04em;
                            padding: 8px;
                            text-align: left;
                            text-transform: uppercase;
                        }
                        td {
                            border-bottom: 1px solid #e2e8f0;
                            padding: 8px;
                            vertical-align: top;
                        }
                        .code,
                        .amount {
                            font-family: Consolas, 'Courier New', monospace;
                            white-space: nowrap;
                        }
                        .code { width: 120px; }
                        .amount {
                            text-align: right;
                            width: 110px;
                        }
                        .status-cell {
                            text-transform: capitalize;
                            white-space: nowrap;
                            width: 72px;
                        }
                        .footer {
                            margin-top: 24px;
                            color: #64748b;
                            font-size: 10px;
                            display: flex;
                            justify-content: space-between;
                        }
                    </style>
                </head>
                <body>
                    <div class="report-header">
                        <div class="brand">MaceSuite</div>
                        <h1>Journal Voucher List</h1>
                        <div class="meta">
                            <span>As of ${escapeHtml(reportDate)}</span>
                            <span>Course: ${escapeHtml(selectedCourse || 'N/A')}</span>
                            <span>User: ${escapeHtml(user?.full_name || '')}</span>
                        </div>
                    </div>
                    <table>
                        <thead>
                            <tr>
                                <th class="code">JV #</th>
                                <th>Date</th>
                                <th>Particulars</th>
                                <th class="amount">Total</th>
                                <th class="status-cell">Status</th>
                            </tr>
                        </thead>
                        <tbody>${rows}</tbody>
                    </table>
                    <div class="footer">
                        <span>Generated by MaceSuite</span>
                        <span>${escapeHtml(generatedAt.toLocaleString('en-PH'))}</span>
                    </div>
                    <script>
                        window.addEventListener('load', () => {
                            window.focus();
                            window.print();
                        });
                    </script>
                </body>
            </html>
        `);
        printWindow.document.close();
    };
    
    const resetDetailForm = useCallback(() => {
        setDetailForm({
            account_code: '',
            account_title: '',
            sub1: '',
            sub2: '',
            debit: '',
            credit: '',
            notes: ''
        });
        setSub1Options([]);
        setSub2Options([]);
    }, []);

    const closeDetailsPopup = useCallback(() => {
        setShowDetailsPopup(false);
        setEditingIndex(null);
        resetDetailForm();
    }, [resetDetailForm]);
    
    const resetForm = useCallback(() => {
        setCurrentVoucher(null);
        setHeaderForm({
            date: new Date().toISOString().split('T')[0],
            reference: '',
            particulars: ''
        });
        setDetails([]);
        setEditingIndex(null);
        setSourceOpenNotice(null);
        resetDetailForm();
    }, [resetDetailForm]);
    
    // Reset form when switching courses
    useEffect(() => {
        resetForm();
    }, [selectedCourse, resetForm]);
    
    // Load voucher for editing
    const loadVoucher = useCallback((voucher, sourceNotice = null) => {
        setCurrentVoucher(voucher);
        setHeaderForm({
            date: voucher.date,
            reference: voucher.reference || '',
            particulars: voucher.particulars
        });
        setDetails(voucher.details || []);
        setSourceOpenNotice(sourceNotice);
    }, []);

    useEffect(() => {
        if (!sourceOpenRequest?.id || sourceOpenRequest.type !== 'jv') return;
        if (lastSourceOpenRequestId.current === sourceOpenRequest.id) return;
        if (displayVouchers.length === 0) return;

        const requestedJvId = String(sourceOpenRequest.jvId || '').trim();
        const requestedRefNo = String(sourceOpenRequest.refNo || '').trim().toLowerCase();
        const sourceLabel = sourceOpenRequest.refNo || sourceOpenRequest.jvId || 'source JV';
        const matchingVoucher = displayVouchers.find((voucher) => {
            const voucherId = String(voucher.id || '').trim();
            const voucherNumber = String(voucher.jv_number || '').trim().toLowerCase();
            const voucherReference = String(voucher.reference || '').trim().toLowerCase();
            return (
                (requestedJvId && voucherId === requestedJvId) ||
                (requestedRefNo && (voucherNumber === requestedRefNo || voucherReference === requestedRefNo))
            );
        });

        lastSourceOpenRequestId.current = sourceOpenRequest.id;

        if (!matchingVoucher) {
            toast.info(`Source JV ${sourceLabel} is not visible in this current course/student review scope.`);
            return;
        }

        const voucherNumber = matchingVoucher.jv_number || sourceLabel;
        loadVoucher(matchingVoucher, {
            voucherNumber,
            accountCode: sourceOpenRequest.accountCode,
            accountTitle: sourceOpenRequest.accountTitle,
            date: sourceOpenRequest.date,
            description: sourceOpenRequest.description,
            sourceType: sourceOpenRequest.sourceType,
            sourceLabel: sourceOpenRequest.sourceLabel,
            sourceLocation: sourceOpenRequest.sourceLocation,
            sourceImpact: sourceOpenRequest.sourceImpact,
            sourceNextAction: sourceOpenRequest.sourceNextAction,
            sourceStatementReference: sourceOpenRequest.sourceStatementReference,
            sourceStatementLineId: sourceOpenRequest.sourceStatementLineId,
            bankReconciliationMatched: sourceOpenRequest.bankReconciliationMatched,
            bankReconciliationMatchStatus: sourceOpenRequest.bankReconciliationMatchStatus,
        });
        toast.success(
            sourceOpenRequest.sourceType === 'bank_reconciliation_adjustment'
                ? `Opened bank reconciliation source JV ${voucherNumber}. Review the posted adjustment evidence before close.`
                : `Opened source JV ${voucherNumber} from General Ledger. This posted voucher is the evidence behind the selected GL line.`
        );
    }, [displayVouchers, loadVoucher, sourceOpenRequest]);
    
    // Handle account code selection
    const handleAccountCodeChange = (code) => {
        const account = chartOfAccounts.find(a => a.code === code);
        if (account) {
            setDetailForm(prev => ({
                ...prev,
                account_code: code,
                account_title: account.title,
                sub1: '',
                sub2: ''
            }));
            // Load Sub1 options based on account's sub1_type
            loadSubOptions(account, 'sub1');
            setSub2Options([]);
        }
    };
    
    // Load subsidiary options based on account type
    const loadSubOptions = (account, subLevel) => {
        const subType = subLevel === 'sub1' ? account.sub1_type : account.sub2_type;
        
        if (!subType || subType === 'None') {
            if (subLevel === 'sub1') setSub1Options([]);
            else setSub2Options([]);
            return;
        }
        
        let options = [];
        if (subType === 'Customer') {
            options = customers.map(c => ({ 
                code: c.id || c.customer_code || c.customer_id, 
                title: c.customer_name 
            }));
        } else if (subType === 'Supplier') {
            options = suppliers.map(s => ({ 
                code: s.supplier_id || s.id, 
                title: s.supplier_name || s.name 
            }));
        } else if (subType === 'Branch' || subType === 'Project') {
            // For Branch/Project, use existing subs array if available
            options = account.subs || [];
        }
        
        if (subLevel === 'sub1') setSub1Options(options);
        else setSub2Options(options);
    };
    
    // Handle Sub1 selection - load Sub2 options
    const handleSub1Change = (sub1Code) => {
        setDetailForm(prev => ({
            ...prev,
            sub1: sub1Code,
            sub2: '' // Reset Sub2 when Sub1 changes
        }));
        
        // Load Sub2 options based on the account's sub2_type
        const account = chartOfAccounts.find(a => a.code === detailForm.account_code);
        if (account && account.sub2_type && account.sub2_type !== 'None') {
            loadSubOptions(account, 'sub2');
        } else {
            setSub2Options([]);
        }
    };
    
    // Add detail line
    const handleAddDetail = () => {
        if (!detailForm.account_code) {
            toast.error('Please select Account Number');
            return;
        }
        
        const debitVal = parseFloat(detailForm.debit) || 0;
        const creditVal = parseFloat(detailForm.credit) || 0;
        
        if (debitVal > 0 && creditVal > 0) {
            toast.error('Cannot have both Debit and Credit amounts on the same line');
            return;
        }
        
        if (debitVal === 0 && creditVal === 0) {
            toast.error('Either Debit or Credit must have a value');
            return;
        }
        
        // Validate Sub1/Sub2 combination
        const account = chartOfAccounts.find(a => a.code === detailForm.account_code);
        if (account) {
            // If Sub1 is required but not selected
            if (account.sub1_type && account.sub1_type !== 'None' && !detailForm.sub1) {
                toast.warning(`Sub1 (${account.sub1_type}) is recommended for this account`);
            }
            // If Sub2 is selected without Sub1
            if (detailForm.sub2 && !detailForm.sub1) {
                toast.error('Cannot select Sub2 without selecting Sub1 first');
                return;
            }
            // If Sub2 is required and Sub1 is selected but Sub2 is not
            if (account.sub2_type && account.sub2_type !== 'None' && detailForm.sub1 && !detailForm.sub2) {
                toast.warning(`Sub2 (${account.sub2_type}) is recommended when Sub1 is selected`);
            }
        }
        
        const newLine = {
            account_code: detailForm.account_code,
            account_title: detailForm.account_title,
            sub1: detailForm.sub1 || null,
            sub2: detailForm.sub2 || null,
            debit: debitVal,
            credit: creditVal,
            notes: detailForm.notes || null
        };
        
        setDetails(prev => [...prev, newLine]);
        resetDetailForm();
        
        // Focus back to account code
        setTimeout(() => {
            accountCodeRef.current?.focus();
        }, 100);
        
        toast.success('Detail line added to this JV screen. Review the balance, then Save/Submit/Post; it will not affect GL or Trial Balance until the voucher is posted.');
    };
    
    // Edit detail line
    const handleEditDetail = (index) => {
        const line = details[index];
        setEditingIndex(index);
        setDetailForm({
            account_code: line.account_code,
            account_title: line.account_title,
            sub1: line.sub1 || '',
            sub2: line.sub2 || '',
            debit: line.debit > 0 ? line.debit.toString() : '',
            credit: line.credit > 0 ? line.credit.toString() : '',
            notes: line.notes || ''
        });
        
        // Load sub options for the selected account
        const account = chartOfAccounts.find(a => a.code === line.account_code);
        if (account) {
            loadSubOptions(account, 'sub1');
            // Also load Sub2 if Sub1 is already selected
            if (line.sub1 && account.sub2_type && account.sub2_type !== 'None') {
                loadSubOptions(account, 'sub2');
            }
        }
        
        setShowDetailsPopup(true);
    };
    
    // Update detail line
    const handleUpdateDetail = () => {
        if (!detailForm.account_code) {
            toast.error('Please select Account Number');
            return;
        }
        
        const debitVal = parseFloat(detailForm.debit) || 0;
        const creditVal = parseFloat(detailForm.credit) || 0;
        
        if (debitVal > 0 && creditVal > 0) {
            toast.error('Cannot have both Debit and Credit amounts on the same line');
            return;
        }
        
        if (debitVal === 0 && creditVal === 0) {
            toast.error('Either Debit or Credit must have a value');
            return;
        }
        
        const updatedLine = {
            account_code: detailForm.account_code,
            account_title: detailForm.account_title,
            sub1: detailForm.sub1 || null,
            sub2: detailForm.sub2 || null,
            debit: debitVal,
            credit: creditVal,
            notes: detailForm.notes || null
        };
        
        setDetails(prev => {
            const newDetails = [...prev];
            newDetails[editingIndex] = updatedLine;
            return newDetails;
        });
        
        setEditingIndex(null);
        resetDetailForm();
        setShowDetailsPopup(false);
        toast.success('Detail line updated on this JV screen. Review the balance, then Save/Submit/Post; GL and Trial Balance remain unchanged until posting.');
    };
    
    // Cancel edit
    const handleCancelEdit = closeDetailsPopup;
    
    // Delete detail line
    const handleDeleteDetail = (index) => {
        setDetails(prev => prev.filter((_, i) => i !== index));
        toast.success('Detail line removed from this JV screen. Save the voucher to keep the change; posted GL and Trial Balance remain unchanged unless a voucher is posted or reversed.');
    };
    
    // Save/Close voucher
    const handleSaveClose = async () => {
        const effectiveDate = headerForm.date || jvDateInputRef.current?.value || '';

        if (!effectiveDate) {
            toast.error('Date is required');
            return;
        }

        if (!headerForm.particulars.trim()) {
            toast.error('Particulars is required');
            return;
        }
        
        if (details.length === 0) {
            toast.error('Please add at least one detail line');
            return;
        }
        
        if (!isBalanced) {
            toast.error('Total Debit and Total Credit are not equal. Please check each details first.');
            return;
        }
        
        setSaving(true);
        try {
            if (currentVoucher) {
                // Update existing voucher
                await axios.put(`${API}/journal-vouchers/${currentVoucher.id}`, {
                    date: effectiveDate,
                    reference: headerForm.reference,
                    particulars: headerForm.particulars,
                    details: details
                });
                
                // Then save it
                const saveRes = await axios.put(`${API}/journal-vouchers/${currentVoucher.id}/save`);
                setCurrentVoucher(saveRes.data.jv);
                toast.success(buildSavedVoucherToast(saveRes.data.jv, true));
            } else {
                // Create new voucher
                const createRes = await axios.post(`${API}/journal-vouchers`, {
                    course_id: selectedCourse,
                    date: effectiveDate,
                    reference: headerForm.reference,
                    particulars: headerForm.particulars,
                    details: details
                });
                
                // Then save it
                const saveRes = await axios.put(`${API}/journal-vouchers/${createRes.data.id}/save`);
                setCurrentVoucher(saveRes.data.jv);
                toast.success(buildSavedVoucherToast(saveRes.data.jv, false));
            }
            onRefresh();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to save');
        }
        setSaving(false);
    };
    
    // Post voucher
    const handlePost = async () => {
        if (!currentVoucher || (!isSaved && !isSubmitted)) {
            toast.error('Please save or submit the voucher first');
            return;
        }
        
        if (!isBalanced) {
            toast.error('Total Debit and Total Credit are not equal. Please check each details first.');
            return;
        }
        
        setConfirmAction({
            title: isSubmitted ? 'Approve and post JV?' : 'Post Journal Voucher?',
            description: isSubmitted
                ? 'This approves the submitted student work and writes the balanced voucher to the General Ledger.'
                : 'This writes the balanced voucher to the General Ledger. Posted vouchers cannot be edited.',
            confirmLabel: isSubmitted ? 'Approve & Post' : 'Post JV',
            tone: 'success',
            onConfirm: async () => {
                setSaving(true);
                try {
                    const studentQuery = currentVoucher.student_id && currentVoucher.student_id !== user?.id
                        ? `?student_id=${encodeURIComponent(currentVoucher.student_id)}`
                        : '';
                    await axios.put(`${API}/journal-vouchers/${currentVoucher.id}/post${studentQuery}`);

                    // Refresh voucher data
                    const voucherRes = await axios.get(`${API}/journal-vouchers/${currentVoucher.id}${studentQuery}`);
                    setCurrentVoucher(voucherRes.data);
                    onSetTrialBalanceDate?.(voucherRes.data?.date || currentVoucher.date);
                    if (voucherRes.data?.student_id && voucherRes.data.student_id !== user?.id) {
                        onReviewStudent?.(voucherRes.data);
                    }
                    const studentName = voucherRes.data?.student_name || voucherRes.data?.submitted_by_name || voucherRes.data?.processed_by || 'the student';
                    const scopeNote = voucherRes.data?.student_id && voucherRes.data.student_id !== user?.id
                        ? ` MaceSuite is now scoped to ${studentName}'s workspace so TB/GL show that student's posted ledger.`
                        : '';
                    toast.success(`${voucherRes.data?.jv_number || 'Journal Voucher'} posted to GL. Check Review > General Ledger and Trial Balance.${scopeNote}`);
                    fetchReviewQueue();
                    onRefresh();
                } catch (error) {
                    toast.error(error.response?.data?.detail || 'Failed to post');
                } finally {
                    setSaving(false);
                }
            }
        });
    };

    const handleSubmitForReview = async () => {
        if (!currentVoucher || !isSaved) {
            toast.error('Please save the voucher before submitting it for review');
            return;
        }
        if (!isBalanced) {
            toast.error('Only balanced vouchers can be submitted for review');
            return;
        }
        setConfirmAction({
            title: isReturned ? 'Resubmit JV for review?' : 'Submit JV for review?',
            description: 'Your instructor will be able to approve/post it or return it with revision feedback. You cannot edit it while it is submitted.',
            confirmLabel: isReturned ? 'Resubmit' : 'Submit',
            tone: 'primary',
            onConfirm: async () => {
                setSaving(true);
                try {
                    const res = await axios.put(`${API}/journal-vouchers/${currentVoucher.id}/submit`);
                    setCurrentVoucher(res.data.jv);
                    toast.success(`Journal Voucher ${res.data.jv?.jv_number || 'was'} submitted for review. Find it in Record > Journal Voucher as Submitted; instructor/admin sees it in the Instructor JV Review Queue. Status: Submitted for Review. No GL, Trial Balance, or report impact until approved and posted.`);
                    fetchReviewQueue();
                    onRefresh();
                } catch (error) {
                    toast.error(error.response?.data?.detail || 'Failed to submit for review');
                } finally {
                    setSaving(false);
                }
            }
        });
    };

    const handlePostQueueVoucher = async (voucher) => {
        if (!voucher?.id) return;
        setConfirmAction({
            title: 'Approve and post student JV?',
            description: `${voucher.jv_number || 'This JV'} for ${voucher.student_name || 'the student'} will be posted to the General Ledger.`,
            confirmLabel: 'Approve & Post',
            tone: 'success',
            onConfirm: async () => {
                setSaving(true);
                try {
                    const studentQuery = voucher.student_id ? `?student_id=${encodeURIComponent(voucher.student_id)}` : '';
                    await axios.put(`${API}/journal-vouchers/${voucher.id}/post${studentQuery}`);
                    onSetTrialBalanceDate?.(voucher.date);
                    onReviewStudent?.(voucher);
                    const studentName = voucher.student_name || voucher.submitted_by_name || voucher.processed_by || 'the student';
                    const scopeNote = voucher.student_id && voucher.student_id !== user?.id
                        ? ` MaceSuite is now scoped to ${studentName}'s workspace so TB/GL show that student's posted ledger.`
                        : '';
                    toast.success(`${voucher.jv_number || 'Journal Voucher'} posted to GL. It now appears in Review > General Ledger and Trial Balance.${scopeNote}`);
                    if (currentVoucher?.id === voucher.id) resetForm();
                    fetchReviewQueue();
                    onRefresh();
                } catch (error) {
                    toast.error(error.response?.data?.detail || 'Failed to post submitted voucher');
                } finally {
                    setSaving(false);
                }
            }
        });
    };

    const openReturnDialog = (voucher) => {
        if (!voucher?.id) return;
        setReturnTarget(voucher);
        setReturnReason('');
        setShowReturnDialog(true);
    };

    const handleReturnForRevision = async () => {
        if (!returnTarget?.id) return;
        if (!returnReason.trim()) {
            toast.error('Please provide revision feedback before returning the JV');
            return;
        }

        setReturning(true);
        try {
            const studentQuery = returnTarget.student_id ? `?student_id=${encodeURIComponent(returnTarget.student_id)}` : '';
            const res = await axios.put(
                `${API}/journal-vouchers/${returnTarget.id}/return${studentQuery}`,
                { reason: returnReason.trim() }
            );
            setShowReturnDialog(false);
            setReturnTarget(null);
            setReturnReason('');
            if (currentVoucher?.id === returnTarget.id) {
                setCurrentVoucher(res.data.jv);
                setHeaderForm({
                    date: res.data.jv.date,
                    reference: res.data.jv.reference || '',
                    particulars: res.data.jv.particulars
                });
                setDetails(res.data.jv.details || []);
            }
            toast.success(`Journal Voucher ${res.data.jv?.jv_number || 'was'} returned for revision. The student can find it in Record > Journal Voucher as Returned for Revision, read the feedback on the row/details, revise, then resubmit. No GL/Trial Balance impact until posted.`);
            fetchReviewQueue();
            onRefresh();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to return voucher for revision');
        } finally {
            setReturning(false);
        }
    };
    
    // Reversal handlers
    const openReversalDialog = (voucher) => {
        setReversalTarget(voucher);
        setReversalReason('');
        setShowReversalDialog(true);
    };

    const handleReverse = async () => {
        if (!reversalReason.trim()) {
            toast.error('Please provide a reason for reversal');
            return;
        }
        setReversing(true);
        try {
            const response = await axios.put(
                `${API}/journal-vouchers/${reversalTarget.id}/reverse`,
                { reason: reversalReason }
            );
            toast.success(`JV reversed. Reversal reference: ${response.data.reversal_ref}. Review it in Record > Journal Voucher and Review > General Ledger; Trial Balance reflects the posted reversal lines.`);
            setShowReversalDialog(false);
            setReversalTarget(null);
            setReversalReason('');
            if (currentVoucher?.id === reversalTarget.id) resetForm();
            onRefresh();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to reverse journal voucher');
        } finally {
            setReversing(false);
        }
    };

    // New voucher
    const handleNew = () => {
        if (details.length > 0 && !currentVoucher?.status) {
            setConfirmAction({
                title: 'Discard unsaved JV?',
                description: 'This clears the current unsaved voucher from the screen. Nothing will be posted to the General Ledger.',
                confirmLabel: 'Discard',
                tone: 'danger',
                onConfirm: () => {
                    resetForm();
                    toast.info('Unsaved journal voucher discarded');
                }
            });
            return;
        }
        resetForm();
    };
    
    return (
        <div className="space-y-4" data-testid="jv-tab">
            {/* Header: Journal Voucher - General Ledger */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                <div className="bg-gradient-to-r from-[#0F2A44] to-[#1E3A8A] px-6 py-4 text-white flex items-center justify-between">
                    <div>
                        <h2 className="font-semibold font-['Manrope'] flex items-center gap-2">
                            <Calculator className="w-5 h-5" /> Journal Voucher - General Ledger
                        </h2>
                        <p className="text-white/70 text-sm mt-1">
                            {jvHeaderSubtitle}
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        {isPosted && (
                            <span className="flex items-center gap-1 px-3 py-1 bg-emerald-500 rounded-full text-sm font-medium">
                                <Lock className="w-4 h-4" /> Posted
                            </span>
                        )}
                        {isSaved && !isPosted && (
                            <span className={`px-3 py-1 rounded-full text-sm font-medium ${isReturned ? 'bg-orange-500' : 'bg-amber-500'}`}>
                                {isReturned ? 'Returned for Revision' : 'Saved'}
                            </span>
                        )}
                        {isSubmitted && (
                            <span className="px-3 py-1 bg-blue-500 rounded-full text-sm font-medium">Submitted for Review</span>
                        )}
                        {!currentVoucher && (
                            <span className="px-3 py-1 bg-slate-500 rounded-full text-sm font-medium">New</span>
                        )}
                    </div>
                </div>
                
                {/* Header Fields */}
                <div className="p-6 space-y-4">
                    {sourceOpenNotice && currentVoucher && (() => {
                        const isBankReconNotice = sourceOpenNotice.sourceType === 'bank_reconciliation_adjustment';
                        const matchLabel = sourceOpenNotice.bankReconciliationMatched
                            ? 'Bank statement evidence is matched'
                            : sourceOpenNotice.bankReconciliationMatchStatus === 'needs_match'
                                ? 'Needs statement evidence match'
                                : 'Bank reconciliation evidence status available';
                        return (
                            <div className={`rounded-lg border p-3 text-sm ${
                                isBankReconNotice
                                    ? 'border-emerald-100 bg-emerald-50 text-emerald-950'
                                    : 'border-indigo-100 bg-indigo-50 text-indigo-900'
                            }`} data-testid="jv-source-open-banner">
                                <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                                    <div className="flex gap-3">
                                        <Eye className={`mt-0.5 h-4 w-4 ${isBankReconNotice ? 'text-emerald-600' : 'text-indigo-600'}`} />
                                        <div>
                                            <p className="font-semibold">
                                                Opened from General Ledger: {isBankReconNotice ? 'Bank Reconciliation source' : sourceOpenNotice.voucherNumber}
                                            </p>
                                            <p className={`mt-1 ${isBankReconNotice ? 'text-emerald-800' : 'text-indigo-700'}`}>
                                                Source evidence for {sourceOpenNotice.accountCode || 'the selected GL account'}
                                                {sourceOpenNotice.accountTitle ? ` - ${sourceOpenNotice.accountTitle}` : ''}
                                                {sourceOpenNotice.date ? ` on ${new Date(sourceOpenNotice.date).toLocaleDateString()}` : ''}.
                                            </p>
                                            {isBankReconNotice && (
                                                <p className="mt-1 text-xs text-emerald-800">
                                                    Statement reference: {sourceOpenNotice.sourceStatementReference || 'not recorded'}.
                                                    {' '}Status: {matchLabel}. Find it in {sourceOpenNotice.sourceLocation || 'Review > Bank Reconciliation > Posted Adjustment Evidence'}.
                                                </p>
                                            )}
                                            <p className={`mt-1 text-xs ${isBankReconNotice ? 'text-emerald-800' : 'text-indigo-700'}`}>
                                                Control impact: {sourceOpenNotice.sourceImpact || 'this posted JV is the source record behind the GL line and is included in General Ledger, Trial Balance, and reports by transaction date.'}
                                            </p>
                                            {sourceOpenNotice.sourceNextAction && (
                                                <p className={`mt-1 text-xs font-semibold ${isBankReconNotice ? 'text-emerald-900' : 'text-indigo-800'}`}>
                                                    Next safe action: {sourceOpenNotice.sourceNextAction}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex flex-wrap gap-2">
                                        {isBankReconNotice && onOpenBankReconPeriod && (
                                            <Button
                                                type="button"
                                                variant="outline"
                                                size="sm"
                                                onClick={openCurrentBankReconEvidence}
                                                className="border-emerald-200 text-emerald-700 hover:bg-white"
                                                data-testid="jv-source-open-bank-recon"
                                            >
                                                <Building2 className="mr-2 h-4 w-4" /> Bank Recon Evidence
                                            </Button>
                                        )}
                                        <Button
                                            type="button"
                                            variant="outline"
                                            size="sm"
                                            onClick={() => setSourceOpenNotice(null)}
                                            className={`${isBankReconNotice ? 'border-emerald-200 text-emerald-700 hover:bg-white' : 'border-indigo-200 text-indigo-700 hover:bg-white'}`}
                                            data-testid="jv-source-open-dismiss"
                                        >
                                            <X className="mr-2 h-4 w-4" /> Dismiss
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        );
                    })()}
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div>
                            <Label className="text-slate-700">Date <span className="text-red-500">*</span></Label>
                            <Input
                                ref={jvDateInputRef}
                                type="date"
                                value={headerForm.date}
                                onChange={e => setHeaderForm(prev => ({ ...prev, date: e.target.value }))}
                                onInput={e => setHeaderForm(prev => ({ ...prev, date: e.currentTarget.value }))}
                                disabled={formLocked}
                                className="mt-1"
                                data-testid="jv-date"
                            />
                        </div>
                        <div>
                            <Label className="text-slate-700">Processed By</Label>
                            <Input
                                value={currentVoucher?.processed_by || user?.full_name || ''}
                                disabled
                                className="mt-1 bg-slate-50"
                            />
                        </div>
                        <div>
                            <Label className="text-slate-700">Reference</Label>
                            <Input
                                value={headerForm.reference}
                                onChange={e => setHeaderForm(prev => ({ ...prev, reference: e.target.value }))}
                                placeholder="e.g., INV-001, PO-123"
                                disabled={formLocked}
                                className="mt-1"
                                data-testid="jv-reference"
                            />
                        </div>
                        <div>
                            <Label className="text-slate-700">JV #</Label>
                            <Input
                                value={jvNumberDisplay}
                                disabled
                                className="mt-1 bg-slate-50 font-mono"
                            />
                        </div>
                    </div>
                    
                    <div>
                        <Label className="text-slate-700">Particulars <span className="text-red-500">*</span></Label>
                        <Textarea
                            value={headerForm.particulars}
                            onChange={e => setHeaderForm(prev => ({ ...prev, particulars: e.target.value }))}
                            placeholder="Describe the transaction..."
                            disabled={formLocked}
                            className="mt-1"
                            rows={2}
                            data-testid="jv-particulars"
                        />
                    </div>
                    
                    {/* Posted info */}
                    {isPosted && currentVoucher && (
                        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 flex items-center gap-4 text-sm">
                            <div><strong>Posted By:</strong> {currentVoucher.posted_by}</div>
                            <div><strong>Posted At:</strong> {new Date(currentVoucher.posted_at).toLocaleString()}</div>
                        </div>
                    )}
                    {isSubmitted && currentVoucher && (
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center gap-4 text-sm text-blue-800">
                            <div><strong>Submitted By:</strong> {currentVoucher.submitted_by_name || currentVoucher.processed_by}</div>
                            <div><strong>Submitted At:</strong> {currentVoucher.submitted_at ? new Date(currentVoucher.submitted_at).toLocaleString() : 'Pending review'}</div>
                        </div>
                    )}
                    {isReturned && currentVoucher && (
                        <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 text-sm text-orange-900 space-y-1">
                            <div className="font-semibold">Returned for revision by {currentVoucher.returned_by_name || 'Instructor'}</div>
                            <div>{currentVoucher.return_reason || 'Please revise this voucher and resubmit for review.'}</div>
                            {currentVoucher.returned_at && (
                                <div className="text-xs text-orange-700">Returned at {new Date(currentVoucher.returned_at).toLocaleString()}</div>
                            )}
                        </div>
                    )}
                    {displayWorkflowTrail && (
                        <div className="border border-slate-200 rounded-lg bg-white p-3 text-sm" data-testid="jv-workflow-trail">
                            <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                                <div>
                                    <div className="flex items-center gap-2 font-semibold text-slate-900">
                                        <ArrowRight className="w-4 h-4 text-[#2F8FCB]" />
                                        Where this JV went
                                    </div>
                                    <p className="mt-1 text-slate-600">{displayWorkflowTrail.whatChanged}</p>
                                </div>
                                <span className={`inline-flex w-fit items-center rounded-full border px-3 py-1 text-xs font-semibold capitalize ${displayWorkflowTrail.statusClass}`}>
                                    {displayWorkflowTrail.statusLabel}
                                </span>
                            </div>
                            <div className="mt-3 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                                <div className="rounded-md bg-slate-50 border border-slate-100 p-3">
                                    <p className="text-xs font-semibold uppercase text-slate-500">Find it now</p>
                                    <p className="mt-1 text-slate-900">{displayWorkflowTrail.whereToFind}</p>
                                </div>
                                <div className="rounded-md bg-slate-50 border border-slate-100 p-3">
                                    <p className="text-xs font-semibold uppercase text-slate-500">Review next</p>
                                    <p className="mt-1 text-slate-900">{displayWorkflowTrail.reviewNext}</p>
                                </div>
                                <div className="rounded-md bg-slate-50 border border-slate-100 p-3">
                                    <p className="text-xs font-semibold uppercase text-slate-500">Report impact</p>
                                    <p className="mt-1 text-slate-900">{displayWorkflowTrail.reportImpact}</p>
                                </div>
                                <div className="rounded-md bg-slate-50 border border-slate-100 p-3">
                                    <p className="text-xs font-semibold uppercase text-slate-500">Next safe action</p>
                                    <p className="mt-1 text-slate-900">{displayWorkflowTrail.nextSafeAction}</p>
                                </div>
                            </div>
                            {isPosted && currentVoucher && onOpenPostedEvidence && (
                                <div className="mt-3 flex flex-wrap gap-2 rounded-md border border-emerald-100 bg-emerald-50 p-3">
                                    {isBankReconVoucher(currentVoucher) && onOpenBankReconPeriod && (
                                        <Button
                                            type="button"
                                            variant="outline"
                                            size="sm"
                                            onClick={openCurrentBankReconEvidence}
                                            data-testid="jv-workflow-open-bank-recon"
                                        >
                                            <Building2 className="w-4 h-4 mr-2" /> Bank Recon Evidence
                                        </Button>
                                    )}
                                    <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        onClick={() => openCurrentVoucherEvidence('gl')}
                                        data-testid="jv-workflow-open-gl"
                                    >
                                        <BookOpen className="w-4 h-4 mr-2" /> Open General Ledger
                                    </Button>
                                    <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        onClick={() => openCurrentVoucherEvidence('trial')}
                                        data-testid="jv-workflow-open-trial"
                                    >
                                        <Scale className="w-4 h-4 mr-2" /> Open Trial Balance
                                    </Button>
                                    <p className="text-xs leading-snug text-emerald-800">
                                        These are review links only. They open posted evidence for this JV and do not post, edit, reverse, or delete records.
                                    </p>
                                </div>
                            )}
                        </div>
                    )}
                    {currentBankReconContext && (
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-900" data-testid="bank-recon-jv-source-card">
                            <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                                <div>
                                    <div className="flex flex-wrap items-center gap-2">
                                        <span className="inline-flex items-center gap-1 rounded-full bg-white border border-blue-200 px-2 py-0.5 text-xs font-semibold text-blue-700">
                                            <Building2 className="w-3 h-3" />
                                            {currentBankReconContext.badge}
                                        </span>
                                        <span className="text-xs text-blue-700">Period: {currentBankReconContext.period}</span>
                                    </div>
                                    <p className="font-semibold mt-2">{currentBankReconContext.label}</p>
                                    <p className="text-xs text-blue-700 mt-1">
                                        Statement reference: <span className="font-mono">{currentBankReconContext.statementReference}</span>
                                    </p>
                                    <p className="text-xs text-blue-700 mt-1">{currentBankReconContext.instruction}</p>
                                </div>
                                <div className="rounded-lg bg-white border border-blue-100 px-3 py-2 text-xs text-blue-800 lg:max-w-[280px] space-y-2">
                                    <p>Check before posting: statement evidence, adjustment type, balanced debit/credit, and normal instructor approval trail.</p>
                                    {onOpenBankReconPeriod && (
                                        <Button
                                            type="button"
                                            variant="outline"
                                            size="sm"
                                            onClick={openCurrentBankReconEvidence}
                                            className="w-full border-blue-200 text-blue-700 hover:bg-blue-50"
                                            data-testid="bank-recon-jv-open-evidence"
                                        >
                                            <Building2 className="w-4 h-4 mr-2" /> Bank Recon Evidence
                                        </Button>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                    {reviewTimeline.length > 0 && (
                        <div className="border border-slate-200 rounded-lg bg-slate-50/70 p-3" data-testid="jv-review-history">
                            <div className="flex items-center gap-2 text-sm font-semibold text-slate-800 mb-3">
                                <History className="w-4 h-4 text-[#2F8FCB]" />
                                Review History
                            </div>
                            <div className="space-y-3">
                                {reviewTimeline.map((event, index) => {
                                    const meta = JV_REVIEW_ACTIONS[event.action] || JV_REVIEW_ACTIONS.submitted;
                                    const Icon = meta.Icon;
                                    const actor = event.reviewed_by_name || (event.action === 'submitted' ? currentVoucher.processed_by || 'Student' : 'Instructor');
                                    return (
                                        <div key={`${event.action}-${event.reviewed_at || index}`} className="flex gap-3">
                                            <div className={`mt-1 h-2.5 w-2.5 rounded-full ${meta.dotClass}`} />
                                            <div className="min-w-0 flex-1">
                                                <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                                                    <span className={`inline-flex w-fit items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-semibold ${meta.badgeClass}`}>
                                                        <Icon className="w-3 h-3" />
                                                        {meta.label}
                                                    </span>
                                                    <span className="text-xs text-slate-500">{formatTimelineDate(event.reviewed_at)}</span>
                                                </div>
                                                <p className="mt-1 text-sm text-slate-700">
                                                    <span className="font-medium">{actor}</span>
                                                    {event.action === 'jv_draft_created' && ' created this JV draft from bank reconciliation evidence.'}
                                                    {event.action === 'submitted' && ' sent this voucher for review.'}
                                                    {event.action === 'returned' && ' returned this voucher with feedback.'}
                                                    {event.action === 'approved' && ' approved and posted this voucher.'}
                                                    {event.action === 'posted' && ' posted this voucher to the General Ledger.'}
                                                </p>
                                                {event.reason && (
                                                    <p className="mt-1 text-sm text-orange-800 bg-orange-50 border border-orange-100 rounded px-2 py-1">
                                                        {event.reason}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>
            </div>
            
            {/* Details Section */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between bg-slate-50">
                    <h3 className="font-semibold text-slate-900">Transaction Details</h3>
                    {!formLocked && (
                        <Button
                            onClick={() => setShowDetailsPopup(true)}
                            className="bg-[#2F8FCB] hover:bg-[#2577a8]"
                            data-testid="add-details-btn"
                        >
                            <Plus className="w-4 h-4 mr-2" /> Add Details
                        </Button>
                    )}
                </div>
                
                {/* Details Grid */}
                {details.length === 0 ? (
                    <div className="p-12 text-center">
                        <FileText className="w-16 h-16 text-slate-200 mx-auto mb-4" />
                        <p className="text-slate-500">{isReadOnlyEmptyVoucher ? 'No voucher selected yet' : 'No detail lines yet'}</p>
                        <p className="text-sm text-slate-400 mt-1">
                            {isReadOnlyEmptyVoucher
                                ? 'Select an existing JV from the list to review its lines, evidence, status, and report impact.'
                                : 'Click "Add Details" to add transaction lines'}
                        </p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-slate-50 text-xs text-slate-500 uppercase tracking-wider">
                                <tr>
                                    <th className="px-4 py-3 text-left">Account Code</th>
                                    <th className="px-4 py-3 text-left">Account Title</th>
                                    <th className="px-4 py-3 text-left">Sub1</th>
                                    <th className="px-4 py-3 text-left">Sub2</th>
                                    <th className="px-4 py-3 text-right">Debit</th>
                                    <th className="px-4 py-3 text-right">Credit</th>
                                    <th className="px-4 py-3 text-left">Notes</th>
                                    {!formLocked && <th className="px-4 py-3 text-center">Actions</th>}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {details.map((line, idx) => (
                                    <tr key={idx} className="hover:bg-slate-50" data-testid={`detail-row-${idx}`}>
                                        <td className="px-4 py-3 font-mono text-sm">{line.account_code}</td>
                                        <td className="px-4 py-3">{line.account_title}</td>
                                        <td className="px-4 py-3 text-sm text-slate-500">{line.sub1 || '-'}</td>
                                        <td className="px-4 py-3 text-sm text-slate-500">{line.sub2 || '-'}</td>
                                        <td className="px-4 py-3 text-right font-mono">
                                            {line.debit > 0 ? fmt(line.debit) : ''}
                                        </td>
                                        <td className="px-4 py-3 text-right font-mono">
                                            {line.credit > 0 ? fmt(line.credit) : ''}
                                        </td>
                                        <td className="px-4 py-3 text-sm text-slate-500">{line.notes || '-'}</td>
                                        {!formLocked && (
                                            <td className="px-4 py-3 text-center">
                                                <div className="flex items-center justify-center gap-1">
                                                    <Tooltip>
                                                        <TooltipTrigger asChild>
                                                            <button
                                                                onClick={() => handleEditDetail(idx)}
                                                                className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg"
                                                                data-testid={`edit-line-${idx}`}
                                                            >
                                                                <Edit2 className="w-4 h-4" />
                                                            </button>
                                                        </TooltipTrigger>
                                                        <TooltipContent>Edit</TooltipContent>
                                                    </Tooltip>
                                                    <Tooltip>
                                                        <TooltipTrigger asChild>
                                                            <button
                                                                onClick={() => handleDeleteDetail(idx)}
                                                                className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                                                                data-testid={`delete-line-${idx}`}
                                                            >
                                                                <Trash2 className="w-4 h-4" />
                                                            </button>
                                                        </TooltipTrigger>
                                                        <TooltipContent>Delete</TooltipContent>
                                                    </Tooltip>
                                                </div>
                                            </td>
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot>
                                <tr className="bg-[#0F2A44] text-white font-semibold">
                                    <td colSpan={4} className="px-4 py-3">Total</td>
                                    <td className="px-4 py-3 text-right font-mono">{fmt(totalDebit)}</td>
                                    <td className="px-4 py-3 text-right font-mono">{fmt(totalCredit)}</td>
                                    <td colSpan={formLocked ? 1 : 2}></td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                )}
                
                {/* Balance Status */}
                <div className={`px-6 py-3 flex items-center gap-2 ${isBalanced ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
                    {isBalanced ? (
                        <><CheckCircle2 className="w-5 h-5" /> Debits = Credits (Balanced)</>
                    ) : (
                        <><AlertCircle className="w-5 h-5" /> Out of balance by {fmt(Math.abs(totalDebit - totalCredit))}</>
                    )}
                </div>
            </div>
            
            {/* Action Buttons */}
            {!isReadOnly && (
            <div className="flex items-center justify-between">
                <div className="flex gap-2">
                    <Button variant="outline" onClick={handleNew} disabled={saving}>
                        <Plus className="w-4 h-4 mr-2" /> New
                    </Button>
                    {!formLocked && !isSaved && (details.length > 0 || headerForm.particulars) && (
                        <Button
                            variant="outline"
                            onClick={() => {
                                setConfirmAction({
                                    title: 'Discard this journal voucher?',
                                    description: 'All unsaved header and transaction details will be cleared from the screen.',
                                    confirmLabel: 'Discard',
                                    tone: 'danger',
                                    onConfirm: () => {
                                        resetForm();
                                        toast.info('Journal voucher discarded');
                                    }
                                });
                            }}
                            disabled={saving}
                            className="border-red-200 text-red-500 hover:bg-red-50 hover:text-red-600"
                            data-testid="discard-btn"
                        >
                            <X className="w-4 h-4 mr-2" /> Discard
                        </Button>
                    )}
                </div>
                
                <div className="flex gap-3">
                    {!formLocked && (
                        <Button
                            onClick={handleSaveClose}
                            disabled={saving || details.length === 0}
                            className="bg-amber-500 hover:bg-amber-600"
                            data-testid="save-btn"
                        >
                            <Save className="w-4 h-4 mr-2" /> Save
                        </Button>
                    )}
                    
                    {isSaved && !isPosted && isBalanced && !perms.can_post && user?.role === 'student' && (
                        <Button
                            onClick={handleSubmitForReview}
                            disabled={saving}
                            className="bg-[#2F8FCB] hover:bg-[#2577a8]"
                            data-testid="submit-jv-review-btn"
                        >
                            <Send className="w-4 h-4 mr-2" /> {isReturned ? 'Resubmit for Review' : 'Submit for Review'}
                        </Button>
                    )}

                    {(isSaved || isSubmitted) && !isPosted && !isReturned && isBalanced && perms.can_post && (
                        <>
                            {isSubmitted && (
                                <Button
                                    variant="outline"
                                    onClick={() => openReturnDialog(currentVoucher)}
                                    disabled={saving || returning}
                                    className="border-orange-200 text-orange-700 hover:bg-orange-50"
                                    data-testid="return-jv-revision-btn"
                                >
                                    <Undo2 className="w-4 h-4 mr-2" /> Return for Revision
                                </Button>
                            )}
                            <Button
                                onClick={handlePost}
                                disabled={saving}
                                className="bg-emerald-600 hover:bg-emerald-700"
                                data-testid="post-btn"
                            >
                                <FileDown className="w-4 h-4 mr-2" /> {isSubmitted ? 'Approve & Post' : 'Post'}
                            </Button>
                        </>
                    )}
                </div>
            </div>
            )}

            {canReviewJvs && !viewingStudentId && (
                <div className="bg-white rounded-xl border border-blue-200 shadow-sm overflow-hidden" data-testid="jv-review-queue">
                    <div className="px-6 py-4 border-b border-blue-100 bg-blue-50 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                        <div>
                            <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                                <CheckSquare className="w-4 h-4 text-[#2F8FCB]" />
                                Instructor JV Review Queue
                            </h3>
                            <p className="text-sm text-slate-500 mt-1">
                                Submitted student vouchers waiting for approval, return, or posting for {reviewScopeLabel}.
                            </p>
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={fetchReviewQueue}
                                disabled={saving || returning}
                                data-testid="refresh-jv-review-queue"
                            >
                                <RefreshCw className="w-4 h-4 mr-2" /> Refresh
                            </Button>
                            <span className="text-xs font-semibold text-blue-700 bg-white border border-blue-100 rounded-full px-3 py-1">
                                {reviewQueue.length} pending
                            </span>
                        </div>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full min-w-[1040px]">
                            <thead className="bg-slate-50 text-xs text-slate-500 uppercase">
                                <tr>
                                    <th className="px-4 py-2 text-left">Student</th>
                                    <th className="px-4 py-2 text-left">Course</th>
                                    <th className="px-4 py-2 text-left">JV #</th>
                                    <th className="px-4 py-2 text-left">Source</th>
                                    <th className="px-4 py-2 text-left">Particulars</th>
                                    <th className="px-4 py-2 text-right">Total</th>
                                    <th className="px-4 py-2 text-left">Submitted</th>
                                    <th className="sticky right-0 z-10 bg-slate-50 px-4 py-2 text-center shadow-lg">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {reviewQueue.length === 0 && (
                                    <tr>
                                        <td colSpan="8" className="px-6 py-8 text-center">
                                            <div className="mx-auto max-w-xl space-y-2">
                                                <p className="font-semibold text-slate-800">No submitted Journal Vouchers are waiting for review.</p>
                                                <p className="text-sm text-slate-500">
                                                    MaceSuite checked {reviewScopeLabel}. When a student saves a balanced JV and clicks Submit for Review, it appears here for return or approve/post. Draft, submitted, and returned JVs do not affect GL, Trial Balance, or reports until posted.
                                                </p>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                                {reviewQueue.slice(0, 8).map((v) => {
                                    const bankReconContext = getBankReconReviewContext(v);
                                    const openingBalanceSource = isOpeningBalanceVoucher(v);
                                    return (
                                        <tr key={v.id} className={bankReconContext || openingBalanceSource ? 'hover:bg-blue-50/40 bg-blue-50/20' : 'hover:bg-blue-50/40'}>
                                            <td className="px-4 py-2">
                                                <p className="font-medium text-slate-900">{v.student_name || 'Student'}</p>
                                                <p className="text-xs text-slate-500">{v.student_email || v.student_id}</p>
                                            </td>
                                            <td className="px-4 py-2 whitespace-nowrap">
                                                <p className="font-medium text-slate-700">{v.course_code || 'Course'}</p>
                                                <p className="text-xs text-slate-500 truncate max-w-[180px]">{v.course_title || v.course_id}</p>
                                            </td>
                                            <td className="px-4 py-2 font-mono text-sm whitespace-nowrap">{v.jv_number || '(Unsaved)'}</td>
                                            <td className="px-4 py-2">
                                                {bankReconContext ? (
                                                    <div className="space-y-1">
                                                        <span className="inline-flex items-center gap-1 rounded-full bg-blue-50 border border-blue-200 px-2 py-0.5 text-xs font-semibold text-blue-700">
                                                            <Building2 className="w-3 h-3" />
                                                            Bank Recon
                                                        </span>
                                                        <p className="text-[11px] text-slate-500">{bankReconContext.period}</p>
                                                        <p className="text-[11px] font-mono text-slate-500 truncate max-w-[160px]">{bankReconContext.statementReference}</p>
                                                    </div>
                                                ) : openingBalanceSource ? (
                                                    <div className="space-y-1">
                                                        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 border border-emerald-200 px-2 py-0.5 text-xs font-semibold text-emerald-700">
                                                            <Scale className="w-3 h-3" />
                                                            Opening Balance
                                                        </span>
                                                        <p className="text-[11px] text-slate-500">Source: Trial Balance setup</p>
                                                    </div>
                                                ) : (
                                                    <span className="text-xs text-slate-500">Manual JV</span>
                                                )}
                                            </td>
                                            <td className="px-4 py-2 truncate max-w-xs">{v.particulars}</td>
                                            <td className="px-4 py-2 text-right font-mono">{fmt(v.total_debit)}</td>
                                            <td className="px-4 py-2 text-sm text-slate-500 whitespace-nowrap">
                                                {v.submitted_at ? new Date(v.submitted_at).toLocaleString() : 'Pending'}
                                            </td>
                                            <td className="sticky right-0 bg-white px-4 py-2 text-center shadow-lg">
                                                <div className="flex items-center justify-center gap-2">
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        onClick={() => loadVoucher(v)}
                                                        data-testid={`review-load-jv-${v.id}`}
                                                    >
                                                        {bankReconContext || openingBalanceSource ? 'Review Evidence' : 'Review'}
                                                    </Button>
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        onClick={() => openReturnDialog(v)}
                                                        disabled={returning}
                                                        className="border-orange-200 text-orange-700 hover:bg-orange-50"
                                                        data-testid={`review-return-jv-${v.id}`}
                                                    >
                                                        Return
                                                    </Button>
                                                    <Button
                                                        size="sm"
                                                        onClick={() => handlePostQueueVoucher(v)}
                                                        disabled={saving}
                                                        className="bg-emerald-600 hover:bg-emerald-700"
                                                        data-testid={`review-post-jv-${v.id}`}
                                                    >
                                                        Approve & Post
                                                    </Button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
            
            {/* Saved Vouchers List */}
            {displayVouchers.length > 0 && (
                <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                    <div className="px-6 py-4 border-b border-slate-200 bg-slate-50 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                        <h3 className="font-semibold text-slate-900">Recent Vouchers</h3>
                        <div className="flex flex-wrap gap-2">
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={handleExportVoucherListCSV}
                                data-testid="jv-export-csv"
                            >
                                <FileDown className="w-4 h-4 mr-2" /> Export CSV
                            </Button>
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={handlePrintVoucherList}
                                data-testid="jv-print"
                            >
                                <Printer className="w-4 h-4 mr-2" /> Print
                            </Button>
                        </div>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full min-w-[760px]">
                            <thead className="bg-slate-50 text-xs text-slate-500 uppercase">
                                <tr>
                                    <th className="px-4 py-2 text-left">JV #</th>
                                    <th className="px-4 py-2 text-left">Date</th>
                                    <th className="px-4 py-2 text-left">Particulars</th>
                                    <th className="px-4 py-2 text-right">Total</th>
                                    <th className="px-4 py-2 text-center">Status</th>
                                    <th className="px-4 py-2 text-center">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {displayVouchers.slice(0, 10).map((v) => (
                                    <tr key={v.id} className={`hover:bg-slate-50 ${currentVoucher?.id === v.id ? 'bg-blue-50' : ''}`}>
                                        <td className="px-4 py-2 font-mono text-sm whitespace-nowrap">{v.jv_number || '(Unsaved)'}</td>
                                        <td className="px-4 py-2 whitespace-nowrap">{new Date(v.date).toLocaleDateString()}</td>
                                        <td className="px-4 py-2 max-w-xs">
                                            <p className="truncate">{v.particulars}</p>
                                            {v.review_status === 'returned' && (
                                                <div className="mt-1 rounded-md border border-orange-200 bg-orange-50 px-2 py-1 text-xs text-orange-800" data-testid={`returned-jv-row-note-${v.jv_number || v.id}`}>
                                                    <p className="font-semibold">Returned: revise and resubmit from this JV list.</p>
                                                    <p className="mt-0.5 line-clamp-2">Feedback: {v.return_reason || 'Open this JV to read the instructor feedback.'}</p>
                                                    <p className="mt-0.5">No GL, Trial Balance, or report impact until posted.</p>
                                                </div>
                                            )}
                                        </td>
                                        <td className="px-4 py-2 text-right font-mono">{fmt(v.total_debit)}</td>
                                        <td className="px-4 py-2 text-center">
                                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                                v.status === 'posted' ? 'bg-emerald-100 text-emerald-700' :
                                                v.status === 'reversed' ? 'bg-purple-100 text-purple-700' :
                                                v.status === 'submitted' ? 'bg-blue-100 text-blue-700' :
                                                v.review_status === 'returned' ? 'bg-orange-100 text-orange-700' :
                                                isBankReconVoucher(v) && v.status === 'saved' ? 'bg-blue-100 text-blue-700' :
                                                v.status === 'saved' ? 'bg-amber-100 text-amber-700' :
                                                'bg-slate-100 text-slate-700'
                                            }`}>
                                                {voucherStatusLabel(v)}
                                            </span>
                                        </td>
                                        <td className="px-4 py-2 text-center">
                                            <TooltipProvider>
                                            <div className="flex items-center justify-center gap-1">
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => loadVoucher(v)}
                                                    data-testid={`load-voucher-${v.id}`}
                                                >
                                                    {isReadOnly ? 'View' : v.status === 'posted' ? 'View' : v.status === 'reversed' ? 'View' : v.status === 'submitted' ? 'Review' : v.review_status === 'returned' ? 'Revise' : 'Edit'}
                                                </Button>
                                                {!isReadOnly && v.status === 'posted' && (
                                                    <>
                                                        {perms.can_unpost ? (
                                                            <Tooltip>
                                                                <TooltipTrigger asChild>
                                                                    <Button
                                                                        variant="ghost"
                                                                        size="sm"
                                                                        className="text-purple-600"
                                                                        onClick={() => openReversalDialog(v)}
                                                                        data-testid={`reverse-jv-${v.jv_number}`}
                                                                    >
                                                                        <Undo2 className="w-4 h-4" />
                                                                    </Button>
                                                                </TooltipTrigger>
                                                                <TooltipContent><p>Reverse (Unpost) this JV</p></TooltipContent>
                                                            </Tooltip>
                                                        ) : (
                                                            <Tooltip>
                                                                <TooltipTrigger asChild>
                                                                    <Button variant="ghost" size="sm" className="text-slate-300 cursor-not-allowed" disabled>
                                                                        <Lock className="w-4 h-4" />
                                                                    </Button>
                                                                </TooltipTrigger>
                                                                <TooltipContent><p>Only instructors can reverse JVs</p></TooltipContent>
                                                            </Tooltip>
                                                        )}
                                                    </>
                                                )}
                                                {v.status === 'reversed' && v.reversal_ref && (
                                                    <Tooltip>
                                                        <TooltipTrigger asChild>
                                                            <span className="text-xs text-purple-600 font-mono cursor-help">{v.reversal_ref}</span>
                                                        </TooltipTrigger>
                                                        <TooltipContent>
                                                            <p>Reversed by: {v.reversed_by_name || 'N/A'}</p>
                                                            <p>Reason: {v.reversal_reason || 'N/A'}</p>
                                                            {v.reversed_at && <p>At: {new Date(v.reversed_at).toLocaleString()}</p>}
                                                        </TooltipContent>
                                                    </Tooltip>
                                                )}
                                            </div>
                                            </TooltipProvider>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
            
            {/* Details Popup Dialog */}
            <Dialog
                open={showDetailsPopup}
                onOpenChange={(open) => {
                    if (open) setShowDetailsPopup(true);
                    else closeDetailsPopup();
                }}
            >
                <DialogContent className="max-w-2xl">
                    <DialogHeader>
                        <DialogTitle>
                            {editingIndex !== null ? 'Edit Detail Line' : 'Add Detail Line'}
                        </DialogTitle>
                        <DialogDescription>
                            Add or edit a Journal Voucher detail line. Changes affect GL and Trial Balance only after the voucher is posted.
                        </DialogDescription>
                    </DialogHeader>
                    
                    <div className="space-y-4 py-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label>Account Code <span className="text-red-500">*</span></Label>
                                <Select
                                    value={detailForm.account_code}
                                    onValueChange={handleAccountCodeChange}
                                >
                                    <SelectTrigger className="mt-1" ref={accountCodeRef} data-testid="detail-account-code">
                                        <SelectValue placeholder="Select Account Code" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {chartOfAccounts.map(acc => (
                                            <SelectItem key={acc.code} value={acc.code}>
                                                {acc.code} - {acc.title}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                {chartOfAccounts.length === 0 && (
                                    <p className="text-xs text-amber-600 mt-1" data-testid="coa-empty-hint">No accounts found. Go to Setup &rarr; Chart of Accounts to add account codes.</p>
                                )}
                            </div>
                            <div>
                                <Label>Account Title</Label>
                                <Input
                                    value={detailForm.account_title}
                                    disabled
                                    className="mt-1 bg-slate-50"
                                    placeholder="(Auto-filled)"
                                />
                            </div>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label>Sub1 (Optional)</Label>
                                <Select
                                    value={detailForm.sub1}
                                    onValueChange={handleSub1Change}
                                    disabled={sub1Options.length === 0}
                                >
                                    <SelectTrigger className="mt-1" data-testid="detail-sub1">
                                        <SelectValue placeholder={sub1Options.length === 0 ? 'N/A - Not required' : 'Select Sub1'} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {sub1Options.map(sub => (
                                            <SelectItem key={sub.code} value={sub.code}>
                                                {sub.code} - {sub.title}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                {sub1Options.length > 0 && (
                                    <p className="text-xs text-slate-500 mt-1">
                                        {chartOfAccounts.find(a => a.code === detailForm.account_code)?.sub1_type || ''} subsidiary
                                    </p>
                                )}
                            </div>
                            <div>
                                <Label>Sub2 (Optional)</Label>
                                <Select
                                    value={detailForm.sub2}
                                    onValueChange={v => setDetailForm(prev => ({ ...prev, sub2: v }))}
                                    disabled={sub2Options.length === 0 || !detailForm.sub1}
                                >
                                    <SelectTrigger className={`mt-1 ${!detailForm.sub1 && sub2Options.length === 0 ? 'opacity-50' : ''}`} data-testid="detail-sub2">
                                        <SelectValue placeholder={
                                            sub2Options.length === 0 
                                                ? (detailForm.sub1 ? 'N/A - Not required' : 'Select Sub1 first') 
                                                : 'Select Sub2'
                                        } />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {sub2Options.map(sub => (
                                            <SelectItem key={sub.code} value={sub.code}>
                                                {sub.code} - {sub.title}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                {sub2Options.length > 0 && (
                                    <p className="text-xs text-slate-500 mt-1">
                                        {chartOfAccounts.find(a => a.code === detailForm.account_code)?.sub2_type || ''} subsidiary
                                    </p>
                                )}
                                {!detailForm.sub1 && chartOfAccounts.find(a => a.code === detailForm.account_code)?.sub2_type !== 'None' && (
                                    <p className="text-xs text-amber-600 mt-1">Select Sub1 to enable Sub2</p>
                                )}
                            </div>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label>Debit</Label>
                                <Input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    value={detailForm.debit}
                                    onChange={e => setDetailForm(prev => ({ ...prev, debit: e.target.value, credit: e.target.value ? '' : prev.credit }))}
                                    placeholder="0.00"
                                    className="mt-1 font-mono text-right"
                                    data-testid="detail-debit"
                                />
                            </div>
                            <div>
                                <Label>Credit</Label>
                                <Input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    value={detailForm.credit}
                                    onChange={e => setDetailForm(prev => ({ ...prev, credit: e.target.value, debit: e.target.value ? '' : prev.debit }))}
                                    placeholder="0.00"
                                    className="mt-1 font-mono text-right"
                                    data-testid="detail-credit"
                                />
                            </div>
                        </div>
                        
                        <div>
                            <Label>Notes (Optional)</Label>
                            <Input
                                value={detailForm.notes}
                                onChange={e => setDetailForm(prev => ({ ...prev, notes: e.target.value }))}
                                placeholder="Additional notes..."
                                className="mt-1"
                                data-testid="detail-notes"
                            />
                        </div>
                    </div>
                    
                    <DialogFooter>
                        {editingIndex !== null ? (
                            <>
                                <DialogClose asChild>
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={handleCancelEdit}
                                        data-testid="cancel-detail-edit-btn"
                                    >
                                        Cancel
                                    </Button>
                                </DialogClose>
                                <Button onClick={handleUpdateDetail} className="bg-[#2F8FCB]" data-testid="update-detail-btn">
                                    Update
                                </Button>
                            </>
                        ) : (
                            <>
                                <DialogClose asChild>
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={closeDetailsPopup}
                                        data-testid="close-detail-dialog-btn"
                                    >
                                        Close
                                    </Button>
                                </DialogClose>
                                <Button onClick={handleAddDetail} className="bg-[#2F8FCB]" data-testid="add-detail-btn">
                                    <Plus className="w-4 h-4 mr-2" /> Add
                                </Button>
                            </>
                        )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* JV Confirmation Dialog */}
            <Dialog open={!!confirmAction} onOpenChange={(open) => !open && setConfirmAction(null)}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className={
                            confirmAction?.tone === 'danger'
                                ? 'text-red-700'
                                : confirmAction?.tone === 'success'
                                    ? 'text-emerald-700'
                                    : 'text-[#0F2A44]'
                        }>
                            {confirmAction?.title || 'Confirm action'}
                        </DialogTitle>
                        <DialogDescription>
                            {confirmAction?.description || 'Confirm this Journal Voucher action before it changes the voucher workflow state.'}
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter className="mt-4">
                        <Button
                            variant="outline"
                            onClick={() => setConfirmAction(null)}
                            disabled={saving}
                            data-testid="jv-confirm-cancel-btn"
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={runConfirmedAction}
                            disabled={saving}
                            className={
                                confirmAction?.tone === 'danger'
                                    ? 'bg-red-600 hover:bg-red-700'
                                    : confirmAction?.tone === 'success'
                                        ? 'bg-emerald-600 hover:bg-emerald-700'
                                        : 'bg-[#2F8FCB] hover:bg-[#2577a8]'
                            }
                            data-testid="jv-confirm-action-btn"
                        >
                            {confirmAction?.confirmLabel || 'Confirm'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* JV Return For Revision Dialog */}
            <Dialog open={showReturnDialog} onOpenChange={setShowReturnDialog}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="text-orange-700">Return JV for Revision</DialogTitle>
                        <DialogDescription>
                            Return the submitted voucher to the student as editable saved work without posting to GL.
                        </DialogDescription>
                    </DialogHeader>
                    {returnTarget && (
                        <div className="bg-slate-50 rounded-lg p-3 mb-4">
                            <p className="text-sm"><strong>Student:</strong> {returnTarget.student_name || returnTarget.submitted_by_name || returnTarget.processed_by || 'Student'}</p>
                            <p className="text-sm"><strong>JV #:</strong> {returnTarget.jv_number}</p>
                            <p className="text-sm"><strong>Particulars:</strong> {returnTarget.particulars}</p>
                            <p className="text-sm"><strong>Amount:</strong> {fmt(returnTarget.total_debit)}</p>
                        </div>
                    )}
                    <div className="space-y-2">
                        <Label htmlFor="jv-return-reason">Revision feedback *</Label>
                        <Textarea
                            id="jv-return-reason"
                            placeholder="Example: Correct the account code and resubmit for review."
                            value={returnReason}
                            onChange={(e) => setReturnReason(e.target.value)}
                            rows={3}
                            data-testid="jv-return-reason-input"
                        />
                    </div>
                    <DialogFooter className="mt-4">
                        <Button
                            variant="outline"
                            onClick={() => setShowReturnDialog(false)}
                            disabled={returning}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleReturnForRevision}
                            disabled={returning || !returnReason.trim()}
                            className="bg-orange-600 hover:bg-orange-700"
                            data-testid="jv-confirm-return-btn"
                        >
                            {returning ? 'Returning...' : 'Return to Student'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* JV Reversal Confirmation Dialog */}
            <Dialog open={showReversalDialog} onOpenChange={setShowReversalDialog}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="text-purple-700">Reverse Journal Voucher</DialogTitle>
                        <DialogDescription>
                            Create reversing GL entries and preserve the original posted JV as audit evidence.
                        </DialogDescription>
                    </DialogHeader>
                    {reversalTarget && (
                        <div className="bg-slate-50 rounded-lg p-3 mb-4">
                            <p className="text-sm"><strong>JV #:</strong> {reversalTarget.jv_number}</p>
                            <p className="text-sm"><strong>Particulars:</strong> {reversalTarget.particulars}</p>
                            <p className="text-sm"><strong>Amount:</strong> {fmt(reversalTarget.total_debit)}</p>
                        </div>
                    )}
                    <div className="space-y-2">
                        <Label htmlFor="jv-reversal-reason">Reason for Reversal *</Label>
                        <Textarea
                            id="jv-reversal-reason"
                            placeholder="e.g., Incorrect account codes, duplicate entry, etc."
                            value={reversalReason}
                            onChange={(e) => setReversalReason(e.target.value)}
                            rows={3}
                            data-testid="jv-reversal-reason-input"
                        />
                    </div>
                    <DialogFooter className="mt-4">
                        <Button variant="outline" onClick={() => setShowReversalDialog(false)}>Cancel</Button>
                        <Button
                            onClick={handleReverse}
                            disabled={reversing || !reversalReason.trim()}
                            className="bg-purple-600 hover:bg-purple-700"
                            data-testid="jv-confirm-reversal-btn"
                        >
                            {reversing ? 'Reversing...' : 'Confirm Reversal'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

// ================= GL ENTRIES TAB =================

const GL_ENTRY_MODES = [
    { id: 'standard', label: 'Standard General Ledger', desc: 'Per-account detail with balance forwarded and running balance' },
    { id: 'modified_a', label: 'Modified A (Account Per Page)', desc: 'One account per page' },
    { id: 'modified_b', label: 'Modified B (1st Level Subsidiary)', desc: 'Account -> Sub1 grouping' },
    { id: 'book_check', label: 'Book/Check Reference', desc: 'Add Book Ref + Check Ref columns' },
    { id: 'single_line', label: 'Single Line / Group by Transaction', desc: 'One row per transaction' },
];

function GLEntriesTab({ selectedCourse, chartOfAccounts, fmt, viewingStudentId, viewingStudentName, traceRequest, onOpenSource, onNavigate, onTraceTrialBalance, onOpenBankReconPeriod }) {
    const now = new Date();
    const initialMonth = now.getMonth() + 1;
    const initialYear = now.getFullYear();
    const initialGlRange = getYearDateRange(initialYear);
    const [filters, setFilters] = useState({
        account_from: '',
        account_to: '',
        date_from: initialGlRange.dateFrom,
        date_to: initialGlRange.dateTo,
        mode: 'standard'
    });
    const [datePreset, setDatePreset] = useState('annual');
    const [periodMonth, setPeriodMonth] = useState(initialMonth);
    const [periodYear, setPeriodYear] = useState(initialYear);
    const [showAdvancedModes, setShowAdvancedModes] = useState(false);
    const [entries, setEntries] = useState([]);
    const [accountSections, setAccountSections] = useState([]);
    const [reportPeriod, setReportPeriod] = useState({ date_from: '', date_to: '' });
    const [loading, setLoading] = useState(false);
    const [progress, setProgress] = useState(0);
    const [totals, setTotals] = useState({ debit: 0, credit: 0 });
    const [previewData, setPreviewData] = useState(null);
    const [showPreview, setShowPreview] = useState(false);
    const [activeTraceRequest, setActiveTraceRequest] = useState(null);
    const selectedMode = GL_ENTRY_MODES.find(mode => mode.id === filters.mode) || GL_ENTRY_MODES[0];
    const normalBalanceLookup = useMemo(
        () => buildNormalBalanceLookup(chartOfAccounts),
        [chartOfAccounts]
    );

    const applyDatePreset = (presetId) => {
        setDatePreset(presetId);
        if (presetId === 'custom') return;
        const range = presetId === 'monthly'
            ? getMonthDateRange(periodYear, periodMonth)
            : getYearDateRange(periodYear);
        setFilters(prev => ({
            ...prev,
            date_from: range.dateFrom,
            date_to: range.dateTo,
        }));
    };

    const handlePeriodMonthChange = (month) => {
        setDatePreset('monthly');
        setPeriodMonth(month);
        const range = getMonthDateRange(periodYear, month);
        setFilters(prev => ({ ...prev, date_from: range.dateFrom, date_to: range.dateTo }));
    };

    const handlePeriodYearChange = (year) => {
        setPeriodYear(year);
        const range = datePreset === 'annual'
            ? getYearDateRange(year)
            : getMonthDateRange(year, periodMonth);
        setFilters(prev => ({ ...prev, date_from: range.dateFrom, date_to: range.dateTo }));
    };

    const handleDateChange = (field, value) => {
        setDatePreset('custom');
        setFilters(prev => ({ ...prev, [field]: value }));
    };
    
    const processGlEntries = useCallback(async (filterSet, successMessage) => {
        if (!selectedCourse) {
            toast.error('Please select a course');
            return;
        }
        
        setLoading(true);
        setProgress(10);
        
        try {
            setProgress(30);
            const res = await axios.post(`${API}/gl-entries`, {
                course_id: selectedCourse,
                student_id: viewingStudentId || null,
                account_from: filterSet.account_from || null,
                account_to: filterSet.account_to || null,
                date_from: filterSet.date_from || null,
                date_to: filterSet.date_to || null,
                mode: filterSet.mode
            });
            
            setProgress(70);
            await new Promise(r => setTimeout(r, 300)); // Simulate processing
            
            setEntries(res.data.entries);
            setAccountSections(res.data.account_sections || []);
            setReportPeriod(res.data.period || {
                date_from: filterSet.date_from || '',
                date_to: filterSet.date_to || '',
            });
            setTotals({ debit: res.data.total_debit, credit: res.data.total_credit });
            setProgress(100);
            
            toast.success(
                successMessage ||
                `Loaded ${res.data.entries.length} posted GL entries across ${(res.data.account_sections || []).length} account sections. No source or accounting records were changed.`
            );
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to load GL entries');
        }
        
        setTimeout(() => {
            setLoading(false);
            setProgress(0);
        }, 500);
    }, [selectedCourse, viewingStudentId]);

    // Process and load GL entries
    const handleProcess = async () => {
        await processGlEntries(filters);
    };

    useEffect(() => {
        if (!traceRequest) return;
        const nextFilters = {
            account_from: traceRequest.accountFrom || '',
            account_to: traceRequest.accountTo || '',
            date_from: traceRequest.dateFrom || '',
            date_to: traceRequest.dateTo || '',
            mode: 'standard',
        };
        setFilters(nextFilters);
        setDatePreset('custom');
        setShowAdvancedModes(false);
        setPreviewData(null);
        setShowPreview(false);
        setEntries([]);
        setAccountSections([]);
        setReportPeriod({ date_from: '', date_to: '' });
        setTotals({ debit: 0, credit: 0 });
        setActiveTraceRequest(traceRequest);
        processGlEntries(nextFilters, `${traceRequest.label} evidence loaded in General Ledger`);
    }, [traceRequest, processGlEntries]);
    
    // Generate preview based on selected mode
    const handlePreview = async () => {
        if (accountSections.length === 0) {
            toast.error('Please process entries first');
            return;
        }
        
        setLoading(true);
        try {
            const res = await axios.post(`${API}/gl-entries/preview`, {
                course_id: selectedCourse,
                student_id: viewingStudentId || null,
                account_from: filters.account_from || null,
                account_to: filters.account_to || null,
                date_from: filters.date_from || null,
                date_to: filters.date_to || null,
                mode: filters.mode
            });
            
            setPreviewData(res.data);
            setShowPreview(true);
            toast.success('General Ledger preview generated. Review it on this page before export/print; no accounting records were changed.');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to generate preview');
        }
        setLoading(false);
    };

    const formatLedgerBalance = (value) => {
        const amount = Number(value || 0);
        if (Math.abs(amount) < 0.005) return fmt(0);
        return `${fmt(Math.abs(amount))} ${amount < 0 ? 'Cr' : 'Dr'}`;
    };

    const getLedgerPeriodLabel = (period) => {
        const dateFrom = period?.date_from;
        const dateTo = period?.date_to;
        if (dateFrom && dateTo) return `${dateFrom} to ${dateTo}`;
        if (dateTo) return `Through ${dateTo}`;
        if (dateFrom) return `From ${dateFrom}`;
        return 'All posted dates';
    };

    const reportPeriodLabel = getLedgerPeriodLabel(reportPeriod);
    const previewSections = previewData?.mode === 'standard'
        ? previewData?.account_sections
        : previewData?.sections;

    const handlePrintPreview = () => {
        const sections = previewData?.account_sections || previewData?.sections || [];
        if (!sections.length) {
            toast.error('No per-account ledger sections are available to print');
            return;
        }

        const escapeHtml = (value) => String(value ?? '')
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .split(String.fromCharCode(34)).join('&quot;')
            .replace(/'/g, '&#039;');
        const formatPrintDate = (value) => value
            ? new Date(value).toLocaleDateString('en-PH')
            : '';
        const printPeriodLabel = getLedgerPeriodLabel(previewData?.period || previewData?.filters);
        const sectionHtml = sections.map((section) => {
            const rows = (section.entries || []).map((entry) => `
                <tr>
                    <td>${escapeHtml(formatPrintDate(entry.date))}</td>
                    <td class='code'>${escapeHtml(entry.ref_no || entry.jv_id || entry.source_id || '-')}</td>
                    <td>${escapeHtml(entry.description || entry.account_title || '')}</td>
                    <td class='amount'>${entry.debit > 0 ? escapeHtml(fmt(entry.debit)) : ''}</td>
                    <td class='amount'>${entry.credit > 0 ? escapeHtml(fmt(entry.credit)) : ''}</td>
                    <td class='amount strong'>${escapeHtml(formatLedgerBalance(entry.running_balance))}</td>
                </tr>
            `).join('');
            return `
                <section class='account-section'>
                    <div class='account-heading'>
                        <div>
                            <div class='account-label'>Account</div>
                            <h2>${escapeHtml(section.account_code || '-')} - ${escapeHtml(section.account_title || section.account || 'Unmapped account')}</h2>
                        </div>
                        <div class='period'>Period covered: ${escapeHtml(printPeriodLabel)}</div>
                    </div>
                    <table>
                        <thead>
                            <tr>
                                <th>Date</th>
                                <th>Transaction Code</th>
                                <th>Particulars</th>
                                <th class='amount'>Debit</th>
                                <th class='amount'>Credit</th>
                                <th class='amount'>Running Balance</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr class='balance-forward'>
                                <td colspan='5'><em>Bal. Forwarded</em></td>
                                <td class='amount strong'>${escapeHtml(formatLedgerBalance(section.opening_balance))}</td>
                            </tr>
                            ${rows || `<tr><td colspan='6' class='empty'>No posted movement inside this period.</td></tr>`}
                        </tbody>
                        <tfoot>
                            <tr>
                                <td colspan='3'>Account Total / Ending Balance</td>
                                <td class='amount'>${escapeHtml(fmt(section.period_debit || section.subtotal_debit || 0))}</td>
                                <td class='amount'>${escapeHtml(fmt(section.period_credit || section.subtotal_credit || 0))}</td>
                                <td class='amount'>${escapeHtml(formatLedgerBalance(section.ending_balance))}</td>
                            </tr>
                        </tfoot>
                    </table>
                </section>
            `;
        }).join('');

        const printWindow = window.open('', '_blank', 'width=1100,height=800');
        if (!printWindow) {
            toast.error('Print preview was blocked. Please allow pop-ups for MaceSuite.');
            return;
        }

        printWindow.document.write(`
            <!doctype html>
            <html>
                <head>
                    <title>MaceSuite General Ledger - ${escapeHtml(printPeriodLabel)}</title>
                    <style>
                        @page { size: A4 landscape; margin: 12mm; }
                        * { box-sizing: border-box; }
                        body { margin: 0; color: #0f172a; font-family: Arial, Helvetica, sans-serif; font-size: 10px; }
                        .report-header { border-bottom: 2px solid #0f2a44; margin-bottom: 14px; padding-bottom: 10px; }
                        .brand { color: #0f2a44; font-size: 20px; font-weight: 700; }
                        h1 { font-size: 18px; margin: 4px 0; }
                        .meta { color: #475569; display: flex; gap: 18px; flex-wrap: wrap; }
                        .account-section + .account-section { break-before: page; page-break-before: always; }
                        .account-heading { align-items: end; display: flex; justify-content: space-between; margin: 0 0 8px; }
                        .account-label { color: #64748b; font-size: 9px; font-weight: 700; letter-spacing: .08em; text-transform: uppercase; }
                        h2 { font-size: 15px; margin: 2px 0 0; }
                        .period { color: #475569; font-size: 10px; }
                        table { border-collapse: collapse; width: 100%; }
                        thead { display: table-header-group; }
                        th { background: #e2e8f0; color: #334155; font-size: 9px; letter-spacing: .04em; padding: 7px; text-align: left; text-transform: uppercase; }
                        td { border-bottom: 1px solid #e2e8f0; padding: 7px; vertical-align: top; }
                        .amount { font-family: Consolas, 'Courier New', monospace; text-align: right; white-space: nowrap; }
                        .code { font-family: Consolas, 'Courier New', monospace; white-space: nowrap; }
                        .strong { font-weight: 700; }
                        .balance-forward td { background: #f8fafc; }
                        .empty { color: #64748b; padding: 18px; text-align: center; }
                        tfoot td { background: #0f2a44; border-bottom: none; color: #fff; font-weight: 700; }
                        .report-footer { color: #64748b; display: flex; font-size: 9px; justify-content: space-between; margin-top: 16px; }
                        .screen-actions { margin: 0 0 14px; }
                        .screen-actions button { background: #0f2a44; border: 0; border-radius: 6px; color: white; cursor: pointer; padding: 8px 12px; }
                        @media print { .screen-actions { display: none; } }
                    </style>
                </head>
                <body>
                    <div class='screen-actions'><button type='button' onclick='window.print()'>Print / Save PDF</button></div>
                    <header class='report-header'>
                        <div class='brand'>MaceSuite</div>
                        <h1>General Ledger</h1>
                        <div class='meta'>
                            <span>Period: ${escapeHtml(printPeriodLabel)}</span>
                            <span>Course: ${escapeHtml(selectedCourse || 'N/A')}</span>
                            <span>Workspace: ${escapeHtml(viewingStudentName || 'Current workspace')}</span>
                            <span>Generated by: ${escapeHtml(previewData?.generated_by || '')}</span>
                        </div>
                    </header>
                    ${sectionHtml}
                    <footer class='report-footer'>
                        <span>Posted GL evidence only. This report does not edit source records.</span>
                        <span>${escapeHtml(new Date().toLocaleString('en-PH'))}</span>
                    </footer>
                </body>
            </html>
        `);
        printWindow.document.close();
    };
    
    // Clear filters
    const handleClear = () => {
        setFilters({
            account_from: '',
            account_to: '',
            date_from: '',
            date_to: '',
            mode: 'standard'
        });
        setDatePreset('custom');
        setShowAdvancedModes(false);
        setActiveTraceRequest(null);
        setEntries([]);
        setAccountSections([]);
        setReportPeriod({ date_from: '', date_to: '' });
        setTotals({ debit: 0, credit: 0 });
    };

    const traceDateLabel = activeTraceRequest
        ? activeTraceRequest.dateFrom && activeTraceRequest.dateTo
            ? `${activeTraceRequest.dateFrom} to ${activeTraceRequest.dateTo}`
            : activeTraceRequest.dateTo
                ? `Through ${activeTraceRequest.dateTo}`
                : activeTraceRequest.dateFrom
                    ? `From ${activeTraceRequest.dateFrom}`
                    : 'All posted dates'
        : '';
    const isCloseReviewTrace = activeTraceRequest && [
        'Period Close Readiness',
        'Bank Reconciliation evidence package',
    ].includes(activeTraceRequest.sourceReport);
    const activeTracePeriod = String(activeTraceRequest?.dateFrom || activeTraceRequest?.dateTo || '').slice(0, 7);

    const openGlTrail = (tab, label) => {
        if (tab === 'trial' && activeTraceRequest?.dateTo && onTraceTrialBalance) {
            onTraceTrialBalance({
                sourceReport: activeTraceRequest.sourceReport || 'General Ledger',
                label: activeTraceRequest.label || label,
                scopeLabel: activeTraceRequest.scopeLabel || viewingStudentName || 'Current workspace',
                asOfDate: activeTraceRequest.dateTo,
                sourceLocation: 'Review > General Ledger',
                evidenceLocation: activeTraceRequest.sourceReport === 'Bank Reconciliation evidence package'
                    ? 'Review > Bank Reconciliation'
                    : 'Home > Close Readiness',
                reviewer: 'Student clears blockers. Instructor/admin reviews evidence. Auditor path is read-only.',
                impact: 'Trial Balance summarizes these posted GL entries when the same as-of date and student scope are used.',
                nextAction: 'Confirm Trial Balance totals, then return to Bank Reconciliation evidence if a close blocker or source match needs review.',
            });
            return;
        }
        onNavigate?.(tab);
        toast.info(`Opening ${label}. General Ledger is posted evidence only; use matching date coverage and Source Ref to verify the original work.`);
    };
    
    return (
        <div className="space-y-4" data-testid="gl-entries-tab">
            {/* Header */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                <div className="bg-gradient-to-r from-[#0F2A44] to-[#1E3A8A] px-6 py-4 text-white">
                    <h2 className="font-semibold font-['Manrope'] flex items-center gap-2">
                        <FileText className="w-5 h-5" /> General Ledger Report
                    </h2>
                    <p className="text-white/70 text-sm mt-1">Posted ledger only. Drafts and saved work stay in source lists until posted.</p>
                    {viewingStudentName && (
                        <p className="text-blue-100 text-xs mt-2">Report scope: {viewingStudentName}'s posted ledger</p>
                    )}
                </div>
                
                {/* Filters */}
                <div className="p-6 space-y-4">
                    {activeTraceRequest && (
                        <div className="rounded-lg border border-indigo-100 bg-indigo-50 p-4" data-testid="gl-trace-evidence-banner">
                            <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                                <div className="space-y-1">
                                    <p className="font-semibold text-indigo-900">
                                        Tracing {activeTraceRequest.sourceReport}: {activeTraceRequest.label}
                                    </p>
                                    <p className="text-sm text-indigo-700">
                                        Scope: {activeTraceRequest.scopeLabel || viewingStudentName || 'Current workspace'} | Accounts: {activeTraceRequest.accountLabel || `${filters.account_from || 'All'} to ${filters.account_to || 'All'}`} | Dates: {traceDateLabel}
                                    </p>
                                    {activeTraceRequest.amount !== undefined && activeTraceRequest.amount !== null && (
                                        <p className="text-xs font-semibold text-indigo-800">
                                            Report amount being traced: {fmt(activeTraceRequest.amount)}
                                        </p>
                                    )}
                                    <p className="text-xs text-indigo-700">
                                        Next safe action: {activeTraceRequest.nextAction || 'Review Source Ref, then open the original voucher or module record if an amount needs correction.'}
                                    </p>
                                    {isCloseReviewTrace && (
                                        <div className="mt-3 grid gap-2 md:grid-cols-3" data-testid="gl-close-handoff-trail">
                                            <div className="rounded-md border border-indigo-100 bg-white/80 p-2">
                                                <p className="text-[11px] font-semibold uppercase text-indigo-600">Evidence source</p>
                                                <p className="text-xs text-slate-700 mt-1">
                                                    {activeTraceRequest.sourceReport === 'Bank Reconciliation evidence package'
                                                        ? 'Bank Reconciliation evidence package'
                                                        : 'Home > Close Readiness'}
                                                </p>
                                            </div>
                                            <div className="rounded-md border border-indigo-100 bg-white/80 p-2">
                                                <p className="text-[11px] font-semibold uppercase text-indigo-600">Posted ledger status</p>
                                                <p className="text-xs text-slate-700 mt-1">Loaded rows are posted evidence only. Drafts and matches do not post here.</p>
                                            </div>
                                            <div className="rounded-md border border-indigo-100 bg-white/80 p-2">
                                                <p className="text-[11px] font-semibold uppercase text-indigo-600">Next report check</p>
                                                <p className="text-xs text-slate-700 mt-1">Open Trial Balance using the same as-of date and student scope.</p>
                                            </div>
                                        </div>
                                    )}
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    {isCloseReviewTrace && (
                                        <>
                                            <Button
                                                type="button"
                                                variant="outline"
                                                size="sm"
                                                onClick={() => activeTracePeriod && onOpenBankReconPeriod
                                                    ? onOpenBankReconPeriod(activeTracePeriod, 'General Ledger trace')
                                                    : onNavigate?.('bankrecon')}
                                                data-testid="gl-trace-open-bank-recon"
                                            >
                                                <Building2 className="w-4 h-4 mr-2" /> Bank Recon Evidence
                                            </Button>
                                            <Button type="button" variant="outline" size="sm" onClick={() => openGlTrail('trial', 'Trial Balance')} data-testid="gl-trace-open-trial">
                                                <Scale className="w-4 h-4 mr-2" /> Trial Balance
                                            </Button>
                                        </>
                                    )}
                                    <Button type="button" variant="outline" size="sm" onClick={handleClear} data-testid="gl-clear-trace-btn">
                                        <X className="w-4 h-4 mr-2" /> Clear Trace
                                    </Button>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="rounded-lg border border-blue-100 bg-blue-50 p-3" data-testid="gl-date-presets">
                        <ReportPeriodSelector
                            mode={datePreset}
                            onModeChange={applyDatePreset}
                            month={periodMonth}
                            year={periodYear}
                            onMonthChange={handlePeriodMonthChange}
                            onYearChange={handlePeriodYearChange}
                            dateFrom={filters.date_from}
                            dateTo={filters.date_to}
                            onDateFromChange={(value) => handleDateChange('date_from', value)}
                            onDateToChange={(value) => handleDateChange('date_to', value)}
                            effectText="Posted GL entries inside this coverage are loaded. Use Custom with blank dates only when you need all posted dates."
                            testId="gl-date-coverage"
                        />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                            <Label className="text-slate-700">Company</Label>
                            <Input
                                value="MaceSuite University"
                                disabled
                                className="mt-1 bg-slate-50"
                            />
                        </div>
                        <div>
                            <Label className="text-slate-700">Account From</Label>
                            <Select
                                value={filters.account_from || 'all'}
                                onValueChange={v => setFilters(prev => ({ ...prev, account_from: v === 'all' ? '' : v }))}
                            >
                                <SelectTrigger className="mt-1" data-testid="gl-account-from">
                                    <SelectValue placeholder="All Accounts" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">All Accounts</SelectItem>
                                    {chartOfAccounts.map(acc => (
                                        <SelectItem key={acc.code} value={acc.code}>
                                            {acc.code} - {acc.title}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="text-slate-700">Account To</Label>
                            <Select
                                value={filters.account_to || 'all'}
                                onValueChange={v => setFilters(prev => ({ ...prev, account_to: v === 'all' ? '' : v }))}
                            >
                                <SelectTrigger className="mt-1" data-testid="gl-account-to">
                                    <SelectValue placeholder="All Accounts" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">All Accounts</SelectItem>
                                    {chartOfAccounts.map(acc => (
                                        <SelectItem key={acc.code} value={acc.code}>
                                            {acc.code} - {acc.title}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    
                    {/* Mode Selector */}
                    <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                            <div>
                                <Label className="text-slate-700 block">Report Format</Label>
                                <p className="text-xs text-slate-500 mt-1">
                                    Default: per-account General Ledger with Balance Forwarded and running balance. Advanced formats remain available for legacy review.
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={() => setShowAdvancedModes(prev => !prev)}
                                className="inline-flex items-center justify-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:border-[#2F8FCB] hover:text-[#0F2A44]"
                                data-testid="gl-advanced-format-toggle"
                            >
                                {selectedMode.label.split('(')[0].trim()}
                                <ChevronDown className={`h-4 w-4 transition-transform ${showAdvancedModes ? 'rotate-180' : ''}`} />
                            </button>
                        </div>
                        {showAdvancedModes && (
                            <div className="mt-3 grid grid-cols-1 md:grid-cols-5 gap-2">
                                {GL_ENTRY_MODES.map(mode => (
                                    <button
                                        key={mode.id}
                                        type="button"
                                        onClick={() => setFilters(prev => ({ ...prev, mode: mode.id }))}
                                        className={`p-3 rounded-lg border text-left transition-all ${
                                            filters.mode === mode.id
                                                ? 'border-[#2F8FCB] bg-[#E0F2FE] text-[#0F2A44]'
                                                : 'border-slate-200 bg-white hover:border-slate-300 text-slate-600'
                                        }`}
                                        data-testid={`gl-mode-${mode.id}`}
                                    >
                                        <div className="font-medium text-sm">{mode.label.split('(')[0].trim()}</div>
                                        <div className="text-xs opacity-70 mt-1">{mode.desc}</div>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                    
                    {/* Action Buttons */}
                    <div className="flex items-center gap-3">
                        <Button
                            onClick={handleProcess}
                            disabled={loading}
                            className="bg-[#2F8FCB] hover:bg-[#2577a8]"
                            data-testid="gl-process-btn"
                        >
                            {loading ? (
                                <>
                                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                                    Processing...
                                </>
                            ) : (
                                <>
                                    <Search className="w-4 h-4 mr-2" /> Process
                                </>
                            )}
                        </Button>
                        <Button
                            variant="outline"
                            onClick={handlePreview}
                            disabled={loading || (entries.length === 0 && accountSections.length === 0)}
                            data-testid="gl-preview-btn"
                        >
                            <Printer className="w-4 h-4 mr-2" /> Preview
                        </Button>
                        <Button
                            variant="outline"
                            onClick={handleClear}
                            disabled={loading}
                        >
                            <X className="w-4 h-4 mr-2" /> Clear
                        </Button>
                    </div>
                    
                    {/* Progress Bar */}
                    {loading && progress > 0 && (
                        <div className="w-full bg-slate-200 rounded-full h-2.5">
                            <div 
                                className="bg-[#2F8FCB] h-2.5 rounded-full transition-all duration-300"
                                style={{ width: `${progress}%` }}
                            />
                        </div>
                    )}
                </div>
            </div>

            <div className="rounded-lg border border-emerald-100 bg-emerald-50 p-4" data-testid="gl-entries-where-it-appears">
                <div className="grid gap-4 md:grid-cols-4">
                    <div>
                        <p className="text-xs font-semibold uppercase text-emerald-700">What changed</p>
                        <p className="mt-1 text-sm text-slate-700">
                            {!selectedCourse
                                ? 'No course is selected, so GL entries were not loaded yet. No source, JV, AR/AP, GL, or report record was edited.'
                                : accountSections.length > 0
                                    ? `Loaded ${entries.length} posted GL entr${entries.length === 1 ? 'y' : 'ies'} for the selected filters. No source, JV, AR/AP, GL, or report record was edited.`
                                    : 'No posted GL entries are loaded for the selected filters yet. No source, JV, AR/AP, GL, or report record was edited.'}
                        </p>
                    </div>
                    <div>
                        <p className="text-xs font-semibold uppercase text-emerald-700">Where to find it</p>
                        <p className="mt-1 text-sm text-slate-700">
                            {accountSections.length > 0
                                ? "This page is Review > General Ledger. Use each row's Source Ref or Open Source button to trace back to the original JV, AR/AP, bank recon, or opening balance evidence."
                                : 'This page is Review > General Ledger. After a course is selected and posted work exists, rows appear here with Source Ref and Open Source evidence.'}
                        </p>
                    </div>
                    <div>
                        <p className="text-xs font-semibold uppercase text-emerald-700">Report effect</p>
                        <p className="mt-1 text-sm text-slate-700">
                            {accountSections.length > 0
                                ? 'These posted entries feed Trial Balance, Balance Sheet, and Income Statement when the same student scope and date coverage are used.'
                                : 'Only posted entries affect GL, Trial Balance, Balance Sheet, and Income Statement. Draft, saved, submitted, or returned work is evidence only.'}
                        </p>
                    </div>
                    <div>
                        <p className="text-xs font-semibold uppercase text-emerald-700">Next safe action</p>
                        <p className="mt-1 text-sm text-slate-700">
                            {!selectedCourse
                                ? 'Select a course first, then click Process so the posted ledger can load for that scope.'
                                : accountSections.length > 0
                                    ? 'Compare totals, open the source record before correcting, then use a reversal or adjustment JV for posted errors.'
                                    : 'If you expected posted entries, check the source status, posting date, student scope, and date coverage before creating any correction.'}
                        </p>
                    </div>
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                    <Button type="button" variant="outline" size="sm" onClick={() => openGlTrail('trial', 'Trial Balance')} data-testid="gl-entries-open-trial">
                        <Scale className="w-4 h-4 mr-2" /> Trial Balance
                    </Button>
                    <Button type="button" variant="outline" size="sm" onClick={() => openGlTrail('balancesheet', 'Balance Sheet')} data-testid="gl-entries-open-balance-sheet">
                        <BarChart3 className="w-4 h-4 mr-2" /> Balance Sheet
                    </Button>
                    <Button type="button" variant="outline" size="sm" onClick={() => openGlTrail('incomestatement', 'Income Statement')} data-testid="gl-entries-open-income-statement">
                        <TrendingUp className="w-4 h-4 mr-2" /> Income Statement
                    </Button>
                </div>
            </div>
            
            {/* Results Grid */}
            {accountSections.length > 0 && (
                <div className="space-y-4" data-testid="gl-account-sections">
                    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                        <div className="px-6 py-4 border-b border-slate-200 bg-slate-50 flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
                            <div>
                                <h3 className="font-semibold text-slate-900">
                                    General Ledger ({accountSections.length} account{accountSections.length === 1 ? '' : 's'}, {entries.length} transaction{entries.length === 1 ? '' : 's'})
                                </h3>
                                <p className="text-xs text-slate-500 mt-1">Period covered: {reportPeriodLabel}</p>
                            </div>
                            <div className="flex flex-wrap items-center gap-4 text-sm">
                                <span>Total Debit: <strong className="text-emerald-600">{fmt(totals.debit)}</strong></span>
                                <span>Total Credit: <strong className="text-red-600">{fmt(totals.credit)}</strong></span>
                            </div>
                        </div>
                    </div>

                    {accountSections.map((section, sectionIdx) => (
                        <section
                            key={section.account_code || section.account_title || sectionIdx}
                            className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden"
                            data-testid={`gl-account-section-${sectionIdx}`}
                        >
                            <div className="border-b border-slate-200 bg-[#0F2A44] px-5 py-4 text-white">
                                <div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
                                    <div>
                                        <p className="text-[11px] uppercase tracking-[0.12em] text-blue-200">Account</p>
                                        <h4 className="text-lg font-semibold">
                                            {section.account_code || '-'} - {section.account_title || 'Unmapped account'}
                                        </h4>
                                    </div>
                                    <p className="text-xs text-blue-100">Period covered: {reportPeriodLabel}</p>
                                </div>
                            </div>

                            <div className="overflow-x-auto">
                                <table className="w-full min-w-[980px]">
                                    <thead className="bg-slate-50 text-xs text-slate-500 uppercase tracking-wider">
                                        <tr>
                                            <th className="px-4 py-3 text-left">Date</th>
                                            <th className="px-4 py-3 text-left">Transaction Code</th>
                                            <th className="px-4 py-3 text-left">Particulars</th>
                                            <th className="px-4 py-3 text-right">Debit</th>
                                            <th className="px-4 py-3 text-right">Credit</th>
                                            <th className="px-4 py-3 text-right">Running Balance</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100">
                                        <tr className="bg-slate-50/80" data-testid={`gl-balance-forwarded-${sectionIdx}`}>
                                            <td colSpan={5} className="px-4 py-3 text-sm italic text-slate-600">Bal. Forwarded</td>
                                            <td className="px-4 py-3 text-right font-mono text-sm font-semibold text-slate-800">
                                                {formatLedgerBalance(section.opening_balance)}
                                            </td>
                                        </tr>

                                        {(section.entries || []).map((entry, entryIdx) => {
                                            const isBankReconSource = entry.source_type === 'bank_reconciliation_adjustment';
                                            const sourceButtonLabels = {
                                                ar_invoice: 'Open AR Invoice',
                                                ar_invoice_reversal: 'Open AR Invoice',
                                                ar_collection: 'Open AR Collection',
                                                ar_collection_reversal: 'Open AR Collection',
                                                ap_voucher: 'Open AP Voucher',
                                                ap_voucher_reversal: 'Open AP Voucher',
                                                ap_payment: 'Open AP Payment',
                                                ap_payment_reversal: 'Open AP Payment',
                                            };
                                            const sourceButtonLabel = isBankReconSource
                                                ? 'Open Bank Recon JV'
                                                : sourceButtonLabels[entry.source_type] || 'Open Source';
                                            const canOpenSource = entry.ref_no || entry.jv_id || entry.source_id || entry.source_adjustment_id;
                                            const sourceRefForLabel = entry.ref_no || entry.source_id || entry.jv_id || entry.source_adjustment_id || 'source record';
                                            return (
                                                <tr
                                                    key={entry.id || `${sectionIdx}-${entryIdx}`}
                                                    className="hover:bg-slate-50"
                                                    data-testid={`gl-entry-${sectionIdx}-${entryIdx}`}
                                                >
                                                    <td className="px-4 py-3 text-sm">{new Date(entry.date).toLocaleDateString()}</td>
                                                    <td className="px-4 py-3 text-sm">
                                                        <div className="font-mono text-slate-800">{entry.ref_no || entry.jv_id || entry.source_id || '-'}</div>
                                                        <div className="text-[11px] text-slate-400">
                                                            {entry.source_location || 'Record > Journal Voucher'}
                                                        </div>
                                                        {entry.source_label && (
                                                            <span className={`mt-1 inline-flex rounded-full border px-2 py-0.5 text-[11px] font-semibold ${
                                                                isBankReconSource
                                                                    ? 'border-emerald-100 bg-emerald-50 text-emerald-700'
                                                                    : 'border-slate-200 bg-slate-50 text-slate-600'
                                                            }`}>
                                                                {entry.source_label}
                                                            </span>
                                                        )}
                                                        {isBankReconSource && entry.source_statement_reference && (
                                                            <div className="mt-1 text-[11px] text-emerald-700">
                                                                Statement {entry.source_statement_reference} - {entry.bank_reconciliation_matched ? 'source matched' : 'needs source match'}
                                                            </div>
                                                        )}
                                                        {canOpenSource && (
                                                            <Button
                                                                type="button"
                                                                variant="outline"
                                                                size="sm"
                                                                onClick={() => onOpenSource?.(entry)}
                                                                className={`mt-2 h-7 px-2 text-xs ${
                                                                    isBankReconSource
                                                                        ? 'border-emerald-200 text-emerald-700 hover:bg-emerald-50'
                                                                        : 'border-blue-100 text-[#0F2A44] hover:border-[#2F8FCB] hover:bg-blue-50'
                                                                }`}
                                                                data-testid={`gl-open-source-${sectionIdx}-${entryIdx}`}
                                                                aria-label={`${sourceButtonLabel} ${sourceRefForLabel}`}
                                                                title={`${sourceButtonLabel} ${sourceRefForLabel}`}
                                                            >
                                                                <Eye className="mr-1 h-3.5 w-3.5" /> {sourceButtonLabel}
                                                            </Button>
                                                        )}
                                                    </td>
                                                    <td className="px-4 py-3 text-sm">{entry.description || entry.account_title}</td>
                                                    <td className="px-4 py-3 text-right font-mono text-sm text-slate-700">
                                                        {entry.debit > 0 ? fmt(entry.debit) : ''}
                                                    </td>
                                                    <td className="px-4 py-3 text-right font-mono text-sm text-slate-700">
                                                        {entry.credit > 0 ? fmt(entry.credit) : ''}
                                                    </td>
                                                    <td className={`px-4 py-3 text-right font-mono text-sm font-semibold ${
                                                        getLedgerBalanceToneClass({
                                                            balance: entry.running_balance,
                                                            accountCode: section.account_code,
                                                            accountTitle: section.account_title || section.account,
                                                            normalBalanceLookup,
                                                        })
                                                    }`}>
                                                        {formatLedgerBalance(entry.running_balance)}
                                                    </td>
                                                </tr>
                                            );
                                        })}

                                        {(section.entries || []).length === 0 && (
                                            <tr>
                                                <td colSpan={6} className="px-4 py-6 text-center text-sm text-slate-500">
                                                    No posted movement inside this period. The account is shown because it has a Balance Forwarded.
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                    <tfoot>
                                        <tr className="bg-slate-100 font-semibold text-slate-800">
                                            <td colSpan={3} className="px-4 py-3">Account Total / Ending Balance</td>
                                            <td className="px-4 py-3 text-right font-mono">{fmt(section.period_debit || 0)}</td>
                                            <td className="px-4 py-3 text-right font-mono">{fmt(section.period_credit || 0)}</td>
                                            <td className="px-4 py-3 text-right font-mono">{formatLedgerBalance(section.ending_balance)}</td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>
                        </section>
                    ))}
                </div>
            )}

            {accountSections.length === 0 && entries.length > 0 && (
                <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                    <div className="px-6 py-4 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
                        <h3 className="font-semibold text-slate-900">
                            Posted Entry Lines ({entries.length} lines)
                        </h3>
                        <div className="flex items-center gap-4 text-sm">
                            <span>Total Debit: <strong className="text-emerald-600">{fmt(totals.debit)}</strong></span>
                            <span>Total Credit: <strong className="text-red-600">{fmt(totals.credit)}</strong></span>
                        </div>
                    </div>
                    
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-slate-50 text-xs text-slate-500 uppercase tracking-wider">
                                <tr>
                                    <th className="px-4 py-3 text-left">Date</th>
                                    <th className="px-4 py-3 text-left">Source Ref</th>
                                    <th className="px-4 py-3 text-left">GL Code</th>
                                    <th className="px-4 py-3 text-left">Description</th>
                                    <th className="px-4 py-3 text-right">Debit</th>
                                    <th className="px-4 py-3 text-right">Credit</th>
                                    <th className="px-4 py-3 text-right">Running Balance</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {entries.map((entry, idx) => {
                                    const isBankReconSource = entry.source_type === 'bank_reconciliation_adjustment';
                                    const sourceButtonLabels = {
                                        ar_invoice: 'Open AR Invoice',
                                        ar_invoice_reversal: 'Open AR Invoice',
                                        ar_collection: 'Open AR Collection',
                                        ar_collection_reversal: 'Open AR Collection',
                                        ap_voucher: 'Open AP Voucher',
                                        ap_voucher_reversal: 'Open AP Voucher',
                                        ap_payment: 'Open AP Payment',
                                        ap_payment_reversal: 'Open AP Payment',
                                    };
                                    const sourceButtonLabel = isBankReconSource
                                        ? 'Open Bank Recon JV'
                                        : sourceButtonLabels[entry.source_type] || 'Open Source';
                                    const canOpenSource = entry.ref_no || entry.jv_id || entry.source_id || entry.source_adjustment_id;
                                    const sourceRefForLabel = entry.ref_no || entry.source_id || entry.jv_id || entry.source_adjustment_id || 'source record';
                                    return (
                                        <tr key={entry.id || idx} className="hover:bg-slate-50" data-testid={`gl-entry-${idx}`}>
                                            <td className="px-4 py-3 text-sm">{new Date(entry.date).toLocaleDateString()}</td>
                                            <td className="px-4 py-3 text-sm">
                                                <div className="font-mono text-slate-800">{entry.ref_no || entry.jv_id || entry.source_id || '-'}</div>
                                                <div className="text-[11px] text-slate-400">
                                                    {entry.source_location || 'Record > Journal Voucher'}
                                                </div>
                                                {entry.source_label && (
                                                    <span className={`mt-1 inline-flex rounded-full border px-2 py-0.5 text-[11px] font-semibold ${
                                                        isBankReconSource
                                                            ? 'border-emerald-100 bg-emerald-50 text-emerald-700'
                                                            : 'border-slate-200 bg-slate-50 text-slate-600'
                                                    }`}>
                                                        {entry.source_label}
                                                    </span>
                                                )}
                                                {isBankReconSource && entry.source_statement_reference && (
                                                    <div className="mt-1 text-[11px] text-emerald-700">
                                                        Statement {entry.source_statement_reference} - {entry.bank_reconciliation_matched ? 'source matched' : 'needs source match'}
                                                    </div>
                                                )}
                                                {canOpenSource && (
                                                    <Button
                                                        type="button"
                                                        variant="outline"
                                                        size="sm"
                                                        onClick={() => onOpenSource?.(entry)}
                                                        className={`mt-2 h-7 px-2 text-xs ${
                                                            isBankReconSource
                                                                ? 'border-emerald-200 text-emerald-700 hover:bg-emerald-50'
                                                                : 'border-blue-100 text-[#0F2A44] hover:border-[#2F8FCB] hover:bg-blue-50'
                                                        }`}
                                                        data-testid={`gl-open-source-${idx}`}
                                                        aria-label={`${sourceButtonLabel} ${sourceRefForLabel}`}
                                                        title={`${sourceButtonLabel} ${sourceRefForLabel}`}
                                                    >
                                                        <Eye className="mr-1 h-3.5 w-3.5" /> {sourceButtonLabel}
                                                    </Button>
                                                )}
                                            </td>
                                            <td className="px-4 py-3 font-mono text-sm">
                                                {entry.account_code || '-'}
                                            </td>
                                            <td className="px-4 py-3 text-sm">{entry.description || entry.account_title}</td>
                                            <td className="px-4 py-3 text-right font-mono text-sm text-slate-700">
                                                {entry.debit > 0 ? fmt(entry.debit) : ''}
                                            </td>
                                            <td className="px-4 py-3 text-right font-mono text-sm text-slate-700">
                                                {entry.credit > 0 ? fmt(entry.credit) : ''}
                                            </td>
                                            <td className={`px-4 py-3 text-right font-mono text-sm font-medium ${
                                                getLedgerBalanceToneClass({
                                                    balance: entry.running_balance,
                                                    accountCode: entry.account_code,
                                                    accountTitle: entry.account_title || entry.account,
                                                    normalBalanceLookup,
                                                })
                                            }`}>
                                                {fmt(Math.abs(entry.running_balance))} {entry.running_balance < 0 ? 'Cr' : 'Dr'}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                            <tfoot>
                                <tr className="bg-[#0F2A44] text-white font-semibold">
                                    <td colSpan={4} className="px-4 py-3">Total</td>
                                    <td className="px-4 py-3 text-right font-mono">{fmt(totals.debit)}</td>
                                    <td className="px-4 py-3 text-right font-mono">{fmt(totals.credit)}</td>
                                    <td className="px-4 py-3"></td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                </div>
            )}
            
            {entries.length === 0 && accountSections.length === 0 && !loading && (
                <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-12 text-center">
                    <FileText className="w-16 h-16 text-slate-200 mx-auto mb-4" />
                    <p className="text-slate-500">No entries to display</p>
                    <p className="text-sm text-slate-400 mt-1">Set your filters and click Process to load GL entries</p>
                </div>
            )}
            
            {/* Preview Dialog */}
            <Dialog open={showPreview} onOpenChange={setShowPreview}>
                <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Printer className="w-5 h-5" />
                            General Ledger Preview - {GL_ENTRY_MODES.find(m => m.id === filters.mode)?.label}
                        </DialogTitle>
                        <DialogDescription>
                            Print preview of the General Ledger for the selected mode. This preview does not change source records, GL, or reports.
                        </DialogDescription>
                    </DialogHeader>
                    
                    {previewData && (
                        <div className="space-y-4 py-4">
                            {/* Report Header */}
                            <div className="text-center border-b border-slate-200 pb-4">
                                <h2 className="text-xl font-bold text-slate-900">{previewData.title}</h2>
                                <p className="text-sm text-slate-500 mt-1">
                                    Generated: {new Date(previewData.generated_at).toLocaleString()} by {previewData.generated_by}
                                </p>
                                <p className="text-sm text-slate-500">
                                    Mode: {GL_ENTRY_MODES.find(m => m.id === previewData.mode)?.label}
                                </p>
                            </div>
                            
                            {/* Mode-specific Content */}
                            {previewData.mode === 'single_line' && previewData.transactions && (
                                <table className="w-full text-sm">
                                    <thead className="bg-slate-100">
                                        <tr>
                                            <th className="px-3 py-2 text-left">Date</th>
                                            <th className="px-3 py-2 text-left">JV Number</th>
                                            <th className="px-3 py-2 text-left">Description</th>
                                            <th className="px-3 py-2 text-right">Total Debit</th>
                                            <th className="px-3 py-2 text-right">Total Credit</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {previewData.transactions.map((txn, idx) => (
                                            <tr key={idx} className="border-b">
                                                <td className="px-3 py-2">{new Date(txn.date).toLocaleDateString()}</td>
                                                <td className="px-3 py-2 font-mono">{txn.jv_number}</td>
                                                <td className="px-3 py-2">{txn.description}</td>
                                                <td className="px-3 py-2 text-right font-mono">{fmt(txn.total_debit)}</td>
                                                <td className="px-3 py-2 text-right font-mono">{fmt(txn.total_credit)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                            
                            {previewData.mode === 'book_check' && previewData.entries && (
                                <table className="w-full text-sm">
                                    <thead className="bg-slate-100">
                                        <tr>
                                            <th className="px-2 py-2 text-left">Date</th>
                                            <th className="px-2 py-2 text-left">Ref</th>
                                            <th className="px-2 py-2 text-left">Account</th>
                                            <th className="px-2 py-2 text-left">Description</th>
                                            <th className="px-2 py-2 text-left">Book Ref</th>
                                            <th className="px-2 py-2 text-left">Check Ref</th>
                                            <th className="px-2 py-2 text-right">Debit</th>
                                            <th className="px-2 py-2 text-right">Credit</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {previewData.entries.map((entry, idx) => (
                                            <tr key={idx} className="border-b">
                                                <td className="px-2 py-2">{new Date(entry.date).toLocaleDateString()}</td>
                                                <td className="px-2 py-2 font-mono text-xs">{entry.ref_no}</td>
                                                <td className="px-2 py-2 font-mono text-xs">{entry.account_code}</td>
                                                <td className="px-2 py-2">{entry.description}</td>
                                                <td className="px-2 py-2">{entry.book_ref || '-'}</td>
                                                <td className="px-2 py-2">{entry.check_ref || '-'}</td>
                                                <td className="px-2 py-2 text-right font-mono">{entry.debit > 0 ? fmt(entry.debit) : ''}</td>
                                                <td className="px-2 py-2 text-right font-mono">{entry.credit > 0 ? fmt(entry.credit) : ''}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                            
                            {(previewData.mode === 'standard' || previewData.mode === 'modified_a' || previewData.mode === 'modified_b') && previewSections && (
                                <div className="space-y-6">
                                    {previewSections.map((section, sIdx) => (
                                        <div key={sIdx} className={`${previewData.mode === 'modified_a' ? 'border-t-4 border-slate-300 pt-4' : ''}`}>
                                            <div className="rounded bg-slate-100 px-3 py-2">
                                                <h3 className="font-bold text-lg">
                                                    {section.account_code ? `${section.account_code} - ` : ''}{section.account_title || section.account}
                                                </h3>
                                                <p className="text-xs text-slate-500">Period covered: {getLedgerPeriodLabel(previewData.period || previewData.filters)}</p>
                                            </div>
                                            
                                            {section.sub_sections ? (
                                                section.sub_sections.map((sub, subIdx) => (
                                                    <div key={subIdx} className="ml-4 mt-2">
                                                        <h4 className="font-medium text-slate-700 bg-slate-50 px-2 py-1">{sub.sub1}</h4>
                                                        
                                                        {sub.sub2_sections ? (
                                                            sub.sub2_sections.map((sub2, sub2Idx) => (
                                                                <div key={sub2Idx} className="ml-4 mt-1">
                                                                    <h5 className="text-sm text-slate-600 px-2">{sub2.sub2}</h5>
                                                                    <div className="text-xs text-slate-500 px-2">
                                                                        Subtotal: Dr {fmt(sub2.subtotal_debit)} | Cr {fmt(sub2.subtotal_credit)}
                                                                    </div>
                                                                </div>
                                                            ))
                                                        ) : (
                                                            <div className="text-xs text-slate-500 px-2 ml-4">
                                                                Subtotal: Dr {fmt(sub.subtotal_debit)} | Cr {fmt(sub.subtotal_credit)}
                                                            </div>
                                                        )}
                                                    </div>
                                                ))
                                            ) : section.entries ? (
                                                <div className="mt-2">
                                                    <table className="w-full text-xs">
                                                        <thead className="bg-slate-50">
                                                            <tr>
                                                                <th className="px-2 py-1 text-left">Date</th>
                                                                <th className="px-2 py-1 text-left">Transaction Code</th>
                                                                <th className="px-2 py-1 text-left">Particulars</th>
                                                                <th className="px-2 py-1 text-right">Debit</th>
                                                                <th className="px-2 py-1 text-right">Credit</th>
                                                                <th className="px-2 py-1 text-right">Running Balance</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            <tr className="bg-slate-50">
                                                                <td colSpan={5} className="px-2 py-1 italic text-slate-600">Bal. Forwarded</td>
                                                                <td className="px-2 py-1 text-right font-mono font-semibold">{formatLedgerBalance(section.opening_balance)}</td>
                                                            </tr>
                                                            {section.entries.map((entry, eIdx) => (
                                                                <tr key={eIdx}>
                                                                    <td className="px-2 py-1">{new Date(entry.date).toLocaleDateString()}</td>
                                                                    <td className="px-2 py-1 font-mono">{entry.ref_no || entry.jv_id || entry.source_id || '-'}</td>
                                                                    <td className="px-2 py-1">{entry.description}</td>
                                                                    <td className="px-2 py-1 text-right font-mono">{entry.debit > 0 ? fmt(entry.debit) : ''}</td>
                                                                    <td className="px-2 py-1 text-right font-mono">{entry.credit > 0 ? fmt(entry.credit) : ''}</td>
                                                                    <td className="px-2 py-1 text-right font-mono">{formatLedgerBalance(entry.running_balance)}</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                    <div className="text-sm font-medium px-2 py-1 bg-slate-100 mt-1">
                                                        Account Total: Dr {fmt(section.period_debit ?? section.subtotal_debit)} | Cr {fmt(section.period_credit ?? section.subtotal_credit)} |
                                                        Ending Balance: {formatLedgerBalance(section.ending_balance)}
                                                    </div>
                                                </div>
                                            ) : null}
                                        </div>
                                    ))}
                                </div>
                            )}
                            
                            {/* Report Footer */}
                            <div className="border-t border-slate-300 pt-4 mt-4">
                                <div className="flex justify-between text-sm font-bold">
                                    <span>Grand Total:</span>
                                    <span>Debit: {fmt(previewData.total_debit)} | Credit: {fmt(previewData.total_credit)}</span>
                                </div>
                            </div>
                        </div>
                    )}
                    
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowPreview(false)}>
                            Close
                        </Button>
                        <Button onClick={handlePrintPreview} className="bg-[#2F8FCB]" data-testid="gl-print-standard-report">
                            <Printer className="w-4 h-4 mr-2" /> Print Standard GL
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

// ================= SUPPLIER LIST TAB =================

function JournalEntryTab({ form, setForm, handleSubmit, handleAI, aiLoading, entries, handleDelete, handleUnpost, handlePostDraft, fmt, currentStep, setCurrentStep }) {
    return (
        <div className="space-y-6">
            {/* Entry Form */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                <div className="bg-gradient-to-r from-[#0F2A44] to-[#1E3A8A] px-6 py-4 text-white">
                    <h2 className="font-semibold font-['Manrope'] flex items-center gap-2">
                        <Plus className="w-5 h-5" /> New Journal Entry
                    </h2>
                    <p className="text-white/70 text-sm mt-1">Record business transactions with double-entry bookkeeping</p>
                </div>
                
                <form onSubmit={(e) => handleSubmit(e, false)} className="p-6 space-y-6">
                    {/* Step indicator */}
                    <div className="flex items-center gap-2 mb-6">
                        {[1, 2, 3, 4].map((step) => (
                            <div key={step} className="flex items-center">
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                                    currentStep >= step ? 'bg-[#2F8FCB] text-white' : 'bg-slate-200 text-slate-500'
                                }`}>
                                    {currentStep > step ? <CheckCircle2 className="w-5 h-5" /> : step}
                                </div>
                                {step < 4 && <div className={`w-12 h-1 ${currentStep > step ? 'bg-[#2F8FCB]' : 'bg-slate-200'}`} />}
                            </div>
                        ))}
                        <span className="ml-2 text-sm text-slate-500">
                            {currentStep === 1 && 'Enter transaction details'}
                            {currentStep === 2 && 'Enter amount'}
                            {currentStep === 3 && 'Select accounts'}
                            {currentStep === 4 && 'Review & post'}
                        </span>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        <div>
                            <Label className="text-slate-700">Date <span className="text-red-500">*</span></Label>
                            <Input 
                                type="date" 
                                value={form.date} 
                                onChange={e => { setForm({...form, date: e.target.value}); if (currentStep < 2) setCurrentStep(1); }}
                                className="mt-1" 
                                required 
                                data-testid="entry-date"
                            />
                        </div>
                        <div className="lg:col-span-2">
                            <Label className="text-slate-700">Description <span className="text-red-500">*</span></Label>
                            <Input 
                                value={form.description} 
                                onChange={e => { setForm({...form, description: e.target.value}); if (e.target.value && currentStep < 2) setCurrentStep(2); }}
                                placeholder="e.g., Received cash payment from customer for services" 
                                className="mt-1" 
                                required 
                                data-testid="entry-desc"
                            />
                        </div>
                        <div>
                            <Label className="text-slate-700">Amount (PHP) <span className="text-red-500">*</span></Label>
                            <Input 
                                type="number" 
                                step="0.01" 
                                min="0.01"
                                value={form.amount} 
                                onChange={e => { setForm({...form, amount: e.target.value}); if (e.target.value && currentStep < 3) setCurrentStep(3); }}
                                placeholder="0.00" 
                                className="mt-1 font-mono text-right" 
                                required 
                                data-testid="entry-amount"
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                            <Label className="text-slate-700">Debit Account</Label>
                            <Select value={form.debit_account} onValueChange={v => { setForm({...form, debit_account: v}); if (form.credit_account) setCurrentStep(4); }}>
                                <SelectTrigger className="mt-1" data-testid="debit-select">
                                    <SelectValue placeholder="Select account to debit" />
                                </SelectTrigger>
                                <SelectContent>
                                    {ACCOUNTS.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="text-slate-700">Credit Account</Label>
                            <Select value={form.credit_account} onValueChange={v => { setForm({...form, credit_account: v}); if (form.debit_account) setCurrentStep(4); }}>
                                <SelectTrigger className="mt-1" data-testid="credit-select">
                                    <SelectValue placeholder="Select account to credit" />
                                </SelectTrigger>
                                <SelectContent>
                                    {ACCOUNTS.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="flex items-end">
                            <Button 
                                type="button" 
                                variant="outline" 
                                onClick={handleAI} 
                                disabled={aiLoading}
                                className="w-full rounded-lg border-[#2F8FCB] text-[#2F8FCB] hover:bg-[#E0F2FE]"
                                data-testid="ai-btn"
                            >
                                {aiLoading ? (
                                    <div className="w-4 h-4 border-2 border-[#2F8FCB] border-t-transparent rounded-full animate-spin" />
                                ) : (
                                    <><Sparkles className="w-4 h-4 mr-2" /> AI Suggest Accounts</>
                                )}
                            </Button>
                        </div>
                    </div>

                    <div className="flex items-center justify-between pt-4 border-t border-slate-200">
                        <div className="text-sm text-slate-500">
                            {form.debit_account && form.credit_account && form.amount && (
                                form.debit_account !== form.credit_account ? (
                                    <span className="text-emerald-600 flex items-center gap-1">
                                        <CheckCircle2 className="w-4 h-4" /> Debits = Credits (Balanced)
                                    </span>
                                ) : (
                                    <span className="text-red-600 flex items-center gap-1">
                                        <AlertCircle className="w-4 h-4" /> Error: Same account for Dr/Cr
                                    </span>
                                )
                            )}
                        </div>
                        <div className="flex gap-3">
                            <Button type="button" variant="outline" className="rounded-lg" onClick={() => setForm({ date: new Date().toISOString().split('T')[0], description: '', debit_account: '', credit_account: '', amount: '' })}>
                                <X className="w-4 h-4 mr-2" /> Clear
                            </Button>
                            <Button 
                                type="button" 
                                variant="outline" 
                                className="rounded-lg border-amber-300 text-amber-700 hover:bg-amber-50"
                                onClick={(e) => handleSubmit(e, true)}
                                data-testid="save-draft-btn"
                            >
                                <Save className="w-4 h-4 mr-2" /> Save Draft
                            </Button>
                            <Button type="submit" className="bg-[#0F2A44] hover:bg-[#1E3A8A] rounded-lg" data-testid="post-btn">
                                <FileDown className="w-4 h-4 mr-2" /> Post Entry
                            </Button>
                        </div>
                    </div>
                </form>
            </div>

            {/* Journal Entries Table */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
                    <h3 className="font-semibold text-slate-900">General Journal</h3>
                    <div className="flex items-center gap-4 text-sm">
                        <span className="text-slate-500">{entries.length} entries</span>
                        <div className="flex items-center gap-2">
                            <span className="w-2 h-2 bg-emerald-500 rounded-full"></span><span className="text-slate-500">Posted</span>
                            <span className="w-2 h-2 bg-amber-500 rounded-full"></span><span className="text-slate-500">Draft</span>
                            <span className="w-2 h-2 bg-red-500 rounded-full"></span><span className="text-slate-500">Reversed</span>
                        </div>
                    </div>
                </div>
                
                {entries.length === 0 ? (
                    <div className="p-12 text-center">
                        <FileText className="w-16 h-16 text-slate-200 mx-auto mb-4" />
                        <p className="text-slate-500">No journal entries yet</p>
                        <p className="text-sm text-slate-400 mt-1">Create your first entry above to get started</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-slate-50 text-xs text-slate-500 uppercase tracking-wider">
                                <tr>
                                    <th className="px-4 py-3 text-left">Ref #</th>
                                    <th className="px-4 py-3 text-left">Date</th>
                                    <th className="px-4 py-3 text-left">Description</th>
                                    <th className="px-4 py-3 text-left">Account</th>
                                    <th className="px-4 py-3 text-right">Debit</th>
                                    <th className="px-4 py-3 text-right">Credit</th>
                                    <th className="px-4 py-3 text-center">Status</th>
                                    <th className="px-4 py-3 text-center">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {entries.map((entry) => (
                                    <JournalEntryRows 
                                        key={entry.id} 
                                        entry={entry} 
                                        fmt={fmt} 
                                        onDelete={handleDelete}
                                        onUnpost={handleUnpost}
                                        onPostDraft={handlePostDraft}
                                    />
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}

function JournalEntryRows({ entry, fmt, onDelete, onUnpost, onPostDraft }) {
    const statusColors = {
        posted: 'bg-emerald-100 text-emerald-700',
        draft: 'bg-amber-100 text-amber-700',
        reversed: 'bg-red-100 text-red-700'
    };
    const status = entry.status || 'posted';
    
    return (
        <>
            <tr className={`hover:bg-slate-50 ${status === 'reversed' ? 'opacity-60' : ''}`} data-testid={`entry-${entry.id}`}>
                <td className="px-4 py-3 font-mono text-xs text-slate-500">{entry.ref_no || 'N/A'}</td>
                <td className="px-4 py-3 font-mono text-sm text-slate-600">{new Date(entry.date).toLocaleDateString()}</td>
                <td className="px-4 py-3 text-slate-900">{entry.description}</td>
                <td className="px-4 py-3 font-medium text-slate-900">{entry.debit_account}</td>
                <td className="px-4 py-3 text-right font-mono text-slate-900">{fmt(entry.amount)}</td>
                <td className="px-4 py-3"></td>
                <td className="px-4 py-3 text-center">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors[status]}`}>
                        {status}
                    </span>
                </td>
                <td className="px-4 py-3 text-center">
                    <div className="flex items-center justify-center gap-1">
                        {status === 'draft' && (
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <button 
                                        onClick={() => onPostDraft(entry)} 
                                        className="p-1.5 text-emerald-600 hover:bg-emerald-50 rounded-lg"
                                    >
                                        <CheckCircle2 className="w-4 h-4" />
                                    </button>
                                </TooltipTrigger>
                                <TooltipContent>Post this draft</TooltipContent>
                            </Tooltip>
                        )}
                        {status === 'posted' && (
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <button 
                                        onClick={() => onUnpost(entry)} 
                                        className="p-1.5 text-amber-600 hover:bg-amber-50 rounded-lg"
                                    >
                                        <Undo2 className="w-4 h-4" />
                                    </button>
                                </TooltipTrigger>
                                <TooltipContent>Reverse this entry</TooltipContent>
                            </Tooltip>
                        )}
                        {status === 'draft' && (
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <button 
                                        onClick={() => onDelete(entry.id, entry)} 
                                        className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </TooltipTrigger>
                                <TooltipContent>Delete draft</TooltipContent>
                            </Tooltip>
                        )}
                    </div>
                </td>
            </tr>
            <tr className={`bg-slate-50/50 ${status === 'reversed' ? 'opacity-60' : ''}`}>
                <td className="px-4 py-3"></td>
                <td className="px-4 py-3"></td>
                <td className="px-4 py-3"></td>
                <td className="px-4 py-3 font-medium text-slate-900 pl-8">{entry.credit_account}</td>
                <td className="px-4 py-3"></td>
                <td className="px-4 py-3 text-right font-mono text-slate-900">{fmt(entry.amount)}</td>
                <td className="px-4 py-3"></td>
                <td className="px-4 py-3"></td>
            </tr>
        </>
    );
}

function LedgerTab({ ledger, fmt, viewingStudentName, onTraceGl }) {
    const accounts = Object.entries(ledger);
    const entryCount = accounts.reduce((total, [, data]) => total + (data.entries?.length || 0), 0);
    const formatSourceRef = (entry = {}) => entry.ref_no || entry.jv_id || entry.source_id || entry.source_adjustment_id || '-';
    const formatSourceLocation = (entry = {}) => entry.source_location || entry.source_label || 'Record > Journal Voucher';
    const traceLedgerInGl = (label, accountCode = '', accountName = '', amount) => {
        if (!onTraceGl) return;
        onTraceGl({
            sourceReport: 'General Ledger',
            label,
            accountFrom: accountCode,
            accountTo: accountCode,
            dateFrom: '',
            dateTo: '',
            amount,
            accountLabel: accountCode ? `${accountCode} ${accountName}` : 'All accounts',
            scopeLabel: viewingStudentName || 'Your workspace',
            sourceLocation: 'Review > General Ledger',
            evidenceLocation: 'Review > General Ledger with Source Ref and Open Source buttons',
            impact: 'General Ledger is posted evidence only. Trial Balance, Balance Sheet, and Income Statement should agree when the same student scope and date coverage are used.',
            nextAction: 'Review row-level Source Ref in the canonical General Ledger, open the original source evidence, then use a reversal or adjustment JV if posted work needs correction.',
        });
    };
    const traceAllLedgerInGl = () => {
        traceLedgerInGl(
            viewingStudentName ? `${viewingStudentName}'s posted ledger` : 'Posted ledger',
            '',
            '',
            null
        );
    };
    const LedgerTrail = () => (
        <ReportWorkflowTrail
            title="General Ledger trail"
            testId="general-ledger-workflow-trail"
            scope={viewingStudentName ? `Reviewing ${viewingStudentName}` : 'Your workspace'}
            items={[
                {
                    label: 'Source records',
                    value: 'Uses posted GL entries from posted JVs, AR/AP documents, opening balances, and adjustment sources.'
                },
                {
                    label: 'Where to verify',
                    value: 'Use the canonical General Ledger to see row-level Source Ref buttons, then open the original JV, AR/AP, bank recon, or opening balance evidence.'
                },
                {
                    label: 'GL / Trial Balance',
                    value: 'This is the posted ledger. Trial Balance and financial reports summarize these same posted entries.'
                },
                {
                    label: 'Next safe action',
                    value: 'Review the account activity, trace unusual balances to source records, then post an adjustment JV if correction is needed.'
                }
            ]}
        />
    );
    
    if (accounts.length === 0) {
        return (
            <div className="space-y-4" data-testid="ledger-tab">
                <LedgerTrail />
                <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
                    <BookOpen className="w-16 h-16 text-slate-200 mx-auto mb-4" />
                    <p className="text-slate-500">No ledger data available</p>
                    <p className="text-sm text-slate-400 mt-1">Post journal entries or source documents to populate the ledger</p>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-4" data-testid="ledger-tab">
            <LedgerTrail />
            <div className="rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-600">
                <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                    <div>
                        Showing {accounts.length} ledger account{accounts.length === 1 ? '' : 's'} and {entryCount} posted entr{entryCount === 1 ? 'y' : 'ies'}
                        {viewingStudentName ? <> for <strong className="text-slate-900">{viewingStudentName}</strong></> : null}.
                        <span className="ml-2 inline-flex rounded-full border border-emerald-100 bg-emerald-50 px-2 py-0.5 text-xs font-semibold text-emerald-700">
                            Posted evidence only
                        </span>
                    </div>
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={traceAllLedgerInGl}
                        data-testid="ledger-trace-all-gl-entries"
                        aria-label="Open canonical General Ledger"
                        title="Open canonical General Ledger"
                    >
                        <BookOpen className="w-4 h-4 mr-2" /> Open Canonical Ledger
                    </Button>
                </div>
            </div>
            {accounts.map(([account, data]) => (
                <div key={account} className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                    <div className="bg-slate-50 px-6 py-3 border-b border-slate-200 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                        <div>
                            <h3 className="font-semibold text-slate-900">{account}</h3>
                            <p className="text-xs text-slate-500">
                                {data.account_code ? `Account code ${data.account_code}` : 'Account code not available'} | Source details continue in the canonical General Ledger
                            </p>
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                            <span className="text-sm font-mono text-slate-600">
                                Balance: {fmt(data.debit - data.credit)}
                            </span>
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => traceLedgerInGl(
                                    `${data.account_code ? `${data.account_code} ` : ''}${account}`,
                                    data.account_code || '',
                                    account,
                                    data.debit - data.credit
                                )}
                                data-testid={`ledger-trace-account-${String(data.account_code || account).replace(/[^a-zA-Z0-9_-]/g, '-')}`}
                                aria-label={`Trace ${account} in the canonical General Ledger`}
                                title={`Trace ${account} in the canonical General Ledger`}
                            >
                                <BookOpen className="w-3.5 h-3.5 mr-1.5" /> Trace
                            </Button>
                        </div>
                    </div>
                    <table className="w-full">
                        <thead className="bg-slate-50 text-xs text-slate-500 uppercase">
                            <tr>
                                <th className="px-4 py-2 text-left">Date</th>
                                <th className="px-4 py-2 text-left">Source Ref</th>
                                <th className="px-4 py-2 text-left">Description</th>
                                <th className="px-4 py-2 text-right">Debit</th>
                                <th className="px-4 py-2 text-right">Credit</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {data.entries.map((en, i) => (
                                <tr key={i} className="hover:bg-slate-50">
                                    <td className="px-4 py-2 font-mono text-sm">{new Date(en.date).toLocaleDateString()}</td>
                                    <td className="px-4 py-2 text-sm">
                                        <div className="font-mono text-slate-800">{formatSourceRef(en)}</div>
                                        <div className="text-[11px] text-slate-400">{formatSourceLocation(en)}</div>
                                    </td>
                                    <td className="px-4 py-2">{en.description}</td>
                                    <td className="px-4 py-2 text-right font-mono">{en.type === 'debit' ? fmt(en.amount) : ''}</td>
                                    <td className="px-4 py-2 text-right font-mono">{en.type === 'credit' ? fmt(en.amount) : ''}</td>
                                </tr>
                            ))}
                            <tr className="bg-[#0F2A44] text-white font-semibold">
                                <td colSpan={3} className="px-4 py-2">Total</td>
                                <td className="px-4 py-2 text-right font-mono">{fmt(data.debit)}</td>
                                <td className="px-4 py-2 text-right font-mono">{fmt(data.credit)}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            ))}
        </div>
    );
}

function TrialBalanceTab({
    trialBalance,
    fmt,
    asOfDate,
    onAsOfDateChange,
    traceRequest,
    onClearTrace,
    viewingStudentName,
    journalVouchers = [],
    selectedCourse,
    chartOfAccounts,
    onRefresh,
    isReadOnly,
    permissions,
    user,
    onNavigate,
    onTraceGl,
    onOpenBankReconPeriod,
}) {
    const accounts = trialBalance?.accounts || [];
    const totalDebit = trialBalance?.total_debit || 0;
    const totalCredit = trialBalance?.total_credit || 0;
    const [showOpeningBalanceForm, setShowOpeningBalanceForm] = useState(false);
    const reportDate = asOfDate || trialBalance?.as_of_date || new Date().toISOString().split('T')[0];
    const reportDateParts = String(reportDate || '').slice(0, 10).split('-').map(part => parseInt(part, 10));
    const fallbackDate = new Date();
    const initialTrialYear = Number.isFinite(reportDateParts[0]) ? reportDateParts[0] : fallbackDate.getFullYear();
    const initialTrialMonth = Number.isFinite(reportDateParts[1]) ? reportDateParts[1] : fallbackDate.getMonth() + 1;
    const [trialDateMode, setTrialDateMode] = useState('annual');
    const [trialDateMonth, setTrialDateMonth] = useState(initialTrialMonth);
    const [trialDateYear, setTrialDateYear] = useState(initialTrialYear);
    const formatReportDate = (value) => {
        if (!value) return new Date().toLocaleDateString('en-PH');
        const parsed = new Date(`${value}T00:00:00`);
        return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleDateString('en-PH');
    };
    const reportDateLabel = formatReportDate(reportDate);
    const reportDateKey = String(reportDate || '').slice(0, 10);
    const traceAsOfDateLabel = traceRequest?.asOfDate
        ? formatReportDate(String(traceRequest.asOfDate).slice(0, 10))
        : reportDateLabel;
    const tracePeriod = String(traceRequest?.asOfDate || reportDate || '').slice(0, 7);

    const openingBalanceVouchers = useMemo(() => {
        const toDateKey = (value) => String(value || '').slice(0, 10);
        const isOpeningBalanceVoucher = (voucher) => {
            const source = String(voucher?.source || '').toLowerCase();
            const reference = String(voucher?.reference || '').toLowerCase();
            const particulars = String(voucher?.particulars || '').toLowerCase();
            return source === 'opening_balance'
                || reference.includes('opening-balance')
                || reference.includes('opening balance')
                || particulars.includes('opening balance')
                || particulars.includes('opening balances');
        };
        const statusRank = {
            posted: 0,
            submitted: 1,
            returned: 2,
            saved: 3,
        };

        return (journalVouchers || [])
            .filter(isOpeningBalanceVoucher)
            .map((voucher) => {
                const dateKey = toDateKey(voucher.date);
                const status = String(voucher.status || '').toLowerCase();
                const reviewStatus = String(voucher.review_status || '').toLowerCase();
                const posted = status === 'posted';
                const includedInReport = posted && dateKey && reportDateKey && dateKey <= reportDateKey;
                const amount = Number(voucher.total_debit || voucher.total || 0);

                if (posted && includedInReport) {
                    return {
                        ...voucher,
                        dateKey,
                        amount,
                        workflowStatus: status,
                        statusLabel: 'Posted',
                        statusClass: 'bg-emerald-100 text-emerald-700 border-emerald-200',
                        trailLabel: 'Included in this Trial Balance',
                        trailClass: 'bg-emerald-50 text-emerald-700 border-emerald-200',
                        reviewNext: 'No reviewer needed. This source JV is posted.',
                        impact: 'Affects GL, Trial Balance, and reports for this as-of date.',
                        nextSafeAction: 'Use this Trial Balance as posted evidence, or open the JV list if you need the source voucher.',
                    };
                }

                if (posted) {
                    return {
                        ...voucher,
                        dateKey,
                        amount,
                        workflowStatus: status,
                        statusLabel: 'Posted',
                        statusClass: 'bg-emerald-100 text-emerald-700 border-emerald-200',
                        trailLabel: 'Posted, outside selected date',
                        trailClass: 'bg-blue-50 text-blue-700 border-blue-200',
                        reviewNext: 'No reviewer needed. The selected Trial Balance date is earlier than this JV.',
                        impact: `Affects GL and Trial Balance when the as-of date is ${dateKey || 'the JV date'} or later.`,
                        nextSafeAction: 'Set the Trial Balance date to the JV date or later to include it.',
                    };
                }

                if (reviewStatus === 'pending' || status === 'submitted') {
                    return {
                        ...voucher,
                        dateKey,
                        amount,
                        workflowStatus: status,
                        statusLabel: 'Submitted for Review',
                        statusClass: 'bg-blue-100 text-blue-700 border-blue-200',
                        trailLabel: 'Not included yet',
                        trailClass: 'bg-amber-50 text-amber-700 border-amber-200',
                        reviewNext: 'Instructor/admin reviews next in the Instructor JV Review Queue.',
                        impact: 'No GL, Trial Balance, or report impact until posted.',
                        nextSafeAction: 'Wait for review, or open the JV list to check feedback/status.',
                    };
                }

                if (reviewStatus === 'returned') {
                    return {
                        ...voucher,
                        dateKey,
                        amount,
                        workflowStatus: status,
                        statusLabel: 'Returned for Revision',
                        statusClass: 'bg-rose-100 text-rose-700 border-rose-200',
                        trailLabel: 'Revision needed',
                        trailClass: 'bg-rose-50 text-rose-700 border-rose-200',
                        reviewNext: 'Student revises and resubmits before instructor posting.',
                        impact: 'No GL, Trial Balance, or report impact until revised, approved, and posted.',
                        nextSafeAction: 'Open Record > Journal Voucher, read feedback, revise, then resubmit.',
                    };
                }

                return {
                    ...voucher,
                    dateKey,
                    amount,
                    workflowStatus: status,
                    statusLabel: 'Saved Draft',
                    statusClass: 'bg-amber-100 text-amber-700 border-amber-200',
                    trailLabel: 'Draft only',
                    trailClass: 'bg-amber-50 text-amber-700 border-amber-200',
                    reviewNext: 'Submit for instructor review, or post if you have posting permission.',
                    impact: 'Draft only. No GL, Trial Balance, or report impact yet.',
                    nextSafeAction: 'Open Record > Journal Voucher and submit or post when ready.',
                };
            })
            .sort((a, b) => {
                const rankA = statusRank[a.workflowStatus] ?? 9;
                const rankB = statusRank[b.workflowStatus] ?? 9;
                if (rankA !== rankB) return rankA - rankB;
                return String(b.dateKey || '').localeCompare(String(a.dateKey || ''));
            });
    }, [journalVouchers, reportDateKey]);

    const openingBalanceSummary = useMemo(() => {
        return openingBalanceVouchers.reduce((summary, voucher) => {
            if (voucher.trailLabel === 'Included in this Trial Balance') summary.included += 1;
            if (voucher.workflowStatus === 'posted' && voucher.trailLabel !== 'Included in this Trial Balance') summary.postedOutsideDate += 1;
            if (voucher.workflowStatus !== 'posted') summary.openWork += 1;
            return summary;
        }, { included: 0, postedOutsideDate: 0, openWork: 0 });
    }, [openingBalanceVouchers]);

    const openOpeningBalances = () => setShowOpeningBalanceForm((current) => !current);
    const openJournalVoucherList = () => onNavigate?.('jv');
    const setDateToOpeningBalance = (dateKey) => {
        if (dateKey) onAsOfDateChange?.(dateKey);
    };

    const openTrialTrail = (tab, label) => {
        onNavigate?.(tab);
        toast.info(`Opening ${label}. Use the same student scope and date coverage as this Trial Balance to verify the posted totals.`);
    };

    const traceTrialBalanceAccount = (account) => {
        if (!account?.account_code) {
            toast.info('This Trial Balance row does not have an account code to trace yet. Open General Ledger and filter by account name if needed.');
            return;
        }
        const debit = Number(account.debit || 0);
        const credit = Number(account.credit || 0);
        const amount = debit > 0 ? debit : credit;
        const balanceSide = debit > 0 ? 'Debit' : credit > 0 ? 'Credit' : 'Zero';
        onTraceGl?.({
            sourceReport: 'Trial Balance',
            label: `${account.account_code} - ${account.account}`,
            dateFrom: '',
            dateTo: reportDateKey,
            accountFrom: account.account_code,
            accountTo: account.account_code,
            accountLabel: `${account.account_code} - ${account.account}`,
            scopeLabel: viewingStudentName || 'Current workspace',
            amount,
            evidenceLocation: 'Review > General Ledger with Source Ref and Open Source buttons',
            nextAction: `Review posted ${balanceSide.toLowerCase()} movement for this account, then open Source Ref before creating any correction.`,
        });
    };

    const handlePrint = () => {
        const escapeHtml = (value) => String(value ?? '')
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#039;');
        const dateStr = reportDateLabel;
        const isBalanced = Math.abs(totalDebit - totalCredit) < 0.01;
        const rows = accounts.map((a) => `
            <tr>
                <td class="code">${escapeHtml(a.account_code || '-')}</td>
                <td>${escapeHtml(a.account)}</td>
                <td class="amount">${a.debit > 0 ? escapeHtml(fmt(a.debit)) : ''}</td>
                <td class="amount">${a.credit > 0 ? escapeHtml(fmt(a.credit)) : ''}</td>
            </tr>
        `).join('');
        const printWindow = window.open('', '_blank', 'width=900,height=700');
        if (!printWindow) {
            toast.error('Print preview was blocked. Please allow pop-ups for MaceSuite.');
            return;
        }
        printWindow.document.write(`
            <!doctype html>
            <html>
                <head>
                    <title>MaceSuite Trial Balance - ${escapeHtml(dateStr)}</title>
                    <style>
                        @page { size: A4 portrait; margin: 16mm; }
                        * { box-sizing: border-box; }
                        body {
                            margin: 0;
                            color: #0f172a;
                            font-family: Arial, Helvetica, sans-serif;
                            font-size: 12px;
                        }
                        .report-header {
                            border-bottom: 2px solid #0f2a44;
                            margin-bottom: 18px;
                            padding-bottom: 12px;
                        }
                        .brand {
                            color: #0f2a44;
                            font-size: 20px;
                            font-weight: 700;
                            margin-bottom: 10px;
                        }
                        h1 {
                            font-size: 18px;
                            margin: 0 0 4px;
                        }
                        .meta {
                            color: #475569;
                            font-size: 12px;
                        }
                        table {
                            width: 100%;
                            border-collapse: collapse;
                        }
                        th {
                            background: #e2e8f0;
                            color: #334155;
                            font-size: 11px;
                            letter-spacing: 0.04em;
                            padding: 8px;
                            text-align: left;
                            text-transform: uppercase;
                        }
                        td {
                            border-bottom: 1px solid #e2e8f0;
                            padding: 8px;
                            vertical-align: top;
                        }
                        .amount {
                            font-family: Consolas, 'Courier New', monospace;
                            text-align: right;
                            white-space: nowrap;
                        }
                        .code {
                            color: #475569;
                            font-family: Consolas, 'Courier New', monospace;
                            white-space: nowrap;
                            width: 72px;
                        }
                        tfoot td {
                            background: #0f2a44;
                            color: #fff;
                            font-weight: 700;
                            border-bottom: none;
                        }
                        .status {
                            margin-top: 14px;
                            padding: 10px 12px;
                            border: 1px solid ${isBalanced ? '#a7f3d0' : '#fecaca'};
                            background: ${isBalanced ? '#ecfdf5' : '#fef2f2'};
                            color: ${isBalanced ? '#047857' : '#dc2626'};
                            font-weight: 600;
                        }
                        .footer {
                            margin-top: 24px;
                            color: #64748b;
                            font-size: 10px;
                            display: flex;
                            justify-content: space-between;
                        }
                    </style>
                </head>
                <body>
                    <div class="report-header">
                        <div class="brand">MaceSuite</div>
                        <h1>Trial Balance</h1>
                        <div class="meta">As of ${escapeHtml(dateStr)}</div>
                    </div>
                    <table>
                        <thead>
                            <tr>
                                <th class="code">Code</th>
                                <th>Account</th>
                                <th class="amount">Debit</th>
                                <th class="amount">Credit</th>
                            </tr>
                        </thead>
                        <tbody>${rows}</tbody>
                        <tfoot>
                            <tr>
                                <td></td>
                                <td>Total</td>
                                <td class="amount">${escapeHtml(fmt(totalDebit))}</td>
                                <td class="amount">${escapeHtml(fmt(totalCredit))}</td>
                            </tr>
                        </tfoot>
                    </table>
                    <div class="status">
                        ${isBalanced
                            ? 'Debits equal credits. Trial balance is in balance.'
                            : `Out of balance by ${escapeHtml(fmt(Math.abs(totalDebit - totalCredit)))}.`}
                    </div>
                    <div class="footer">
                        <span>Generated by MaceSuite</span>
                        <span>${escapeHtml(new Date().toLocaleString('en-PH'))}</span>
                    </div>
                    <script>
                        window.addEventListener('load', () => {
                            window.focus();
                            window.print();
                        });
                    </script>
                </body>
            </html>
        `);
        printWindow.document.close();
    };

    const handleExportCSV = () => {
        const dateStr = reportDateLabel;
        const rows = [
            ['MACESUITE - TRIAL BALANCE'],
            [`As of ${dateStr}`],
            [],
            ['Code', 'Account', 'Debit', 'Credit'],
        ];
        accounts.forEach(a => {
            rows.push([a.account_code || '-', a.account, a.debit > 0 ? a.debit.toFixed(2) : '', a.credit > 0 ? a.credit.toFixed(2) : '']);
        });
        rows.push([]);
        rows.push(['', 'TOTAL', totalDebit.toFixed(2), totalCredit.toFixed(2)]);

        const csv = rows.map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `trial-balance-${new Date().toISOString().split('T')[0]}.csv`;
        link.click();
        URL.revokeObjectURL(url);
        toast.success('Trial Balance CSV exported for the selected as-of date. Export only; GL, Trial Balance, and source records were not changed.');
    };

    const setTrialBalanceAsOfDate = (nextDate) => {
        if (nextDate) {
            onAsOfDateChange?.(nextDate);
        }
    };

    const applyTrialDateMode = (mode) => {
        setTrialDateMode(mode);
        if (mode === 'monthly') {
            setTrialBalanceAsOfDate(getMonthEndDate(trialDateYear, trialDateMonth));
            return;
        }
        if (mode === 'annual') {
            setTrialBalanceAsOfDate(`${trialDateYear}-12-31`);
        }
    };

    const handleTrialDateMonthChange = (month) => {
        setTrialDateMode('monthly');
        setTrialDateMonth(month);
        setTrialBalanceAsOfDate(getMonthEndDate(trialDateYear, month));
    };

    const handleTrialDateYearChange = (year) => {
        setTrialDateYear(year);
        const nextDate = trialDateMode === 'annual'
            ? `${year}-12-31`
            : getMonthEndDate(year, trialDateMonth);
        setTrialBalanceAsOfDate(nextDate);
    };

    const handleCustomTrialDateChange = (nextDate) => {
        setTrialDateMode('custom');
        setTrialBalanceAsOfDate(nextDate);
    };

    const TrialBalanceTrail = () => (
        <ReportWorkflowTrail
            title="Trial Balance trail"
            testId="trial-balance-workflow-trail"
            scope={viewingStudentName ? `Reviewing ${viewingStudentName}` : 'Your workspace'}
            items={[
                {
                    label: 'Source records',
                    value: `Uses posted GL entries dated on or before ${reportDateLabel}.`
                },
                {
                    label: 'Where to verify',
                    value: 'Use Trace GL on a Trial Balance row, then open Source Ref from General Ledger to reach the original JV/AR/AP document.'
                },
                {
                    label: 'GL / Trial Balance',
                    value: 'Drafts, saved vouchers, submitted work, and returned work do not affect Trial Balance until posted.'
                },
                {
                    label: 'Next safe action',
                    value: 'Set the as-of date, review opening balance status, then post or correct the source document if a balance is missing.'
                }
            ]}
        />
    );

    const TrialBalanceTraceBanner = () => {
        if (!traceRequest) return null;
        return (
            <div className="rounded-lg border border-indigo-100 bg-indigo-50 p-4" data-testid="trial-balance-close-trace-banner">
                <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                    <div>
                        <p className="text-xs font-semibold uppercase tracking-widest text-indigo-600">Close Review Handoff</p>
                        <h3 className="mt-1 font-semibold text-indigo-950">
                            {traceRequest.sourceReport || 'Posted evidence'}: {traceRequest.label || 'Selected evidence'}
                        </h3>
                        <p className="mt-1 text-sm text-indigo-800">
                            As of {traceAsOfDateLabel}. Scope: {traceRequest.scopeLabel || viewingStudentName || 'Current workspace'}.
                        </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => tracePeriod && onOpenBankReconPeriod
                                ? onOpenBankReconPeriod(tracePeriod, 'Trial Balance handoff')
                                : openTrialTrail('bankrecon', 'Bank Reconciliation')}
                            data-testid="trial-trace-open-bank-recon"
                        >
                            <Building2 className="w-4 h-4 mr-2" /> Bank Recon Evidence
                        </Button>
                        <Button type="button" variant="outline" size="sm" onClick={() => openTrialTrail('glentries', 'General Ledger')} data-testid="trial-trace-open-gl">
                            <FileText className="w-4 h-4 mr-2" /> General Ledger
                        </Button>
                        {onClearTrace && (
                            <Button type="button" variant="outline" size="sm" onClick={onClearTrace} data-testid="trial-trace-clear">
                                <X className="w-4 h-4 mr-2" /> Clear Trail
                            </Button>
                        )}
                    </div>
                </div>
                <div className="mt-3 grid gap-2 md:grid-cols-4">
                    <div className="rounded-md border border-indigo-100 bg-white/80 p-3">
                        <p className="text-[11px] font-semibold uppercase text-indigo-600">Where it came from</p>
                        <p className="mt-1 text-xs text-slate-700">{traceRequest.sourceLocation || traceRequest.sourceReport || 'Close review evidence'}</p>
                    </div>
                    <div className="rounded-md border border-indigo-100 bg-white/80 p-3">
                        <p className="text-[11px] font-semibold uppercase text-indigo-600">Where to verify</p>
                        <p className="mt-1 text-xs text-slate-700">{traceRequest.evidenceLocation || 'Review > General Ledger and Bank Reconciliation evidence'}</p>
                    </div>
                    <div className="rounded-md border border-indigo-100 bg-white/80 p-3">
                        <p className="text-[11px] font-semibold uppercase text-indigo-600">GL / report effect</p>
                        <p className="mt-1 text-xs text-slate-700">{traceRequest.impact || 'Only posted GL entries affect this Trial Balance.'}</p>
                    </div>
                    <div className="rounded-md border border-indigo-100 bg-white/80 p-3">
                        <p className="text-[11px] font-semibold uppercase text-indigo-600">Next safe action</p>
                        <p className="mt-1 text-xs text-slate-700">{traceRequest.nextAction || 'Compare balances to General Ledger and source evidence before any correction.'}</p>
                    </div>
                </div>
                {traceRequest.reviewer && (
                    <p className="mt-3 text-xs text-indigo-700">
                        Reviewer path: {traceRequest.reviewer}
                    </p>
                )}
            </div>
        );
    };

    const DateFilter = () => (
        <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm" data-testid="trial-balance-date-filter">
            <div className="grid gap-4 lg:grid-cols-[1fr_360px] lg:items-start">
                <ReportPeriodSelector
                    mode={trialDateMode}
                    onModeChange={applyTrialDateMode}
                    month={trialDateMonth}
                    year={trialDateYear}
                    onMonthChange={handleTrialDateMonthChange}
                    onYearChange={handleTrialDateYearChange}
                    asOfDate={asOfDate || reportDate}
                    onAsOfDateChange={handleCustomTrialDateChange}
                    customMode="single"
                    title="Trial Balance Date Coverage"
                    effectText="Trial Balance includes posted GL entries dated on or before the selected as-of date."
                    testId="trial-balance-date-coverage"
                />
                <div className="rounded-lg bg-blue-50 border border-blue-100 px-4 py-3 text-sm text-blue-900">
                    <strong>Report scope:</strong> Posted GL entries dated on or before <strong>{reportDateLabel}</strong>
                    {viewingStudentName ? <> for <strong>{viewingStudentName}</strong></> : null}. Saved or draft vouchers do not appear until posted.
                </div>
            </div>
        </div>
    );

    const OpeningBalanceSetupPanel = () => (
        <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm" data-testid="trial-opening-balance-panel">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                <div className="flex items-start gap-3">
                    <div className="rounded-lg bg-blue-50 border border-blue-100 p-2 text-blue-700">
                        <FileText className="w-5 h-5" />
                    </div>
                    <div>
                        <div className="flex flex-wrap items-center gap-2">
                            <h3 className="font-semibold text-slate-900">Opening Balance Setup</h3>
                            <span className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-xs font-semibold text-slate-600">
                                Source JVs
                            </span>
                        </div>
                        <p className="mt-1 text-sm text-slate-600">
                            Trial Balance is generated from posted GL entries. Opening balances stay traceable as Journal Vouchers and appear here by status.
                        </p>
                        <p className="mt-1 text-sm text-slate-600">
                            {isReadOnly
                                ? 'Opening Balance Setup is not a second ledger; this review scope opens read-only source evidence only.'
                                : 'Opening Balance Setup is not a second ledger; it is a guided way to create the beginning-balance JV correctly.'}
                        </p>
                        <p className="mt-1 text-xs text-slate-500">
                            Scope: {viewingStudentName ? `${viewingStudentName}'s workspace` : 'current workspace/course'}.
                        </p>
                    </div>
                </div>
                <div className="flex flex-wrap gap-2">
                    <Button type="button" onClick={openOpeningBalances} className="bg-[#2F8FCB] hover:bg-[#2577a8]" data-testid="tb-create-opening-balance">
                        {showOpeningBalanceForm ? <ChevronDown className="w-4 h-4 mr-2" /> : isReadOnly ? <Eye className="w-4 h-4 mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                        {showOpeningBalanceForm ? (isReadOnly ? 'Hide Review' : 'Hide Setup') : isReadOnly ? 'Review Setup Evidence' : 'Open Setup Here'}
                    </Button>
                    <Button type="button" variant="outline" onClick={openJournalVoucherList} data-testid="tb-open-jv-list">
                        <ArrowRight className="w-4 h-4 mr-2" /> {isReadOnly ? 'Review JV List' : 'Open JV List'}
                    </Button>
                </div>
            </div>

            <div className="mt-4 grid gap-3 md:grid-cols-3">
                <div className="rounded-lg border border-emerald-100 bg-emerald-50 p-3">
                    <p className="text-xs font-semibold uppercase text-emerald-700">Included now</p>
                    <p className="mt-1 text-2xl font-semibold text-emerald-800">{openingBalanceSummary.included}</p>
                    <p className="text-xs text-emerald-700">Posted opening-balance JVs dated on or before {reportDateLabel}.</p>
                </div>
                <div className="rounded-lg border border-blue-100 bg-blue-50 p-3">
                    <p className="text-xs font-semibold uppercase text-blue-700">Posted later</p>
                    <p className="mt-1 text-2xl font-semibold text-blue-800">{openingBalanceSummary.postedOutsideDate}</p>
                    <p className="text-xs text-blue-700">Posted, but outside the selected Trial Balance date.</p>
                </div>
                <div className="rounded-lg border border-amber-100 bg-amber-50 p-3">
                    <p className="text-xs font-semibold uppercase text-amber-700">Needs action</p>
                    <p className="mt-1 text-2xl font-semibold text-amber-800">{openingBalanceSummary.openWork}</p>
                    <p className="text-xs text-amber-700">Saved, submitted, or returned JVs not yet affecting GL/TB.</p>
                </div>
            </div>

            {openingBalanceVouchers.length === 0 ? (
                <div className="mt-4 rounded-lg border border-dashed border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
                    <p className="font-semibold text-slate-800">No opening balance JV found for this scope.</p>
                    <p className="mt-1">
                        {isReadOnly
                            ? 'Review note: no opening balance JV exists for this scope. If balances are expected, ask the student or preparer to create and submit the source JV in their own workspace.'
                            : 'Next safe action: create an Opening Balance JV, then save/submit/post it through the JV workflow. It will appear here immediately and affect Trial Balance only after posting.'}
                    </p>
                </div>
            ) : (
                <div className="mt-4 divide-y divide-slate-100 rounded-lg border border-slate-100">
                    {openingBalanceVouchers.slice(0, 5).map((voucher) => (
                        <div key={voucher.id || voucher.jv_number} className="p-3">
                            <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
                                <div className="min-w-0">
                                    <div className="flex flex-wrap items-center gap-2">
                                        <span className="font-mono text-sm font-semibold text-slate-900">{voucher.jv_number || 'Saved JV'}</span>
                                        <span className={`rounded-full border px-2 py-0.5 text-xs font-semibold ${voucher.statusClass}`}>
                                            {voucher.statusLabel}
                                        </span>
                                        <span className={`rounded-full border px-2 py-0.5 text-xs font-semibold ${voucher.trailClass}`}>
                                            {voucher.trailLabel}
                                        </span>
                                    </div>
                                    <p className="mt-1 truncate text-sm text-slate-600">
                                        {voucher.dateKey ? formatReportDate(voucher.dateKey) : 'No date'} - {voucher.reference || 'Opening balance'} - {fmt(voucher.amount)}
                                    </p>
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    <Button type="button" variant="outline" className="h-8 px-3 text-xs" onClick={() => setDateToOpeningBalance(voucher.dateKey)}>
                                        <Scale className="w-4 h-4 mr-2" /> Use Date
                                    </Button>
                                    <Button type="button" variant="outline" className="h-8 px-3 text-xs" onClick={openJournalVoucherList}>
                                        <FileText className="w-4 h-4 mr-2" /> Source JV
                                    </Button>
                                </div>
                            </div>
                            <div className="mt-3 grid gap-2 text-xs md:grid-cols-4">
                                <div className="rounded-md bg-slate-50 p-2">
                                    <span className="block font-semibold uppercase text-slate-500">Where to find it</span>
                                    <span className="text-slate-800">Record &gt; Journal Voucher</span>
                                </div>
                                <div className="rounded-md bg-slate-50 p-2">
                                    <span className="block font-semibold uppercase text-slate-500">Who reviews next</span>
                                    <span className="text-slate-800">{voucher.reviewNext}</span>
                                </div>
                                <div className="rounded-md bg-slate-50 p-2">
                                    <span className="block font-semibold uppercase text-slate-500">GL / Trial Balance</span>
                                    <span className="text-slate-800">{voucher.impact}</span>
                                </div>
                                <div className="rounded-md bg-slate-50 p-2">
                                    <span className="block font-semibold uppercase text-slate-500">Next safe action</span>
                                    <span className="text-slate-800">{voucher.nextSafeAction}</span>
                                </div>
                            </div>
                        </div>
                    ))}
                    {openingBalanceVouchers.length > 5 && (
                        <div className="px-3 py-2 text-xs text-slate-500">
                            Showing the latest 5 of {openingBalanceVouchers.length}. Open the JV list to review older opening balance source vouchers.
                        </div>
                    )}
                </div>
            )}
        </div>
    );

    const InlineOpeningBalanceSetup = () => {
        if (!showOpeningBalanceForm) return null;
        return (
            <div className="space-y-3 border-l-4 border-blue-200 pl-4" data-testid="trial-inline-opening-balance-setup">
                <div className="rounded-lg bg-blue-50 border border-blue-100 px-4 py-3 text-sm text-blue-900">
                    <strong>Opening Balance Setup is open inside Trial Balance.</strong>{' '}
                    {isReadOnly
                        ? 'This review scope is read-only, so creation/posting actions are disabled.'
                        : 'Enter beginning balances here; MaceSuite will still create the source JV so GL and Trial Balance stay auditable.'}
                </div>
                <OpeningBalancesTab
                    selectedCourse={selectedCourse}
                    chartOfAccounts={chartOfAccounts}
                    onRefresh={onRefresh}
                    fmt={fmt}
                    isReadOnly={isReadOnly}
                    permissions={permissions}
                    user={user}
                    onNavigate={onNavigate}
                    onSetTrialBalanceDate={onAsOfDateChange}
                />
            </div>
        );
    };

    const TrialBalanceResultTrail = () => (
        <div className="rounded-lg border border-emerald-100 bg-emerald-50 p-4" data-testid="trial-balance-where-it-appears">
            <div className="grid gap-4 md:grid-cols-4">
                <div>
                    <p className="text-xs font-semibold uppercase text-emerald-700">What changed</p>
                    <p className="mt-1 text-sm text-slate-700">
                        {!selectedCourse
                            ? 'No course is selected, so posted balances cannot load yet. No voucher, GL entry, or report record was edited.'
                            : accounts.length > 0
                                ? `Trial Balance was refreshed as of ${reportDateLabel}. No voucher, GL entry, or report record was edited.`
                                : `Trial Balance was refreshed as of ${reportDateLabel}, and no posted balances were found for this scope. No voucher, GL entry, or report record was edited.`}
                    </p>
                </div>
                <div>
                    <p className="text-xs font-semibold uppercase text-emerald-700">Where to find it</p>
                    <p className="mt-1 text-sm text-slate-700">
                        This page is Review &gt; Trial Balance. Source details stay in General Ledger and the original JV, AR/AP, bank recon, or opening balance record.
                    </p>
                </div>
                <div>
                    <p className="text-xs font-semibold uppercase text-emerald-700">Report effect</p>
                    <p className="mt-1 text-sm text-slate-700">
                        Only posted GL entries dated on or before {reportDateLabel} are included. Drafts, submitted work, and returned work are evidence only.
                    </p>
                </div>
                <div>
                    <p className="text-xs font-semibold uppercase text-emerald-700">Next safe action</p>
                    <p className="mt-1 text-sm text-slate-700">
                        {!selectedCourse
                            ? 'Select a course first so the as-of balance can be checked against the right class scope.'
                            : accounts.length > 0
                                ? 'Use a row Trace GL button first. If a balance is still missing, check source status and posting date before posting corrections or creating an adjustment JV.'
                                : 'If you expected balances, check source status, posting date, selected course, student scope, and as-of date before creating any correction.'}
                    </p>
                </div>
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
                <Button type="button" variant="outline" size="sm" onClick={() => openTrialTrail('glentries', 'General Ledger')} data-testid="trial-balance-open-gl">
                    <FileText className="w-4 h-4 mr-2" /> General Ledger
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={() => openTrialTrail('balancesheet', 'Balance Sheet')} data-testid="trial-balance-open-balance-sheet">
                    <BarChart3 className="w-4 h-4 mr-2" /> Balance Sheet
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={() => openTrialTrail('incomestatement', 'Income Statement')} data-testid="trial-balance-open-income-statement">
                    <TrendingUp className="w-4 h-4 mr-2" /> Income Statement
                </Button>
            </div>
        </div>
    );

    if (accounts.length === 0) {
        return (
            <div className="space-y-4">
                <TrialBalanceTraceBanner />
                <TrialBalanceTrail />
                <DateFilter />
                <OpeningBalanceSetupPanel />
                <InlineOpeningBalanceSetup />
                <TrialBalanceResultTrail />
                <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
                    <Scale className="w-16 h-16 text-slate-200 mx-auto mb-4" />
                    <p className="text-slate-500">No trial balance data as of {reportDateLabel}</p>
                    <p className="text-sm text-slate-400 mt-1">Post journal vouchers or AR/AP documents dated on or before the selected date.</p>
                </div>
            </div>
        );
    }

    const isBalanced = Math.abs(totalDebit - totalCredit) < 0.01;

    return (
        <div className="space-y-4">
        <TrialBalanceTraceBanner />
        <TrialBalanceTrail />
        <DateFilter />
        <OpeningBalanceSetupPanel />
        <InlineOpeningBalanceSetup />
        <TrialBalanceResultTrail />
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="bg-gradient-to-r from-[#0F2A44] to-[#1E3A8A] px-6 py-4 text-white flex items-center justify-between">
                <div>
                    <h2 className="font-semibold font-['Manrope']">Trial Balance</h2>
                    <p className="text-white/70 text-sm">As of {reportDateLabel}</p>
                </div>
                <div className="flex items-center gap-2">
                    <Button
                        variant="ghost"
                        className="text-white hover:bg-white/20"
                        onClick={handleExportCSV}
                        data-testid="tb-export-csv"
                    >
                        <FileDown className="w-4 h-4 mr-2" /> Export CSV
                    </Button>
                    <Button
                        variant="ghost"
                        className="text-white hover:bg-white/20"
                        onClick={handlePrint}
                        data-testid="tb-print"
                    >
                        <Printer className="w-4 h-4 mr-2" /> Print
                    </Button>
                </div>
            </div>
            
            <div className="overflow-x-auto">
            <table className="w-full min-w-[720px]">
                <thead className="bg-slate-50 text-xs text-slate-500 uppercase">
                    <tr>
                        <th className="px-6 py-3 text-left w-28 whitespace-nowrap">Code</th>
                        <th className="px-6 py-3 text-left">Account</th>
                        <th className="px-6 py-3 text-right">Debit</th>
                        <th className="px-6 py-3 text-right">Credit</th>
                        <th className="px-6 py-3 text-right">Source Trail</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                    {accounts.map((a, i) => (
                        <tr key={i} className="hover:bg-slate-50">
                            <td className="px-6 py-3 font-mono text-sm text-slate-500 whitespace-nowrap">{a.account_code || '-'}</td>
                            <td className="px-6 py-3 font-medium">{a.account}</td>
                            <td className="px-6 py-3 text-right font-mono whitespace-nowrap">{a.debit > 0 ? fmt(a.debit) : ''}</td>
                            <td className="px-6 py-3 text-right font-mono whitespace-nowrap">{a.credit > 0 ? fmt(a.credit) : ''}</td>
                            <td className="px-6 py-3 text-right">
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={() => traceTrialBalanceAccount(a)}
                                    disabled={!a.account_code}
                                    className="h-8 px-3 text-xs"
                                    data-testid={`trial-balance-trace-gl-${i}`}
                                    aria-label={`Trace GL for ${a.account_code || ''} ${a.account || 'Trial Balance row'}`}
                                    title={a.account_code
                                        ? `Trace GL for ${a.account_code} - ${a.account}`
                                        : 'Account code is required before tracing this row'}
                                >
                                    <FileText className="w-3.5 h-3.5 mr-1" /> Trace GL
                                </Button>
                            </td>
                        </tr>
                    ))}
                </tbody>
                <tfoot>
                    <tr className="bg-[#0F2A44] text-white font-semibold">
                        <td className="px-6 py-3"></td>
                        <td className="px-6 py-3">Total</td>
                        <td className="px-6 py-3 text-right font-mono whitespace-nowrap">{fmt(totalDebit)}</td>
                        <td className="px-6 py-3 text-right font-mono whitespace-nowrap">{fmt(totalCredit)}</td>
                        <td className="px-6 py-3"></td>
                    </tr>
                </tfoot>
            </table>
            </div>
            
            <div className={`px-6 py-3 flex items-center gap-2 ${isBalanced ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
                {isBalanced ? (
                    <><CheckCircle2 className="w-5 h-5" /> Trial balance is in balance</>
                ) : (
                    <><AlertCircle className="w-5 h-5" /> Out of balance by {fmt(Math.abs(totalDebit - totalCredit))}</>
                )}
            </div>
        </div>
        </div>
    );
}

// ================= BALANCE SHEET TAB =================

function StatementsTab({ trialBalance, fmt }) {
    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                <div className="bg-emerald-600 px-6 py-4 text-white">
                    <h2 className="font-semibold font-['Manrope']">Income Statement</h2>
                    <p className="text-white/70 text-sm">For the period ending {new Date().toLocaleDateString()}</p>
                </div>
                <div className="p-6">
                    <p className="text-slate-500 text-center py-8">Generate from trial balance data</p>
                    <Button className="w-full bg-emerald-600 hover:bg-emerald-700 rounded-lg">
                        <FileSpreadsheet className="w-4 h-4 mr-2" /> Generate Income Statement
                    </Button>
                </div>
            </div>
            
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                <div className="bg-blue-600 px-6 py-4 text-white">
                    <h2 className="font-semibold font-['Manrope']">Balance Sheet</h2>
                    <p className="text-white/70 text-sm">As of {new Date().toLocaleDateString()}</p>
                </div>
                <div className="p-6">
                    <p className="text-slate-500 text-center py-8">Generate from trial balance data</p>
                    <Button className="w-full bg-blue-600 hover:bg-blue-700 rounded-lg">
                        <FileSpreadsheet className="w-4 h-4 mr-2" /> Generate Balance Sheet
                    </Button>
                </div>
            </div>
        </div>
    );
}

function AuditTrailTab({
    auditLogs,
    entries,
    fmt,
    user,
    viewingStudentName,
    isReadOnly,
    closeReadinessPeriod,
    onNavigate,
    onOpenBankReconPeriod,
}) {
    const actionColors = {
        CREATE: 'bg-blue-100 text-blue-700',
        POST: 'bg-emerald-100 text-emerald-700',
        UNPOST: 'bg-amber-100 text-amber-700',
        DELETE: 'bg-red-100 text-red-700',
        REVERSE: 'bg-purple-100 text-purple-700',
        RETURN: 'bg-orange-100 text-orange-700',
        APPROVE: 'bg-emerald-100 text-emerald-700',
        SUPERSEDE: 'bg-slate-100 text-slate-700',
        LOCK: 'bg-indigo-100 text-indigo-700',
        UNLOCK: 'bg-amber-100 text-amber-700',
        VIEW: 'bg-slate-100 text-slate-700',
        VOID: 'bg-red-100 text-red-700',
    };
    const scopeLabel = viewingStudentName || user?.full_name || 'Current workspace';
    const periodLabel = closeReadinessPeriod
        ? new Date(`${closeReadinessPeriod}-01T00:00:00`).toLocaleDateString('en-PH', { month: 'long', year: 'numeric' })
        : 'selected close month';
    const openBankReconEvidence = () => {
        if (onOpenBankReconPeriod) {
            onOpenBankReconPeriod(closeReadinessPeriod, 'Audit Trail');
            return;
        }
        onNavigate?.('bankrecon');
    };
    
    return (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-testid="audit-trail-tab">
            <div className="bg-slate-800 px-6 py-4 text-white flex items-center justify-between">
                <div>
                    <h2 className="font-semibold font-['Manrope'] flex items-center gap-2">
                        <History className="w-5 h-5" /> Audit Trail
                    </h2>
                    <p className="text-white/70 text-sm">Complete action history (read-only for compliance)</p>
                    {viewingStudentName && (
                        <p className="text-white/70 text-xs mt-1">Review scope: {viewingStudentName}</p>
                    )}
                </div>
            </div>

            <div className="p-6 border-b border-slate-100">
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-4" data-testid="audit-trail-guidance">
                    <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                        <div>
                            <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                                <History className="w-4 h-4 text-slate-600" />
                                Audit evidence trail
                            </div>
                            <p className="mt-1 text-sm text-slate-600">
                                Showing read-only audit history for {scopeLabel}. Use this to confirm who created, submitted, posted, returned, reversed, locked, reopened, or approved work.
                            </p>
                        </div>
                        <span className={`inline-flex w-fit rounded-full border px-3 py-1 text-xs font-semibold ${isReadOnly ? 'border-amber-200 bg-amber-50 text-amber-700' : 'border-slate-200 bg-white text-slate-700'}`}>
                            {isReadOnly ? 'Read-only review' : 'Own workspace'}
                        </span>
                    </div>
                    <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                        <div className="rounded-md border border-slate-100 bg-white p-3">
                            <p className="text-xs font-semibold uppercase text-slate-500">Where this appears</p>
                            <p className="mt-1 text-sm text-slate-800">Review &gt; Audit Trail. Related source evidence stays in JV, Bank Recon, General Ledger, and Trial Balance.</p>
                        </div>
                        <div className="rounded-md border border-slate-100 bg-white p-3">
                            <p className="text-xs font-semibold uppercase text-slate-500">Who reviews next</p>
                            <p className="mt-1 text-sm text-slate-800">Student resolves source work. Instructor/admin reviews evidence. Auditor reviews read-only.</p>
                        </div>
                        <div className="rounded-md border border-slate-100 bg-white p-3">
                            <p className="text-xs font-semibold uppercase text-slate-500">GL / Report impact</p>
                            <p className="mt-1 text-sm text-slate-800">Opening Audit Trail does not change GL, Trial Balance, reports, source records, or close status.</p>
                        </div>
                        <div className="rounded-md border border-slate-100 bg-white p-3">
                            <p className="text-xs font-semibold uppercase text-slate-500">Next safe action</p>
                            <p className="mt-1 text-sm text-slate-800">Trace timestamps and entity refs, then open Bank Recon, General Ledger, or Trial Balance for posted evidence.</p>
                        </div>
                    </div>
                    <div className="mt-4 flex flex-wrap gap-2">
                        <Button type="button" variant="outline" size="sm" onClick={openBankReconEvidence} data-testid="audit-trail-open-bank-recon">
                            <Building2 className="w-4 h-4 mr-2" /> Bank Recon Evidence
                        </Button>
                        <Button type="button" variant="outline" size="sm" onClick={() => onNavigate?.('glentries')} data-testid="audit-trail-open-gl">
                            <BookOpen className="w-4 h-4 mr-2" /> General Ledger
                        </Button>
                        <Button type="button" variant="outline" size="sm" onClick={() => onNavigate?.('trial')} data-testid="audit-trail-open-trial">
                            <Scale className="w-4 h-4 mr-2" /> Trial Balance
                        </Button>
                        <span className="text-xs text-slate-500 self-center">
                            Close review month: {periodLabel}
                        </span>
                    </div>
                </div>
            </div>
            
            {auditLogs.length === 0 ? (
                <div className="p-12 text-center">
                    <History className="w-16 h-16 text-slate-200 mx-auto mb-4" />
                    <p className="text-slate-500">No audit trail records yet</p>
                    <p className="text-sm text-slate-400 mt-1">Actions will be logged here automatically. If this is a selected-student review, confirm the student and course scope at the top of the workspace.</p>
                </div>
            ) : (
                <div className="divide-y divide-slate-100">
                    {auditLogs.map((log, i) => (
                        <div key={i} className="px-6 py-4 hover:bg-slate-50">
                            <div className="flex items-start justify-between gap-4">
                                <div className="flex items-start gap-3">
                                    <span className={`px-2 py-1 rounded text-xs font-medium ${actionColors[log.action] || 'bg-slate-100 text-slate-700'}`}>
                                        {log.action}
                                    </span>
                                    <div>
                                        <span className="text-sm font-medium text-slate-900">{log.details}</span>
                                        <p className="text-xs text-slate-500 mt-1">
                                            By: {log.user_name} | Entity: {log.entity_type} | Ref: {log.entity_id || 'N/A'}
                                        </p>
                                        {(log.student_id || log.metadata?.student_id || log.course_id || log.metadata?.course_id) && (
                                            <p className="text-[11px] text-slate-400 mt-1">
                                                Student scope: {log.student_id || log.metadata?.student_id || 'N/A'} | Course: {log.course_id || log.metadata?.course_id || 'N/A'}
                                            </p>
                                        )}
                                    </div>
                                </div>
                                <div className="text-right flex-shrink-0">
                                    <span className="text-xs text-slate-500">{new Date(log.timestamp).toLocaleString()}</span>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function HelpContent({ activeTab, currentStep }) {
    const tips = {
        jv: [
            { step: 1, title: 'Fill Header Fields', desc: 'Enter Date, Reference (optional), and Particulars (transaction description).' },
            { step: 2, title: 'Add Detail Lines', desc: 'Click "Add Details" to add each account line. Select Account Code, enter Debit OR Credit amount.' },
            { step: 3, title: 'Balance Your Entry', desc: 'Total Debits must equal Total Credits. The system will show a balance indicator.' },
            { step: 4, title: 'Save & Post', desc: 'Save when balanced. Then Post to record in GL, TB, and Reports.' },
        ],
        journal: [
            { step: 1, title: 'Enter Transaction Date', desc: 'Select the date when the transaction occurred. Use the actual business date, not today\'s date.' },
            { step: 2, title: 'Describe the Transaction', desc: 'Write a clear description. Example: "Received cash from Customer ABC for services rendered"' },
            { step: 3, title: 'Select Accounts', desc: 'Choose which accounts to debit and credit. Remember: Debits increase assets/expenses, Credits increase liabilities/revenue.' },
            { step: 4, title: 'Review & Post', desc: 'Double-check that debits equal credits before posting. You cannot edit posted entries.' },
        ],
        ledger: [
            { step: 1, title: 'Understanding the Ledger', desc: 'The general ledger shows all transactions grouped by account.' },
            { step: 2, title: 'Account Balances', desc: 'Each account shows running balances calculated from debits and credits.' },
        ],
        trial: [
            { step: 1, title: 'Trial Balance Check', desc: 'Total debits must equal total credits. If not, there\'s an error in your entries.' },
            { step: 2, title: 'Account Classification', desc: 'Assets and expenses have debit balances. Liabilities, equity, and revenue have credit balances.' },
        ],
        incomestatement: [
            { step: 1, title: 'Set the Reporting Period', desc: 'Choose the month, year, or date range you need. Only posted GL entries dated inside that period are included.' },
            { step: 2, title: 'Review Revenue', desc: 'Revenue is calculated as credits less debits for revenue and income accounts.' },
            { step: 3, title: 'Review Expenses', desc: 'Expenses are calculated as debits less credits for expense accounts.' },
            { step: 4, title: 'Confirm Net Income', desc: 'Net Income equals Total Revenue less Total Expenses. Trace unexpected balances to the General Ledger using the same student and date range.' },
        ],
        openingbalances: [
            { step: 1, title: 'Set Opening Date', desc: 'Choose the as-of date for the beginning balances and keep a clear reference.' },
            { step: 2, title: 'Enter Account Balances', desc: 'Enter each opening amount as a debit or credit based on the normal account balance.' },
            { step: 3, title: 'Balance The Worksheet', desc: 'Total debits and credits must match before the opening JV can be saved.' },
            { step: 4, title: 'Save Opening JV', desc: 'Save creates a balanced saved Journal Voucher. It does not post automatically.' },
        ],
    };

    const currentTips = tips[activeTab] || tips.jv;
    const quickTips = {
        jv: 'Each line must have either Debit OR Credit, not both. Total Debits must equal Total Credits to Save.',
        openingbalances: 'Opening Balance Setup is a guided beginning-balance JV creator. Reports are not affected until the JV is posted.',
        trial: 'Trial Balance reads posted GL entries only. Use Opening Balance Setup when the report needs beginning-balance source JVs.',
        ledger: 'Use the ledger views to trace how posted vouchers affect each account balance.',
        incomestatement: 'Income Statement is read-only reporting. It includes posted GL activity inside the selected period; processing, printing, and exporting do not change source records.',
    };
    const shortcutRows = activeTab === 'openingbalances'
        ? [
            ['Clear Worksheet', 'Button'],
            ['Save Opening JV', 'Button'],
        ]
        : activeTab === 'incomestatement'
            ? [
                ['Process', 'Generate report'],
                ['Trace', 'Open GL evidence'],
                ['Preview / Print', 'After Process'],
                ['Export PDF', 'After Process'],
            ]
            : [
                ['Save Draft', 'Ctrl+S'],
                ['Post Entry', 'Ctrl+Enter'],
                ['AI Suggest', 'Ctrl+I'],
                ['Clear Form', 'Esc'],
            ];

    return (
        <div className="space-y-4">
            <div className="bg-[#E0F2FE] rounded-lg p-3 border border-[#2F8FCB]/20">
                <h4 className="font-medium text-[#0F2A44] text-sm">Quick Tip</h4>
                <p className="text-xs text-slate-600 mt-1">
                    {quickTips[activeTab] || 'Use each module action button to move through the accounting workflow in order.'}
                </p>
            </div>

            <div>
                <h4 className="font-medium text-slate-900 text-sm mb-3">Steps Checklist</h4>
                <div className="space-y-3">
                    {currentTips.map((tip, i) => (
                        <div key={i} className={`p-3 rounded-lg border ${currentStep >= tip.step ? 'border-emerald-200 bg-emerald-50' : 'border-slate-200 bg-white'}`}>
                            <div className="flex items-start gap-2">
                                <div className={`w-5 h-5 rounded-full flex items-center justify-center text-xs flex-shrink-0 ${
                                    currentStep >= tip.step ? 'bg-emerald-500 text-white' : 'bg-slate-200 text-slate-500'
                                }`}>
                                    {currentStep > tip.step ? <CheckCircle2 className="w-3 h-3" /> : tip.step}
                                </div>
                                <div>
                                    <h5 className="text-sm font-medium text-slate-900">{tip.title}</h5>
                                    <p className="text-xs text-slate-500 mt-0.5">{tip.desc}</p>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            <div className="border-t border-slate-200 pt-4">
                <h4 className="font-medium text-slate-900 text-sm mb-2">
                    {['openingbalances', 'incomestatement'].includes(activeTab) ? 'Key Actions' : 'Keyboard Shortcuts'}
                </h4>
                <div className="space-y-1 text-xs">
                    {shortcutRows.map(([label, value]) => (
                        <div key={label} className="flex justify-between gap-3">
                            <span className="text-slate-500">{label}</span>
                            <kbd className="px-1.5 py-0.5 bg-slate-100 rounded text-slate-600">{value}</kbd>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}
